import { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = "https://ourmediationcase.com/api";
const STRIPE_PK = null; // Loaded from server in ClientPortal

const getToken = () => {
  try { return JSON.parse(localStorage.getItem("crm_user"))?.token || ""; }
  catch { return ""; }
};

const handleAuthError = (res) => {
  if (res && (res.error === "Unauthorized — invalid or expired session" || res.error === "Unauthorized — no token")) {
    localStorage.removeItem("crm_user");
  }
  return res;
};

const api = {
  get: (endpoint) => fetch(`${API_BASE}/${endpoint}`, {
    cache: "no-store",
    headers: { "X-Auth-Token": getToken() }
  }).then(r => r.json()).then(handleAuthError),

  post: (endpoint, data) => fetch(`${API_BASE}/${endpoint}`, {
    method: "POST",
    cache: "no-store",
    headers: { "Content-Type": "application/json", "X-Auth-Token": getToken() },
    body: JSON.stringify(data)
  }).then(r => r.json()).then(handleAuthError),

  put: (endpoint, data) => fetch(`${API_BASE}/${endpoint}`, {
    method: "PUT",
    cache: "no-store",
    headers: { "Content-Type": "application/json", "X-Auth-Token": getToken() },
    body: JSON.stringify(data)
  }).then(r => r.json()).then(handleAuthError),

  delete: (endpoint) => fetch(`${API_BASE}/${endpoint}`, {
    method: "DELETE",
    cache: "no-store",
    headers: { "Content-Type": "application/json", "X-Auth-Token": getToken() },
    body: JSON.stringify({ _method: "DELETE" })
  }).then(r => r.json()).then(handleAuthError),
};

// ─── RESPONSIVE CSS (shared by ClientPortal and MediatorApp) ─────────────────
const MOBILE_CSS = `
  @media (max-width: 767px) {
    /* ── Shared layout utilities ── */
    .r-sidebar { display: none !important; }
    .r-bottom-nav {
      display: flex !important;
      position: fixed; bottom: 0; left: 0; right: 0;
      background: #1B2B4B;
      border-top: 1px solid rgba(255,255,255,0.1);
      z-index: 200;
      overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none;
    }
    .r-bottom-nav::-webkit-scrollbar { display: none; }
    .r-bottom-nav button {
      display: flex !important; flex-direction: column; align-items: center; gap: 2px;
      padding: 8px 10px; min-width: 52px; flex-shrink: 0;
      font-size: 9px; font-weight: 600; letter-spacing: 0.02em;
      color: rgba(255,255,255,0.45); border-radius: 0; border: none; background: none; cursor: pointer;
      font-family: inherit;
    }
    .r-bottom-nav button.active { color: #fff; background: rgba(255,255,255,0.12); }
    .r-bottom-nav .r-nav-icon { font-size: 19px; line-height: 1; }
    .r-main { padding-bottom: 70px !important; }

    /* ── Page padding ── */
    .r-page { padding: 16px 14px !important; }

    /* ── Grid utilities ── */
    .r-grid2 { grid-template-columns: 1fr !important; }
    .r-grid4 { grid-template-columns: 1fr 1fr !important; }

    /* ── Case detail ── */
    .r-case-header { flex-direction: column !important; align-items: flex-start !important; }
    .r-case-actions { flex-wrap: wrap; width: 100%; }
    .r-case-actions button { flex: 1; min-width: 80px; }
    .r-case-stats { flex-wrap: wrap; gap: 16px !important; }
    .r-case-tabs { overflow-x: auto !important; scrollbar-width: none; }
    .r-case-tabs::-webkit-scrollbar { display: none; }
    .r-case-tabs button { white-space: nowrap; flex-shrink: 0; padding: 8px 10px !important; font-size: 12px !important; }

    /* ── Row ── */
    .r-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
    .r-row-end { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }

    /* ── Modal ── */
    .r-modal {
      border-radius: 16px 16px 0 0 !important;
      max-width: 100% !important;
      width: 100% !important;
      max-height: 92vh !important;
      margin: 0 !important;
      align-self: flex-end;
    }

    /* ── Login ── */
    .r-login-card { padding: 28px 20px !important; }

    /* ── Calendar scroll ── */
    .r-cal-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .r-cal-wrap > * { min-width: 340px; }

    /* ── Tables ── */
    .r-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .r-table-wrap table { min-width: 480px; font-size: 12px; }
  }
`;



const statusColor = (s) => {
  if (s === "Active") return { bg: "#E6F4F2", text: "#2D7D72" };
  if (s === "Resolved") return { bg: "#EAF4EA", text: "#2E7D32" };
  if (s === "On Hold") return { bg: "#FDF3E7", text: "#C8873A" };
  return { bg: "#F0F0F0", text: "#555" };
};

const AgreementHeader = ({ header }) => {
  if (!header || (!header.logo_url && !header.practice_name)) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "20px 0 20px", borderBottom: "2px solid #1B2B4B", marginBottom: 24 }}>
      {header.logo_url && (
        <img src={header.logo_url} alt="Logo" style={{ height: 64, objectFit: "contain", flexShrink: 0 }} />
      )}
      <div style={{ flex: 1 }}>
        {header.practice_name && <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, color: "#1B2B4B", lineHeight: 1.2 }}>{header.practice_name}</div>}
        {header.tagline && <div style={{ fontSize: 12, color: "#9B9490", marginTop: 2 }}>{header.tagline}</div>}
        <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
          {header.address && <span style={{ fontSize: 11, color: "#4A5568" }}>📍 {header.address}</span>}
          {header.phone && <span style={{ fontSize: 11, color: "#4A5568" }}>📞 {header.phone}</span>}
          {header.email && <span style={{ fontSize: 11, color: "#4A5568" }}>✉ {header.email}</span>}
          {header.website && <span style={{ fontSize: 11, color: "#4A5568" }}>🌐 {header.website}</span>}
        </div>
      </div>
    </div>
  );
};

const Badge = ({ label, color }) => (
  <span style={{ background: color.bg, color: color.text, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>{label}</span>
);

const ProgressBar = ({ value }) => (
  <div style={{ background: "#E2DDD5", borderRadius: 99, height: 6, width: "100%", overflow: "hidden" }}>
    <div style={{ width: `${value}%`, height: "100%", borderRadius: 99, background: value === 100 ? "#2E7D32" : "#2D7D72", transition: "width 0.6s ease" }} />
  </div>
);

const Stat = ({ label, value, sub, accent }) => (
  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 22px" }}>
    <div style={{ fontSize: 11, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 800, color: accent || "#1B2B4B", fontFamily: "'Playfair Display', serif" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#9B9490", marginTop: 4 }}>{sub}</div>}
  </div>
);

const Input = ({ label, value, onChange, type = "text", placeholder, full }) => (
  <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
    {label && <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>{label}</div>}
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || label}
      style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 7, padding: "8px 12px", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div>
    {label && <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>{label}</div>}
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 7, padding: "8px 12px", fontSize: 13, outline: "none", fontFamily: "inherit", background: "#fff" }}>
      {options.map(o => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Modal = ({ title, onClose, children, wide }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="r-modal" style={{ background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: wide ? 680 : 560, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>{title}</div>
        <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Msg = ({ msg }) => msg ? (
  <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 13, background: msg.type === "success" ? "#EAF4EA" : "#FDEEEC", color: msg.type === "success" ? "#2E7D32" : "#C0392B", marginBottom: 16 }}>{msg.text}</div>
) : null;

// ─── STAFF REGISTER PAGE ───────────────────────────────────────────────────────
function StaffRegisterPage({ token, onSuccess }) {
  const [status, setStatus] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/staff_register.php?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(res => { if (res.valid) { setInvite(res); setStatus("form"); } else { setError(res.error || "Invalid invite."); setStatus("error"); } })
      .catch(() => { setError("Could not connect to server."); setStatus("error"); });
  }, []);

  const handleRegister = async () => {
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setError(""); setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/staff_register.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name, password })
      }).then(r => r.json());
      if (res.success) { setStatus("done"); setTimeout(() => { window.location.href = "https://ourmediationcase.com/"; }, 2000); }
      else { setError(res.error || "Registration failed."); }
    } catch { setError("Could not connect to server."); }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1B2B4B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }`}</style>
      <div style={{ background: "#fff", borderRadius: 16, padding: "44px 40px", width: "100%", maxWidth: 440, boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: "#1B2B4B" }}>Our<span style={{ color: "#2D7D72" }}>Mediation</span>Case</div>
          <div style={{ fontSize: 13, color: "#9B9490", marginTop: 6 }}>Staff Account Setup</div>
        </div>
        {status === "loading" && <div style={{ textAlign: "center", color: "#9B9490" }}>Loading…</div>}
        {status === "error" && <div style={{ padding: "14px 18px", background: "#FDEEEC", borderRadius: 10, color: "#C0392B", fontSize: 13, textAlign: "center" }}>{error}</div>}
        {status === "done" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Account created!</div>
            <div style={{ fontSize: 13, color: "#9B9490" }}>Redirecting you to login…</div>
          </div>
        )}
        {status === "form" && (
          <>
            {invite && (
              <div style={{ background: "#E6F4F2", borderRadius: 10, padding: "12px 16px", marginBottom: 24, fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: "#2D7D72", marginBottom: 4 }}>Staff Invitation</div>
                <div style={{ color: "#4A5568" }}>Role: <strong>{invite.role}</strong></div>
                <div style={{ color: "#9B9490", fontSize: 12, marginTop: 4 }}>Email: {invite.email}</div>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
              <Input label="Your Full Name" value={name} onChange={setName} />
              <Input label="Create Password" value={password} onChange={setPassword} type="password" />
              <Input label="Confirm Password" value={confirm} onChange={setConfirm} type="password" />
            </div>
            {error && <div style={{ marginBottom: 14, padding: "10px 14px", background: "#FDEEEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{error}</div>}
            <button onClick={handleRegister} disabled={submitting} style={{ width: "100%", padding: 12, background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Creating account…" : "Create Account"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CLIENT REGISTER PAGE ──────────────────────────────────────────────────────
function RegisterPage({ token }) {
  const [status, setStatus] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/register.php?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(res => {
        if (res.valid) { setInvite(res); setStatus("form"); }
        else { setError(res.error || "Invalid invite link."); setStatus("error"); }
      })
      .catch(() => { setError("Could not connect to server."); setStatus("error"); });
  }, []);

  const handleRegister = async () => {
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setError(""); setSubmitting(true);
    try {
      const rawRes = await fetch(`${API_BASE}/register.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name, password })
      });
      const text = await rawRes.text();
      let res;
      try { res = JSON.parse(text); } catch { setError("Server error: " + text.substring(0, 200)); setSubmitting(false); return; }
      if (res.success) { setStatus("done"); setTimeout(() => { window.location.href = "https://ourmediationcase.com/"; }, 2000); }
      else { setError(res.error || "Registration failed."); }
    } catch(e) { setError("Network error: " + e.message); }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1B2B4B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }`}</style>
      <div style={{ background: "#fff", borderRadius: 16, padding: "44px 40px", width: "100%", maxWidth: 440, boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: "#1B2B4B" }}>Our<span style={{ color: "#2D7D72" }}>Mediation</span>Case</div>
          <div style={{ fontSize: 13, color: "#9B9490", marginTop: 6 }}>Client Portal Registration</div>
        </div>
        {status === "loading" && <div style={{ textAlign: "center", color: "#9B9490" }}>Loading…</div>}
        {status === "error" && <div style={{ padding: "14px 18px", background: "#FDEEEC", borderRadius: 10, color: "#C0392B", fontSize: 13, textAlign: "center" }}>{error}</div>}
        {status === "done" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Account created!</div>
            <div style={{ fontSize: 13, color: "#9B9490" }}>Redirecting you to login…</div>
          </div>
        )}
        {status === "form" && invite && (
          <>
            <div style={{ background: "#E6F4F2", borderRadius: 10, padding: "12px 16px", marginBottom: 24, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: "#2D7D72", marginBottom: 4 }}>You've been invited!</div>
              <div style={{ color: "#4A5568" }}>Case: <strong>{invite.client1_name} &amp; {invite.client2_name}</strong></div>
              <div style={{ color: "#4A5568" }}>Role: <strong>{invite.case_role}</strong></div>
              <div style={{ color: "#9B9490", fontSize: 12, marginTop: 4 }}>Email: {invite.email}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
              <Input label="Your Full Name" value={name} onChange={setName} />
              <Input label="Create Password" value={password} onChange={setPassword} type="password" />
              <Input label="Confirm Password" value={confirm} onChange={setConfirm} type="password" />
            </div>
            {error && <div style={{ marginBottom: 14, padding: "10px 14px", background: "#FDEEEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{error}</div>}
            <button onClick={handleRegister} disabled={submitting} style={{ width: "100%", padding: 12, background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Creating account…" : "Create Account"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // login | forgot | forgot_done

  const [resetEmail, setResetEmail] = useState("");
  const [resetMsg, setResetMsg] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.post("auth.php", { action: "login", email, password });
      if (res.success) { localStorage.setItem("crm_user", JSON.stringify({...res.user, token: res.token})); onLogin({...res.user, token: res.token}); }
      else setError(res.error || "Invalid email or password");
    } catch { setError("Could not connect to server."); }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) { setResetMsg({ type: "error", text: "Please enter your email address." }); return; }
    setResetLoading(true); setResetMsg(null);
    try {
      const res = await fetch(`${API_BASE}/forgot_password.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail })
      }).then(r => r.json());
      if (res.success) { setView("forgot_done"); }
      else { setResetMsg({ type: "error", text: res.error || "Something went wrong." }); }
    } catch { setResetMsg({ type: "error", text: "Could not connect to server." }); }
    setResetLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1B2B4B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "16px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }` + MOBILE_CSS}</style>
      <div className="r-login-card" style={{ background: "#fff", borderRadius: 16, padding: "44px 40px", width: "100%", maxWidth: 420, boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, color: "#1B2B4B" }}>Our<span style={{ color: "#2D7D72" }}>Mediation</span>Case</div>
          <div style={{ fontSize: 13, color: "#9B9490", marginTop: 6 }}>
            {view === "login" ? "Sign in to your account" : view === "forgot" ? "Reset your password" : "Check your email"}
          </div>
        </div>

        {view === "login" && (<>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Input label="Email Address" value={email} onChange={setEmail} type="email" />
            <Input label="Password" value={password} onChange={setPassword} type="password" />
          </div>
          {error && <Msg msg={{ type: "error", text: error }} />}
          <button onClick={handleLogin} disabled={loading}
            style={{ marginTop: 20, width: "100%", padding: 12, background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
            <button onClick={() => { setView("forgot"); setResetMsg(null); setResetEmail(""); }} style={{ fontSize: 13, color: "#2D7D72", fontWeight: 600 }}>Forgot your password?</button>
            <button onClick={() => alert("Please email support@lakeshoremediation.com for assistance.")} style={{ fontSize: 13, color: "#9B9490" }}>Forgot your email?</button>
          </div>
        </>)}

        {view === "forgot" && (<>
          <div style={{ fontSize: 13, color: "#4A5568", marginBottom: 20, lineHeight: 1.6 }}>Enter your email address and we'll send you a link to reset your password.</div>
          <Input label="Email Address" value={resetEmail} onChange={setResetEmail} type="email" />
          {resetMsg && <Msg msg={resetMsg} />}
          <button onClick={handleForgotPassword} disabled={resetLoading}
            style={{ marginTop: 16, width: "100%", padding: 12, background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: resetLoading ? 0.7 : 1 }}>
            {resetLoading ? "Sending…" : "Send Reset Link"}
          </button>
          <button onClick={() => setView("login")} style={{ marginTop: 12, width: "100%", fontSize: 13, color: "#9B9490", textAlign: "center" }}>← Back to Sign In</button>
        </>)}

        {view === "forgot_done" && (<>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>📧</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: "#1B2B4B" }}>Check your inbox</div>
            <div style={{ fontSize: 13, color: "#9B9490", lineHeight: 1.7, marginBottom: 24 }}>If an account exists for <strong>{resetEmail}</strong>, a password reset link has been sent. The link expires in 1 hour.</div>
            <button onClick={() => setView("login")} style={{ fontSize: 13, color: "#2D7D72", fontWeight: 600 }}>← Back to Sign In</button>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─── CLIENT PORTAL ─────────────────────────────────────────────────────────────
function ClientPortal({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [intakeMsg, setIntakeMsg] = useState(null);
  const [clientTasks, setClientTasks] = useState([]);
  const [clientSigDocs, setClientSigDocs] = useState([]);
  const [clientSigningDoc, setClientSigningDoc] = useState(null);
  const [clientNotifSettings, setClientNotifSettings] = useState({ new_session: 1, session_reminder: 1, new_task: 1, task_complete: 1, signature_request: 1, document_signed: 1, new_document: 1, invite_sent: 1 });
  const [clientNotifMsg, setClientNotifMsg] = useState(null);
  const [taskFileMsg, setTaskFileMsg] = useState(null);
  const [intakeData, setIntakeData] = useState({ full_name: "", dob: "", phone: "", alt_phone: "", email: user.email || "", address: "", city: "", state: "", zip: "", employer: "", occupation: "", income: "", goals: "", concerns: "", prior_mediation: "No", attorney: "" });
  const [intakeSubmitted, setIntakeSubmitted] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [checkinForm, setCheckinForm] = useState({ stress_level: 5, coparenting_score: 5, readiness_score: 5, sleep_quality: 3, overall_mood: 5, notes: "", flag_for_mediator: false });
  const [checkinMsg, setCheckinMsg] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  // Intake step flow
  const [intakeStep, setIntakeStep] = useState(0); // 0=welcome, 1=personal, 2=agreements, 3=payment, 4=done
  const [initials, setInitials] = useState({}); // keyed by index of {{initial}} token
  const [clientAgreements, setClientAgreements] = useState([]);
  const [currentAgreementIdx, setCurrentAgreementIdx] = useState(0);
  const [sigMode, setSigMode] = useState("typed"); // typed or drawn
  const [typedSig, setTypedSig] = useState("");
  const [drawnSig, setDrawnSig] = useState(null);
  const [signingMsg, setSigningMsg] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const stripeInstanceRef = useRef(null);
  const stripeCardRef = useRef(null);
  const stripeClientSecretRef = useRef(null);
  const [cardMsg, setCardMsg] = useState(null);
  const [cardOnFile, setCardOnFile] = useState(null);
  const [saveForFuture, setSaveForFuture] = useState(true);
  const [preferredPayment, setPreferredPayment] = useState("card"); // card, cash, check
  const [paymentType, setPaymentType] = useState("card"); // card or ach — the actual payment method being set up
  const [agreementHeader, setAgreementHeader] = useState({ logo_url: "", practice_name: "Lakeshore Mediation", tagline: "", address: "", phone: "", email: "", website: "" });

  const loadData = useCallback(async () => {
    try {
      const res = await api.get(`client_data.php?user_id=${user.id}&section=all`);
      setData(res);
      if (res.intake) { setIntakeData(d => ({ ...d, ...res.intake })); if (res.intake.status === "Complete") setIntakeSubmitted(true); }
      if (res.case) {
        const [tasks, sigdocs, ns, ags, card] = await Promise.all([
          api.get(`tasks.php?case_id=${res.case.id}&role=client&case_role=${encodeURIComponent(user.case_role || "Client 1")}`),
          api.get(`signatures.php?case_id=${res.case.id}`),
          api.get(`notifications.php?user_id=${user.id}`),
          api.get(`agreements.php?action=get_for_client&case_id=${res.case.id}&user_id=${user.id}`).catch(() => []),
          api.post("stripe.php", { action: "get_card", case_id: res.case.id }).catch(() => null),
        ]);
        setClientTasks(Array.isArray(tasks) ? tasks : []);
        setClientSigDocs(Array.isArray(sigdocs) ? sigdocs : []);
        if (ns) setClientNotifSettings(ns);
        setClientAgreements(Array.isArray(ags) ? ags : []);
        if (card) setCardOnFile(card);
        // Load agreement header
        const hdr = await api.get("agreements.php?action=get_header").catch(() => null);
        if (hdr && hdr.practice_name) setAgreementHeader(hdr);
        // Load today's check-in and client notes
        const [tc, notes] = await Promise.all([
          api.get(`checkins.php?action=get_today_checkin&case_id=${res.case.id}&user_id=${user.id}`).catch(() => null),
          api.get(`checkins.php?action=get_client_notes&case_id=${res.case.id}&user_id=${user.id}`).catch(() => []),
        ]);
        if (tc) { setTodayCheckin(tc); setCheckinForm({ stress_level: tc.stress_level || 5, coparenting_score: tc.coparenting_score || 5, readiness_score: tc.readiness_score || 5, sleep_quality: tc.sleep_quality || 3, overall_mood: tc.overall_mood || 5, notes: tc.notes || "", flag_for_mediator: !!tc.flag_for_mediator }); }
        setClientNotes(Array.isArray(notes) ? notes : []);
        // Determine if intake needs to be completed
        const unsignedAgs = (Array.isArray(ags) ? ags : []).filter(a => !a.signed_at);
        const hasCard = card?.has_card;
        if (!res.intake || !res.intake.full_name) setIntakeStep(1);
        else if (unsignedAgs.length > 0) { setIntakeStep(2); setCurrentAgreementIdx(0); }
        else if (!hasCard) setIntakeStep(3);
        else setIntakeStep(4);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [user.id, user.case_role]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setInitials({}); setTypedSig(""); setDrawnSig(null); }, [currentAgreementIdx]);

  // Load Stripe when on payment step
  useEffect(() => {
    if (intakeStep !== 3) return;
    let mounted = true;
    stripeClientSecretRef.current = null;
    stripeInstanceRef.current = null;
    stripeCardRef.current = null;
    setStripeReady(false);

    const run = async () => {
      const setupRes = await api.post("stripe_setup.php", { action: "create_setup", case_id: data?.case?.id, email: user.email, name: user.name, payment_type: paymentType });
      if (!setupRes?.success || !mounted) { setCardMsg({ type: "error", text: setupRes?.error || "Failed to initialize payment form." }); return; }
      stripeClientSecretRef.current = setupRes.client_secret;

      const configRes = await fetch(`${API_BASE}/config_public.php`).then(r => r.json()).catch(() => null);
      const stripePk = configRes?.stripe_pk;
      if (!stripePk || !mounted) return;

      const loadAndInit = () => {
        const stripe = window.Stripe(stripePk);
        stripeInstanceRef.current = stripe;

        if (paymentType === 'card') {
          const elements = stripe.elements();
          const card = elements.create("card", { style: { base: { fontSize: "16px", color: "#1B2B4B", fontFamily: "'DM Sans', sans-serif" } } });
          stripeCardRef.current = card;
          card.on("ready", () => { if (mounted) setStripeReady(true); });
          card.mount("#stripe-card-element");
        } else {
          // ACH — ready immediately, uses collectBankAccountForSetup
          if (mounted) setStripeReady(true);
        }
      };

      if (!window.Stripe) {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/";
        script.onload = loadAndInit;
        document.head.appendChild(script);
      } else {
        loadAndInit();
      }
    };
    run();
    return () => { mounted = false; };
  }, [intakeStep, paymentType]);

  const uploadDocument = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file); formData.append("case_id", data.case.id);
    formData.append("uploaded_by", user.id); formData.append("uploader_name", user.name); formData.append("uploader_role", "client");
    try {
      const res = await fetch(`${API_BASE}/client_upload.php`, { method: "POST", body: formData, headers: { "X-Auth-Token": getToken() } });
      const result = await res.json();
      if (result.success) { setUploadMsg({ type: "success", text: "Document uploaded!" }); loadData(); }
      else setUploadMsg({ type: "error", text: result.error });
    } catch { setUploadMsg({ type: "error", text: "Upload failed." }); }
    setTimeout(() => setUploadMsg(null), 4000);
  };

  const saveIntake = async () => {
    const res = await api.post(`client_data.php?user_id=${user.id}`, { action: "save_intake", intake: intakeData });
    if (res.success) { setIntakeMsg({ type: "success", text: "Intake form saved!" }); setIntakeSubmitted(true); loadData(); }
    else setIntakeMsg({ type: "error", text: "Failed to save." });
    setTimeout(() => setIntakeMsg(null), 4000);
  };

  const clientCompleteTask = async (taskId) => {
    await api.post("tasks.php", { action: "client_complete", task_id: taskId, completed_by: user.name });
    loadData();
  };

  const uploadTaskFileClient = async (file, taskId, caseId) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("task_file", file);
    formData.append("task_id", taskId);
    formData.append("case_id", caseId);
    formData.append("uploaded_by", user.id);
    formData.append("uploader_name", user.name);
    try {
      const res = await fetch(`${API_BASE}/tasks.php`, { method: "POST", body: formData, headers: { "X-Auth-Token": getToken() } });
      const result = await res.json();
      if (result.success) { setTaskFileMsg({ type: "success", text: "File uploaded!" }); loadData(); }
      else setTaskFileMsg({ type: "error", text: result.error });
    } catch { setTaskFileMsg({ type: "error", text: "Upload failed." }); }
    setTimeout(() => setTaskFileMsg(null), 4000);
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'DM Sans', sans-serif", color: "#9B9490" }}>Loading your portal…</div>;
  if (!data?.case) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'DM Sans', sans-serif", color: "#9B9490", gap: 16 }}>
      <div>No case found for your account.</div>
      <button onClick={onLogout} style={{ padding: "8px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, color: "#4A5568", background: "#fff", cursor: "pointer" }}>Sign Out</button>
    </div>
  );

  const { case: caseData, sessions = [], parenting, intake, invoices = [], documents = [] } = data || {};

  // ── INTAKE STEP FLOW ──────────────────────────────────────────────────────
  const unsignedAgreements = clientAgreements.filter(a => !a.signed_at);
  if (intakeStep > 0 && intakeStep < 4) {
    const currentAg = unsignedAgreements[currentAgreementIdx];
    const totalSteps = 3 + (unsignedAgreements.length > 0 ? 1 : 0) + (cardOnFile?.has_card ? 0 : 1);
    const stepLabels = ["Personal Info", ...(unsignedAgreements.length > 0 ? ["Agreements"] : []), ...(!cardOnFile?.has_card ? ["Payment"] : []), "Done"];

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDrawnSig(null);
    };

    const startDraw = (e) => {
      setIsDrawing(true);
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      const ctx = canvas.getContext("2d");
      ctx.beginPath(); ctx.moveTo(x, y);
    };
    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      const ctx = canvas.getContext("2d");
      ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1B2B4B";
      ctx.lineTo(x, y); ctx.stroke();
    };
    const endDraw = () => {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) setDrawnSig(canvas.toDataURL());
    };

    const signCurrentAgreement = async () => {
      // Check all initials are filled
      const tokenCount = ((currentAg.body || "").match(/\{\{initial\}\}|<span[^>]*class="initial-token"[^>]*>/gi) || []).length;
      const filledCount = Object.values(initials).filter(v => typeof v === "string" && v.trim()).length;
      if (tokenCount > 0 && filledCount < tokenCount) {
        setSigningMsg({ type: "error", text: `Please complete all ${tokenCount} initials before signing.` });
        return;
      }
      const sigData = sigMode === "typed" ? typedSig : drawnSig;
      if (!sigData) { setSigningMsg({ type: "error", text: "Please provide your signature." }); return; }
      if (sigMode === "typed" && sigData.trim().length < 2) { setSigningMsg({ type: "error", text: "Please type your full name." }); return; }
      const sigPayload = JSON.stringify({ signature: sigData, initials, signed_at: new Date().toISOString() });
      const res = await api.post("agreements.php", { action: "sign", case_id: caseData.id, agreement_id: currentAg.id, user_id: user.id, signer_name: user.name, signer_email: user.email, signature_data: sigPayload, signature_type: sigMode });
      if (res.success) {
        setSigningMsg(null); setTypedSig(""); setDrawnSig(null); setInitials({}); clearCanvas();
        const nextIdx = currentAgreementIdx + 1;
        if (nextIdx < unsignedAgreements.length) {
          setCurrentAgreementIdx(nextIdx);
        } else {
          const updatedAgs = await api.get(`agreements.php?action=get_for_client&case_id=${caseData.id}&user_id=${user.id}`).catch(() => []);
          setClientAgreements(Array.isArray(updatedAgs) ? updatedAgs : []);
          setIntakeStep(cardOnFile?.has_card ? 4 : 3);
        }
      } else setSigningMsg({ type: "error", text: res.error || "Failed to save signature." });
    };

    return (
      <div style={{ minHeight: "100vh", background: "#F7F5F0", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 16px", position: "relative" }}>
        <button onClick={onLogout} style={{ position: "absolute", top: 24, right: 24, padding: "8px 16px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#4A5568", background: "#fff", cursor: "pointer" }}>Sign Out</button>
        <div style={{ width: "100%", maxWidth: 680 }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: "#1B2B4B", marginBottom: 4 }}>Lakeshore Mediation</div>
            <div style={{ fontSize: 14, color: "#9B9490" }}>Client Intake — {caseData.client1_name} & {caseData.client2_name}</div>
          </div>

          {/* Progress bar */}
          <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
            {stepLabels.map((label, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center" }}>
                <div style={{ height: 4, borderRadius: 4, background: i < intakeStep ? "#2D7D72" : i === intakeStep - 1 ? "#2D7D72" : "#E2DDD5", marginBottom: 6 }} />
                <div style={{ fontSize: 11, color: i <= intakeStep - 1 ? "#2D7D72" : "#9B9490", fontWeight: i === intakeStep - 1 ? 700 : 400 }}>{label}</div>
              </div>
            ))}
          </div>

          <div style={{ background: "#fff", borderRadius: 16, padding: 32, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

            {/* ── STEP 1: Personal Info ── */}
            {intakeStep === 1 && (
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Personal Information</div>
                <div style={{ fontSize: 14, color: "#9B9490", marginBottom: 24 }}>Please complete your personal information below.</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Input label="Full Legal Name" value={intakeData.full_name} onChange={v => setIntakeData(d => ({ ...d, full_name: v }))} />
                  <Input label="Date of Birth" type="date" value={intakeData.dob} onChange={v => setIntakeData(d => ({ ...d, dob: v }))} />
                  <Input label="Phone Number" type="tel" value={intakeData.phone} onChange={v => setIntakeData(d => ({ ...d, phone: v }))} />
                  <Input label="Alternate Phone" type="tel" value={intakeData.alt_phone} onChange={v => setIntakeData(d => ({ ...d, alt_phone: v }))} />
                  <div style={{ gridColumn: "1/-1" }}><Input label="Email Address" type="email" value={intakeData.email} onChange={v => setIntakeData(d => ({ ...d, email: v }))} /></div>
                  <div style={{ gridColumn: "1/-1" }}><Input label="Street Address" value={intakeData.address} onChange={v => setIntakeData(d => ({ ...d, address: v }))} /></div>
                  <Input label="City" value={intakeData.city} onChange={v => setIntakeData(d => ({ ...d, city: v }))} />
                  <Input label="State" value={intakeData.state} onChange={v => setIntakeData(d => ({ ...d, state: v }))} />
                  <Input label="ZIP Code" value={intakeData.zip} onChange={v => setIntakeData(d => ({ ...d, zip: v }))} />
                  <Input label="Employer" value={intakeData.employer} onChange={v => setIntakeData(d => ({ ...d, employer: v }))} />
                  <Input label="Occupation" value={intakeData.occupation} onChange={v => setIntakeData(d => ({ ...d, occupation: v }))} />
                  <Input label="Annual Income" value={intakeData.income} onChange={v => setIntakeData(d => ({ ...d, income: v }))} />
                  <div style={{ gridColumn: "1/-1" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Goals for Mediation</div>
                    <textarea value={intakeData.goals} onChange={e => setIntakeData(d => ({ ...d, goals: e.target.value }))} style={{ width: "100%", minHeight: 80, padding: "10px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ gridColumn: "1/-1" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Concerns or Questions</div>
                    <textarea value={intakeData.concerns} onChange={e => setIntakeData(d => ({ ...d, concerns: e.target.value }))} style={{ width: "100%", minHeight: 80, padding: "10px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ gridColumn: "1/-1" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Do you have an attorney?</div>
                    <Select label="" value={intakeData.prior_mediation} onChange={v => setIntakeData(d => ({ ...d, prior_mediation: v }))} options={["No", "Yes"]} />
                  </div>
                  {intakeData.prior_mediation === "Yes" && <div style={{ gridColumn: "1/-1" }}><Input label="Attorney Name and Contact" value={intakeData.attorney} onChange={v => setIntakeData(d => ({ ...d, attorney: v }))} /></div>}
                </div>
                <Msg msg={intakeMsg} />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                  <button onClick={async () => {
                    if (!intakeData.full_name || !intakeData.phone) { setIntakeMsg({ type: "error", text: "Please fill in your name and phone number." }); return; }
                    const res = await api.post(`client_data.php?user_id=${user.id}`, { action: "save_intake", intake: intakeData });
                    if (res.success) { setIntakeMsg(null); setIntakeStep(unsignedAgreements.length > 0 ? 2 : (cardOnFile?.has_card ? 4 : 3)); }
                    else setIntakeMsg({ type: "error", text: "Failed to save. Please try again." });
                  }} style={{ padding: "12px 32px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700 }}>Continue →</button>
                </div>
              </div>
            )}

            {/* ── STEP 2: Agreement Signing ── */}
            {intakeStep === 2 && currentAg && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800 }}>{currentAg.title}</div>
                  <div style={{ fontSize: 12, color: "#9B9490" }}>{currentAgreementIdx + 1} of {unsignedAgreements.length}</div>
                </div>
                <div style={{ fontSize: 14, color: "#9B9490", marginBottom: 20 }}>Please read the agreement below carefully before signing.</div>
                {/* Agreement text with inline interactive fields */}
                <div style={{ background: "#F7F5F0", borderRadius: 10, padding: "20px 24px", maxHeight: 380, overflowY: "auto", marginBottom: 24, fontSize: 13, lineHeight: 1.8, color: "#2D3748" }}>
                  <AgreementHeader header={agreementHeader} />
                  {(() => {
                    const body = currentAg.body || "";
                    // Split on all token types
                    const tokenRegex = /(<span[^>]*class="initial-token"[^>]*>.*?<\/span>|<span[^>]*class="checkbox-token"[^>]*>.*?<\/span>|<span[^>]*class="number-token"[^>]*>.*?<\/span>|\{\{initial\}\}|\{\{checkbox\}\}|\{\{number\}\})/gi;
                    const parts = body.split(tokenRegex);
                    let tokenIdx = 0;
                    return parts.map((part, i) => {
                      const isInitial = /initial-token|\{\{initial\}\}/i.test(part);
                      const isCheckbox = /checkbox-token|\{\{checkbox\}\}/i.test(part);
                      const isNumber = /number-token|\{\{number\}\}/i.test(part);
                      if (isInitial || isCheckbox || isNumber) {
                        const idx = tokenIdx++;
                        if (isInitial) return (
                          <input key={i} value={initials[idx] || ""} onChange={e => setInitials(prev => ({ ...prev, [idx]: e.target.value }))}
                            placeholder="Init" maxLength={5}
                            style={{ display: "inline", width: 48, borderBottom: `2px solid ${initials[idx] ? "#2D7D72" : "#C8873A"}`, borderTop: "none", borderLeft: "none", borderRight: "none", background: "transparent", textAlign: "center", fontSize: 13, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "#1B2B4B", outline: "none", padding: "0 2px", margin: "0 2px", verticalAlign: "baseline" }} />
                        );
                        if (isCheckbox) {
                          const isChildInclusive = (() => {
                            const before = parts.slice(0, i).join("").toLowerCase();
                            return before.includes("child-inclusive") || before.includes("child inclusive");
                          })();
                          return (
                            <span key={i} style={{ display: "inline", verticalAlign: "middle" }}>
                              <input type="checkbox" checked={!!initials[`cb_${idx}`]} onChange={e => setInitials(prev => ({ ...prev, [`cb_${idx}`]: e.target.checked }))}
                                style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#1B2B4B", verticalAlign: "middle", margin: "0 2px" }} />
                              {isChildInclusive && initials[`cb_${idx}`] && (
                                <span style={{ display: "inline" }}>
                                  &nbsp;Number of children:&nbsp;
                                  <input type="number" min="1" max="10" value={initials[`children_${idx}`] || ""} onChange={e => setInitials(prev => ({ ...prev, [`children_${idx}`]: e.target.value }))}
                                    style={{ display: "inline", width: 44, border: "none", borderBottom: "2px solid #2D7D72", background: "transparent", textAlign: "center", fontSize: 13, fontWeight: 700, outline: "none", verticalAlign: "baseline" }} />
                                </span>
                              )}
                            </span>
                          );
                        }
                        if (isNumber) return (
                          <input key={i} type="number" min="0" value={initials[`num_${idx}`] || ""} onChange={e => setInitials(prev => ({ ...prev, [`num_${idx}`]: e.target.value }))}
                            style={{ display: "inline", width: 48, borderBottom: "2px solid #2D7D72", borderTop: "none", borderLeft: "none", borderRight: "none", background: "transparent", textAlign: "center", fontSize: 13, fontWeight: 700, color: "#1B2B4B", outline: "none", padding: "0 2px", margin: "0 2px", verticalAlign: "baseline" }} />
                        );
                      }
                      return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
                    });
                  })()}
                </div>
                {/* Initial completion indicator */}
                {(() => {
                  const initialCount = ((currentAg.body || "").match(/initial-token|\{\{initial\}\}/gi) || []).length;
                  const filledCount = Object.entries(initials).filter(([k, v]) => !k.startsWith("cb_") && !k.startsWith("num_") && !k.startsWith("children_") && String(v).trim()).length;
                  if (initialCount === 0) return null;
                  return (
                    <div style={{ fontSize: 12, color: filledCount === initialCount ? "#2D7D72" : "#C8873A", marginBottom: 16, fontWeight: 600 }}>
                      {filledCount === initialCount ? `✓ All ${initialCount} initials completed` : `${filledCount} of ${initialCount} initials completed — please initial all highlighted fields above`}
                    </div>
                  );
                })()}
                {/* Signature */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B", marginBottom: 12 }}>Your Signature</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    <button onClick={() => setSigMode("typed")} style={{ padding: "7px 16px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: sigMode === "typed" ? "#1B2B4B" : "#fff", color: sigMode === "typed" ? "#fff" : "#4A5568", border: "1px solid #E2DDD5" }}>✍ Type</button>
                    <button onClick={() => setSigMode("drawn")} style={{ padding: "7px 16px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: sigMode === "drawn" ? "#1B2B4B" : "#fff", color: sigMode === "drawn" ? "#fff" : "#4A5568", border: "1px solid #E2DDD5" }}>✏ Draw</button>
                  </div>
                  {sigMode === "typed" ? (
                    <div>
                      <input value={typedSig} onChange={e => setTypedSig(e.target.value)} placeholder="Type your full legal name"
                        style={{ width: "100%", padding: "14px 16px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 22, fontFamily: "'Playfair Display', serif", color: "#1B2B4B", boxSizing: "border-box" }} />
                      {typedSig && <div style={{ marginTop: 8, fontSize: 11, color: "#9B9490" }}>Signed as: <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, color: "#1B2B4B" }}>{typedSig}</span></div>}
                    </div>
                  ) : (
                    <div>
                      <canvas ref={canvasRef} width={580} height={160}
                        style={{ border: "1px solid #E2DDD5", borderRadius: 8, width: "100%", height: 160, cursor: "crosshair", touchAction: "none" }}
                        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
                      <button onClick={clearCanvas} style={{ marginTop: 8, fontSize: 11, color: "#9B9490", background: "none", border: "none", cursor: "pointer" }}>Clear</button>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 20, lineHeight: 1.6 }}>
                  By signing above, I acknowledge that I have read, understood, and agree to the terms of this {currentAg.title}. This electronic signature is legally binding.
                </div>
                <Msg msg={signingMsg} />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={signCurrentAgreement} style={{ padding: "12px 32px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700 }}>
                    {currentAgreementIdx < unsignedAgreements.length - 1 ? "Sign & Continue →" : "Sign & Proceed →"}
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Payment ── */}
            {intakeStep === 3 && (
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Payment Authorization</div>
                <div style={{ fontSize: 14, color: "#9B9490", marginBottom: 24 }}>A payment method on file is required. Select how you'd like to pay.</div>

                {/* Preferred payment method */}
                <div style={{ fontSize: 12, fontWeight: 700, color: "#4A5568", marginBottom: 8 }}>Preferred payment method</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                  {[{ value: "card", label: "💳 Credit or debit card" }, { value: "ach", label: "🏦 Bank account (ACH)" }, { value: "cash", label: "💵 Cash" }, { value: "check", label: "📝 Check" }].map(opt => (
                    <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", border: `1px solid ${preferredPayment === opt.value ? "#2D7D72" : "#E2DDD5"}`, borderRadius: 8, cursor: "pointer", background: preferredPayment === opt.value ? "#E6F4F2" : "#fff" }}>
                      <input type="radio" name="preferredPayment" value={opt.value} checked={preferredPayment === opt.value} onChange={() => {
                        setPreferredPayment(opt.value);
                        if (opt.value === "card" || opt.value === "ach") setPaymentType(opt.value);
                        else setPaymentType("card");
                      }} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}
                        {opt.value === "card" && <span style={{ fontSize: 11, color: "#9B9490", fontWeight: 400, marginLeft: 6 }}>2.9% processing fee applies</span>}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Cash/Check authorization language */}
                {(preferredPayment === "cash" || preferredPayment === "check") && (
                  <div style={{ background: "#FDF3E7", border: "1px solid #F0C080", borderRadius: 8, padding: "14px 16px", fontSize: 12, color: "#7D4E00", lineHeight: 1.7, marginBottom: 16 }}>
                    <strong>Important:</strong> I understand that if payment is not made at the time of service, the card on file will be charged for the balance due, including any applicable late cancellation or no-show fees.
                  </div>
                )}

                {/* Payment method setup */}
                <div style={{ fontSize: 12, fontWeight: 700, color: "#4A5568", marginBottom: 8 }}>
                  {preferredPayment === "ach" ? "Connect your bank account" : "Card on file (required)"}
                </div>

                {/* Card element — shown for card and cash/check */}
                {paymentType === "card" && (
                  <div id="stripe-card-element" style={{ padding: "14px 16px", border: "1px solid #E2DDD5", borderRadius: 8, minHeight: 44, background: "#fff", marginBottom: 16 }} />
                )}

                {/* ACH — button to open bank connection modal */}
                {paymentType === "ach" && (
                  <div style={{ padding: "14px 16px", border: "1px solid #E2DDD5", borderRadius: 8, background: "#F7F5F0", marginBottom: 16, fontSize: 13, color: "#4A5568" }}>
                    Click "Connect Bank Account" below to securely link your bank through your bank's login. Your credentials are never stored by us.
                  </div>
                )}

                {/* Authorization language */}
                <div style={{ background: "#F7F5F0", borderRadius: 8, padding: "14px 16px", fontSize: 12, color: "#4A5568", lineHeight: 1.7, marginBottom: 16 }}>
                  By providing your payment information, you authorize Lakeshore Mediation to charge this payment method for mediation services as outlined in your fee agreement. Lakeshore Mediation may utilize your payment method on file for any balances, including late cancellation and no-show fees, without additional authorization as outlined in the agreement. In cases of no-show or late cancellation fees, you agree not to dispute charges as services not provided.
                </div>

                <Msg msg={cardMsg} />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={async () => {
                    if (!stripeInstanceRef.current || !stripeClientSecretRef.current) { setCardMsg({ type: "error", text: "Payment form not ready. Please wait." }); return; }
                    setCardMsg(null);

                    let setupIntent, error;

                    if (paymentType === "ach") {
                      // ACH Financial Connections
                      const result = await stripeInstanceRef.current.collectBankAccountForSetup({
                        clientSecret: stripeClientSecretRef.current,
                        params: { payment_method_type: "us_bank_account", payment_method_data: { billing_details: { name: user.name, email: user.email } } },
                        expand: ["payment_method"],
                      });
                      if (result.error) { setCardMsg({ type: "error", text: result.error.message }); return; }
                      // Confirm the setup
                      const confirmed = await stripeInstanceRef.current.confirmUsBankAccountSetup(stripeClientSecretRef.current);
                      if (confirmed.error) { setCardMsg({ type: "error", text: confirmed.error.message }); return; }
                      setupIntent = confirmed.setupIntent;
                    } else {
                      // Card
                      if (!stripeCardRef.current) { setCardMsg({ type: "error", text: "Card form not ready." }); return; }
                      const result = await stripeInstanceRef.current.confirmCardSetup(stripeClientSecretRef.current, { payment_method: { card: stripeCardRef.current, billing_details: { name: user.name, email: user.email } } });
                      if (result.error) { setCardMsg({ type: "error", text: result.error.message }); return; }
                      setupIntent = result.setupIntent;
                    }

                    const res = await api.post("stripe_setup.php", { action: "confirm_setup", case_id: data?.case?.id, setup_intent_id: setupIntent.id, preferred_payment: preferredPayment });
                    if (res.success) { setCardOnFile({ has_card: true, ...res }); setIntakeStep(4); }
                    else setCardMsg({ type: "error", text: res.error || "Failed to save payment method." });
                  }} disabled={!stripeReady} style={{ padding: "12px 32px", background: stripeReady ? "#2D7D72" : "#ccc", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700 }}>
                    {stripeReady ? (paymentType === "ach" ? "Connect Bank Account →" : "Save Card & Continue →") : "Loading..."}
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 4: Done ── */}
            {intakeStep === 4 && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Intake Complete</div>
                <div style={{ background: "#F7F5F0", borderRadius: 10, padding: "20px 24px", marginBottom: 24, textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B", marginBottom: 10 }}>You have completed the following:</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 13, color: "#2D7D72" }}>✓ Personal information submitted</div>
                    {clientAgreements.length > 0 && <div style={{ fontSize: 13, color: "#2D7D72" }}>✓ Agreements signed</div>}
                    {cardOnFile?.has_card && <div style={{ fontSize: 13, color: "#2D7D72" }}>✓ Payment method saved — {cardOnFile.card_brand} ending in {cardOnFile.card_last4}</div>}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 28, lineHeight: 1.7 }}>
                  Thank you for completing your intake. Your mediator will be in touch shortly. You can now access your portal to view case updates, documents, and messages.
                </div>
                <button onClick={() => setIntakeStep(0)} style={{ padding: "12px 32px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700 }}>Go to My Portal →</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const upcomingSessions = (sessions || []).filter(s => s.status === "Upcoming");
  const pendingTasks = clientTasks.filter(t => t.status === "Pending").length;
  const pendingSigs = clientSigDocs.filter(d => d.status !== "Executed" && (d.require_client1 || d.require_client2) && !d.signatures?.some(s => s.signer_id === user.id)).length;
  const navItems = [
    { id: "home", label: "My Case", icon: "🏠" },
    { id: "tasks", label: `Tasks${pendingTasks > 0 ? ` (${pendingTasks})` : ""}`, icon: "✅" },
    { id: "signatures", label: `Sign Docs${pendingSigs > 0 ? ` (${pendingSigs})` : ""}`, icon: "✍️" },
    { id: "sessions", label: "Sessions", icon: "📅" },
    ...(caseData?.wellness_enabled === null || caseData?.wellness_enabled === undefined || parseInt(caseData?.wellness_enabled) === 1 ? [{ id: "wellness", label: "Wellness", icon: "💚" }] : []),
    { id: "notes", label: "My Notes", icon: "📝" },
    ...(parenting ? [{ id: "parenting", label: "Parenting Plan", icon: "👨‍👩‍👧" }] : []),
    { id: "intake", label: "Intake Form", icon: "📋" },
    { id: "documents", label: "Documents", icon: "📄" },
    { id: "billing", label: "Billing", icon: "💳" },
    { id: "settings", label: "Settings", icon: "⚙" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "'DM Sans', sans-serif", background: "#F7F5F0", color: "#1B2B4B", overflow: "hidden", flexDirection: "column" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-thumb { background: #E2DDD5; border-radius: 3px; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }` + MOBILE_CSS}</style>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {sidebarOpen && (
      <div className="r-sidebar" style={{ width: 220, background: "#1B2B4B", display: "flex", flexDirection: "column", flexShrink: 0, overflowY: "auto" }}>
        <div style={{ padding: "28px 20px 16px", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#fff", fontWeight: 800 }}>Our<span style={{ color: "#C8873A" }}>Mediation</span>Case</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 3, letterSpacing: "0.1em", textTransform: "uppercase" }}>Client Portal</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} title="Collapse sidebar" style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", padding: 4, flexShrink: 0 }}>✕</button>
        </div>
        <nav style={{ flex: 1, padding: "8px 12px" }}>
          {navItems.map(n => (
            <button key={n.id} onClick={() => setTab(n.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 8, marginBottom: 2, background: tab === n.id ? "rgba(255,255,255,0.12)" : "transparent", color: tab === n.id ? "#fff" : "rgba(255,255,255,0.45)", fontSize: 13, fontWeight: 600, textAlign: "left" }}>
              <span style={{ fontSize: 15 }}>{n.icon}</span>{n.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{user.name}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>{user.case_role}</div>
          <button onClick={onLogout} style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontWeight: 600 }}>Sign out</button>
        </div>
      </div>
      )}
      {!sidebarOpen && (
        <button onClick={() => setSidebarOpen(true)} title="Open sidebar" className="r-sidebar"
          style={{ width: 28, background: "#1B2B4B", color: "rgba(255,255,255,0.45)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 28, fontSize: 14 }}>☰</button>
      )}

      <div className="r-main" style={{ flex: 1, overflow: "auto" }}>
        {tab === "home" && (
          <div className="r-page" style={{ padding: "32px 36px" }}>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Welcome, {user.name?.split(" ")[0]}</h1>
              <div style={{ fontSize: 13, color: "#9B9490" }}>Here's an overview of your mediation case.</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800, marginBottom: 6 }}>{caseData.client1_name} & {caseData.client2_name}</div>
                  <div style={{ fontSize: 12, color: "#9B9490" }}>Case {caseData.case_number} · Opened {caseData.opened}</div>
                </div>
                <Badge label={caseData.status} color={statusColor(caseData.status)} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Current Stage: {caseData.stage}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#2D7D72" }}>{caseData.progress}%</span>
              </div>
              <ProgressBar value={parseInt(caseData.progress || 0)} />
            </div>
            <div className="r-grid4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
              <Stat label="Upcoming Sessions" value={upcomingSessions.length} />
              <Stat label="Pending Tasks" value={pendingTasks} accent={pendingTasks > 0 ? "#C8873A" : undefined} />
              <Stat label="Documents" value={documents.length} />
              <Stat label="Outstanding" value={`$${invoices.filter(i => i.status === "Outstanding").reduce((a, b) => a + parseFloat(b.amount || 0), 0).toLocaleString()}`} accent="#C8873A" />
            </div>
            {upcomingSessions.length > 0 && (
              <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Next Session</div>
                {upcomingSessions.slice(0, 1).map(s => (<div key={s.id}><div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{s.topic}</div><div style={{ fontSize: 13, color: "#9B9490" }}>{s.session_date} · {formatTime(s.session_time)} · {s.duration} · {s.type}</div></div>))}
              </div>
            )}
          </div>
        )}

        {tab === "signatures" && (
          <div style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Sign Documents</h1>
            <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>Documents sent to you for signature by your mediator.</div>
            {clientSigningDoc && (
              <SigningModal doc={clientSigningDoc} user={user} onClose={() => setClientSigningDoc(null)} onSigned={() => { setClientSigningDoc(null); loadData(); }} />
            )}
            {clientSigDocs.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No documents to sign yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {clientSigDocs.map(doc => {
                const sigs = doc.signatures || [];
                const myRole = user.case_role || "Client 1";
                const needsMySignature = (myRole === "Client 1" && doc.require_client1) || (myRole === "Client 2" && doc.require_client2);
                const iSigned = sigs.some(s => s.signer_id === user.id);
                const isExecuted = doc.status === "Executed";
                return (
                  <div key={doc.id} style={{ background: "#fff", border: `1px solid ${isExecuted ? "#A8D5A2" : iSigned ? "#E2DDD5" : "#C8873A"}`, borderLeft: `4px solid ${isExecuted ? "#2E7D32" : iSigned ? "#2D7D72" : "#C8873A"}`, borderRadius: 12, padding: "20px 24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                          <span style={{ fontSize: 20 }}>📝</span>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>{doc.title}</div>
                          <Badge label={isExecuted ? "✓ Executed" : iSigned ? "✓ Signed by you" : "Action Required"} color={isExecuted ? { bg: "#EAF4EA", text: "#2E7D32" } : iSigned ? { bg: "#E6F4F2", text: "#2D7D72" } : { bg: "#FDF3E7", text: "#C8873A" }} />
                        </div>
                        <div style={{ fontSize: 12, color: "#9B9490" }}>{doc.document_type}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <a href={"https://ourmediationcase.com" + doc.file_path} target="_blank" rel="noreferrer" style={{ padding: "7px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>View PDF</a>
                        {needsMySignature && !iSigned && !isExecuted && (
                          <button onClick={() => setClientSigningDoc(doc)} style={{ padding: "7px 18px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Sign Now</button>
                        )}
                      </div>
                    </div>
                    <div style={{ background: "#F7F5F0", borderRadius: 8, padding: "12px 16px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 8 }}>SIGNATURES</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {sigs.map(sig => (
                          <div key={sig.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span>✅</span>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{sig.signer_name}</span>
                            {sig.signature_type === "draw" && <img src={sig.signature_data} alt="sig" style={{ height: 28, maxWidth: 80, border: "1px solid #E2DDD5", borderRadius: 4, background: "#fff" }} />}
                            {sig.signature_type === "type" && <span style={{ fontFamily: "cursive", fontSize: 16, color: "#1B2B4B" }}>{sig.signature_data}</span>}
                            <span style={{ fontSize: 11, color: "#9B9490" }}>{sig.signed_at?.split(" ")[0]}</span>
                          </div>
                        ))}
                        {sigs.length === 0 && <div style={{ fontSize: 12, color: "#9B9490" }}>No signatures yet.</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "tasks" && (
          <div style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>My Tasks</h1>
            <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>
              {clientTasks.filter(t => t.status === "Pending").length} pending · {clientTasks.filter(t => t.status === "Complete").length} complete
            </div>
            <Msg msg={taskFileMsg} />
            {clientTasks.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No tasks assigned yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {clientTasks.map(task => {
                const isComplete = task.status === "Complete";
                const priorityColor = task.priority === "High" ? { bg: "#FDEEEC", text: "#C0392B" } : task.priority === "Medium" ? { bg: "#FDF3E7", text: "#C8873A" } : { bg: "#EAF4EA", text: "#2E7D32" };
                return (
                  <div key={task.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderLeft: `4px solid ${isComplete ? "#2E7D32" : task.priority === "High" ? "#C0392B" : task.priority === "Medium" ? "#C8873A" : "#2D7D72"}`, borderRadius: 10, padding: "18px 20px", opacity: isComplete ? 0.8 : 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: task.notes ? 10 : 0 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, textDecoration: isComplete ? "line-through" : "none", color: isComplete ? "#9B9490" : "#1B2B4B" }}>{task.title}</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Badge label={task.priority} color={priorityColor} />
                          {task.due_date && <span style={{ fontSize: 11, color: new Date(task.due_date) < new Date() && !isComplete ? "#C0392B" : "#9B9490", fontWeight: 600 }}>📅 Due {task.due_date}</span>}
                          {isComplete && <span style={{ fontSize: 11, color: "#2E7D32", fontWeight: 600 }}>✓ Completed</span>}
                        </div>
                      </div>
                      {!isComplete && (
                        <button onClick={() => clientCompleteTask(task.id)} style={{ padding: "8px 18px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, flexShrink: 0, marginLeft: 12 }}>Mark Complete</button>
                      )}
                    </div>
                    {task.notes && <div style={{ fontSize: 13, color: "#4A5568", background: "#F7F5F0", borderRadius: 7, padding: "10px 14px", marginBottom: 10, lineHeight: 1.6 }}>{task.notes}</div>}
                    {task.files && task.files.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 6 }}>ATTACHED FILES</div>
                        {task.files.map(f => (
                          <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "#F7F5F0", borderRadius: 7, padding: "8px 12px", marginBottom: 4 }}>
                            <span>📎</span>
                            <a href={"https://ourmediationcase.com" + f.file_path} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#2D7D72", fontWeight: 600, textDecoration: "none" }}>{f.file_name}</a>
                          </div>
                        ))}
                      </div>
                    )}
                    <label style={{ fontSize: 12, color: "#2D7D72", fontWeight: 600, cursor: "pointer" }}>
                      📎 Upload file for this task
                      <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) uploadTaskFileClient(e.target.files[0], task.id, caseData.id); e.target.value=""; }} />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "sessions" && (
          <div className="r-page" style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 24 }}>Sessions</h1>
            {sessions.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No sessions scheduled yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sessions.map(s => (<div key={s.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 22px" }}><div style={{ display: "flex", justifyContent: "space-between" }}><div><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.topic}</div><div style={{ fontSize: 12, color: "#9B9490" }}>{s.session_date} · {formatTime(s.session_time)} · {s.duration} · {s.type}</div></div><Badge label={s.status} color={s.status === "Upcoming" ? { bg: "#FDF3E7", text: "#C8873A" } : { bg: "#EAF4EA", text: "#2E7D32" }} /></div></div>))}
            </div>
          </div>
        )}

        {tab === "parenting" && parenting && (
          <div style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Parenting Plan</h1>
            {parenting.finalized != 1 ? (
              <div style={{ marginTop: 24, background: "#FDF3E7", border: "1px solid #C8873A", borderRadius: 12, padding: "24px 28px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ fontSize: 28 }}>⏳</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1B2B4B", marginBottom: 6 }}>Parenting Plan In Progress</div>
                  <div style={{ fontSize: 13, color: "#4A5568", lineHeight: 1.6 }}>Your parenting plan is currently being prepared by your mediator. You will be able to view and download the finalized plan here once it has been completed.</div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>Your parenting plan has been finalized. You can view and download it below.</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
                  <a href={`${API_BASE}/pp_export.php?case_id=${caseData.id}&format=pdf&auth_token=${getToken()}`} target="_blank" rel="noreferrer"
                    style={{ padding: "10px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>🖨 View / Print PDF</a>
                  <a href={`${API_BASE}/pp_export.php?case_id=${caseData.id}&format=docx&auth_token=${getToken()}`} target="_blank" rel="noreferrer"
                    style={{ padding: "10px 20px", border: "1px solid #2D7D72", color: "#2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>📄 Download Word Doc</a>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "intake" && (
          <div style={{ padding: "32px 36px", maxWidth: 700 }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Intake Form</h1>
            <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>{intakeSubmitted ? "Your intake form has been submitted. You can update it below." : "Please complete your intake form before your first session."}</div>
            {intakeSubmitted && <div style={{ marginBottom: 20, padding: "10px 16px", background: "#EAF4EA", borderRadius: 8, fontSize: 13, color: "#2E7D32", fontWeight: 600 }}>✅ Form submitted on {intake?.submitted_date}</div>}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28 }}>
              {[{ title: "Personal Information", fields: [{ label: "Full Name", key: "full_name" }, { label: "Date of Birth", key: "dob", type: "date" }, { label: "Phone", key: "phone" }, { label: "Email", key: "email", type: "email" }, { label: "Address", key: "address", full: true }] }, { title: "Employment", fields: [{ label: "Employer", key: "employer" }, { label: "Annual Income", key: "income" }] }, { title: "Marriage Details", fields: [{ label: "Date of Marriage", key: "marriage_date", type: "date" }, { label: "Date of Separation", key: "separation_date", type: "date" }, { label: "Attorney (if any)", key: "attorney" }, { label: "Prior Mediation?", key: "prior_mediation" }] }, { title: "Assets & Debts", fields: [{ label: "Assets", key: "assets", full: true }, { label: "Debts", key: "debts", full: true }] }, { title: "Goals & Concerns", fields: [{ label: "Goals for Mediation", key: "goals", full: true }, { label: "Concerns", key: "concerns", full: true }] }].map(section => (
                <div key={section.title} style={{ marginBottom: 28 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>{section.title}</div>
                  <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    {section.fields.map(f => (
                      <div key={f.key} style={{ gridColumn: f.full ? "1 / -1" : undefined }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>{f.label}</div>
                        {f.full ? <textarea value={intakeData[f.key] || ""} onChange={e => setIntakeData({ ...intakeData, [f.key]: e.target.value })} style={{ width: "100%", minHeight: 80, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} /> : <input type={f.type || "text"} value={intakeData[f.key] || ""} onChange={e => setIntakeData({ ...intakeData, [f.key]: e.target.value })} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 7, padding: "8px 12px", fontSize: 13, outline: "none" }} />}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Msg msg={intakeMsg} />
              <button onClick={saveIntake} style={{ padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>{intakeSubmitted ? "Update Form" : "Submit Intake Form"}</button>
            </div>
          </div>
        )}

        {tab === "documents" && (
          <div style={{ padding: "32px 36px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Documents</h1>
              <label style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Upload Document<input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: "none" }} onChange={e => uploadDocument(e.target.files[0])} /></label>
            </div>
            <Msg msg={uploadMsg} />
            <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 16 }}>Accepted: PDF, JPG, PNG, DOC, DOCX · Max 10MB</div>
            {documents.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No documents yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {documents.map(d => (<div key={d.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}><div style={{ display: "flex", gap: 14, alignItems: "center" }}><span style={{ fontSize: 22 }}>📄</span><div><div style={{ fontSize: 14, fontWeight: 600 }}>{d.file_name}</div><div style={{ fontSize: 11, color: "#9B9490" }}>Uploaded by {d.uploader_name} · {d.created_at?.split(" ")[0]}</div></div></div><Badge label={d.uploader_role === "client" ? "You" : "Mediator"} color={d.uploader_role === "client" ? { bg: "#E6F4F2", text: "#2D7D72" } : { bg: "#FDF3E7", text: "#C8873A" }} /></div>))}
            </div>
          </div>
        )}

        {tab === "wellness" && (
          <div style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Daily Wellness Check-in</h1>
            <div style={{ fontSize: 14, color: "#9B9490", marginBottom: 24 }}>How are you doing today? Your mediator can see these check-ins to better support you.</div>
            {todayCheckin && <div style={{ fontSize: 13, color: "#2D7D72", fontWeight: 700, marginBottom: 16 }}>✓ You checked in today — you can update it below.</div>}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28, maxWidth: 560 }}>
              {[
                { key: "overall_mood", label: "Overall Mood", max: 10, color: "#2D7D72", desc: "1 = very low, 10 = very good" },
                { key: "stress_level", label: "Stress Level", max: 10, color: "#C0392B", desc: "1 = very low, 10 = very high" },
                { key: "coparenting_score", label: "Co-parenting Interactions", max: 10, color: "#1B2B4B", desc: "1 = very difficult, 10 = very smooth" },
                { key: "readiness_score", label: "Readiness to Negotiate", max: 10, color: "#2D7D72", desc: "1 = not ready, 10 = very ready" },
                { key: "sleep_quality", label: "Sleep Quality", max: 5, color: "#9B9490", desc: "1 = very poor, 5 = excellent" },
              ].map(({ key, label, max, color, desc }) => (
                <div key={key} style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 11, color: "#9B9490" }}>{desc}</div>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800, color, minWidth: 36, textAlign: "right" }}>{checkinForm[key]}</div>
                  </div>
                  <input type="range" min="1" max={max} value={checkinForm[key]} onChange={e => setCheckinForm(f => ({ ...f, [key]: parseInt(e.target.value) }))}
                    style={{ width: "100%", accentColor: color }} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9B9490" }}>
                    <span>1</span><span>{max}</span>
                  </div>
                </div>
              ))}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Anything you'd like to share with your mediator? (optional)</div>
                <textarea value={checkinForm.notes} onChange={e => setCheckinForm(f => ({ ...f, notes: e.target.value }))} style={{ width: "100%", minHeight: 80, padding: "10px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} placeholder="How are you feeling about the process? Any concerns?" />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 20 }}>
                <input type="checkbox" checked={checkinForm.flag_for_mediator} onChange={e => setCheckinForm(f => ({ ...f, flag_for_mediator: e.target.checked }))} />
                Flag this check-in for my mediator's attention
              </label>
              <Msg msg={checkinMsg} />
              <button onClick={async () => {
                const res = await api.post("checkins.php", { action: "save_checkin", case_id: caseData.id, user_id: user.id, checkin_date: new Date().toISOString().split("T")[0], ...checkinForm });
                if (res.success) { setCheckinMsg({ type: "success", text: "Check-in saved." }); setTodayCheckin(checkinForm); setTimeout(() => setCheckinMsg(null), 3000); }
                else setCheckinMsg({ type: "error", text: "Failed to save." });
              }} style={{ padding: "10px 28px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700 }}>Save Check-in</button>
            </div>
          </div>
        )}

        {tab === "notes" && (
          <div style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>My Notes</h1>
            <div style={{ fontSize: 14, color: "#9B9490", marginBottom: 24 }}>Jot down thoughts between sessions. Your mediator can see these notes.</div>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 20, marginBottom: 20, maxWidth: 640 }}>
              <textarea value={newNote} onChange={e => setNewNote(e.target.value)} style={{ width: "100%", minHeight: 100, padding: "10px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} placeholder="Write a note..." />
              <button onClick={async () => {
                if (!newNote.trim()) return;
                const res = await api.post("checkins.php", { action: "save_client_note", case_id: caseData.id, user_id: user.id, note: newNote });
                if (res.success) { setNewNote(""); const notes = await api.get(`checkins.php?action=get_client_notes&case_id=${caseData.id}&user_id=${user.id}`).catch(() => []); setClientNotes(Array.isArray(notes) ? notes : []); }
              }} style={{ padding: "8px 20px", background: "#1B2B4B", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Add Note</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 640 }}>
              {clientNotes.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No notes yet.</div>}
              {clientNotes.map(note => (
                <div key={note.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ fontSize: 13, color: "#2D3748", lineHeight: 1.6, flex: 1 }}>{note.note}</div>
                    <button onClick={async () => {
                      await api.post("checkins.php", { action: "delete_client_note", id: note.id });
                      const notes = await api.get(`checkins.php?action=get_client_notes&case_id=${caseData.id}&user_id=${user.id}`).catch(() => []);
                      setClientNotes(Array.isArray(notes) ? notes : []);
                    }} style={{ color: "#C0392B", background: "none", border: "none", cursor: "pointer", fontSize: 14, marginLeft: 12, flexShrink: 0 }}>✕</button>
                  </div>
                  <div style={{ fontSize: 11, color: "#9B9490", marginTop: 6 }}>{new Date(note.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "billing" && (
          <div style={{ padding: "32px 36px" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 24 }}>Billing</h1>
            <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
              <Stat label="Total Billed" value={`$${invoices.reduce((a, b) => a + parseFloat(b.amount || 0), 0).toLocaleString()}`} />
              <Stat label="Outstanding" value={`$${invoices.filter(i => i.status === "Outstanding").reduce((a, b) => a + parseFloat(b.amount || 0), 0).toLocaleString()}`} accent="#C8873A" />
            </div>
            {invoices.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No invoices yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {invoices.map(inv => (
                <div key={inv.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 22px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: inv.status === "Outstanding" && inv.stripe_payment_link ? 12 : 0 }}>
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: 3 }}>{inv.invoice_number} · ${parseFloat(inv.amount || 0).toLocaleString()}</div>
                      <div style={{ fontSize: 12, color: "#9B9490" }}>{inv.description} · {inv.invoice_date}</div>
                    </div>
                    <Badge label={inv.status} color={inv.status === "Paid" ? { bg: "#EAF4EA", text: "#2E7D32" } : { bg: "#FDF3E7", text: "#C8873A" }} />
                  </div>
                  {inv.status !== "Paid" && inv.stripe_payment_link && (
                    <a href={inv.stripe_payment_link} target="_blank" rel="noreferrer" style={{ display: "block", background: "#2D7D72", color: "#fff", textAlign: "center", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none", marginTop: 10 }}>
                      💳 Pay Now — ${parseFloat(inv.amount || 0).toLocaleString()}
                    </a>
                  )}
                  {inv.status !== "Paid" && !inv.stripe_payment_link && (
                    <div style={{ fontSize: 12, color: "#9B9490", marginTop: 8, fontStyle: "italic" }}>Payment link will appear here once sent by your mediator.</div>
                  )}
                  {inv.status === "Paid" && inv.paid_at && (
                    <div style={{ fontSize: 12, color: "#2E7D32", marginTop: 6 }}>✓ Paid on {inv.paid_at?.split(" ")[0]}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div style={{ padding: "32px 36px", maxWidth: 580 }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Settings</h1>
            <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 28 }}>Manage your notification preferences.</div>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28, marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Email Notifications</div>
              <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 18 }}>Choose which emails you receive from your mediator.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {[
                  { key: "new_session", label: "New session scheduled", desc: "When a session is added to your case" },
                  { key: "session_reminder", label: "Session reminder", desc: "Reminder the day before a session" },
                  { key: "new_task", label: "New task assigned", desc: "When your mediator assigns you a task" },
                  { key: "signature_request", label: "Signature request", desc: "When a document needs your signature" },
                  { key: "new_document", label: "New document", desc: "When a document is added to your case" },
                ].map(({ key, label, desc }, i) => (
                  <label key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < 4 ? "1px solid #F0EDE8" : "none", cursor: "pointer" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1B2B4B" }}>{label}</div>
                      <div style={{ fontSize: 11, color: "#9B9490", marginTop: 2 }}>{desc}</div>
                    </div>
                    <div onClick={() => setClientNotifSettings({ ...clientNotifSettings, [key]: clientNotifSettings[key] ? 0 : 1 })}
                      style={{ width: 44, height: 24, borderRadius: 99, background: clientNotifSettings[key] ? "#2D7D72" : "#E2DDD5", position: "relative", transition: "background 0.2s", flexShrink: 0, cursor: "pointer" }}>
                      <div style={{ position: "absolute", top: 3, left: clientNotifSettings[key] ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                    </div>
                  </label>
                ))}
              </div>
              <Msg msg={clientNotifMsg} />
              <button onClick={async () => {
                const res = await api.post("notifications.php", { ...clientNotifSettings, user_id: user.id });
                if (res.success) setClientNotifMsg({ type: "success", text: "Preferences saved!" });
                else setClientNotifMsg({ type: "error", text: "Failed to save." });
                setTimeout(() => setClientNotifMsg(null), 4000);
              }} style={{ marginTop: 18, padding: "9px 22px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Preferences</button>
            </div>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Payment Method</div>
              {cardOnFile?.has_card ? (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 44, height: 30, background: "#1B2B4B", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: 10, color: "#fff", fontWeight: 700 }}>{cardOnFile.card_brand?.toUpperCase()?.slice(0,4) || "CARD"}</span>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>•••• •••• •••• {cardOnFile.card_last4}</div>
                      <div style={{ fontSize: 11, color: "#2D7D72", fontWeight: 700 }}>✓ Active card on file</div>
                    </div>
                  </div>
                  <button onClick={() => setIntakeStep(3)} style={{ padding: "8px 18px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Update Card</button>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 12 }}>No card on file.</div>
                  <button onClick={() => setIntakeStep(3)} style={{ padding: "8px 18px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>Add Card</button>
                </div>
              )}
            </div>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#C0392B", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Account</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div><div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Sign out</div><div style={{ fontSize: 12, color: "#9B9490" }}>You'll need to sign back in.</div></div>
                <button onClick={onLogout} style={{ padding: "9px 22px", border: "1px solid #C0392B", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#C0392B" }}>Sign Out</button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>{/* end inner flex */}
      {/* ── BOTTOM NAV (mobile) ── */}
      <nav className="r-bottom-nav">
        {navItems.map(n => (
          <button key={n.id} onClick={() => setTab(n.id)} className={tab === n.id ? "active" : ""}>
            <span className="r-nav-icon">{n.icon}</span>
            {n.label.replace(/ \(\d+\)$/, "")}
          </button>
        ))}
        <button onClick={onLogout}>
          <span className="r-nav-icon">🚪</span>
          Out
        </button>
      </nav>
    </div>
  );
}

// ─── PARENTING PLAN TEMPLATE EDITOR ──────────────────────────────────────────
const FIELD_TYPES = [
  { value: "header", label: "Section Header (bold title, no answer)" },
  { value: "text", label: "Text Field" },
  { value: "textarea", label: "Text Area" },
  { value: "radio", label: "Radio (pick one)" },
  { value: "checkbox", label: "Checkboxes (pick many)" },
  { value: "dropdown", label: "Dropdown" },
  { value: "yesno", label: "Yes / No" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "name", label: "Name (First, Middle, Last)" },
  { value: "address", label: "Address" },
  { value: "phone", label: "Phone Number" },
  { value: "email", label: "Email Address" },
  { value: "children", label: "Children List (Name + DOB/Age)" },
];

const htmlToPlainText = (html) => (html || "")
  .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
  .replace(/<br\s*\/?>/gi, "\n")
  .replace(/<[^>]+>/g, "")
  .replace(/&nbsp;/g, " ")
  .replace(/&amp;/g, "&")
  .replace(/\n{3,}/g, "\n\n")
  .trim();

const calcAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  if (isNaN(birth)) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : null;
};

// Matches the "Parenting Plan for These Children" section so the Builder can
// auto-fill it from the Children tab, mirroring the PHP logic in
// api/pp_export.php and api/parenting_plan.php (generate_text).
const isChildrenSectionTitle = (title) => {
  const t = (title || "").trim();
  return /parenting plan for these children/i.test(t)
      || /^children\s*$/i.test(t)
      || /^parenting plan for/i.test(t);
};

// ─── RICH TEXT EDITOR (Full WYSIWYG) ─────────────────────────────────────────
function RichTextArea({ value, onChange, placeholder, dark }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || isInternalChange.current) return;
    if (el.innerHTML !== (value || "")) el.innerHTML = value || "";
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    emitChange();
  };

  // execCommand('fontSize') only supports the legacy 1-7 scale, not real point
  // sizes — wrap the selection with the placeholder size, then swap it for a
  // real CSS font-size so exact point values (and the Word/PDF export, which
  // reads font-size from inline styles) work correctly.
  const setFontSize = (pt) => {
    const el = editorRef.current;
    el.focus();
    document.execCommand('fontSize', false, '7');
    el.querySelectorAll('font[size="7"]').forEach(node => {
      node.removeAttribute('size');
      node.style.fontSize = pt + 'pt';
    });
    emitChange();
  };

  const emitChange = () => {
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
    setTimeout(() => { isInternalChange.current = false; }, 0);
  };

  const bg = dark ? "#1a2a40" : "#fff";
  const border = dark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #D4CFC9";
  const toolbarBg = dark ? "#152030" : "#F0EDE8";
  const btnBase = {
    padding: "4px 7px", borderRadius: 5, fontSize: 12, cursor: "pointer",
    border: dark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #D4CFC9",
    background: dark ? "rgba(255,255,255,0.07)" : "#fff",
    color: dark ? "#e8e0d8" : "#1B2B4B",
    lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.1s",
  };
  const sep = <div style={{ width: 1, background: dark ? "rgba(255,255,255,0.15)" : "#D4CFC9", margin: "2px 3px", alignSelf: "stretch" }} />;

  const FONTS = ["Georgia", "Times New Roman", "Century Schoolbook", "Bookman Old Style", "Arial", "Helvetica", "Courier New", "Verdana", "Trebuchet MS", "Palatino"];
  const SIZES = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
  const COLORS = ["#1B2B4B","#2D7D72","#C8873A","#C0392B","#27AE60","#8E44AD","#000000","#555555","#888888","#ffffff"];

  const selectStyle = {
    padding: "3px 6px", borderRadius: 5, fontSize: 11, cursor: "pointer",
    border: dark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #D4CFC9",
    background: dark ? "rgba(255,255,255,0.07)" : "#fff",
    color: dark ? "#e8e0d8" : "#1B2B4B",
    outline: "none", height: 26,
  };

  return (
    <div style={{ borderRadius: 8, border, display: "flex", flexDirection: "column" }}>
      {/* ── Toolbar ── */}
      <div style={{ background: toolbarBg, padding: "6px 8px", display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center", borderBottom: border, flexShrink: 0 }}>

        {/* Font family */}
        <select style={{...selectStyle, width: 130}} defaultValue=""
          onChange={e => { if (e.target.value) exec("fontName", e.target.value); e.target.value = ""; }}>
          <option value="" disabled>Font…</option>
          {FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>

        {/* Font size */}
        <select style={{...selectStyle, width: 70}} defaultValue=""
          onChange={e => { if (e.target.value) setFontSize(e.target.value); e.target.value = ""; }}>
          <option value="" disabled>Size…</option>
          {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {sep}

        {/* Bold / Italic / Underline / Strikethrough */}
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("bold"); }} title="Bold"><b style={{ fontSize: 13 }}>B</b></button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("italic"); }} title="Italic"><i style={{ fontSize: 13 }}>I</i></button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("underline"); }} title="Underline"><u style={{ fontSize: 13 }}>U</u></button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("strikeThrough"); }} title="Strikethrough"><s style={{ fontSize: 12 }}>S</s></button>

        {sep}

        {/* Alignment */}
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("justifyLeft"); }} title="Align left">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="14" height="2"/><rect x="0" y="5" width="9" height="2"/><rect x="0" y="9" width="14" height="2"/><rect x="0" y="13" width="9" height="2" opacity="0.4"/></svg>
        </button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("justifyCenter"); }} title="Center">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="14" height="2"/><rect x="2.5" y="5" width="9" height="2"/><rect x="0" y="9" width="14" height="2"/><rect x="2.5" y="13" width="9" height="2" opacity="0.4"/></svg>
        </button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("justifyRight"); }} title="Align right">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="14" height="2"/><rect x="5" y="5" width="9" height="2"/><rect x="0" y="9" width="14" height="2"/><rect x="5" y="13" width="9" height="2" opacity="0.4"/></svg>
        </button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("justifyFull"); }} title="Justify">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="14" height="2"/><rect x="0" y="5" width="14" height="2"/><rect x="0" y="9" width="14" height="2"/><rect x="0" y="13" width="14" height="2" opacity="0.4"/></svg>
        </button>

        {sep}

        {/* Lists */}
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("insertUnorderedList"); }} title="Bullet list">
          <span style={{ fontSize: 13 }}>• ≡</span>
        </button>
        <button type="button" style={btnBase} onMouseDown={e => {
          e.preventDefault();
          editorRef.current.focus();
          const sel = window.getSelection();
          const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
          const li = range ? range.startContainer.parentElement?.closest('li') : null;
          const ol = li ? li.closest('ol') : null;
          if (ol) {
            // Cycle: decimal → lower-alpha → upper-alpha → remove list
            const cur = ol.style.listStyleType || 'decimal';
            if (cur === 'decimal') {
              ol.style.listStyleType = 'lower-alpha';
            } else if (cur === 'lower-alpha') {
              ol.style.listStyleType = 'upper-alpha';
            } else {
              // Remove the list — unwrap all <li> items to plain paragraphs
              const frag = document.createDocumentFragment();
              Array.from(ol.querySelectorAll('li')).forEach(liEl => {
                const p = document.createElement('p');
                p.innerHTML = liEl.innerHTML;
                frag.appendChild(p);
              });
              ol.replaceWith(frag);
            }
          } else {
            // Not in a list — insert new decimal ordered list
            const selectedText = sel && sel.toString() ? sel.toString() : "";
            const items = selectedText ? selectedText.split('\n').filter(Boolean) : [''];
            const listHtml = `<ol style="list-style-type:decimal;margin-left:22px;margin-bottom:8px">${items.map(t => `<li>${t || '&nbsp;'}</li>`).join('')}</ol>`;
            document.execCommand('insertHTML', false, listHtml);
          }
          emitChange();
        }} title="Ordered list — click to cycle: 1. → a. → A. → remove">
          {(() => {
            const sel = typeof window !== 'undefined' ? window.getSelection() : null;
            const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
            const li = range ? range.startContainer?.parentElement?.closest?.('li') : null;
            const ol = li ? li.closest('ol') : null;
            const cur = ol ? (ol.style.listStyleType || 'decimal') : 'decimal';
            const next = cur === 'decimal' ? 'a.' : cur === 'lower-alpha' ? 'A.' : cur === 'upper-alpha' ? '×' : '1.';
            return <span style={{ fontSize: 12 }}>{cur === 'decimal' ? '1.' : cur === 'lower-alpha' ? 'a.' : cur === 'upper-alpha' ? 'A.' : '1.'} ≡</span>;
          })()}
        </button>

        {sep}

        {/* Indent / Outdent */}
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("outdent"); }} title="Outdent">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="4" y="1" width="10" height="2"/><polygon points="0,5 4,7 0,9"/><rect x="4" y="6" width="10" height="2"/><rect x="4" y="11" width="10" height="2"/></svg>
        </button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("indent"); }} title="Indent">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="14" height="2"/><polygon points="0,5 4,7 0,9" transform="rotate(180,2,7)"/><rect x="4" y="6" width="10" height="2"/><rect x="0" y="11" width="14" height="2"/></svg>
        </button>

        {sep}

        {/* Headings */}
        <select style={{...selectStyle, width: 100}} defaultValue=""
          onChange={e => { if (e.target.value) exec("formatBlock", e.target.value); e.target.value = ""; }}>
          <option value="" disabled>Heading…</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="p">Paragraph</option>
          <option value="blockquote">Quote</option>
        </select>

        {sep}

        {/* Text color swatches */}
        <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
          <span style={{ fontSize: 10, color: dark ? "rgba(255,255,255,0.5)" : "#9B9490", marginRight: 2 }}>A</span>
          {COLORS.map(c => (
            <button key={c} type="button" title={c}
              onMouseDown={e => { e.preventDefault(); exec("foreColor", c); }}
              style={{ width: 16, height: 16, borderRadius: 3, background: c, border: c === "#ffffff" ? "1px solid #ccc" : "none", cursor: "pointer", padding: 0 }} />
          ))}
        </div>

        {sep}

        {/* Undo / Redo */}
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("undo"); }} title="Undo">↩</button>
        <button type="button" style={btnBase} onMouseDown={e => { e.preventDefault(); exec("redo"); }} title="Redo">↪</button>

        {sep}

        {/* Clear */}
        <button type="button" style={{...btnBase, opacity: 0.7, fontSize: 11}}
          onMouseDown={e => { e.preventDefault(); exec("removeFormat"); emitChange(); }} title="Clear formatting">✕ Clear</button>

        {sep}

        {/* Agreement tokens */}
        <span style={{ fontSize: 10, color: dark ? "rgba(255,255,255,0.5)" : "#9B9490" }}>Insert:</span>
        <button type="button" style={{...btnBase, fontSize: 11, background: "#FDF3E7", color: "#C8873A", border: "1px solid #C8873A", fontWeight: 700}}
          onMouseDown={e => { e.preventDefault(); exec("insertHTML", '<span class="initial-token" contenteditable="false" style="display:inline-block;border-bottom:2px solid #C8873A;min-width:48px;color:#C8873A;font-size:11px;font-weight:700;text-align:center;margin:0 2px;padding:0 4px;background:#FEF9F0;border-radius:3px;vertical-align:baseline;">✍ initial</span>'); emitChange(); }} title="Insert initial field">✍ Initial</button>
        <button type="button" style={{...btnBase, fontSize: 11, background: "#EEF0F8", color: "#1B2B4B", border: "1px solid #1B2B4B", fontWeight: 700}}
          onMouseDown={e => { e.preventDefault(); exec("insertHTML", '<span class="checkbox-token" contenteditable="false" style="display:inline-block;width:14px;height:14px;border:2px solid #1B2B4B;margin:0 2px;background:#fff;border-radius:2px;vertical-align:middle;"></span>'); emitChange(); }} title="Insert checkbox">☐ Check</button>
        <button type="button" style={{...btnBase, fontSize: 11, background: "#E6F4F2", color: "#2D7D72", border: "1px solid #2D7D72", fontWeight: 700}}
          onMouseDown={e => { e.preventDefault(); exec("insertHTML", '<span class="number-token" contenteditable="false" style="display:inline-block;border-bottom:2px solid #2D7D72;min-width:36px;color:#2D7D72;font-size:11px;font-weight:700;text-align:center;margin:0 2px;padding:0 4px;background:#F0FAF9;border-radius:3px;vertical-align:baseline;"># num</span>'); emitChange(); }} title="Insert number field"># Num</button>
      </div>

      {/* ── Editor area ── */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onKeyDown={e => {
          if (e.key === "Tab") {
            e.preventDefault();
            if (e.shiftKey) {
              document.execCommand("outdent", false, null);
            } else {
              document.execCommand("indent", false, null);
            }
            emitChange();
          }
        }}
        data-placeholder={placeholder}
        style={{
          minHeight: 300, maxHeight: 500, padding: "12px 14px",
          fontSize: "12pt", lineHeight: 1.75,
          fontFamily: "'Century Schoolbook', Georgia, serif",
          background: bg,
          color: dark ? "#e8e0d8" : "#1B2B4B",
          outline: "none",
          overflowY: "auto",
          flex: 1,
        }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder); color: #aaa; pointer-events: none; font-style: italic;
        }
        [contenteditable] ul { margin-left: 22px; list-style-type: disc; margin-bottom: 8px; }
        [contenteditable] ol { margin-left: 22px; list-style-type: decimal; margin-bottom: 8px; }
        [contenteditable] ol[style*="lower-alpha"] { list-style-type: lower-alpha; }
        [contenteditable] ol[style*="upper-alpha"] { list-style-type: upper-alpha; }
        [contenteditable] li { margin-bottom: 4px; }
        [contenteditable] h1 { font-size: 1.6em; font-weight: 800; margin-bottom: 8px; }
        [contenteditable] h2 { font-size: 1.3em; font-weight: 700; margin-bottom: 6px; }
        [contenteditable] h3 { font-size: 1.1em; font-weight: 700; margin-bottom: 4px; }
        [contenteditable] blockquote { border-left: 3px solid #2D7D72; padding-left: 12px; color: #666; margin: 8px 0; font-style: italic; }
        [contenteditable] p { margin-bottom: 6px; }
      `}</style>
    </div>
  );
}

function PPTemplateEditor({ onClose }) {
  const [sections, setSections] = useState([]);
  const [activeSec, setActiveSec] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [editingSec, setEditingSec] = useState(null);
  const [editingQ, setEditingQ] = useState(null);
  const [secForm, setSecForm] = useState({ title: "", description: "", section_type: "questions", fixed_text: "", intro_text: "", outro_text: "" });
  const [qForm, setQForm] = useState({ label: "", field_type: "text", help_text: "", narrative_prefix: "", narrative_suffix: "", required: 0, choices: [], has_custom_provision: 0 });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [secIntroText, setSecIntroText] = useState("");
  const [secOutroText, setSecOutroText] = useState("");
  const [introOutroMsg, setIntroOutroMsg] = useState(null);
  const rightPanelRef = useRef(null);
  const leftSidebarRef = useRef(null);

  const loadSections = async () => {
    const scroll = leftSidebarRef.current?.scrollTop ?? 0;
    const res = await api.get("parenting_plan.php?action=get_sections");
    setSections(Array.isArray(res) ? res : []);
    setTimeout(() => { if (leftSidebarRef.current) leftSidebarRef.current.scrollTop = scroll; }, 0);
  };

  const loadQuestions = async (sid, showSpinner = false) => {
    if (showSpinner) setLoading(true);
    const res = await api.get(`parenting_plan.php?action=get_questions&section_id=${sid}`);
    setQuestions(Array.isArray(res) ? res : []);
    if (showSpinner) setLoading(false);
  };

  // Lock page scroll while this modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => { loadSections(); }, []);

  // Depend on activeSec?.id — not the object — so a sections reload doesn't re-trigger this
  useEffect(() => {
    if (activeSec) {
      loadQuestions(activeSec.id, true);
      setSecIntroText(activeSec.intro_text || "");
      setSecOutroText(activeSec.outro_text || "");
      setIntroOutroMsg(null);
    }
  }, [activeSec?.id]);

  const seedMissingProvisions = async () => {
    if (!window.confirm("Fill in starter questions for any existing section that's currently empty? This only adds content to sections that have zero questions — it won't touch or duplicate anything already there.")) return;
    try {
      const res = await api.post("parenting_plan.php?action=seed_missing_provisions", {});
      if (!res || res.success !== true) { alert(res?.error || "Failed to fill in provisions. Please try again."); return; }
      alert(res.sections_filled.length > 0 ? `Added ${res.questions_added} question(s) across ${res.sections_filled.length} section(s):\n${res.sections_filled.join(", ")}` : "No matching empty sections found — nothing to fill in.");
      loadSections();
      if (activeSec) loadQuestions(activeSec.id);
    } catch {
      alert("Could not reach the server. Please try again.");
    }
  };

  const saveSection = async () => {
    if (!secForm.title.trim()) return;
    const payload = editingSec ? { ...secForm, id: editingSec.id } : secForm;
    try {
      const res = await api.post("parenting_plan.php?action=save_section", payload);
      if (!res || res.success !== true) { setMsg({ type: "error", text: res?.error || "Failed to save section. Please try again." }); return; }
      if (editingSec) {
        // Update the section in-place — no reload, no scroll reset
        setSections(prev => prev.map(s => s.id === editingSec.id ? { ...s, ...secForm } : s));
        setEditingSec(null);
        setSecForm({ title: "", description: "", section_type: "questions", fixed_text: "", intro_text: "", outro_text: "" });
      } else {
        // New section — need to reload to get the new id
        setSecForm({ title: "", description: "", section_type: "questions", fixed_text: "", intro_text: "", outro_text: "" });
        loadSections();
      }
      setMsg(null);
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm("Delete this section and all its questions?")) return;
    await api.delete(`parenting_plan.php?action=delete_section&id=${id}`);
    if (activeSec?.id === id) { setActiveSec(null); setQuestions([]); }
    loadSections();
  };

  const duplicateSection = async (sec) => {
    // 1. Create the new section
    const newSecRes = await api.post("parenting_plan.php?action=save_section", {
      title: sec.title + " (copy)",
      description: sec.description || "",
      section_type: sec.section_type || "questions",
      fixed_text: sec.fixed_text || "",
      intro_text: sec.intro_text || "",
      outro_text: sec.outro_text || "",
    });
    if (!newSecRes?.id) { alert("Failed to duplicate section."); return; }
    const newSecId = newSecRes.id;
    // 2. Load questions from original section and copy them
    const qs = await api.get(`parenting_plan.php?action=get_questions&section_id=${sec.id}`);
    if (Array.isArray(qs)) {
      for (const q of qs) {
        await api.post("parenting_plan.php?action=save_question", {
          section_id: newSecId,
          label: q.label,
          field_type: q.field_type,
          help_text: q.help_text || "",
          narrative_prefix: q.narrative_prefix || "",
          narrative_suffix: q.narrative_suffix || "",
          required: q.required || 0,
          has_custom_provision: q.has_custom_provision || 0,
          choices: q.choices || [],
        });
      }
    }
    await loadSections();
  };

  const [dragOverId, setDragOverId] = useState(null);
  const dragSecId = useRef(null);

  const onDragStart = (e, sec) => {
    dragSecId.current = sec.id;
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, sec) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(sec.id);
  };

  const onDrop = async (e, targetSec) => {
    e.preventDefault();
    setDragOverId(null);
    const fromId = dragSecId.current;
    if (!fromId || fromId === targetSec.id) return;
    const fromIdx = sections.findIndex(s => s.id === fromId);
    const toIdx = sections.findIndex(s => s.id === targetSec.id);
    const reordered = [...sections];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setSections(reordered);
    await api.post("parenting_plan.php?action=reorder_sections", { order: reordered.map(s => s.id) });
  };

  const onDragEnd = () => { setDragOverId(null); dragSecId.current = null; };

  const startEditSec = (sec) => { setEditingSec(sec); setSecForm({ title: sec.title, description: sec.description || "", section_type: sec.section_type || "questions", fixed_text: sec.fixed_text || "", intro_text: sec.intro_text || "", outro_text: sec.outro_text || "" }); };

  const saveIntroOutro = async () => {
    if (!activeSec) return;
    try {
      const res = await api.post("parenting_plan.php?action=save_section", { ...activeSec, intro_text: secIntroText, outro_text: secOutroText });
      if (!res || res.success !== true) { setMsg({ type: "error", text: res?.error || "Failed to save. Please try again." }); return; }
      setSections(prev => prev.map(s => s.id === activeSec.id ? {...s, intro_text: secIntroText, outro_text: secOutroText} : s));
      setIntroOutroMsg("Saved!");
      setTimeout(() => setIntroOutroMsg(null), 2000);
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const resetQForm = () => setQForm({ label: "", field_type: "text", help_text: "", narrative_prefix: "", narrative_suffix: "", required: 0, choices: [], has_custom_provision: 0 });

  const saveQuestion = async () => {
    if (!qForm.label.trim()) { setMsg({ type: "error", text: "Question label is required." }); return; }
    const payload = editingQ ? { ...qForm, id: editingQ.id, section_id: activeSec.id } : { ...qForm, section_id: activeSec.id };
    try {
      const res = await api.post("parenting_plan.php?action=save_question", payload);
      if (!res || res.success !== true) { setMsg({ type: "error", text: res?.error || "Failed to save question. Please try again." }); return; }
      if (editingQ) {
        // Update in-place — preserve order
        setQuestions(prev => prev.map(q => q.id === editingQ.id ? { ...q, ...qForm } : q));
        setEditingQ(null); resetQForm(); setMsg(null);
      } else {
        // New question — reload to get server-assigned id and sort_order
        setEditingQ(null); resetQForm(); setMsg(null);
        loadQuestions(activeSec.id);
      }
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`parenting_plan.php?action=delete_question&id=${id}`);
    loadQuestions(activeSec.id);
  };

  const duplicateQuestion = async (q) => {
    const payload = {
      section_id: activeSec.id,
      label: q.label + " (copy)",
      field_type: q.field_type,
      help_text: q.help_text || "",
      narrative_prefix: q.narrative_prefix || "",
      narrative_suffix: q.narrative_suffix || "",
      required: q.required || 0,
      has_custom_provision: q.has_custom_provision || 0,
      choices: q.choices || [],
    };
    await api.post("parenting_plan.php?action=save_question", payload);
    loadQuestions(activeSec.id);
  };

  const [dragOverQId, setDragOverQId] = useState(null);
  const dragQId = useRef(null);

  const onQDragStart = (e, q) => {
    dragQId.current = q.id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onQDragOver = (e, q) => {
    e.preventDefault();
    setDragOverQId(q.id);
  };
  const onQDrop = async (e, targetQ) => {
    e.preventDefault();
    setDragOverQId(null);
    const fromId = dragQId.current;
    if (!fromId || fromId === targetQ.id) return;
    const list = [...questions];
    const fromIdx = list.findIndex(q => q.id === fromId);
    const toIdx   = list.findIndex(q => q.id === targetQ.id);
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setQuestions(list);
    await api.post("parenting_plan.php?action=reorder_questions", { order: list.map(q => q.id) });
  };
  const onQDragEnd = () => { dragQId.current = null; setDragOverQId(null); };

  const startEditQ = (q) => {
    setEditingQ(q);
    setQForm({ label: q.label, field_type: q.field_type, help_text: q.help_text || "", narrative_prefix: q.narrative_prefix || "", narrative_suffix: q.narrative_suffix || "", required: q.required || 0, choices: q.choices || [], has_custom_provision: q.has_custom_provision || 0 });
  };

  const addChoice = () => setQForm(f => ({ ...f, choices: [...f.choices, { label: "", value: "", narrative: "" }] }));
  const updateChoice = (i, key, val) => setQForm(f => { const c = [...f.choices]; c[i] = { ...c[i], [key]: val, ...(key === "label" ? { value: val.toLowerCase().replace(/\s+/g, "_") } : {}) }; return { ...f, choices: c }; });
  const removeChoice = (i) => setQForm(f => ({ ...f, choices: f.choices.filter((_, idx) => idx !== i) }));

  const needsChoices = ["radio","checkbox","dropdown","yesno"].includes(qForm.field_type);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [showSecPreview, setShowSecPreview] = useState(false);

  const applyBulkEdit = () => {
    const lines = bulkText.split("\n").map(l => l.trim()).filter(l => l.length > 0);
    const choices = lines.map(l => ({ label: l, value: l.toLowerCase().replace(/\s+/g, "_"), narrative: "" }));
    setQForm(f => ({ ...f, choices }));
    setBulkEditMode(false);
    setBulkText("");
  };

  const openBulkEdit = () => {
    setBulkText(qForm.choices.map(c => c.label).join("\n"));
    setBulkEditMode(true);
  };

  return (
    <>
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex" }}>
      <div style={{ width: 280, background: "#1B2B4B", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <div style={{ padding: "20px 20px 12px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 800, color: "#fff" }}>Template Editor</div>
            <button onClick={onClose} style={{ color: "rgba(255,255,255,0.5)", fontSize: 20 }}>×</button>
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>Manage sections & questions</div>
          <button onClick={seedMissingProvisions} title="Add starter questions to any existing section that has none yet" style={{ marginTop: 10, width: "100%", padding: "6px 0", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 7, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.75)" }}>✨ Fill In Missing Provisions</button>
        </div>
        <div ref={leftSidebarRef} style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
          {sections.map(sec => (
            <div key={sec.id}
              draggable
              onDragStart={e => onDragStart(e, sec)}
              onDragOver={e => onDragOver(e, sec)}
              onDrop={e => onDrop(e, sec)}
              onDragEnd={onDragEnd}
              onClick={() => { setActiveSec(sec); setEditingQ(null); resetQForm(); }}
              style={{
                padding: "10px 12px 10px 16px", cursor: "pointer",
                background: dragOverId === sec.id ? "rgba(45,125,114,0.5)" : activeSec?.id === sec.id ? "rgba(45,125,114,0.3)" : "transparent",
                borderLeft: activeSec?.id === sec.id ? "3px solid #2D7D72" : "3px solid transparent",
                borderTop: dragOverId === sec.id ? "2px solid #2D7D72" : "2px solid transparent",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                transition: "background 0.12s",
                userSelect: "none",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                <span title="Drag to reorder" style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, cursor: "grab", flexShrink: 0, lineHeight: 1 }}>⠿</span>
                <span style={{ fontSize: 13, color: "#fff", fontWeight: activeSec?.id === sec.id ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sec.title}</span>
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => startEditSec(sec)} style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>Edit</button>
                <button onClick={() => duplicateSection(sec)} title="Duplicate section with all questions" style={{ fontSize: 11, color: "#C8873A", fontWeight: 600 }}>Copy</button>
                <button onClick={() => deleteSection(sec.id)} style={{ fontSize: 11, color: "#C0392B", fontWeight: 600 }}>Del</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: 16, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          {editingSec ? (
            <div>
              <input value={secForm.title} onChange={e => setSecForm({...secForm, title: e.target.value})} placeholder="Section title" style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "none", fontSize: 12, marginBottom: 6 }} />
              <input value={secForm.description} onChange={e => setSecForm({...secForm, description: e.target.value})} placeholder="Description (optional)" style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "none", fontSize: 12, marginBottom: 6 }} />
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>Section Type</div>
                <div style={{ display: "flex", gap: 4 }}>
                  {["questions","fixed_text"].map(t => (
                    <button key={t} onClick={() => setSecForm({...secForm, section_type: t})}
                      style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10, fontWeight: 700, border: secForm.section_type === t ? "1px solid #2D7D72" : "1px solid rgba(255,255,255,0.2)", background: secForm.section_type === t ? "#2D7D72" : "transparent", color: "#fff" }}>
                      {t === "questions" ? "Questions" : "Fixed Text"}
                    </button>
                  ))}
                </div>
              </div>
              {secForm.section_type === "fixed_text" && (
                <RichTextArea dark value={secForm.fixed_text} onChange={v => setSecForm({...secForm, fixed_text: v})} placeholder="Enter the fixed/mandatory text for this section…" />
              )}
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={saveSection} style={{ flex: 1, padding: "7px 0", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save</button>
                <button onClick={() => { setEditingSec(null); setSecForm({ title: "", description: "", section_type: "questions", fixed_text: "", intro_text: "", outro_text: "" }); }} style={{ padding: "7px 12px", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 7, fontSize: 12, color: "#fff" }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <input value={secForm.title} onChange={e => setSecForm({...secForm, title: e.target.value})} placeholder="New section name…" style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "none", fontSize: 12, marginBottom: 6 }} />
              <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                {["questions","fixed_text"].map(t => (
                  <button key={t} onClick={() => setSecForm({...secForm, section_type: t})}
                    style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10, fontWeight: 700, border: secForm.section_type === t ? "1px solid #2D7D72" : "1px solid rgba(255,255,255,0.2)", background: secForm.section_type === t ? "#2D7D72" : "transparent", color: "#fff" }}>
                    {t === "questions" ? "Questions" : "Fixed Text"}
                  </button>
                ))}
              </div>
              <button onClick={saveSection} style={{ width: "100%", padding: "7px 0", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>+ Add Section</button>
            </div>
          )}
        </div>
      </div>

      <div ref={rightPanelRef} style={{ flex: 1, background: "#F7F5F0", overflowY: "auto" }}>
        {!activeSec ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9B9490", fontSize: 14 }}>← Select a section to edit its questions</div>
        ) : (
          <div style={{ padding: 32, maxWidth: 780 }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, color: "#1B2B4B" }}>{activeSec.title}</div>
                  {activeSec.section_type === "fixed_text" && <span style={{ fontSize: 11, fontWeight: 700, background: "#FDF3E7", color: "#C8873A", padding: "3px 10px", borderRadius: 20 }}>Fixed Text</span>}
                </div>
                <button onClick={() => setShowSecPreview(true)}
                  style={{ padding: "7px 16px", background: "#1B2B4B", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  👁 Preview Section
                </button>
              </div>
              {activeSec.description && <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 12 }}>{activeSec.description}</div>}

              {/* Intro / Outro text editors */}
              <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Section Intro Text</div>
                <RichTextArea value={secIntroText} onChange={v => setSecIntroText(v)} placeholder="Text to appear before questions in this section (optional)…" />
                <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.08em", margin: "14px 0 8px" }}>Section Outro Text</div>
                <RichTextArea value={secOutroText} onChange={v => setSecOutroText(v)} placeholder="Text to appear after questions in this section (optional)…" />
                <button onClick={saveIntroOutro} style={{ marginTop: 8, padding: "6px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save Intro/Outro</button>
                {introOutroMsg && <span style={{ marginLeft: 10, fontSize: 12, color: "#2E7D32", fontWeight: 600 }}>{introOutroMsg}</span>}
              </div>

              {activeSec.section_type === "fixed_text" && (
                <div style={{ background: "#fff", border: "1px solid #C8873A", borderRadius: 10, padding: "18px 20px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#C8873A", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Fixed Text Content</div>
                  <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 8 }}>
                    This text appears in every case using this section. Use these tokens to auto-fill each case's actual names — click to insert at the end, or type them directly:
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {[
                      { tok: "{{petitioner_first}}", label: "Petitioner's first name" },
                      { tok: "{{respondent_first}}", label: "Respondent's first name" },
                      { tok: "{{child_first}}", label: "First child's first name" },
                      { tok: "{{children}}", label: "All children, joined with \"and\"" },
                    ].map(t => (
                      <button key={t.tok} title={t.label} onClick={() => {
                        const v = (activeSec.fixed_text || "") + (activeSec.fixed_text ? " " : "") + t.tok;
                        setSections(prev => prev.map(s => s.id === activeSec.id ? {...s, fixed_text: v} : s));
                        setActiveSec(prev => ({...prev, fixed_text: v}));
                      }} style={{ padding: "4px 10px", border: "1px solid #C8873A", borderRadius: 20, fontSize: 11, fontWeight: 700, color: "#C8873A", background: "#FDF3E7" }}>
                        + {t.tok}
                      </button>
                    ))}
                  </div>
                  <RichTextArea value={activeSec.fixed_text || ""} onChange={v => {
                    setSections(prev => prev.map(s => s.id === activeSec.id ? {...s, fixed_text: v} : s));
                    setActiveSec(prev => ({...prev, fixed_text: v}));
                  }} placeholder="Enter the fixed/mandatory text for this section…" />
                  <button onClick={async () => {
                    await api.post("parenting_plan.php?action=save_section", {...activeSec});
                    setMsg({ type: "success", text: "Fixed text saved!" });
                    setTimeout(() => setMsg(null), 2000);
                  }} style={{ marginTop: 10, padding: "8px 20px", background: "#C8873A", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>Save Fixed Text</button>
                  {msg && <span style={{ marginLeft: 10, fontSize: 12, color: msg.type === "success" ? "#2E7D32" : "#C0392B", fontWeight: 600 }}>{msg.text}</span>}
                </div>
              )}
            </div>

            {/* Question list with inline editing */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
              {loading && <div style={{ fontSize: 13, color: "#9B9490" }}>Loading…</div>}
              {!loading && questions.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No questions yet. Add one below.</div>}
              {questions.map((q, i) => (
                <div key={q.id}
                  onDragOver={e => onQDragOver(e, q)}
                  onDrop={e => onQDrop(e, q)}
                  onDragEnd={onQDragEnd}
                  style={{ background: "#fff", border: dragOverQId === q.id ? "2px solid #2D7D72" : editingQ?.id === q.id ? "1.5px solid #2D7D72" : "1px solid #E2DDD5", borderRadius: 10, overflow: "hidden", transition: "border-color 0.12s" }}>
                  {/* Question row */}
                  <div style={{ padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: 1 }}>
                      <span draggable onDragStart={e => onQDragStart(e, q)} title="Drag to reorder" style={{ fontSize: 16, color: "#C8C4BE", cursor: "grab", marginTop: 2, flexShrink: 0 }}>⠿</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", background: "#E6F4F2", padding: "2px 8px", borderRadius: 20 }}>{FIELD_TYPES.find(f=>f.value===q.field_type)?.label || q.field_type}</span>
                          {q.required ? <span style={{ fontSize: 10, color: "#C0392B", fontWeight: 700 }}>REQUIRED</span> : null}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#1B2B4B" }}>{q.label}</div>
                        {q.help_text && <div style={{ fontSize: 11, color: "#9B9490", marginTop: 3 }}>{q.help_text}</div>}
                        {!editingQ && q.choices && q.choices.length > 0 && (
                          <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {q.choices.map((c,ci) => <span key={ci} style={{ fontSize: 11, background: "#F7F5F0", border: "1px solid #E2DDD5", borderRadius: 20, padding: "2px 8px", color: "#4A5568" }}>{c.label}</span>)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginLeft: 12, alignItems: "center", flexShrink: 0 }}>
                      {editingQ?.id === q.id
                        ? <button onClick={() => { setEditingQ(null); resetQForm(); setMsg(null); }} style={{ fontSize: 12, color: "#9B9490", fontWeight: 600 }}>Cancel</button>
                        : <button onClick={() => startEditQ(q)} style={{ fontSize: 12, color: "#2D7D72", fontWeight: 600 }}>Edit</button>
                      }
                      <button onClick={() => duplicateQuestion(q)} title="Duplicate question" style={{ fontSize: 12, color: "#C8873A", fontWeight: 600 }}>Copy</button>
                      <button onClick={() => deleteQuestion(q.id)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 600 }}>Delete</button>
                    </div>
                  </div>
                  {/* Inline edit form — expands inside the card */}
                  {editingQ?.id === q.id && (
                    <div style={{ borderTop: "1px solid #E2DDD5", background: "#F7F5F0", padding: "18px 18px 18px" }}>
                      <Msg msg={msg} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Question Label *</div>
                          <input value={qForm.label} onChange={e => setQForm({...qForm, label: e.target.value})} placeholder="e.g. Who will make education decisions?" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff" }} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Field Type</div>
                            <select value={qForm.field_type} onChange={e => setQForm({...qForm, field_type: e.target.value, choices: ["yesno"].includes(e.target.value) ? [{label:"Yes",value:"yes",narrative:""},{label:"No",value:"no",narrative:""}] : qForm.choices })}
                              style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff" }}>
                              {FIELD_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 18 }}>
                            {qForm.field_type !== "header" && (<>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                              <input type="checkbox" checked={qForm.required === 1} onChange={e => setQForm({...qForm, required: e.target.checked ? 1 : 0})} style={{ width: 15, height: 15 }} />
                              Required
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                              <input type="checkbox" checked={qForm.has_custom_provision === 1} onChange={e => setQForm({...qForm, has_custom_provision: e.target.checked ? 1 : 0})} style={{ width: 15, height: 15 }} />
                              Custom Provision
                            </label>
                            </>)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>{qForm.field_type === "header" ? "Subtitle (optional)" : "Help Text"}</div>
                          <input value={qForm.help_text} onChange={e => setQForm({...qForm, help_text: e.target.value})} placeholder="Optional explanation…" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff" }} />
                        </div>
                        {qForm.field_type !== "header" && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Narrative Prefix</div>
                            <input value={qForm.narrative_prefix} onChange={e => setQForm({...qForm, narrative_prefix: e.target.value})} placeholder='"The parties agree that "' style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff" }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Narrative Suffix</div>
                            <input value={qForm.narrative_suffix} onChange={e => setQForm({...qForm, narrative_suffix: e.target.value})} placeholder='" shall be the primary residence."' style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff" }} />
                          </div>
                        </div>
                        )}
                        {needsChoices && (
                          <div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490" }}>Answer Choices</div>
                              {qForm.field_type !== "yesno" && (
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button onClick={openBulkEdit} style={{ fontSize: 12, color: "#C8873A", fontWeight: 700 }}>⚡ Bulk Edit</button>
                                  <button onClick={addChoice} style={{ fontSize: 12, color: "#2D7D72", fontWeight: 700 }}>+ Add Choice</button>
                                </div>
                              )}
                            </div>
                            {bulkEditMode && qForm.field_type !== "yesno" ? (
                              <div style={{ background: "#FDF3E7", border: "1px solid #C8873A", borderRadius: 8, padding: "14px" }}>
                                <div style={{ fontSize: 11, color: "#C8873A", fontWeight: 700, marginBottom: 6 }}>⚡ BULK EDIT — one option per line</div>
                                <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
                                  placeholder={"Option One\nOption Two\nOption Three"}
                                  rows={6} style={{ width: "100%", border: "1px solid #C8873A", borderRadius: 7, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: "inherit", resize: "vertical", marginBottom: 10 }} />
                                <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 10 }}>Each line becomes one choice. Narrative text can be added individually after.</div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button onClick={applyBulkEdit} style={{ padding: "7px 16px", background: "#C8873A", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Apply</button>
                                  <button onClick={() => { setBulkEditMode(false); setBulkText(""); }} style={{ padding: "7px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {qForm.choices.map((c, i) => (
                                <div key={i} style={{ background: "#fff", borderRadius: 8, padding: "10px 14px", border: "1px solid #E2DDD5" }}>
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "center", marginBottom: 6 }}>
                                    <input value={c.label} onChange={e => updateChoice(i, "label", e.target.value)} placeholder="Label — use {{input}} for short blank, {{textarea}} for long text" style={{ border: "1px solid #E2DDD5", borderRadius: 6, padding: "6px 10px", fontSize: 12, outline: "none" }} />
                                    <input value={c.value} onChange={e => updateChoice(i, "value", e.target.value)} placeholder="Value (auto)" style={{ border: "1px solid #E2DDD5", borderRadius: 6, padding: "6px 10px", fontSize: 12, outline: "none", color: "#9B9490" }} />
                                    {qForm.field_type !== "yesno" && <button onClick={() => removeChoice(i)} style={{ fontSize: 14, color: "#C0392B", fontWeight: 700 }}>×</button>}
                                  </div>
                                  <RichTextArea value={c.narrative} onChange={v => updateChoice(i, "narrative", v)} placeholder="Narrative text for this choice…" />
                                </div>
                              ))}
                            </div>
                            )}
                          </div>
                        )}
                        <button onClick={saveQuestion} style={{ padding: "9px 0", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Save Changes</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add new question form */}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B", marginBottom: 16 }}>＋ New Question</div>
              <Msg msg={msg} />
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Question Label *</div>
                  <input value={editingQ ? "" : qForm.label} onChange={e => !editingQ && setQForm({...qForm, label: e.target.value})} placeholder="e.g. Who will make education decisions?" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Field Type</div>
                    <select value={editingQ ? "text" : qForm.field_type} onChange={e => !editingQ && setQForm({...qForm, field_type: e.target.value, choices: ["yesno"].includes(e.target.value) ? [{label:"Yes",value:"yes",narrative:""},{label:"No",value:"no",narrative:""}] : qForm.choices })}
                      style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}>
                      {FIELD_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 20 }}>
                    {qForm.field_type !== "header" && (<>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" checked={!editingQ && qForm.required === 1} onChange={e => !editingQ && setQForm({...qForm, required: e.target.checked ? 1 : 0})} style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Required field</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="checkbox" checked={!editingQ && qForm.has_custom_provision === 1} onChange={e => !editingQ && setQForm({...qForm, has_custom_provision: e.target.checked ? 1 : 0})} style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Allow Custom Provision</span>
                    </div>
                    </>)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>{qForm.field_type === "header" ? "Subtitle (optional, shown to user)" : "Help Text (shown to user)"}</div>
                  <input value={editingQ ? "" : qForm.help_text} onChange={e => !editingQ && setQForm({...qForm, help_text: e.target.value})} placeholder="Optional explanation or instructions…" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                </div>
                {qForm.field_type !== "header" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Narrative Prefix</div>
                    <input value={editingQ ? "" : qForm.narrative_prefix} onChange={e => !editingQ && setQForm({...qForm, narrative_prefix: e.target.value})} placeholder='e.g. "The parties agree that "' style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Narrative Suffix</div>
                    <input value={editingQ ? "" : qForm.narrative_suffix} onChange={e => !editingQ && setQForm({...qForm, narrative_suffix: e.target.value})} placeholder='e.g. " shall be the primary residence."' style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                  </div>
                </div>
                )}
                <button onClick={saveQuestion} disabled={!!editingQ} style={{ padding: "10px 0", background: editingQ ? "#ccc" : "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: editingQ ? "not-allowed" : "pointer" }}>Add Question</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* ── Section Preview Overlay ── */}
    {showSecPreview && activeSec && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", justifyContent: "flex-end" }}>
        <div style={{ width: "min(680px, 90vw)", background: "#fff", height: "100%", overflowY: "auto", boxShadow: "-8px 0 40px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>
          <div style={{ background: "#1B2B4B", color: "#fff", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
            <div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 800 }}>Section Preview</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>How this section will appear in the plan builder</div>
            </div>
            <button onClick={() => setShowSecPreview(false)} style={{ fontSize: 22, color: "rgba(255,255,255,0.6)" }}>×</button>
          </div>
          <div style={{ padding: "32px 36px" }}>
            <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", color: "#1B2B4B", borderBottom: "2px solid #1B2B4B", paddingBottom: 5, marginBottom: 18, letterSpacing: "0.5px" }}>{activeSec.title}</div>
            {secIntroText && (
              <div style={{ background: "#EEF0F8", border: "1px solid #C5CAE9", borderRadius: 9, padding: "12px 16px", fontSize: 13, color: "#1B2B4B", lineHeight: 1.7, marginBottom: 16 }}
                dangerouslySetInnerHTML={{ __html: secIntroText.replace(/\n/g, "<br/>") }} />
            )}
            {activeSec.section_type === "fixed_text" ? (
              <div style={{ background: "#FDF3E7", border: "1px solid #C8873A", borderRadius: 10, padding: "16px 18px", marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#C8873A", textTransform: "uppercase", marginBottom: 8 }}>Fixed Language</div>
                <div style={{ fontSize: 13, color: "#4A5568", lineHeight: 1.75 }}
                  dangerouslySetInnerHTML={{ __html: (activeSec.fixed_text || "No content yet.").replace(/\n/g, "<br/>") }} />
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {questions.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No questions in this section yet.</div>}
                {questions.map((q) => (
                  q.field_type === "header" ? (
                    <div key={q.id} style={{ fontSize: 16, fontWeight: 800, color: "#1B2B4B", borderBottom: "2px solid #1B2B4B", paddingBottom: 6 }}>
                      {q.label}
                      {q.help_text && <div style={{ fontSize: 12, fontWeight: 400, color: "#9B9490", marginTop: 4 }}>{q.help_text}</div>}
                    </div>
                  ) : (
                  <div key={q.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1B2B4B", marginBottom: q.help_text ? 4 : 12 }}>{q.label}{q.required ? <span style={{ color: "#C0392B" }}> *</span> : null}</div>
                    {q.help_text && <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 12, lineHeight: 1.5 }}>{q.help_text}</div>}
                    {q.field_type === "text" && <input disabled placeholder="Text answer…" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0" }} />}
                    {q.field_type === "textarea" && <textarea disabled placeholder="Long text answer…" rows={3} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0", resize: "none", fontFamily: "inherit" }} />}
                    {q.field_type === "date" && <input type="date" disabled style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0" }} />}
                    {q.field_type === "time" && <input type="time" disabled style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0" }} />}
                    {q.field_type === "phone" && <input disabled placeholder="(555) 555-5555" style={{ width: "100%", maxWidth: 260, border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0" }} />}
                    {q.field_type === "email" && <input disabled placeholder="email@example.com" style={{ width: "100%", maxWidth: 320, border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0" }} />}
                    {q.field_type === "name" && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                        {["First","Middle","Last"].map(p => <div key={p}><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>{p} Name</div><input disabled style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, background: "#F7F5F0" }} /></div>)}
                      </div>
                    )}
                    {q.field_type === "yesno" && (
                      <div style={{ display: "flex", gap: 10 }}>
                        {["✓ Yes","✗ No"].map(v => <div key={v} style={{ flex: 1, padding: "10px 0", borderRadius: 9, fontSize: 13, fontWeight: 700, border: "2px solid #E2DDD5", background: "#F7F5F0", color: "#9B9490", textAlign: "center" }}>{v}</div>)}
                      </div>
                    )}
                    {["radio","checkbox"].includes(q.field_type) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(q.choices||[]).map((c, ci) => (
                          <div key={ci} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, border: "1px solid #E2DDD5", background: "#F7F5F0" }}>
                            <input type={q.field_type === "radio" ? "radio" : "checkbox"} disabled />
                            <span style={{ fontSize: 13, color: "#4A5568" }}>
                              {c.label.split(/(\{\{input\}\})/gi).map((part, pi) =>
                                /^\{\{input\}\}$/i.test(part)
                                  ? <input key={pi} disabled placeholder="____" style={{ display: "inline-block", width: 70, margin: "0 4px", border: "2px solid #2D7D72", borderRadius: 5, padding: "1px 4px", fontSize: 12, background: "#E6F4F2", textAlign: "center", verticalAlign: "middle" }} />
                                  : /^\{\{textarea\}\}$/i.test(part)
                                  ? <textarea key={pi} disabled placeholder="Enter details…" rows={2} style={{ display: "block", width: "100%", margin: "4px 0", border: "2px solid #2D7D72", borderRadius: 6, padding: "4px 8px", fontSize: 12, background: "#E6F4F2", resize: "none", fontFamily: "inherit" }} />
                                  : <span key={pi}>{part}</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {q.field_type === "dropdown" && (
                      <select disabled style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, background: "#F7F5F0", color: "#9B9490" }}>
                        <option>— Select —</option>
                        {(q.choices||[]).map((c, ci) => <option key={ci}>{c.label.replace(/\{\{input\}\}/gi, "____")}</option>)}
                      </select>
                    )}
                    {(q.narrative_prefix || q.narrative_suffix) && (
                      <div style={{ marginTop: 10, padding: "8px 12px", background: "#F0F9F8", borderRadius: 7, fontSize: 11, color: "#2D7D72", fontStyle: "italic" }}>
                        Narrative: "{q.narrative_prefix || ""}…{q.narrative_suffix || ""}"
                      </div>
                    )}
                  </div>
                  )
                ))}
              </div>
            )}
            {secOutroText && (
              <div style={{ background: "#EEF0F8", border: "1px solid #C5CAE9", borderRadius: 9, padding: "12px 16px", fontSize: 13, color: "#1B2B4B", lineHeight: 1.7, marginTop: 16 }}
                dangerouslySetInnerHTML={{ __html: secOutroText.replace(/\n/g, "<br/>") }} />
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── Email Templates (Invite / Welcome / Pre-Session Prep) ───────────────────
const EMAIL_TEMPLATE_CATEGORIES = [
  { value: "invite", label: "Agreement to Sign (Invite)" },
  { value: "welcome", label: "Welcome Email" },
  { value: "prep", label: "Pre-Session Prep" },
];
const EMAIL_MERGE_FIELDS = ["client_name", "other_party_name", "mediator_name", "case_number", "firm_name", "firm_phone", "firm_email", "firm_address", "invite_link"];

function EmailTemplateEditor({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template row, or {} for new
  const [form, setForm] = useState({ name: "", category: "welcome", subject: "", body: "" });
  const [msg, setMsg] = useState(null);

  const load = async () => {
    const res = await api.get("email_templates.php?action=list");
    setTemplates(Array.isArray(res) ? res : []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  const startEdit = (t) => { setEditing(t); setForm({ name: t.name || "", category: t.category || "welcome", subject: t.subject || "", body: t.body || "" }); setMsg(null); };
  const startNew = () => { setEditing({}); setForm({ name: "", category: "welcome", subject: "", body: "" }); setMsg(null); };

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) { setMsg({ type: "error", text: "Name, subject, and body are all required." }); return; }
    const payload = editing?.id ? { ...form, id: editing.id } : form;
    const res = await api.post("email_templates.php?action=save_template", payload);
    if (!res || res.success !== true) { setMsg({ type: "error", text: res?.error || "Failed to save template." }); return; }
    setEditing(null);
    await load();
  };

  const duplicate = async (t) => { await api.post("email_templates.php?action=duplicate_template", { id: t.id }); await load(); };
  const remove = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; await api.post("email_templates.php?action=delete_template", { id: t.id }); if (editing?.id === t.id) setEditing(null); await load(); };
  const setDefault = async (t) => { await api.post("email_templates.php?action=set_default", { id: t.id }); await load(); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#F7F5F0", width: "100%", maxWidth: 900, height: "100%", overflow: "auto", boxShadow: "-10px 0 40px rgba(0,0,0,0.2)" }}>
        <div style={{ background: "#1B2B4B", color: "#fff", padding: "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>Email Templates</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>Manage reusable templates for client emails</div>
          </div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#fff" }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          {!editing && (
            <>
              <button onClick={startNew} style={{ marginBottom: 18, padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ New Template</button>
              {EMAIL_TEMPLATE_CATEGORIES.map(cat => (
                <div key={cat.value} style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>{cat.label}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {templates.filter(t => t.category === cat.value).map(t => (
                      <div key={t.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                            {t.name}
                            {!!t.is_default && <span style={{ fontSize: 10, fontWeight: 700, background: "#EAF4EA", color: "#2E7D32", padding: "2px 8px", borderRadius: 20 }}>DEFAULT</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "#9B9490", marginTop: 2 }}>{t.subject}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {!t.is_default && <button onClick={() => setDefault(t)} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Set Default</button>}
                          <button onClick={() => startEdit(t)} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                          <button onClick={() => duplicate(t)} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C8873A" }}>Copy</button>
                          <button onClick={() => remove(t)} style={{ padding: "6px 12px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                        </div>
                      </div>
                    ))}
                    {templates.filter(t => t.category === cat.value).length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No templates yet.</div>}
                  </div>
                </div>
              ))}
            </>
          )}

          {editing && (
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{editing.id ? "Edit Template" : "New Template"}</div>
              <Msg msg={msg} />
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Template Name</div>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Category</div>
                    <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}>
                      {EMAIL_TEMPLATE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Subject</div>
                  <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Body</div>
                  <RichTextArea value={form.body} onChange={v => setForm({ ...form, body: v })} placeholder="Write the email body here…" />
                </div>
                <div style={{ background: "#F7F5F0", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#4A5568" }}>
                  <strong>Merge fields:</strong> {EMAIL_MERGE_FIELDS.map(f => `{{${f}}}`).join("  ")}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={save} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Save</button>
                  <button onClick={() => setEditing(null)} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Client Onboarding Panel (case "Send Emails" tab) ─────────────────────────
// Step 1 reuses the existing invite/e-signature flow (invites + resendInvite,
// passed in from the parent). Steps 2 and 3 are template-driven compose cards
// that share the same send mechanics, differing only by template category.
function fillMergeFields(str, fields) {
  let out = String(str || "");
  Object.keys(fields || {}).forEach(k => { out = out.split(`{{${k}}}`).join(String(fields[k] ?? "")); });
  return out;
}

function EmailComposeCard({ title, description, category, activeCase, caseRole, caseRepoDocs, sendAction, logLabel }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [docIds, setDocIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get(`email_templates.php?action=list&category=${category}`).then(res => {
      const list = Array.isArray(res) ? res : [];
      setTemplates(list);
      const def = list.find(t => t.is_default) || list[0];
      if (def) applyTemplate(def.id, list);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const applyTemplate = async (id, list) => {
    const t = (list || templates).find(t => String(t.id) === String(id));
    if (!t || !activeCase) return;
    setTemplateId(id);
    const fields = await api.get(`client_emails.php?action=get_merge_fields&case_id=${activeCase.id}&case_role=${encodeURIComponent(caseRole)}`);
    setSubject(fillMergeFields(t.subject, fields));
    setBody(fillMergeFields(t.body, fields));
  };

  const toggleDoc = (id) => setDocIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { setMsg({ type: "error", text: "Subject and body are required." }); return; }
    setSending(true);
    const res = await api.post(`client_emails.php?action=${sendAction}`, { case_id: activeCase.id, case_role: caseRole, subject, body, doc_ids: docIds });
    setSending(false);
    if (res?.success) setMsg({ type: "success", text: `${logLabel} sent to ${res.sent_to}.` });
    else setMsg({ type: "error", text: res?.error || `Failed to send ${logLabel.toLowerCase()}.` });
  };

  const attachableDocs = (caseRepoDocs || []).filter(d => d.file_path);

  return (
    <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 14 }}>{description}</div>
      <Msg msg={msg} />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Template</div>
          <select value={templateId} onChange={e => applyTemplate(e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }}>
            {templates.length === 0 && <option value="">No templates — add one in Comms → Email Templates</option>}
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Subject</div>
          <input value={subject} onChange={e => setSubject(e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Body</div>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={7} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", fontFamily: "inherit", resize: "vertical" }} />
        </div>
        {attachableDocs.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 6 }}>Attach from Doc Repository</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 140, overflow: "auto" }}>
              {attachableDocs.map(d => (
                <label key={d.id} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={docIds.includes(d.id)} onChange={() => toggleDoc(d.id)} /> {d.title}
                </label>
              ))}
            </div>
          </div>
        )}
        <button onClick={send} disabled={sending || !templates.length} style={{ alignSelf: "flex-start", padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: sending ? 0.6 : 1 }}>
          {sending ? "Sending…" : `Send to ${caseRole}`}
        </button>
      </div>
    </div>
  );
}

function ClientOnboardingPanel({ activeCase, invites, caseRepoDocs, onOpenInvite, onResendInvite }) {
  const roles = [...new Set((invites || []).map(i => i.case_role))];
  const [activeRole, setActiveRole] = useState(roles[0] || "Client 1");
  useEffect(() => { if (roles.length && !roles.includes(activeRole)) setActiveRole(roles[0]); }, [invites]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Client Onboarding Emails</div><div style={{ fontSize: 13, color: "#9B9490" }}>Send the agreement, welcome, and prep emails for this case — replies go straight to your inbox.</div></div>

      {/* Step 1: Agreement to sign */}
      <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>1. Send Agreement to Sign</div>
        <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 14 }}>Sends the portal invite — client creates an account, logs in, and signs the agreement(s) assigned to this case.</div>
        {invites.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 10 }}>No invite sent yet.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {invites.map(inv => (
            <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F7F5F0", borderRadius: 8, padding: "10px 14px" }}>
              <div><div style={{ fontWeight: 600, fontSize: 13 }}>{inv.email}</div><div style={{ fontSize: 11, color: "#9B9490" }}>{inv.case_role}</div></div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Badge label={inv.status} color={inv.status === "Accepted" ? { bg: "#EAF4EA", text: "#2E7D32" } : { bg: "#FDF3E7", text: "#C8873A" }} />
                {inv.status === "Pending" && <button onClick={() => onResendInvite(inv)} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Resend</button>}
              </div>
            </div>
          ))}
        </div>
        <button onClick={onOpenInvite} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>✉ Send Invite</button>
      </div>

      {roles.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            {roles.map(r => (
              <button key={r} onClick={() => setActiveRole(r)} style={{ padding: "7px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: activeRole === r ? "1px solid #2D7D72" : "1px solid #E2DDD5", background: activeRole === r ? "#E6F4F2" : "#fff", color: activeRole === r ? "#2D7D72" : "#4A5568" }}>{r}</button>
            ))}
          </div>

          {/* Step 2: Welcome email */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1B2B4B", marginBottom: 8 }}>2. Send Welcome Email</div>
            <EmailComposeCard
              title="Welcome Email" description="A friendly welcome, with any onboarding documents attached."
              category="welcome" activeCase={activeCase} caseRole={activeRole} caseRepoDocs={caseRepoDocs}
              sendAction="send_welcome" logLabel="Welcome email"
            />
          </div>

          {/* Step 3: Prep email(s) */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1B2B4B", marginBottom: 8 }}>3. Send Pre-Session Prep Email</div>
            <EmailComposeCard
              title="Pre-Session Prep Email" description="Send ahead of any session — can be sent as many times as needed."
              category="prep" activeCase={activeCase} caseRole={activeRole} caseRepoDocs={caseRepoDocs}
              sendAction="send_prep" logLabel="Pre-session prep email"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Render choice label with inline fill-in fields ──────────────────────────
// Labels containing {{input}} or {{textarea}} get inline fill-in fields substituted in
function ChoiceLabel({ label, qId, choiceValue, fillIns, setFillIn }) {
  const parts = label.split(/(\{\{input\}\}|\{\{textarea\}\})/gi);
  if (parts.length === 1) return <span>{label}</span>;
  let idx = 0;
  return (
    <span>
      {parts.map((part, i) => {
        if (/^\{\{input\}\}$/i.test(part)) {
          const key = `${qId}_fill_${choiceValue}_${idx++}`;
          return (
            <input key={key} type="text"
              value={fillIns?.[key] || ""}
              onChange={e => setFillIn(key, e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder="fill in"
              style={{ display: "inline-block", width: 80, margin: "0 5px", border: "2px solid #2D7D72", borderRadius: 5, outline: "none", fontSize: 13, fontWeight: 700, color: "#1B2B4B", background: "#E6F4F2", textAlign: "center", padding: "2px 6px", verticalAlign: "middle" }} />
          );
        }
        if (/^\{\{textarea\}\}$/i.test(part)) {
          const key = `${qId}_fill_${choiceValue}_${idx++}`;
          return (
            <textarea key={key}
              value={fillIns?.[key] || ""}
              onChange={e => setFillIn(key, e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder="Enter details…"
              rows={3}
              style={{ display: "block", width: "100%", margin: "6px 0", border: "2px solid #2D7D72", borderRadius: 7, outline: "none", fontSize: 13, color: "#1B2B4B", background: "#E6F4F2", padding: "6px 10px", resize: "vertical", fontFamily: "inherit" }} />
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function PPBuilder({ caseData, user, onClose }) {
  const [sections, setSections] = useState([]);
  const [activeSec, setActiveSec] = useState(null);
  const [questions, setQuestions] = useState({});
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSections, setPreviewSections] = useState([]);
  const [finalized, setFinalized] = useState(false);
  const [activeBuilderTab, setActiveBuilderTab] = useState("children");

  useEffect(() => {
    const init = async () => {
      const secs = await api.get("parenting_plan.php?action=get_sections");
      if (Array.isArray(secs) && secs.length > 0) {
        setSections(secs);
        setActiveSec(secs[0]);
      }
      const plan = await api.get(`parenting_plan.php?action=get_plan&case_id=${caseData.id}`);
      if (plan && plan.answers) {
        setAnswers(plan.answers);
        if (plan.answers.__fillIns) setFillIns(plan.answers.__fillIns);
      }
      if (plan && plan.finalized == 1) setFinalized(true);
    };
    init();
  }, [caseData.id]);

  useEffect(() => {
    if (!activeSec) return;
    if (questions[activeSec.id]) return;
    api.get(`parenting_plan.php?action=get_questions&section_id=${activeSec.id}`).then(qs => {
      setQuestions(prev => ({ ...prev, [activeSec.id]: Array.isArray(qs) ? qs : [] }));
    });
  }, [activeSec]);

  const setAnswer = (qid, val) => setAnswers(prev => ({ ...prev, [qid]: val }));
  const [fillIns, setFillIns] = useState({});
  const setFillIn = (key, val) => setFillIns(prev => ({ ...prev, [key]: val }));

  // Build final narrative text for a choice label by substituting fill-in values
  const resolveFillIns = (label, qId, choiceValue) => {
    let idx = 0;
    return label.replace(/\{\{input\}\}|\{\{textarea\}\}/gi, () => {
      const key = `${qId}_fill_${choiceValue}_${idx++}`;
      return fillIns[key] || '___';
    });
  };

  // Mirrors the narrative computation in generate_text/pp_export on the PHP side —
  // used only to give the mediator a starting point when editing the wording for one case.
  const computeDefaultNarrative = (q) => {
    const ans = answers[q.id];
    if (ans === null || ans === undefined || ans === '' || (Array.isArray(ans) && ans.length === 0)) return '';
    const prefix = q.narrative_prefix || '';
    const suffix = q.narrative_suffix || '';
    const choices = q.choices || [];
    let narrative = '';
    if (q.field_type === 'radio' || q.field_type === 'dropdown') {
      const c = choices.find(c => c.value === ans);
      if (c) {
        const raw = c.narrative ? c.narrative : prefix + c.label + suffix;
        narrative = resolveFillIns(raw, q.id, c.value);
        if (/^other/i.test(c.label.trim())) {
          const otherDetail = (answers[`other_${q.id}_${c.value}`] || '').trim();
          if (otherDetail) narrative = narrative.replace(/\.$/, '') + ': ' + otherDetail + '.';
        }
      }
    } else if (q.field_type === 'checkbox') {
      const sel = Array.isArray(ans) ? ans : [ans];
      const narrs = []; const labs = [];
      choices.forEach(c => {
        if (sel.includes(c.value)) {
          const otherDetail = (answers[`other_${q.id}_${c.value}`] || '').trim();
          let item = c.narrative ? resolveFillIns(c.narrative, q.id, c.value) : resolveFillIns(c.label, q.id, c.value);
          if (/^other/i.test(c.label.trim()) && otherDetail) item = item.replace(/\.$/, '') + ': ' + otherDetail + '.';
          if (c.narrative) narrs.push(item); else labs.push(item);
        }
      });
      narrative = narrs.length > 0 ? narrs.join(' ') : prefix + labs.join(', ') + suffix;
    } else if (q.field_type === 'yesno') {
      const c = choices.find(c => c.value === ans && c.narrative);
      narrative = c ? c.narrative : prefix + (ans === 'yes' ? 'Yes' : 'No') + suffix;
    } else if (q.field_type === 'name') {
      const v = ans || {};
      const full = [v.first, v.middle, v.last].filter(Boolean).join(' ').trim();
      narrative = full ? prefix + full + suffix : '';
    } else if (q.field_type === 'address') {
      const v = ans || {};
      const addr = `${v.street || ''}, ${v.city || ''}, ${v.state || ''} ${v.zip || ''}`;
      narrative = addr.replace(/^[,\s]+|[,\s]+$/g, '') ? prefix + addr + suffix : '';
    } else if (q.field_type !== 'children') {
      narrative = prefix + ans + suffix;
    }
    return (narrative || '').trim();
  };

  // Substitutes {{petitioner_first}}, {{respondent_first}}, {{child_first}}, {{children}} in
  // Fixed Text sections with this case's actual names — mirrors resolveFixedTextPlaceholders in PHP.
  const resolvePlaceholders = (text) => {
    const petitionerFirst = (caseData.client1_name || '').trim().split(/\s+/)[0] || '';
    const respondentFirst = (caseData.client2_name || '').trim().split(/\s+/)[0] || '';
    const kids = Array.isArray(answers.__children) ? answers.__children : [];
    const firsts = kids.map(k => (k.first || '').trim()).filter(Boolean);
    const childFirst = firsts[0] || '';
    let childrenList = '';
    if (firsts.length === 1) childrenList = firsts[0];
    else if (firsts.length === 2) childrenList = `${firsts[0]} and ${firsts[1]}`;
    else if (firsts.length > 2) childrenList = `${firsts.slice(0, -1).join(', ')}, and ${firsts[firsts.length - 1]}`;
    return (text || "")
      .replace(/\{\{petitioner_first\}\}/gi, petitionerFirst)
      .replace(/\{\{respondent_first\}\}/gi, respondentFirst)
      .replace(/\{\{child_first\}\}/gi, childFirst)
      .replace(/\{\{children\}\}/gi, childrenList);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const mergedAnswers = { ...answers, __fillIns: fillIns };
      const res = await api.post("parenting_plan.php?action=save_plan", { case_id: caseData.id, answers: mergedAnswers, created_by: user.id, updated_by: user.id });
      if (res && res.success) setSaveMsg({ type: "success", text: "Plan saved!" });
      else setSaveMsg({ type: "error", text: res?.error || "Failed to save." });
    } catch {
      setSaveMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 3000);
  };

  const toggleFinalize = async () => {
    await savePlan();
    try {
      const newVal = finalized ? 0 : 1;
      const res = await api.post("parenting_plan.php?action=set_finalized", { case_id: caseData.id, finalized: newVal });
      if (res && res.success) setFinalized(newVal === 1);
      else setSaveMsg({ type: "error", text: res?.error || "Failed to update plan status." });
    } catch {
      setSaveMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const generatePreview = async () => {
    await savePlan();
    setGenerating(true);
    try {
      const res = await api.get(`parenting_plan.php?action=generate_text&case_id=${caseData.id}`);
      if (res && res.success) { setPreviewSections(res.sections || []); setShowPreview(true); }
      else setSaveMsg({ type: "error", text: res?.error || "Failed to generate preview." });
    } catch {
      setSaveMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setGenerating(false);
  };

  const exportDoc = async (format) => {
    await savePlan();
    window.open(`${API_BASE}/pp_export.php?case_id=${caseData.id}&format=${format}&auth_token=${getToken()}&_=${Date.now()}`, "_blank");
  };

  const currentQuestions = activeSec ? (questions[activeSec.id] || []) : [];
  const answeredCount = sections.reduce((acc, sec) => {
    const qs = questions[sec.id] || [];
    return acc + qs.filter(q => answers[q.id] !== undefined && answers[q.id] !== '').length;
  }, 0);

  if (showPreview) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#F7F5F0", zIndex: 2000, overflowY: "auto" }}>
        <div style={{ background: "#1B2B4B", color: "#fff", padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 800 }}>Preview — {caseData.client1_name} & {caseData.client2_name}</div>
          <div style={{ display: "flex", gap: 10 }}>
            {finalized && <span style={{ fontSize: 11, color: "#A8D5A2", fontWeight: 700, background: "rgba(168,213,162,0.15)", padding: "4px 12px", borderRadius: 20, border: "1px solid rgba(168,213,162,0.3)" }}>✓ Finalized — Visible to Clients</span>}
            <button onClick={() => exportDoc("pdf")} style={{ padding: "7px 16px", background: "#C8873A", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>🖨 Export PDF</button>
            <button onClick={() => exportDoc("docx")} style={{ padding: "7px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>📄 Export Word</button>
            <button onClick={toggleFinalize} style={{ padding: "7px 16px", background: finalized ? "#555" : "#6B4FA0", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>{finalized ? "🔒 Unfinalize" : "✅ Finalize & Share with Clients"}</button>
            <button onClick={() => setShowPreview(false)} style={{ padding: "7px 16px", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 7, fontSize: 12, color: "#fff", fontWeight: 600 }}>← Back to Builder</button>
            <button onClick={onClose} style={{ fontSize: 20, color: "rgba(255,255,255,0.5)" }}>×</button>
          </div>
        </div>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 36px", background: "#fff", minHeight: "100vh", boxShadow: "0 0 40px rgba(0,0,0,0.08)" }}>
          {(() => {
            const headerSec = previewSections.find(s => /plan.header.information/i.test(s.title));
            if (headerSec) {
              return (
                <div style={{ textAlign: "center", marginBottom: 40 }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 800, marginBottom: 16 }}>PARENTING PLAN</div>
                  {headerSec.paragraphs.map((p, pi) => {
                    if (!p) return null;
                    if (typeof p === 'object' && p.html) {
                      return <div key={pi} style={{ marginBottom: 4, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: p.html.replace(/&amp;/g,'&') }} />;
                    }
                    if (typeof p === 'object') return null;
                    return <div key={pi} style={{ fontSize: 13, color: "#4A5568", marginBottom: 4 }}>{String(p).replace(/&nbsp;/g,' ')}</div>;
                  })}
                </div>
              );
            }
            return (
              <div style={{ textAlign: "center", marginBottom: 40 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 800, marginBottom: 8 }}>PARENTING PLAN</div>
                <div style={{ fontSize: 15 }}>{caseData.client1_name} and {caseData.client2_name}</div>
                {caseData.case_number && <div style={{ fontSize: 13, color: "#9B9490", marginTop: 4 }}>Case No: {caseData.case_number}</div>}
                <div style={{ fontSize: 13, color: "#9B9490", marginTop: 4 }}>{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
              </div>
            );
          })()}
          {previewSections.length === 0 && <div style={{ textAlign: "center", color: "#9B9490", fontSize: 14 }}>No content generated yet. Fill in answers in the builder first.</div>}
          {previewSections.filter(sec => !/plan.header.information/i.test(sec.title)).map((sec, i) => (
            <div key={i} style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", color: "#1B2B4B", borderBottom: "2px solid #1B2B4B", paddingBottom: 5, marginBottom: 14, letterSpacing: "0.5px" }}>{sec.title}</div>
              {sec.__is_schedule && sec.paragraphs[0]?.__schedule ? (() => {
                const { p_label, r_label, sch, times, days_short } = sec.paragraphs[0];
                const thS = { background: "#1B2B4B", color: "#fff", padding: "5px 4px", border: "1px solid #1B2B4B", textAlign: "center", fontSize: 10 };
                const tdS = { padding: "3px 4px", border: "1px solid #D4CFC9", textAlign: "center", fontSize: 10, fontWeight: 600 };
                const tdT = { padding: "4px 8px", border: "1px solid #D4CFC9", background: "#F0EDE8", fontWeight: 600, fontSize: 10, whiteSpace: "nowrap" };
                return (
                  <div>
                    <div style={{ fontSize: 11, marginBottom: 10 }}><b>Petitioner:</b> {p_label} &nbsp;&nbsp; <b>Respondent:</b> {r_label}</div>
                    {[1, 2].map(week => {
                      const weekStart = week === 1 && sch.week1_start ? ` — begins ${new Date(sch.week1_start + 'T12:00:00').toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : "";
                      return (
                        <div key={week} style={{ marginBottom: 20 }}>
                          <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6 }}>Week {week} Schedule{weekStart}</div>
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 500 }}>
                              <thead>
                                <tr>
                                  <th style={{...thS, width: 80, textAlign: "left", paddingLeft: 8 }}>Time</th>
                                  {days_short.map(d => <th key={d} style={thS}>{d}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {times.map((time, ti) => (
                                  <tr key={ti} style={{ background: ti % 2 === 0 ? "#FAFAF9" : "#fff" }}>
                                    <td style={tdT}>{time}</td>
                                    {[0,1,2,3,4,5,6].map(di => (
                                      <td key={di} style={tdS}>{sch[`w${week}_${ti}_${di}`] || ""}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })() : sec.__is_schoolbreaks && sec.paragraphs[0]?.__schoolbreaks ? (() => {
                const sb = sec.paragraphs[0].sb;
                const DEFAULT_BREAKS = ["Winter Break","Spring Break","Summer Break","Fall Break"];
                const customIds = Array.isArray(sb.__custom) ? sb.__custom : [];
                const fmtDate = (d) => { if (!d) return ""; try { return new Date(d + 'T12:00:00').toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };
                const fmtTime = (t) => { if (!t) return ""; const [h,m] = t.split(":"); const hr = parseInt(h); return `${hr%12||12}:${m} ${hr<12?"AM":"PM"}`; };
                const allBreaks = [
                  ...DEFAULT_BREAKS.map(name => ({ name, d: sb["b_" + name.toLowerCase().replace(/[^a-z0-9]/g,"_")] || {} })),
                  ...customIds.map(id => ({ name: sb[`${id}_name`]||"Custom Break", d: sb[id]||{} }))
                ].filter(b => b.d.start || b.d.end || b.d.who);
                const thS = { background: "#1B2B4B", color: "#fff", padding: "6px 10px", border: "1px solid #1B2B4B", textAlign: "left", fontSize: 10, fontWeight: 700 };
                const tdS = { padding: "6px 10px", border: "1px solid #D4CFC9", fontSize: 10, verticalAlign: "top" };
                return (
                  <div>
                    {allBreaks.length === 0
                      ? <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No school break information entered.</div>
                      : <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 10 }}>
                          <thead>
                            <tr>
                              <th style={thS}>Break</th>
                              <th style={thS}>Dates</th>
                              <th style={thS}>Who Has Children</th>
                              <th style={thS}>Exchange</th>
                              <th style={thS}>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allBreaks.map((b, i) => (
                              <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAF9" : "#fff" }}>
                                <td style={{ ...tdS, fontWeight: 700 }}>{b.name}</td>
                                <td style={tdS}>{fmtDate(b.d.start)}{b.d.end ? ` – ${fmtDate(b.d.end)}` : ""}</td>
                                <td style={tdS}>{b.d.who || ""}</td>
                                <td style={tdS}>{fmtTime(b.d.exchange_time)}{b.d.exchange_location ? ` @ ${b.d.exchange_location}` : ""}</td>
                                <td style={tdS}>{b.d.notes || ""}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    }
                  </div>
                );
              })() : sec.__is_holiday && sec.paragraphs[0]?.__holiday ? (() => {
                const hol = sec.paragraphs[0].hol;
                const mode = hol.mode || "individual";
                const thS = { background: "#1B2B4B", color: "#fff", padding: "5px 8px", border: "1px solid #1B2B4B", textAlign: "center", fontSize: 10 };
                const tdS = { padding: "4px 8px", border: "1px solid #D4CFC9", fontSize: 10 };

                if (mode === "groups") {
                  const groupA = hol.groupA || ["Thanksgiving Day", "Christmas Eve", "New Year's Eve"];
                  const groupB = hol.groupB || ["4th of July", "Labor Day", "Christmas Day"];
                  const maxRows = Math.max(groupA.length, groupB.length);
                  return (
                    <div>
                      <div style={{ fontSize: 11, marginBottom: 12, lineHeight: 1.7 }}>
                        {(() => {
                          const pFirst = (caseData.client1_name || '').trim().split(/\s+/)[0] || 'Petitioner';
                          const rFirst = (caseData.client2_name || '').trim().split(/\s+/)[0] || 'Respondent';
                          return (
                            <>
                              The holidays will be divided between the parents as follows:<br/>
                              In <b>even-numbered years</b>, {pFirst} will have the minor children on Group A holidays and {rFirst} will have the minor children on Group B holidays.<br/>
                              In <b>odd-numbered years</b>, {pFirst} will have the minor children on Group B holidays and {rFirst} will have the minor children on Group A holidays.
                            </>
                          );
                        })()}
                      </div>
                      <table style={{ borderCollapse: "collapse", width: "60%", fontSize: 10 }}>
                        <thead>
                          <tr>
                            <th style={thS}>Group A Holidays</th>
                            <th style={thS}>Group B Holidays</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({ length: maxRows }).map((_, r) => (
                            <tr key={r} style={{ background: r % 2 === 0 ? "#FAFAF9" : "#fff" }}>
                              <td style={tdS}>{groupA[r] || ""}</td>
                              <td style={tdS}>{groupB[r] || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                } else {
                  const DEFAULT_HOLIDAYS = ["New Year's Day","Mother's Day","Memorial Day","Father's Day","July 4","Labor Day","Halloween","Thanksgiving","Christmas Eve","Christmas Day","New Year's Eve","Rosh Hashana","Yom Kippur","Sukkos","Chanukah","Purim","Passover","Shavuos"];
                  const customIds = Array.isArray(hol.__custom) ? hol.__custom : [];
                  const allHols = [
                    ...DEFAULT_HOLIDAYS.map(name => {
                      const hkey = "h_" + name.toLowerCase().replace(/[^a-z0-9]/g, "_");
                      const d = hol[hkey] || {};
                      return { name, d };
                    }).filter(h => h.d.odd || h.d.even || h.d.start || h.d.end),
                    ...customIds.map(id => {
                      const name = hol[`${id}_name`] || "Other";
                      const d = hol[id] || {};
                      return { name, d };
                    }).filter(h => h.name !== "Other" || h.d.odd || h.d.even)
                  ];
                  const chunks = [];
                  for (let i = 0; i < allHols.length; i += 4) chunks.push(allHols.slice(i, i + 4));
                  const fmtTime = (t) => { if (!t) return ""; try { const [h, m] = t.split(":"); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr < 12 ? "AM" : "PM"}`; } catch { return t; } };
                  return (
                    <div>
                      <div style={{ fontSize: 11, marginBottom: 12, lineHeight: 1.7 }}>
                        The parents will divide holidays as follows. The parent's name or initials show which parent has the children in even and odd years for each holiday.
                      </div>
                      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 10 }}>
                        {chunks.map((row, ci) => (
                          <tbody key={ci}>
                            <tr>
                              {row.map((h, i) => <th key={i} style={thS}>{h.name}</th>)}
                              {Array.from({ length: 4 - row.length }).map((_, i) => <th key={`p${i}`} style={{ ...thS, background: "#1B2B4B" }} />)}
                            </tr>
                            {["odd","even","start","end"].map(field => (
                              <tr key={field}>
                                {row.map((h, i) => (
                                  <td key={i} style={tdS}>
                                    <span style={{ color: "#9B9490", fontSize: 9 }}>{field === "odd" ? "ODD: " : field === "even" ? "EVEN: " : field === "start" ? "Start: " : "End: "}</span>
                                    {field === "start" || field === "end" ? fmtTime(h.d[field]) : h.d[field] || ""}
                                  </td>
                                ))}
                                {Array.from({ length: 4 - row.length }).map((_, i) => <td key={`p${i}`} style={tdS} />)}
                              </tr>
                            ))}
                            <tr><td colSpan={4} style={{ padding: 4, border: "none" }} /></tr>
                          </tbody>
                        ))}
                      </table>
                    </div>
                  );
                }
              })() : sec.paragraphs.map((p, pi) => {
                if (!p) return null;
                // html paragraph (from intro/outro)
                if (typeof p === 'object' && p.html) {
                  let decoded = p.html
                    .replace(/&amp;/g, '&')
                    .replace(/\n/g, '<br/>');
                  return <div key={pi} style={{ marginBottom: 10, lineHeight: 1.75 }}
                    dangerouslySetInnerHTML={{ __html:
                      `<style>
                        .pp-html-para>*:first-child{margin-top:0!important;padding-top:0!important}
                        .pp-html-para{line-height:1.75}
                        .pp-html-para, .pp-html-para *{font-family:'Bookman Old Style',Georgia,serif!important;font-size:12pt!important}
                        .pp-html-para p{margin:0 0 6px 0;line-height:1.75}
                        .pp-html-para div{line-height:1.75;min-height:1em}
                        .pp-html-para ul{margin-left:24px;margin-bottom:8px;list-style-type:disc}
                        .pp-html-para ol{margin-left:24px;margin-bottom:8px;list-style-type:decimal}
                        .pp-html-para ol[style*="lower-alpha"]{list-style-type:lower-alpha}
                        .pp-html-para ol[style*="upper-alpha"]{list-style-type:upper-alpha}
                        .pp-html-para li{margin-bottom:3px}
                        .pp-html-para h1{font-weight:bold;margin-bottom:8px}
                        .pp-html-para h2{font-weight:bold;margin-bottom:6px}
                        .pp-html-para h3{font-weight:bold;margin-bottom:4px}
                        .pp-html-para blockquote{border-left:3px solid #2D7D72;padding-left:12px;color:#555;margin:8px 0;font-style:italic}
                      </style><div class="pp-html-para">${decoded}</div>`
                    }} />;
                }
                // skip any other objects (schedule/holiday markers etc)
                if (typeof p === 'object') return null;
                // plain string — decode common entities
                const text = String(p).replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
                return <div key={pi} style={{ marginBottom: 10, lineHeight: 1.75, textAlign: "justify", fontSize: "12pt", fontFamily: "'Bookman Old Style', Georgia, serif" }}>{text}</div>;
              })}
            </div>
          ))}
          <div style={{ marginTop: 60 }}>
            <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", color: "#1B2B4B", borderBottom: "2px solid #1B2B4B", paddingBottom: 5, marginBottom: 24 }}>Signatures</div>
            <div style={{ marginBottom: 48, fontSize: 11 }}>Petitioner: <span style={{ display: "inline-block", width: 260, borderBottom: "1px solid #000", marginLeft: 8 }}></span> &nbsp;&nbsp; Date: <span style={{ display: "inline-block", width: 140, borderBottom: "1px solid #000", marginLeft: 8 }}></span></div>
            <div style={{ marginBottom: 48, fontSize: 11 }}>Respondent: <span style={{ display: "inline-block", width: 260, borderBottom: "1px solid #000", marginLeft: 8 }}></span> &nbsp;&nbsp; Date: <span style={{ display: "inline-block", width: 140, borderBottom: "1px solid #000", marginLeft: 8 }}></span></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#F7F5F0", zIndex: 2000, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ background: "#1B2B4B", color: "#fff", padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 800 }}>Parenting Plan Builder</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{caseData.client1_name} & {caseData.client2_name} · {answeredCount} answers</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.type === "success" ? "#A8D5A2" : "#E57373", fontWeight: 600 }}>{saveMsg.text}</span>}
          <button onClick={savePlan} disabled={saving} style={{ padding: "7px 16px", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 7, fontSize: 12, color: "#fff", fontWeight: 600 }}>{saving ? "Saving…" : "💾 Save"}</button>
          <button onClick={generatePreview} disabled={generating} style={{ padding: "7px 16px", background: "#C8873A", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>{generating ? "Generating…" : "👁 Preview & Export"}</button>
          <button onClick={onClose} style={{ fontSize: 20, color: "rgba(255,255,255,0.5)", marginLeft: 8 }}>×</button>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Section sidebar */}
        <div style={{ width: 240, background: "#fff", borderRight: "1px solid #E2DDD5", overflowY: "auto", flexShrink: 0 }}>
          {/* Children — always first */}
          <div onClick={() => { setActiveBuilderTab("children"); setActiveSec(null); }}
            style={{ padding: "12px 16px", cursor: "pointer", borderLeft: `3px solid ${activeBuilderTab === "children" ? "#2D7D72" : "transparent"}`, background: activeBuilderTab === "children" ? "#E6F4F2" : "transparent", borderBottom: "1px solid #F0EDE8" }}>
            <div style={{ fontSize: 13, fontWeight: activeBuilderTab === "children" ? 700 : 500, color: activeBuilderTab === "children" ? "#2D7D72" : "#1B2B4B" }}>👶 Children</div>
            <div style={{ fontSize: 10, color: "#9B9490", marginTop: 2 }}>{(answers.__children || []).length} child{(answers.__children || []).length !== 1 ? "ren" : ""} added</div>
          </div>
          {/* Parenting Time Schedule */}
          <div onClick={() => { setActiveBuilderTab("schedule"); setActiveSec(null); }}
            style={{ padding: "12px 16px", cursor: "pointer", borderLeft: `3px solid ${activeBuilderTab === "schedule" ? "#2D7D72" : "transparent"}`, background: activeBuilderTab === "schedule" ? "#E6F4F2" : "transparent", borderBottom: "1px solid #F0EDE8" }}>
            <div style={{ fontSize: 13, fontWeight: activeBuilderTab === "schedule" ? 700 : 500, color: activeBuilderTab === "schedule" ? "#2D7D72" : "#1B2B4B" }}>📅 Parenting Schedule</div>
            <div style={{ fontSize: 10, color: "#9B9490", marginTop: 2 }}>Weekly time grid</div>
          </div>
          {/* Holiday Schedule */}
          <div onClick={() => { setActiveBuilderTab("holiday"); setActiveSec(null); }}
            style={{ padding: "12px 16px", cursor: "pointer", borderLeft: `3px solid ${activeBuilderTab === "holiday" ? "#2D7D72" : "transparent"}`, background: activeBuilderTab === "holiday" ? "#E6F4F2" : "transparent", borderBottom: "1px solid #F0EDE8" }}>
            <div style={{ fontSize: 13, fontWeight: activeBuilderTab === "holiday" ? 700 : 500, color: activeBuilderTab === "holiday" ? "#2D7D72" : "#1B2B4B" }}>🎄 Holiday Schedule</div>
            <div style={{ fontSize: 10, color: "#9B9490", marginTop: 2 }}>Group A/B or individual</div>
          </div>
          {/* School Breaks */}
          <div onClick={() => { setActiveBuilderTab("schoolbreaks"); setActiveSec(null); }}
            style={{ padding: "12px 16px", cursor: "pointer", borderLeft: `3px solid ${activeBuilderTab === "schoolbreaks" ? "#2D7D72" : "transparent"}`, background: activeBuilderTab === "schoolbreaks" ? "#E6F4F2" : "transparent", borderBottom: "1px solid #F0EDE8" }}>
            <div style={{ fontSize: 13, fontWeight: activeBuilderTab === "schoolbreaks" ? 700 : 500, color: activeBuilderTab === "schoolbreaks" ? "#2D7D72" : "#1B2B4B" }}>🎒 School Breaks</div>
            <div style={{ fontSize: 10, color: "#9B9490", marginTop: 2 }}>Break parenting schedule</div>
          </div>
          {sections.map((sec, i) => {
            const qs = questions[sec.id] || [];
            const answered = qs.filter(q => answers[q.id] !== undefined && answers[q.id] !== '').length;
            const isActive = activeBuilderTab === "section" && activeSec?.id === sec.id;
            return (
              <div key={sec.id} onClick={() => { setActiveBuilderTab("section"); setActiveSec(sec); }}
                style={{ padding: "12px 16px", cursor: "pointer", borderLeft: `3px solid ${isActive ? "#2D7D72" : "transparent"}`, background: isActive ? "#E6F4F2" : "transparent", borderBottom: "1px solid #F0EDE8" }}>
                <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? "#2D7D72" : "#1B2B4B" }}>{sec.title}</div>
                {qs.length > 0 && <div style={{ fontSize: 10, color: "#9B9490", marginTop: 2 }}>{answered}/{qs.length} answered</div>}
              </div>
            );
          })}
        </div>

        {/* Questions area */}
        <div style={{ flex: 1, overflowY: "auto", padding: 32 }}>
          {activeBuilderTab === "schoolbreaks" ? (() => {
            const DEFAULT_BREAKS = ["Winter Break","Spring Break","Summer Break","Fall Break"];
            const sb = answers.__schoolbreaks || {};
            const setSB = (val) => setAnswer("__schoolbreaks", val);
            const customBreaks = Array.isArray(sb.__custom) ? sb.__custom : [];
            const addCustomBreak = () => {
              const id = `break_${Date.now()}`;
              setSB({ ...sb, __custom: [...customBreaks, id] });
            };
            const removeCustomBreak = (id) => {
              const updated = { ...sb, __custom: customBreaks.filter(c => c !== id) };
              delete updated[id]; delete updated[`${id}_name`];
              setSB(updated);
            };
            const inputS = { border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", width: "100%", background: "#fff" };
            const labelS = { fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 };

            const BreakCard = ({ name, bkey, editable, onRemove }) => {
              const d = sb[bkey] || {};
              const upd = (k, v) => setSB({ ...sb, [bkey]: { ...d, [k]: v } });
              return (
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ background: "#1B2B4B", color: "#fff", padding: "10px 14px", fontSize: 13, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {editable
                      ? <input value={sb[`${bkey}_name`]||""} onChange={e => setSB({ ...sb, [`${bkey}_name`]: e.target.value })}
                          placeholder="Break name…" style={{ background: "transparent", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, outline: "none", flex: 1 }} />
                      : <span>{name}</span>}
                    {onRemove && <button onClick={onRemove} style={{ color: "rgba(255,255,255,0.6)", fontWeight: 700, fontSize: 16, marginLeft: 8 }}>×</button>}
                  </div>
                  <div style={{ padding: "14px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><div style={labelS}>Start Date</div><input type="date" value={d.start||""} onChange={e => upd("start", e.target.value)} style={inputS} /></div>
                    <div><div style={labelS}>End Date</div><input type="date" value={d.end||""} onChange={e => upd("end", e.target.value)} style={inputS} /></div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={labelS}>Who has the children</div>
                      <input value={d.who||""} onChange={e => upd("who", e.target.value)} placeholder="e.g. Petitioner first half, Respondent second half" style={inputS} />
                    </div>
                    <div><div style={labelS}>Exchange time</div><input type="time" value={d.exchange_time||""} onChange={e => upd("exchange_time", e.target.value)} style={inputS} /></div>
                    <div><div style={labelS}>Exchange location</div><input value={d.exchange_location||""} onChange={e => upd("exchange_location", e.target.value)} placeholder="e.g. School, Home" style={inputS} /></div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={labelS}>Additional notes</div>
                      <textarea value={d.notes||""} onChange={e => upd("notes", e.target.value)} placeholder="Any special arrangements…" rows={2}
                        style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <div style={{ maxWidth: 860 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#1B2B4B" }}>School Breaks Schedule</div>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>Enter parenting time arrangements for each school break.</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  {DEFAULT_BREAKS.map(name => {
                    const bkey = "b_" + name.toLowerCase().replace(/[^a-z0-9]/g, "_");
                    return <BreakCard key={bkey} name={name} bkey={bkey} editable={false} />;
                  })}
                  {customBreaks.map(id => (
                    <BreakCard key={id} name={sb[`${id}_name`]||""} bkey={id} editable={true} onRemove={() => removeCustomBreak(id)} />
                  ))}
                </div>
                <button onClick={addCustomBreak}
                  style={{ padding: "10px 24px", border: "2px dashed #2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent", cursor: "pointer" }}>
                  + Add Custom Break
                </button>
              </div>
            );
          })() : activeBuilderTab === "schedule" ? (() => {
            const TIMES = ["8 AM","9 AM","10 AM","11 AM","Noon","1 PM","2 PM","3 PM","4 PM","5 PM","6 PM","7 PM","8 PM","9 PM – Overnight"];
            const DAYS  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
            const DAYS_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
            const sch = answers.__schedule || {};
            const setSch = (val) => setAnswer("__schedule", val);
            const getCell = (week, t, d) => (sch[`w${week}_${t}_${d}`] || "");
            const setCell = (week, t, d, v) => setSch({ ...sch, [`w${week}_${t}_${d}`]: v });
            const cellStyle = { width: "100%", border: "1px solid #D4CFC9", borderRadius: 4, padding: "3px 4px", fontSize: 11, outline: "none", textAlign: "center", background: "#fff", fontWeight: 600, color: "#1B2B4B" };
            const thStyle = { fontSize: 10, fontWeight: 700, color: "#fff", background: "#1B2B4B", padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap" };
            const tdTimeStyle = { fontSize: 10, fontWeight: 600, color: "#4A5568", background: "#F0EDE8", padding: "4px 8px", whiteSpace: "nowrap", borderBottom: "1px solid #E2DDD5" };

            // Each pattern is 14 entries: week1 Mon..Sun, then week2 Mon..Sun.
            // "A" = the "starting" parent chosen in the picker, "B" = the other parent.
            const SCHEDULE_TEMPLATES = [
              { id: "eow", label: "Every Other Week", split: "50/50",
                desc: "One parent has the children for a full week, then the other parent has the next week.",
                pattern: "AAAAAAABBBBBBB" },
              { id: "223", label: "2-2-3", split: "50/50",
                desc: "2 days, 2 days, then a 3-day weekend — alternating which parent gets the long weekend.",
                pattern: "AABBAAABBAABBB" },
              { id: "2255", label: "2-2-5-5", split: "50/50",
                desc: "2 days, 2 days, then two separate 5-day blocks.",
                pattern: "AABBAAAAABBBBB" },
              { id: "3443", label: "3-4-4-3", split: "50/50",
                desc: "3 days, 4 days, 4 days, 3 days — each parent gets a long weekend every other week.",
                pattern: "AAABBBBAAAABBB" },
              { id: "weekend", label: "Every Weekend", split: "60/40",
                desc: "One parent has every Friday–Sunday; the other has the children Monday–Thursday every week.",
                pattern: "BBBBAAABBBBAAA" },
              { id: "eoweekend", label: "Every Other Weekend", split: "80/20",
                desc: "One parent has the children most of the time; the other gets alternating Friday–Sunday weekends.",
                pattern: "BBBBAAABBBBBBB" },
              { id: "all", label: "All to One Parent", split: "100/0",
                desc: "One parent has the children at all times.",
                pattern: "AAAAAAAAAAAAAA" },
              { id: "custom", label: "Custom", split: "",
                desc: "Skip the quick-fill and build the schedule by hand in the grid below.", pattern: null },
            ];

            const selectedTemplate = sch.template || "";
            const startParent = sch.template_start || "p";
            const pLabel = (sch.p_label || caseData.client1_name || "Petitioner").slice(0, 6);
            const rLabel = (sch.r_label || caseData.client2_name || "Respondent").slice(0, 6);

            const applyTemplate = (templateId, startParentVal) => {
              const tmpl = SCHEDULE_TEMPLATES.find(t => t.id === templateId);
              if (!tmpl || !tmpl.pattern) { setSch({ ...sch, template: templateId }); return; }
              const hasExisting = Object.keys(sch).some(k => k.startsWith("w") && sch[k]);
              if (hasExisting && !window.confirm("Applying this template will overwrite the schedule grid below. Continue?")) return;
              const labelA = startParentVal === "p" ? pLabel : rLabel;
              const labelB = startParentVal === "p" ? rLabel : pLabel;
              const newSch = { ...sch, template: templateId, template_start: startParentVal };
              for (let week = 1; week <= 2; week++) {
                for (let d = 0; d < 7; d++) {
                  const letter = tmpl.pattern[(week - 1) * 7 + d];
                  const label = letter === "A" ? labelA : labelB;
                  for (let t = 0; t < TIMES.length; t++) {
                    newSch[`w${week}_${t}_${d}`] = label;
                  }
                }
              }
              setSch(newSch);
            };

            return (
              <div style={{ maxWidth: 900 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#1B2B4B" }}>Parenting Time Schedule</div>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>Enter each parent's name or initials in the cells below to show who has the children at each time.</div>

                {/* Template picker */}
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px", marginBottom: 24 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#1B2B4B", marginBottom: 4 }}>Quick-Start Templates</div>
                  <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 14 }}>Pick a common schedule pattern to auto-fill the grid below, then fine-tune any cell by hand. Choose "Custom" to skip this and build it manually.</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 10 }}>
                    {SCHEDULE_TEMPLATES.map(t => (
                      <label key={t.id} onClick={() => setSch({ ...sch, template: t.id })}
                        style={{ display: "block", cursor: "pointer", border: selectedTemplate === t.id ? "2px solid #2D7D72" : "1px solid #E2DDD5", background: selectedTemplate === t.id ? "#F0F9F7" : "#FAFAF9", borderRadius: 9, padding: "10px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input type="radio" name="sch_template" readOnly checked={selectedTemplate === t.id} style={{ margin: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B" }}>{t.label}</span>
                          {t.split && <span style={{ fontSize: 11, color: "#9B9490", fontStyle: "italic" }}>{t.split}</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "#9B9490", marginTop: 4, lineHeight: 1.4 }}>{t.desc}</div>
                      </label>
                    ))}
                  </div>

                  {selectedTemplate && selectedTemplate !== "custom" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, paddingTop: 16, borderTop: "1px solid #E2DDD5", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, color: "#4A5568", fontWeight: 600 }}>
                        {selectedTemplate === "all" ? "Which parent has the children?" : "Which parent's block starts the cycle (Week 1, Monday)?"}
                      </span>
                      <select value={startParent} onChange={e => setSch({ ...sch, template_start: e.target.value })}
                        style={{ border: "1px solid #E2DDD5", borderRadius: 7, padding: "6px 10px", fontSize: 12, outline: "none" }}>
                        <option value="p">{pLabel}</option>
                        <option value="r">{rLabel}</option>
                      </select>
                      <button onClick={() => applyTemplate(selectedTemplate, startParent)}
                        style={{ padding: "7px 18px", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#fff", background: "#2D7D72", cursor: "pointer" }}>
                        Apply to Grid Below
                      </button>
                    </div>
                  )}
                </div>

                {/* Parent legend */}
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px", marginBottom: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 6, textTransform: "uppercase" }}>Petitioner label/initials</div>
                    <input value={sch.p_label||""} onChange={e => setSch({...sch, p_label: e.target.value})} placeholder={caseData.client1_name || "e.g. Mom or P"} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 6, textTransform: "uppercase" }}>Respondent label/initials</div>
                    <input value={sch.r_label||""} onChange={e => setSch({...sch, r_label: e.target.value})} placeholder={caseData.client2_name || "e.g. Dad or R"} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }} />
                  </div>
                </div>

                {[1, 2].map(week => (
                  <div key={week} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px", marginBottom: 24, overflowX: "auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#1B2B4B" }}>Week {week} Schedule</div>
                      {week === 1 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "#9B9490" }}>Week 1 begins on:</span>
                          <input type="date" value={sch.week1_start||""} onChange={e => setSch({...sch, week1_start: e.target.value})}
                            style={{ border: "1px solid #E2DDD5", borderRadius: 7, padding: "5px 10px", fontSize: 12, outline: "none" }} />
                        </div>
                      )}
                    </div>
                    <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 620 }}>
                      <thead>
                        <tr>
                          <th style={{...thStyle, width: 90}}>Time</th>
                          {DAYS_SHORT.map(d => <th key={d} style={thStyle}>{d}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {TIMES.map((time, ti) => (
                          <tr key={ti} style={{ background: ti % 2 === 0 ? "#FAFAF9" : "#fff" }}>
                            <td style={tdTimeStyle}>{time}</td>
                            {DAYS.map((day, di) => (
                              <td key={di} style={{ padding: "3px", borderBottom: "1px solid #E2DDD5" }}>
                                <input value={getCell(week, ti, di)} onChange={e => setCell(week, ti, di, e.target.value)}
                                  style={cellStyle} maxLength={6} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            );
          })() : activeBuilderTab === "holiday" ? (() => {
            const hol = answers.__holiday || {};
            const setHol = (val) => setAnswer("__holiday", val);
            const mode = hol.mode || "individual"; // "groups" or "individual"

            const DEFAULT_HOLIDAYS = [
              "New Year's Day", "Mother's Day", "Memorial Day", "Father's Day",
              "July 4", "Labor Day", "Halloween", "Thanksgiving",
              "Christmas Eve", "Christmas Day", "New Year's Eve",
              "Rosh Hashana", "Yom Kippur", "Sukkos", "Chanukah", "Purim", "Passover", "Shavuos"
            ];

            // Custom holidays stored as array in hol.__custom
            const customHols = Array.isArray(hol.__custom) ? hol.__custom : [];
            const addCustomHol = () => {
              const id = `custom_${Date.now()}`;
              setHol({ ...hol, __custom: [...customHols, id] });
            };
            const removeCustomHol = (id) => {
              const updated = { ...hol, __custom: customHols.filter(c => c !== id) };
              delete updated[id]; delete updated[`${id}_name`];
              setHol(updated);
            };

            const inputS = { border: "1px solid #E2DDD5", borderRadius: 6, padding: "5px 8px", fontSize: 12, outline: "none", width: "100%", background: "#fff" };
            const labelS = { fontSize: 10, fontWeight: 700, color: "#9B9490", marginBottom: 3, textTransform: "uppercase" };

            const renderHolCard = (name, hkey, editable, onRemove) => {
              const d = hol[hkey] || {};
              const upd = (k, v) => setHol({ ...hol, [hkey]: { ...d, [k]: v } });
              const isEmpty = !d.odd && !d.even && !d.start && !d.end && (!editable || !hol[`${hkey}_name`]);
              return (
                <div key={hkey} style={{ border: `1px solid ${isEmpty ? "#E2DDD5" : "#2D7D72"}`, borderRadius: 8, overflow: "hidden", opacity: isEmpty ? 0.7 : 1 }}>
                  <div style={{ background: isEmpty ? "#4A5568" : "#1B2B4B", color: "#fff", padding: "5px 10px", fontSize: 11, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {editable
                      ? <input value={hol[`${hkey}_name`]||""} onChange={e => setHol({ ...hol, [`${hkey}_name`]: e.target.value })}
                          placeholder="Holiday name…" style={{ background: "transparent", border: "none", color: "#fff", fontWeight: 700, fontSize: 11, outline: "none", flex: 1 }} />
                      : <span>{name}</span>}
                    {onRemove && <button onClick={onRemove} style={{ color: "rgba(255,255,255,0.6)", fontWeight: 700, fontSize: 14, marginLeft: 6 }}>×</button>}
                  </div>
                  <div style={{ padding: "8px 10px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    <div><div style={labelS}>ODD years</div><input value={d.odd||""} onChange={e => upd("odd", e.target.value)} placeholder="Initials" style={inputS} /></div>
                    <div><div style={labelS}>EVEN years</div><input value={d.even||""} onChange={e => upd("even", e.target.value)} placeholder="Initials" style={inputS} /></div>
                    <div><div style={labelS}>Start time</div><input type="time" value={d.start||""} onChange={e => upd("start", e.target.value)} style={inputS} /></div>
                    <div><div style={labelS}>End time</div><input type="time" value={d.end||""} onChange={e => upd("end", e.target.value)} style={inputS} /></div>
                  </div>
                </div>
              );
            };

            const groupA = hol.groupA || ["Thanksgiving Day", "Christmas Eve", "New Year's Eve"];
            const groupB = hol.groupB || ["4th of July", "Labor Day", "Christmas Day"];
            const setGroup = (g, i, v) => {
              const arr = [...(hol[g] || (g === "groupA" ? groupA : groupB))];
              arr[i] = v;
              setHol({ ...hol, [g]: arr });
            };
            const addToGroup = (g) => setHol({ ...hol, [g]: [...(hol[g] || (g === "groupA" ? groupA : groupB)), ""] });
            const removeFromGroup = (g, i) => {
              const arr = [...(hol[g] || (g === "groupA" ? groupA : groupB))];
              arr.splice(i, 1);
              setHol({ ...hol, [g]: arr });
            };

            return (
              <div style={{ maxWidth: 900 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#1B2B4B" }}>Holiday Schedule</div>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>Choose how holidays will be divided between parents.</div>

                {/* Mode selector */}
                <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                  {[["groups", "Group A / B Model"], ["individual", "Individual Holidays"]].map(([val, label]) => (
                    <button key={val} onClick={() => setHol({ ...hol, mode: val })}
                      style={{ flex: 1, padding: "12px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
                        border: `2px solid ${mode === val ? "#2D7D72" : "#E2DDD5"}`,
                        background: mode === val ? "#2D7D72" : "#fff",
                        color: mode === val ? "#fff" : "#4A5568" }}>
                      {label}
                    </button>
                  ))}
                </div>

                {mode === "groups" ? (
                  <div>

                    <div style={{ fontSize: 13, color: "#4A5568", marginBottom: 16, lineHeight: 1.6 }}>
                      In <b>even-numbered years</b>, Petitioner gets Group A holidays and Respondent gets Group B.<br/>
                      In <b>odd-numbered years</b>, Petitioner gets Group B and Respondent gets Group A.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      {[["groupA", "Group A Holidays"], ["groupB", "Group B Holidays"]].map(([gkey, gtitle]) => {
                        const grp = hol[gkey] || (gkey === "groupA" ? groupA : groupB);
                        return (
                          <div key={gkey} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, overflow: "hidden" }}>
                            <div style={{ background: "#1B2B4B", color: "#fff", padding: "10px 14px", fontSize: 13, fontWeight: 700 }}>{gtitle}</div>
                            <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                              {grp.map((h, i) => (
                                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                  <input value={h} onChange={e => setGroup(gkey, i, e.target.value)}
                                    placeholder="Holiday name" style={{ ...inputS, flex: 1 }} />
                                  <button onClick={() => removeFromGroup(gkey, i)} style={{ color: "#C0392B", fontWeight: 700, fontSize: 14 }}>×</button>
                                </div>
                              ))}
                              <button onClick={() => addToGroup(gkey)}
                                style={{ padding: "6px 0", border: "2px dashed #2D7D72", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#2D7D72", background: "transparent" }}>
                                + Add Holiday
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>

                    <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 12 }}>
                      Cards with no fields filled will <b>not appear</b> in the export. Grayed cards are empty.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                      {DEFAULT_HOLIDAYS.map(name => {
                        const hkey = "h_" + name.toLowerCase().replace(/[^a-z0-9]/g, "_");
                        return renderHolCard(name, hkey, false, null);
                      })}
                      {customHols.map(id => (
                        renderHolCard("", id, true, () => removeCustomHol(id))
                      ))}
                    </div>
                    <button onClick={addCustomHol}
                      style={{ padding: "10px 24px", border: "2px dashed #2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent", cursor: "pointer" }}>
                      + Add Custom Holiday
                    </button>
                  </div>
                )}
              </div>
            );
          })() : activeBuilderTab === "children" ? (() => {
            const children = Array.isArray(answers.__children) ? answers.__children : [];
            const setChildren = (kids) => setAnswer("__children", kids);
            const updateChild = (i, k, v) => setChildren(children.map((c, idx) => idx === i ? {...c, [k]: v} : c));
            const addChild = () => setChildren([...children, { first: "", middle: "", last: "", dob: "" }]);
            const removeChild = (i) => setChildren(children.filter((_, idx) => idx !== i));
            return (
              <div style={{ maxWidth: 680 }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#1B2B4B" }}>Children</div>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>Add all children covered under this parenting plan.</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {children.length === 0 && (
                    <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic", padding: "20px 0" }}>No children added yet. Click the button below to add a child.</div>
                  )}
                  {children.map((child, i) => {
                    const age = calcAge(child.dob);
                    return (
                      <div key={i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 24px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>Child {i + 1}</div>
                          <button onClick={() => removeChild(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>First Name</div>
                            <input value={child.first||""} onChange={e => updateChild(i, "first", e.target.value)} placeholder="First" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Middle Name</div>
                            <input value={child.middle||""} onChange={e => updateChild(i, "middle", e.target.value)} placeholder="Middle" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Last Name</div>
                            <input value={child.last||""} onChange={e => updateChild(i, "last", e.target.value)} placeholder="Last" style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Date of Birth</div>
                            <input type="date" value={child.dob||""} onChange={e => updateChild(i, "dob", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                          </div>
                          {age !== null && (
                            <div style={{ background: "#E6F4F2", borderRadius: 10, padding: "10px 18px", textAlign: "center", marginTop: 18 }}>
                              <div style={{ fontSize: 11, color: "#9B9490" }}>Age</div>
                              <div style={{ fontSize: 22, fontWeight: 800, color: "#2D7D72" }}>{age}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={addChild} style={{ padding: "12px 0", border: "2px dashed #2D7D72", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent", cursor: "pointer" }}>+ Add Child</button>
                </div>
              </div>
            );
          })() : !activeSec ? (
            <div style={{ color: "#9B9490", fontSize: 14 }}>Select a section to begin.</div>
          ) : (
            <div style={{ maxWidth: 680 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#1B2B4B" }}>{activeSec.title}</div>
              {activeSec.description && <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>{activeSec.description}</div>}
              {/* Intro text */}
              {activeSec.intro_text && <div style={{ background: "#EEF0F8", border: "1px solid #C5CAE9", borderRadius: 9, padding: "12px 16px", fontSize: 13, color: "#1B2B4B", lineHeight: 1.7, marginBottom: 8 }} dangerouslySetInnerHTML={{ __html: activeSec.intro_text.replace(/\n/g, "<br/>") }} />}

              {/* Parenting Plan for These Children: auto-filled from the Children tab, read-only */}
              {isChildrenSectionTitle(activeSec.title) ? (
                (() => {
                  const children = Array.isArray(answers.__children) ? answers.__children : [];
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {children.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic", padding: "20px 0" }}>
                          No children added yet. Add children in the Children tab and they'll appear here automatically.
                        </div>
                      ) : children.map((child, i) => {
                        const age = calcAge(child.dob);
                        const fullName = [child.first, child.middle, child.last].filter(Boolean).join(" ");
                        return (
                          <div key={i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "16px 20px", fontSize: 14, color: "#1B2B4B" }}>
                            {fullName || "(unnamed child)"}{age !== null ? `, age ${age}` : ""}
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 12, color: "#9B9490", marginTop: 4 }}>
                        This section is filled in automatically from the Children tab — there's nothing to fill in here.
                      </div>
                    </div>
                  );
                })()
              ) : activeSec.section_type === "fixed_text" ? (() => {
                const overrideKey = `fixed_text_override_${activeSec.id}`;
                const hasOverride = (answers[overrideKey] || '').trim() !== '';
                const showEdit = answers[`fixed_edit_show_${activeSec.id}`];
                const resolvedTemplate = activeSec.fixed_text ? resolvePlaceholders(activeSec.fixed_text) : '';
                return (
                <div>
                  <div style={{ background: "#FDF3E7", border: "1px solid #C8873A", borderRadius: 10, padding: "18px 20px", marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#C8873A", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Mandatory / Fixed Language</div>
                    <div style={{ fontSize: 13, color: "#4A5568", lineHeight: 1.75 }} dangerouslySetInnerHTML={{ __html: hasOverride ? answers[overrideKey].replace(/\n/g, "<br/>") : (resolvedTemplate ? resolvedTemplate.replace(/\n/g, "<br/>") : "No text configured yet.") }} />
                  </div>

                  {resolvedTemplate && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={() => setAnswer(`fixed_edit_show_${activeSec.id}`, !showEdit)}
                          style={{ fontSize: 12, color: showEdit ? "#9B9490" : "#6B4FA0", fontWeight: 700 }}>
                          {showEdit ? "✕ Close" : "✎ Edit wording for this case"}
                        </button>
                        {hasOverride && <span style={{ fontSize: 10, fontWeight: 700, color: "#6B4FA0", background: "#F1ECFA", borderRadius: 20, padding: "1px 8px" }}>Customized for this case</span>}
                      </div>
                      {showEdit && (
                        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ fontSize: 11, color: "#9B9490" }}>Names are already filled in from this case. Edit the text below — formatting and pasted content are preserved — to customize it just for this case. Your template is unaffected.</div>
                          <RichTextArea
                            value={hasOverride ? answers[overrideKey] : resolvedTemplate}
                            onChange={v => setAnswer(overrideKey, v)}
                            placeholder="Edit the wording for this case…" />
                          {hasOverride && (
                            <button onClick={() => setAnswer(overrideKey, '')} style={{ alignSelf: "flex-start", fontSize: 12, color: "#C0392B", fontWeight: 700 }}>Reset to template wording</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10 }}>
                    <input type="checkbox" checked={answers[`fixed_include_${activeSec.id}`] !== false}
                      onChange={e => setAnswer(`fixed_include_${activeSec.id}`, e.target.checked)}
                      style={{ width: 18, height: 18 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>Include this section in the parenting plan</div>
                      <div style={{ fontSize: 11, color: "#9B9490" }}>Uncheck to exclude this section from the exported document</div>
                    </div>
                  </div>
                </div>
                );
              })() : (
                <>
              {currentQuestions.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic", padding: "24px 0" }}>No questions in this section yet. Add some in the Template Editor.</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {currentQuestions.map(q => (
                  q.field_type === "header" ? (
                    <div key={q.id} style={{ fontSize: 16, fontWeight: 800, color: "#1B2B4B", borderBottom: "2px solid #1B2B4B", paddingBottom: 6, marginTop: 10 }}>
                      {q.label}
                      {q.help_text && <div style={{ fontSize: 12, fontWeight: 400, color: "#9B9490", marginTop: 4 }}>{q.help_text}</div>}
                    </div>
                  ) : (
                  <div key={q.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 24px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1B2B4B", marginBottom: q.help_text ? 4 : 12 }}>
                      {q.label} {q.required ? <span style={{ color: "#C0392B" }}>*</span> : null}
                    </div>
                    {q.help_text && <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 12, lineHeight: 1.5 }}>{q.help_text}</div>}

                    {q.field_type === "radio" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(q.choices||[]).map(c => (
                          <div key={c.value}>
                            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 12px", borderRadius: 8, border: `1px solid ${answers[q.id]===c.value ? "#2D7D72" : "#E2DDD5"}`, background: answers[q.id]===c.value ? "#E6F4F2" : "#fff" }}>
                              <input type="radio" name={`q_${q.id}`} value={c.value} checked={answers[q.id]===c.value} onChange={() => setAnswer(q.id, c.value)} style={{ marginTop: 2 }} />
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}><ChoiceLabel label={c.label} qId={q.id} choiceValue={c.value} fillIns={fillIns} setFillIn={setFillIn} /></div>
                                {c.narrative && <div style={{ fontSize: 11, color: "#9B9490", marginTop: 2, fontStyle: "italic" }} dangerouslySetInnerHTML={{ __html: c.narrative }} />}
                              </div>
                            </label>
                            {/^other/i.test(c.label.trim()) && answers[q.id] === c.value && (
                              <div style={{ marginTop: 4, marginLeft: 12 }}>
                                <textarea value={answers[`other_${q.id}_${c.value}`] || ""} onChange={e => setAnswer(`other_${q.id}_${c.value}`, e.target.value)}
                                  placeholder="Please specify…" rows={2}
                                  style={{ width: "100%", border: "2px solid #2D7D72", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", background: "#f0faf9" }} />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {q.field_type === "checkbox" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(q.choices||[]).map(c => {
                          const checked = Array.isArray(answers[q.id]) ? answers[q.id].includes(c.value) : false;
                          return (
                            <div key={c.value}>
                              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 12px", borderRadius: 8, border: `1px solid ${checked ? "#2D7D72" : "#E2DDD5"}`, background: checked ? "#E6F4F2" : "#fff" }}>
                                <input type="checkbox" checked={checked} onChange={e => {
                                  const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                                  setAnswer(q.id, e.target.checked ? [...cur, c.value] : cur.filter(v => v !== c.value));
                                }} style={{ marginTop: 2 }} />
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 600 }}><ChoiceLabel label={c.label} qId={q.id} choiceValue={c.value} fillIns={fillIns} setFillIn={setFillIn} /></div>
                                  {c.narrative && <div style={{ fontSize: 11, color: "#9B9490", marginTop: 2, fontStyle: "italic" }} dangerouslySetInnerHTML={{ __html: c.narrative }} />}
                                </div>
                              </label>
                              {/^other/i.test(c.label.trim()) && checked && (
                                <div style={{ marginTop: 4, marginLeft: 12 }}>
                                  <textarea value={answers[`other_${q.id}_${c.value}`] || ""} onChange={e => setAnswer(`other_${q.id}_${c.value}`, e.target.value)}
                                    placeholder="Please specify…" rows={2}
                                    style={{ width: "100%", border: "2px solid #2D7D72", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", background: "#f0faf9" }} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.field_type === "yesno" && (
                      <div style={{ display: "flex", gap: 10 }}>
                        {["yes","no"].map(v => (
                          <button key={v} onClick={() => setAnswer(q.id, v)}
                            style={{ flex: 1, padding: "10px 0", borderRadius: 9, fontSize: 13, fontWeight: 700, border: `2px solid ${answers[q.id]===v ? "#2D7D72" : "#E2DDD5"}`, background: answers[q.id]===v ? "#2D7D72" : "#fff", color: answers[q.id]===v ? "#fff" : "#4A5568" }}>
                            {v === "yes" ? "✓ Yes" : "✗ No"}
                          </button>
                        ))}
                      </div>
                    )}

                    {q.field_type === "dropdown" && (() => {
                      const selChoice = (q.choices||[]).find(c => c.value === answers[q.id]);
                      const hasInput = selChoice && /\{\{input\}\}/i.test(selChoice.label);
                      return (
                        <div>
                          <select value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)}
                            style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}>
                            <option value="">— Select —</option>
                            {(q.choices||[]).map(c => <option key={c.value} value={c.value}>{c.label.replace(/\{\{input\}\}|\{\{textarea\}\}/gi, "___")}</option>)}
                          </select>
                          {hasInput && (
                            <div style={{ marginTop: 8 }}>
                              <ChoiceLabel label={selChoice.label} qId={q.id} choiceValue={selChoice.value} fillIns={fillIns} setFillIn={setFillIn} />
                            </div>
                          )}
                          {selChoice && /^other/i.test(selChoice.label.trim()) && (
                            <div style={{ marginTop: 8 }}>
                              <textarea value={answers[`other_${q.id}_${selChoice.value}`] || ""} onChange={e => setAnswer(`other_${q.id}_${selChoice.value}`, e.target.value)}
                                placeholder="Please specify…" rows={2}
                                style={{ width: "100%", border: "2px solid #2D7D72", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", background: "#f0faf9" }} />
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {q.field_type === "text" && (
                      <input value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)}
                        style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                    )}

                    {q.field_type === "textarea" && (
                      <textarea value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)} rows={4}
                        style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit" }} />
                    )}

                    {q.field_type === "date" && (
                      <input type="date" value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)}
                        style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                    )}

                    {q.field_type === "time" && (
                      <input type="time" value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)}
                        style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                    )}

                    {q.field_type === "phone" && (
                      <input type="tel" value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)} placeholder="(555) 555-5555"
                        style={{ width: "100%", maxWidth: 260, border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                    )}

                    {q.field_type === "email" && (
                      <input type="email" value={answers[q.id]||""} onChange={e => setAnswer(q.id, e.target.value)} placeholder="email@example.com"
                        style={{ width: "100%", maxWidth: 320, border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }} />
                    )}

                    {q.field_type === "children" && (() => {
                      const children = Array.isArray(answers[q.id]) ? answers[q.id] : [{ first: "", middle: "", last: "", dob: "" }];
                      const setChildren = (kids) => setAnswer(q.id, kids);
                      const updateChild = (i, k, v) => {
                        const updated = children.map((c, idx) => idx === i ? {...c, [k]: v} : c);
                        setChildren(updated);
                      };
                      const addChild = () => setChildren([...children, { first: "", middle: "", last: "", dob: "" }]);
                      const removeChild = (i) => setChildren(children.filter((_, idx) => idx !== i));
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {children.map((child, i) => {
                            const age = calcAge(child.dob);
                            return (
                              <div key={i} style={{ background: "#F7F5F0", borderRadius: 10, padding: "14px 16px", border: "1px solid #E2DDD5" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#2D7D72" }}>Child {i + 1}</div>
                                  {children.length > 1 && <button onClick={() => removeChild(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>}
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                                  <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>First Name</div><input value={child.first||""} onChange={e => updateChild(i, "first", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", background: "#fff" }} /></div>
                                  <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Middle Name</div><input value={child.middle||""} onChange={e => updateChild(i, "middle", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", background: "#fff" }} /></div>
                                  <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Last Name</div><input value={child.last||""} onChange={e => updateChild(i, "last", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", background: "#fff" }} /></div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                  <div style={{ flex: 1 }}><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Date of Birth</div><input type="date" value={child.dob||""} onChange={e => updateChild(i, "dob", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", background: "#fff" }} /></div>
                                  {age !== null && <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B", marginTop: 18 }}>Age: {age}</div>}
                                </div>
                              </div>
                            );
                          })}
                          <button onClick={addChild} style={{ padding: "9px 0", border: "2px dashed #2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent" }}>+ Add Another Child</button>
                        </div>
                      );
                    })()}

                    {q.field_type === "name" && (() => {
                      const val = answers[q.id] || {};
                      const setName = (k,v) => setAnswer(q.id, {...val, [k]: v});
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                          <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>First Name</div><input value={val.first||""} onChange={e => setName("first", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                          <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Middle Name</div><input value={val.middle||""} onChange={e => setName("middle", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                          <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Last Name</div><input value={val.last||""} onChange={e => setName("last", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                        </div>
                      );
                    })()}

                    {q.field_type === "address" && (() => {
                      const val = answers[q.id] || {};
                      const setAddr = (k,v) => setAnswer(q.id, {...val, [k]: v});
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Street Address</div><input value={val.street||""} onChange={e => setAddr("street", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8 }}>
                            <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>City</div><input value={val.city||""} onChange={e => setAddr("city", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                            <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>State</div><input value={val.state||""} onChange={e => setAddr("state", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                            <div><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>ZIP</div><input value={val.zip||""} onChange={e => setAddr("zip", e.target.value)} style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} /></div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Per-case narrative override */}
                    {(() => {
                      const defaultNarrative = computeDefaultNarrative(q);
                      const overrideKey = `narrative_override_${q.id}`;
                      const hasOverride = (answers[overrideKey] || '').trim() !== '';
                      const showEdit = answers[`narr_edit_show_${q.id}`];
                      if (!defaultNarrative && !hasOverride) return null;
                      return (
                        <div style={{ marginTop: 12, borderTop: "1px dashed #E2DDD5", paddingTop: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <button onClick={() => setAnswer(`narr_edit_show_${q.id}`, !showEdit)}
                              style={{ fontSize: 12, color: showEdit ? "#9B9490" : "#6B4FA0", fontWeight: 700 }}>
                              {showEdit ? "✕ Close" : "✎ Edit wording for this case"}
                            </button>
                            {hasOverride && <span style={{ fontSize: 10, fontWeight: 700, color: "#6B4FA0", background: "#F1ECFA", borderRadius: 20, padding: "1px 8px" }}>Customized for this case</span>}
                          </div>
                          {showEdit && (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ fontSize: 11, color: "#9B9490" }}>This is the sentence that will appear in the plan for this question. Edit it — formatting and pasted content are preserved — to customize the wording just for this case. Your template is unaffected.</div>
                              <RichTextArea
                                value={hasOverride ? answers[overrideKey] : defaultNarrative}
                                onChange={v => {
                                  setAnswer(overrideKey, v);
                                  if (q.field_type === 'text' || q.field_type === 'textarea') {
                                    setAnswer(q.id, htmlToPlainText(v));
                                  }
                                }}
                                placeholder="Edit the wording for this case…" />
                              {hasOverride && (
                                <button onClick={() => setAnswer(overrideKey, '')} style={{ alignSelf: "flex-start", fontSize: 12, color: "#C0392B", fontWeight: 700 }}>Reset to template wording</button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Custom Provision */}
                    {q.has_custom_provision == 1 && (() => {
                      const showProv = answers[`prov_show_${q.id}`];
                      return (
                        <div style={{ marginTop: 12, borderTop: "1px dashed #E2DDD5", paddingTop: 12 }}>
                          <button onClick={() => setAnswer(`prov_show_${q.id}`, !showProv)}
                            style={{ fontSize: 12, color: showProv ? "#C0392B" : "#2D7D72", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                            {showProv ? "✕ Remove Custom Provision" : "+ Add Custom Provision"}
                          </button>
                          {showProv && (
                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                              <div>
                                <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Provision Title</div>
                                <input value={answers[`prov_title_${q.id}`]||""} onChange={e => setAnswer(`prov_title_${q.id}`, e.target.value)}
                                  placeholder="e.g. Exception to Holiday Schedule"
                                  style={{ width: "100%", border: "1px solid #C8873A", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none" }} />
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Provision Text</div>
                                <RichTextArea value={answers[`prov_text_${q.id}`]||""} onChange={v => setAnswer(`prov_text_${q.id}`, v)}
                                  placeholder="Describe the custom provision or exception…" />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )
                ))}
              </div>
              </>
              )}

              {/* Outro text */}
              {activeSec.outro_text && <div style={{ background: "#EEF0F8", border: "1px solid #C5CAE9", borderRadius: 9, padding: "12px 16px", fontSize: 13, color: "#1B2B4B", lineHeight: 1.7, marginTop: 8 }} dangerouslySetInnerHTML={{ __html: activeSec.outro_text.replace(/\n/g, "<br/>") }} />}

              {/* Section-level Custom Provisions */}
              {(() => {
                const secProvKey = `sec_provisions_${activeSec.id}`;
                const provisions = answers[secProvKey] || [];
                const updateProv = (idx, field, val) => {
                  const updated = [...provisions];
                  updated[idx] = { ...updated[idx], [field]: val };
                  setAnswer(secProvKey, updated);
                };
                const addProv = () => setAnswer(secProvKey, [...provisions, { title: "", text: "" }]);
                const removeProv = (idx) => setAnswer(secProvKey, provisions.filter((_, i) => i !== idx));
                return (
                  <div style={{ marginTop: 20, borderTop: "2px dashed #E2DDD5", paddingTop: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#4A5568", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Section Custom Provisions {provisions.length > 0 && <span style={{ background: "#2D7D72", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 11, marginLeft: 6 }}>{provisions.length}</span>}
                      </div>
                      <button onClick={addProv}
                        style={{ padding: "6px 14px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                        + Add Custom Provision
                      </button>
                    </div>
                    {provisions.length === 0 && (
                      <div style={{ fontSize: 12, color: "#9B9490", fontStyle: "italic" }}>No custom provisions for this section yet. Click the button above to add one.</div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {provisions.map((prov, idx) => (
                        <div key={idx} style={{ background: "#FFFDF7", border: "1px solid #C8873A", borderRadius: 10, padding: "16px 18px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#C8873A", textTransform: "uppercase", letterSpacing: "0.08em" }}>Custom Provision {idx + 1}</div>
                            <button onClick={() => removeProv(idx)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Provision Title</div>
                            <input value={prov.title || ""} onChange={e => updateProv(idx, "title", e.target.value)}
                              placeholder="e.g. Additional Holiday Agreement"
                              style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 7, padding: "8px 10px", fontSize: 13, outline: "none" }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Provision Text</div>
                            <RichTextArea value={prov.text || ""} onChange={v => updateProv(idx, "text", v)}
                              placeholder="Describe the provision or exception in full…" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Section nav */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32, paddingTop: 24, borderTop: "1px solid #E2DDD5" }}>
                {sections.findIndex(s => s.id === activeSec.id) > 0 ? (
                  <button onClick={() => setActiveSec(sections[sections.findIndex(s => s.id === activeSec.id) - 1])}
                    style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>← Previous</button>
                ) : <div />}
                {sections.findIndex(s => s.id === activeSec.id) < sections.length - 1 ? (
                  <button onClick={() => setActiveSec(sections[sections.findIndex(s => s.id === activeSec.id) + 1])}
                    style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Next →</button>
                ) : (
                  <button onClick={generatePreview} style={{ padding: "9px 20px", background: "#C8873A", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Preview & Export →</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DOCUMENT REPOSITORY MODALS ──────────────────────────────────────────────
const DOC_TYPES = ["Template", "Reference", "Legal Form", "Handout", "Additional Legal Forms"];

function RepoUploadModal({ user, onClose, onSaved, folders = [], initialFolder = null }) {
  const [form, setForm] = useState({ title: "", doc_type: "Reference", description: "", link_url: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(initialFolder);

  // Build flat list with indentation labels for the dropdown
  const buildFolderOptions = (parentId, depth, acc) => {
    folders.filter(f => f.parent_id === (parentId || null)).forEach(f => {
      acc.push({ id: f.id, label: "  ".repeat(depth) + "📁 " + f.name });
      buildFolderOptions(f.id, depth + 1, acc);
    });
    return acc;
  };
  const folderOptions = buildFolderOptions(null, 0, []);

  const upload = async () => {
    if (!file && !form.link_url.trim()) { setMsg({ type: "error", text: "Add a file, a link, or both." }); return; }
    if (!form.title.trim()) { setMsg({ type: "error", text: "Please enter a title." }); return; }
    setUploading(true);
    const fd = new FormData();
    if (file) fd.append("file", file);
    fd.append("link_url", form.link_url);
    fd.append("title", form.title);
    fd.append("doc_type", form.doc_type);
    fd.append("description", form.description);
    fd.append("uploaded_by", user.id);
    fd.append("uploaded_by_name", user.name);
    if (selectedFolder) fd.append("folder_id", selectedFolder);
    try {
      const res = await fetch(`${API_BASE}/doc_repository.php?action=upload`, { method: "POST", body: fd, headers: { "X-Auth-Token": getToken() } }).then(r => r.json());
      if (res && res.success) onSaved(res.doc);
      else setMsg({ type: "error", text: res?.error || "Upload failed." });
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setUploading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800 }}>Upload Document</div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>
        <Msg msg={msg} />
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Document Title *" value={form.title} onChange={v => setForm({...form, title: v})} placeholder="e.g. Financial Disclosure Form" />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Document Type</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {DOC_TYPES.map(t => (
                <button key={t} onClick={() => setForm({...form, doc_type: t})}
                  style={{ padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: form.doc_type === t ? "2px solid #2D7D72" : "2px solid #E2DDD5", background: form.doc_type === t ? "#E6F4F2" : "#fff", color: form.doc_type === t ? "#2D7D72" : "#9B9490" }}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          {folderOptions.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Save to Folder (optional)</div>
              <select value={selectedFolder || ""} onChange={e => setSelectedFolder(e.target.value || null)}
                style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", background: "#fff" }}>
                <option value="">📂 No folder (All Documents)</option>
                {folderOptions.map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Description (optional)</div>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description of this document…" rows={3}
              style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>File (PDF, Word, Excel — max 20MB)</div>
            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={e => { setFile(e.target.files[0]); if (!form.title && e.target.files[0]) setForm(f => ({...f, title: e.target.files[0].name.replace(/\.[^.]+$/, '')})); }}
              style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13 }} />
            {file && <div style={{ fontSize: 11, color: "#2D7D72", marginTop: 4, fontWeight: 600 }}>✓ {file.name} ({(file.size/1024/1024).toFixed(1)}MB)</div>}
          </div>
          <Input label="Link (optional)" value={form.link_url} onChange={v => setForm({...form, link_url: v})} placeholder="e.g. https://example.com/form" />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button onClick={upload} disabled={uploading} style={{ flex: 1, padding: "10px 0", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: uploading ? 0.7 : 1 }}>{uploading ? "Uploading…" : "Upload Document"}</button>
          <button onClick={onClose} style={{ padding: "10px 18px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, color: "#4A5568" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function RepoEditModal({ doc, onClose, onSaved }) {
  const [form, setForm] = useState({ title: doc.title, doc_type: doc.doc_type, description: doc.description || "", link_url: doc.link_url || "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    if (!form.title.trim()) { setMsg({ type: "error", text: "Title is required." }); return; }
    setSaving(true);
    try {
      const res = await api.post("doc_repository.php?action=update", { id: doc.id, ...form });
      if (res && res.success) onSaved({ ...doc, ...form });
      else setMsg({ type: "error", text: res?.error || "Failed to save." });
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800 }}>Edit Document</div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>
        <Msg msg={msg} />
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Title" value={form.title} onChange={v => setForm({...form, title: v})} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Type</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {DOC_TYPES.map(t => (
                <button key={t} onClick={() => setForm({...form, doc_type: t})}
                  style={{ padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: form.doc_type === t ? "2px solid #2D7D72" : "2px solid #E2DDD5", background: form.doc_type === t ? "#E6F4F2" : "#fff", color: form.doc_type === t ? "#2D7D72" : "#9B9490" }}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Description</div>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <Input label="Link (optional)" value={form.link_url} onChange={v => setForm({...form, link_url: v})} placeholder="e.g. https://example.com/form" />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button onClick={save} disabled={saving} style={{ flex: 1, padding: "10px 0", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>{saving ? "Saving…" : "Save Changes"}</button>
          <button onClick={onClose} style={{ padding: "10px 18px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, color: "#4A5568" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function RepoAssignModal({ doc, cases, user, onClose, onAssigned }) {
  const [selectedCases, setSelectedCases] = useState([]);
  const [visibleToClient, setVisibleToClient] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [msg, setMsg] = useState(null);
  const [search, setSearch] = useState("");

  const toggleCase = (id) => setSelectedCases(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const assign = async () => {
    if (selectedCases.length === 0) { setMsg({ type: "error", text: "Select at least one case." }); return; }
    setAssigning(true);
    const res = await api.post("doc_repository.php?action=assign", {
      repo_doc_id: doc.id,
      case_ids: selectedCases,
      assigned_by: user.id,
      assigned_by_name: user.name,
      visible_to_client: visibleToClient ? 1 : 0
    });
    if (res.success) {
      setMsg({ type: "success", text: `Assigned to ${res.assigned} case(s) successfully!` });
      setTimeout(onAssigned, 1200);
    } else setMsg({ type: "error", text: "Assignment failed." });
    setAssigning(false);
  };

  const filteredCases = cases.filter(c =>
    c.status === "Active" &&
    (!search || `${c.client1_name} ${c.client2_name} ${c.case_number}`.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: "100%", maxWidth: 500, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800 }}>Assign to Cases</div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>📎 {doc.title}</div>
        <Msg msg={msg} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cases…"
          style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", marginBottom: 12 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16, maxHeight: 280, overflowY: "auto" }}>
          {filteredCases.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No active cases found.</div>}
          {filteredCases.map(c => (
            <div key={c.id} onClick={() => toggleCase(c.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 9, border: `1px solid ${selectedCases.includes(c.id) ? "#2D7D72" : "#E2DDD5"}`, background: selectedCases.includes(c.id) ? "#E6F4F2" : "#fff", cursor: "pointer" }}>
              <input type="checkbox" checked={selectedCases.includes(c.id)} onChange={() => toggleCase(c.id)} style={{ width: 16, height: 16 }} onClick={e => e.stopPropagation()} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B" }}>{c.client1_name} & {c.client2_name}</div>
                <div style={{ fontSize: 11, color: "#9B9490" }}>{c.case_number}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#F7F5F0", borderRadius: 9, marginBottom: 16 }}>
          <input type="checkbox" checked={visibleToClient} onChange={e => setVisibleToClient(e.target.checked)} style={{ width: 16, height: 16 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Make visible to clients immediately</div>
            <div style={{ fontSize: 11, color: "#9B9490" }}>If unchecked, document is internal only until you share it</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={assign} disabled={assigning || selectedCases.length === 0}
            style={{ flex: 1, padding: "10px 0", background: selectedCases.length > 0 ? "#2D7D72" : "#ccc", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>
            {assigning ? "Assigning…" : `Assign to ${selectedCases.length} Case${selectedCases.length !== 1 ? "s" : ""}`}
          </button>
          <button onClick={onClose} style={{ padding: "10px 18px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, color: "#4A5568" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── AUDIT LOG COMPONENT ─────────────────────────────────────────────────────
function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: "", user: "", date: "" });
  const [page, setPage] = useState(1);
  const perPage = 25;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get("audit_log.php?action=get_logs");
        setLogs(Array.isArray(res) ? res : []);
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const actionColors = {
    LOGIN: { bg: "#E6F4F2", text: "#2D7D72" },
    LOGOUT: { bg: "#F0F0F0", text: "#555" },
    CREATE: { bg: "#EEF0F8", text: "#1B2B4B" },
    UPDATE: { bg: "#FDF3E7", text: "#C8873A" },
    DELETE: { bg: "#FDEEEC", text: "#C0392B" },
    VIEW: { bg: "#F7F5F0", text: "#9B9490" },
  };

  const getActionColor = (action) => {
    for (const [key, val] of Object.entries(actionColors)) {
      if (action?.toUpperCase().includes(key)) return val;
    }
    return { bg: "#F0F0F0", text: "#555" };
  };

  const filtered = logs.filter(l => {
    if (filter.action && !l.action?.toLowerCase().includes(filter.action.toLowerCase())) return false;
    if (filter.user && !l.user_name?.toLowerCase().includes(filter.user.toLowerCase())) return false;
    if (filter.date && !l.created_at?.startsWith(filter.date)) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const uniqueActions = [...new Set(logs.map(l => l.action))].filter(Boolean).sort();

  return (
    <div style={{ padding: "32px 36px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Audit Log</h1>
          <div style={{ fontSize: 13, color: "#9B9490" }}>{filtered.length} events recorded</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input value={filter.user} onChange={e => { setFilter({...filter, user: e.target.value}); setPage(1); }}
            placeholder="Filter by user…"
            style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, outline: "none", width: 160 }} />
          <select value={filter.action} onChange={e => { setFilter({...filter, action: e.target.value}); setPage(1); }}
            style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, outline: "none" }}>
            <option value="">All Actions</option>
            {uniqueActions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input type="date" value={filter.date} onChange={e => { setFilter({...filter, date: e.target.value}); setPage(1); }}
            style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, outline: "none" }} />
          {(filter.action || filter.user || filter.date) && (
            <button onClick={() => { setFilter({ action: "", user: "", date: "" }); setPage(1); }}
              style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, color: "#9B9490" }}>Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#9B9490" }}>Loading audit log…</div>
      ) : paginated.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#9B9490" }}>No log entries found.</div>
      ) : (
        <>
          <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
            {/* Table header */}
            <div style={{ display: "grid", gridTemplateColumns: "160px 140px 120px 140px 1fr", gap: 0, background: "#1B2B4B", padding: "10px 20px" }}>
              {["Timestamp", "User", "Action", "Target", "Details"].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>
            {/* Rows */}
            {paginated.map((log, i) => (
              <div key={log.id} style={{ display: "grid", gridTemplateColumns: "160px 140px 120px 140px 1fr", gap: 0, padding: "12px 20px", borderBottom: i < paginated.length - 1 ? "1px solid #F0EDE8" : "none", background: i % 2 === 0 ? "#fff" : "#FAFAF8", alignItems: "center" }}>
                <div style={{ fontSize: 12, color: "#4A5568" }}>{log.created_at?.replace("T", " ").substring(0, 16)}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1B2B4B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.user_name || "—"}</div>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: getActionColor(log.action).bg, color: getActionColor(log.action).text }}>
                    {log.action}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#4A5568" }}>{log.target_table ? `${log.target_table}${log.target_id ? ` #${log.target_id}` : ""}` : "—"}</div>
                <div style={{ fontSize: 12, color: "#9B9490", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={log.details || ""}>{log.details || "—"}</div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 13, color: page === 1 ? "#C5C0BB" : "#1B2B4B", fontWeight: 600 }}>← Prev</button>
              <span style={{ fontSize: 13, color: "#9B9490" }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 13, color: page === totalPages ? "#C5C0BB" : "#1B2B4B", fontWeight: 600 }}>Next →</button>
            </div>
          )}
        </>
      )}

      {/* Summary stats */}
      <div className="r-grid4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 24 }}>
        {[
          { label: "Total Events", value: logs.length },
          { label: "Logins", value: logs.filter(l => l.action === "LOGIN").length },
          { label: "Deletions", value: logs.filter(l => l.action?.includes("DELETE")).length, accent: "#C0392B" },
          { label: "Unique Users", value: [...new Set(logs.map(l => l.user_name))].filter(Boolean).length },
        ].map(s => (
          <div key={s.label} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.accent || "#1B2B4B" }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BILLING COMPONENTS ───────────────────────────────────────────────────────
const TIME_CATEGORIES = ["Session","Drafting","Communication","Review","Research","Travel","Admin","Custom"];

function TimeEntryModal({ entry, caseData, user, staff, onClose, onSaved }) {
  // For flat fee cases default to non-billable but still track value
  const isFlatFee = caseData.fee_structure === "flat_fee";
  const defaultBillable = entry ? (entry.billable===1||entry.billable===true) : !isFlatFee;
  // Use case hourly rate as default for value tracking even on flat fee
  const defaultRate = entry?.hourly_rate || parseFloat(caseData.hourly_rate||0) || 0;

  const [form, setForm] = useState({
    entry_date: entry?.entry_date || new Date().toISOString().split("T")[0],
    category: entry?.category || "Session",
    custom_category: "",
    description: entry?.description || "",
    hours: entry ? (parseInt(entry.hours) || 0) : 0,
    minutes: entry ? (parseInt(entry.minutes) || 0) : 0,
    staff_id: entry?.staff_id || user.id,
    staff_name: entry?.staff_name || user.name,
    billable: defaultBillable,
    hourly_rate: defaultRate,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const minuteOptions = [0,6,12,18,24,30,36,42,48,54];

  const save = async () => {
    if (!form.description.trim()) { setMsg({type:"error",text:"Description is required."}); return; }
    const totalMins = (parseInt(form.hours)||0)*60 + (parseInt(form.minutes)||0);
    if (totalMins < 6) { setMsg({type:"error",text:"Duration must be at least 6 minutes."}); return; }
    const category = form.category==="Custom" ? form.custom_category || "Custom" : form.category;
    setSaving(true);
    const payload = {
      ...(entry?.id ? {id: entry.id} : {}),
      case_id: caseData.id,
      entry_date: form.entry_date,
      category,
      description: form.description,
      duration_minutes: totalMins,
      hours: parseInt(form.hours) || 0,
      minutes: parseInt(form.minutes) || 0,
      amount: parseFloat(((parseInt(form.hours)||0)*60+(parseInt(form.minutes)||0))/60*(parseFloat(form.hourly_rate)||0)).toFixed(2),
      staff_id: form.staff_id,
      staff_name: form.staff_name,
      billable: form.billable ? 1 : 0,
      hourly_rate: parseFloat(form.hourly_rate)||0,
    };
    let res;
    try {
      res = await api.post("billing.php?action=save_entry", payload);
    } catch(e) {
      setMsg({type:"error",text:"Network error: "+e.message});
      setSaving(false);
      return;
    }
    if (res && res.success) onSaved(res.entry);
    else setMsg({type:"error",text:res?.error || JSON.stringify(res) || "Failed to save."});
    setSaving(false);
  };

  // Always show calculated value regardless of billable flag
  const previewAmount = (((parseInt(form.hours)||0)*60+(parseInt(form.minutes)||0))/60*(parseFloat(form.hourly_rate)||0)).toFixed(2);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:16,padding:32,width:"100%",maxWidth:520,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:800}}>{entry?"Edit Time Entry":"Log Time"}</div>
          <button onClick={onClose} style={{fontSize:22,color:"#9B9490"}}>×</button>
        </div>
        <Msg msg={msg} />
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Date</div>
              <input type="date" value={form.entry_date} onChange={e=>setForm({...form,entry_date:e.target.value})}
                style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Category</div>
              <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})}
                style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}}>
                {TIME_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {form.category==="Custom" && (
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Custom Category Name</div>
              <input value={form.custom_category} onChange={e=>setForm({...form,custom_category:e.target.value})} placeholder="e.g. Court Filing"
                style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
            </div>
          )}
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Description *</div>
            <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3} placeholder="Describe the work done…"
              style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}} />
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Duration (6-min / 0.1 hr increments)</div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <input type="number" min="0" value={form.hours} onChange={e=>setForm({...form,hours:Math.max(0,parseInt(e.target.value)||0)})}
                  style={{width:70,border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 10px",fontSize:13,outline:"none",textAlign:"center"}} />
                <span style={{fontSize:13,color:"#9B9490"}}>hrs</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <select value={form.minutes} onChange={e=>setForm({...form,minutes:parseInt(e.target.value)})}
                  style={{width:80,border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 10px",fontSize:13,outline:"none"}}>
                  {minuteOptions.map(m=><option key={m} value={m}>{String(m).padStart(2,"0")}</option>)}
                </select>
                <span style={{fontSize:13,color:"#9B9490"}}>min</span>
              </div>
              <span style={{fontSize:12,color:"#9B9490"}}>= {(parseInt(form.hours)||0)*60+(parseInt(form.minutes)||0)} min total</span>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Staff Member</div>
              <select value={form.staff_id} onChange={e=>{
                const s=staff.find(s=>s.id===parseInt(e.target.value));
                setForm({...form,staff_id:parseInt(e.target.value),staff_name:s?.name||user.name});
              }} style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}}>
                <option value={user.id}>{user.name} (me)</option>
                {staff.filter(s=>s.id!==user.id).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Hourly Rate ($)</div>
              <input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={e=>setForm({...form,hourly_rate:e.target.value})}
                style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
            </div>
          </div>
          <div style={{background:"#F7F5F0",borderRadius:9,padding:"12px 16px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="checkbox" checked={form.billable} onChange={e=>setForm({...form,billable:e.target.checked})} style={{width:16,height:16}} />
                <div>
                  <div style={{fontSize:13,fontWeight:700}}>{form.billable ? "Billable" : "Non-Billable (No Charge)"}</div>
                  <div style={{fontSize:11,color:"#9B9490"}}>{isFlatFee ? "Flat fee case — time tracked for transparency" : "Uncheck to show as No Charge on invoice"}</div>
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:"#9B9490"}}>Time Value</div>
                <div style={{fontSize:18,fontWeight:800,color:form.billable?"#2D7D72":"#9B9490"}}>${previewAmount}</div>
                {!form.billable && <div style={{fontSize:10,color:"#9B9490"}}>shown on invoice, not charged</div>}
              </div>
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,marginTop:20}}>
          <button onClick={save} disabled={saving} style={{flex:1,padding:"10px 0",background:"#2D7D72",color:"#fff",borderRadius:9,fontSize:13,fontWeight:700}}>{saving?"Saving…":entry?"Save Changes":"Log Time"}</button>
          <button onClick={onClose} style={{padding:"10px 18px",border:"1px solid #E2DDD5",borderRadius:9,fontSize:13,color:"#4A5568"}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function GenerateInvoiceModal({ caseData, entries, user, onClose, onGenerated }) {
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("Professional Services");
  const [tax, setTax] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);

  const billableTotal = entries.filter(e=>e.billable).reduce((a,e)=>a+parseFloat(e.amount||0),0);
  const feeStructure = caseData.fee_structure || "flat_fee";
  const flatFee = parseFloat(caseData.flat_fee_amount||0);
  const estimatedTotal = feeStructure==="flat_fee" ? flatFee+parseFloat(tax) : billableTotal+parseFloat(caseData.flat_fee_amount||0)+parseFloat(tax);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("billing.php?action=generate_invoice", {
        case_id: caseData.id,
        created_by: user.id,
        description,
        notes,
        tax: parseFloat(tax)||0,
      });
      if (res && res.success) onGenerated(res.invoice);
      else setMsg({type:"error",text:res?.error||"Failed to generate invoice."});
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setGenerating(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:16,padding:32,width:"100%",maxWidth:560,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:800}}>Generate Invoice</div>
          <button onClick={onClose} style={{fontSize:22,color:"#9B9490"}}>×</button>
        </div>
        <Msg msg={msg} />

        {/* Summary */}
        <div style={{background:"#F7F5F0",borderRadius:10,padding:"14px 18px",marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:"#9B9490",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Invoice Summary</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span>Fee Structure</span>
              <span style={{fontWeight:700}}>{feeStructure==="flat_fee"?"Flat Fee":feeStructure==="hourly"?"Hourly":feeStructure==="flat_plus_hourly"?"Flat + Hourly":"Custom"}</span>
            </div>
            {(feeStructure==="flat_fee"||feeStructure==="flat_plus_hourly") && <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span>Flat Fee</span><span style={{fontWeight:700}}>${flatFee.toFixed(2)}</span></div>}
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span>Billable Time ({entries.filter(e=>e.billable).length} entries)</span><span style={{fontWeight:700,color:"#2D7D72"}}>${billableTotal.toFixed(2)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span style={{color:"#9B9490"}}>Non-Billable (shown for transparency)</span><span style={{color:"#9B9490"}}>{entries.filter(e=>!e.billable).length} entries</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}><span>Tax</span><span>${parseFloat(tax||0).toFixed(2)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:800,borderTop:"1px solid #E2DDD5",paddingTop:8,marginTop:4}}><span>Estimated Total</span><span style={{color:"#1B2B4B"}}>${estimatedTotal.toFixed(2)}</span></div>
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Invoice Description</div>
            <input value={description} onChange={e=>setDescription(e.target.value)}
              style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Tax ($)</div>
            <input type="number" min="0" step="0.01" value={tax} onChange={e=>setTax(e.target.value)}
              style={{width:160,border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Notes (appear on invoice)</div>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Payment terms, thank you note, etc."
              style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none",resize:"vertical",fontFamily:"inherit"}} />
          </div>
        </div>
        <div style={{display:"flex",gap:8,marginTop:20}}>
          <button onClick={generate} disabled={generating} style={{flex:1,padding:"11px 0",background:"#1B2B4B",color:"#fff",borderRadius:9,fontSize:13,fontWeight:700}}>{generating?"Generating…":"Generate Invoice"}</button>
          <button onClick={onClose} style={{padding:"11px 18px",border:"1px solid #E2DDD5",borderRadius:9,fontSize:13,color:"#4A5568"}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceDetailModal({ invoice, caseData, onClose, onResend }) {
  const billableItems = invoice.line_items?.filter(li=>li.billable) || [];
  const nonBillableItems = invoice.line_items?.filter(li=>!li.billable) || [];
  const allLoggedValue = (invoice.line_items || []).reduce((a,li)=>a+parseFloat(li.amount||0),0);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  const getInvoiceHTML = () => `<!DOCTYPE html><html><head><title>Invoice ${invoice.invoice_number}</title>
    <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Georgia,serif;padding:48px;max-width:800px;margin:0 auto;color:#1a1a1a;}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;padding-bottom:24px;border-bottom:3px solid #1B2B4B;}
    .company{font-size:22px;font-weight:bold;color:#1B2B4B;margin-bottom:4px;}
    .invoice-label{font-size:32px;font-weight:bold;color:#1B2B4B;text-align:right;}
    .invoice-meta{text-align:right;font-size:13px;color:#666;margin-top:6px;}
    .parties{margin-bottom:32px;font-size:13px;color:#444;line-height:1.8;}
    .parties strong{color:#1B2B4B;}
    table{width:100%;border-collapse:collapse;margin-bottom:24px;}
    th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:#fff;background:#1B2B4B;padding:10px 12px;}
    th.right{text-align:right;}
    td{padding:10px 12px;font-size:13px;border-bottom:1px solid #eee;vertical-align:top;}
    td.right{text-align:right;}
    tr:last-child td{border-bottom:none;}
    .section-header td{background:#f0f0f0;font-size:11px;font-weight:bold;text-transform:uppercase;letter-spacing:0.06em;color:#666;padding:8px 12px;}
    .no-charge{color:#999;font-style:italic;}
    .totals{margin-left:auto;width:280px;margin-top:8px;}
    .totals-row{display:flex;justify-content:space-between;padding:6px 0;font-size:14px;border-bottom:1px solid #eee;}
    .totals-row.final{font-size:18px;font-weight:bold;color:#1B2B4B;border-top:2px solid #1B2B4B;border-bottom:none;padding-top:10px;margin-top:4px;}
    .notes{margin-top:32px;padding:16px 20px;background:#f7f7f7;border-left:4px solid #2D7D72;font-size:13px;line-height:1.7;color:#444;}
    .footer{margin-top:48px;padding-top:16px;border-top:1px solid #eee;font-size:11px;color:#999;text-align:center;}
    @media print{@page{margin:0.6in;} .no-print{display:none;}}
    </style></head><body>
    <div class="header">
      <div><div class="company">Lakeshore Mediation</div><div style="font-size:13px;color:#666;">Professional Mediation Services</div></div>
      <div><div class="invoice-label">INVOICE</div><div class="invoice-meta"><strong>${invoice.invoice_number}</strong><br/>Date: ${invoice.invoice_date}<br/>Status: ${invoice.status}</div></div>
    </div>
    <div class="parties">
      <strong>Billed To:</strong><br/>
      ${caseData.client1_name} & ${caseData.client2_name}<br/>
      ${caseData.case_number ? "Case No: "+caseData.case_number : ""}
    </div>
    <table>
      <thead><tr><th style="width:50%">Description</th><th class="right">Hours</th><th class="right">Rate</th><th class="right">Amount</th></tr></thead>
      <tbody>
      ${(invoice.line_items?.length||0)===0?`<tr><td>${invoice.description||"Professional Services"}</td><td class="right">—</td><td class="right">—</td><td class="right"><strong>$${parseFloat(invoice.amount||0).toFixed(2)}</strong></td></tr>`:""}
      ${billableItems.map(li=>{const h=parseInt(li.hours||0);const m=parseInt(li.minutes||0);const dur=h>0?(m>0?`${h}h ${m}m`:`${h}h`):(m>0?`${m}m`:"0m");return`<tr><td>${li.description}</td><td class="right">${dur}</td><td class="right">$${parseFloat(li.hourly_rate||li.unit_price||0).toFixed(2)}</td><td class="right"><strong>$${parseFloat(li.amount||0).toFixed(2)}</strong></td></tr>`;}).join("")}
      ${nonBillableItems.length>0?`<tr class="section-header"><td colspan="4">Time & Activity Log — No Charge (For Transparency)</td></tr>${nonBillableItems.map(li=>{const h=parseInt(li.hours||0);const m=parseInt(li.minutes||0);const dur=h>0?(m>0?`${h}h ${m}m`:`${h}h`):(m>0?`${m}m`:"0m");return`<tr class="no-charge"><td>${li.description}</td><td class="right">${dur}</td><td class="right">$${parseFloat(li.hourly_rate||0).toFixed(2)}</td><td class="right">No Charge</td></tr>`;}).join("")}`:""}
      </tbody>
    </table>
    <div class="totals">
      ${invoice.fee_structure==="flat_fee" && allLoggedValue>parseFloat(invoice.subtotal||0)?`<div class="totals-row"><span>Value of Time Logged</span><span>$${allLoggedValue.toFixed(2)}</span></div><div class="totals-row"><span>Flat Fee (Billed)</span><span>$${parseFloat(invoice.subtotal||0).toFixed(2)}</span></div>`:`<div class="totals-row"><span>Subtotal</span><span>$${parseFloat(invoice.subtotal||0).toFixed(2)}</span></div>`}
      ${parseFloat(invoice.tax||0)>0?`<div class="totals-row"><span>Tax</span><span>$${parseFloat(invoice.tax).toFixed(2)}</span></div>`:""}
      <div class="totals-row final"><span>Total Due</span><span>$${parseFloat(invoice.amount||0).toFixed(2)}</span></div>
    </div>
    ${invoice.notes?`<div class="notes">${invoice.notes}</div>`:""}
    <div class="footer">Thank you for choosing Lakeshore Mediation · lakeshoremediation.com</div>
    </body></html>`;

  const printInvoice = () => {
    const w = window.open("","_blank");
    w.document.write(getInvoiceHTML());
    w.document.close();
    setTimeout(()=>w.print(), 500);
  };

  const downloadPDF = () => {
    const html = getInvoiceHTML();
    const blob = new Blob([html], {type:"text/html"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${invoice.invoice_number}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resendInvoice = async () => {
    setResending(true);
    setResendMsg(null);
    const res = await api.post("billing.php?action=resend_invoice", { id: invoice.id, case_id: caseData.id });
    if (res.success) setResendMsg({type:"success", text:"Invoice resent successfully!"});
    else setResendMsg({type:"error", text: res.error || "Failed to resend."});
    setResending(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:16,padding:0,width:"100%",maxWidth:680,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",maxHeight:"92vh",overflowY:"auto"}}>
        {/* Header */}
        <div style={{background:"#1B2B4B",borderRadius:"16px 16px 0 0",padding:"20px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:800,color:"#fff"}}>{invoice.invoice_number}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:2}}>{caseData.client1_name} & {caseData.client2_name} · {invoice.invoice_date}</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
            <button onClick={printInvoice} style={{padding:"7px 14px",background:"rgba(255,255,255,0.15)",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700}}>🖨 Print</button>
            <button onClick={downloadPDF} style={{padding:"7px 14px",background:"rgba(255,255,255,0.15)",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700}}>⬇ Download</button>
            <button onClick={resendInvoice} disabled={resending} style={{padding:"7px 14px",background:"#2D7D72",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700}}>{resending?"Sending…":"📧 Resend"}</button>
            <button onClick={onClose} style={{fontSize:22,color:"rgba(255,255,255,0.5)"}}>×</button>
          </div>
        </div>
        {resendMsg && <div style={{padding:"10px 28px",background:resendMsg.type==="success"?"#EAF4EA":"#FDEEEC",color:resendMsg.type==="success"?"#2E7D32":"#C0392B",fontSize:13,fontWeight:600}}>{resendMsg.text}</div>}

        <div style={{padding:28}}>
          {/* Billable items */}
          {billableItems.length>0 && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#2D7D72",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Billable Services</div>
              <div style={{background:"#F7F5F0",borderRadius:10,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr auto auto auto",gap:0}}>
                  {["Description","Time","Rate","Amount"].map((h,i)=>(
                    <div key={i} style={{padding:"8px 14px",fontSize:10,fontWeight:700,color:"#9B9490",textTransform:"uppercase",borderBottom:"1px solid #E2DDD5",textAlign:i>0?"right":"left"}}>{h}</div>
                  ))}
                  {billableItems.map((li,i)=>(
                    <div key={i} style={{display:"contents"}}>
                      <div style={{padding:"10px 14px",fontSize:12,color:"#1B2B4B",borderBottom:"1px solid #E2DDD5",lineHeight:1.5}}>{li.description}</div>
                      <div style={{padding:"10px 14px",fontSize:12,textAlign:"right",borderBottom:"1px solid #E2DDD5"}}>
                        {(() => { const h=parseInt(li.hours||0); const m=parseInt(li.minutes||0); return h>0?(m>0?`${h}h ${m}m`:`${h}h`):(m>0?`${m}m`:"0m"); })()}
                      </div>
                      <div style={{padding:"10px 14px",fontSize:12,textAlign:"right",borderBottom:"1px solid #E2DDD5"}}>${parseFloat(li.hourly_rate||li.unit_price||0).toFixed(2)}</div>
                      <div style={{padding:"10px 14px",fontSize:13,fontWeight:700,textAlign:"right",borderBottom:"1px solid #E2DDD5",color:"#1B2B4B"}}>${parseFloat(li.amount||0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Non-billable items */}
          {nonBillableItems.length>0 && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#9B9490",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Time & Activity Log (No Charge — For Your Records)</div>
              <div style={{background:"#F7F5F0",borderRadius:10,overflow:"hidden",opacity:0.85}}>
                {nonBillableItems.map((li,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"10px 14px",borderBottom:i<nonBillableItems.length-1?"1px solid #E2DDD5":"none",fontSize:12,color:"#4A5568"}}>
                    <span style={{flex:1,marginRight:12}}>{li.description}</span>
                    <span style={{color:"#9B9490",fontStyle:"italic",whiteSpace:"nowrap"}}>No Charge</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div style={{background:"#1B2B4B",borderRadius:10,padding:"16px 20px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",color:"rgba(255,255,255,0.6)",fontSize:13,marginBottom:6}}>
              <span>Subtotal</span><span>${parseFloat(invoice.subtotal||0).toFixed(2)}</span>
            </div>
            {parseFloat(invoice.tax||0)>0 && <div style={{display:"flex",justifyContent:"space-between",color:"rgba(255,255,255,0.6)",fontSize:13,marginBottom:6}}><span>Tax</span><span>${parseFloat(invoice.tax).toFixed(2)}</span></div>}
            <div style={{display:"flex",justifyContent:"space-between",color:"#fff",fontSize:18,fontWeight:800,borderTop:"1px solid rgba(255,255,255,0.2)",paddingTop:10,marginTop:6}}>
              <span>Total Due</span><span>${parseFloat(invoice.amount||0).toFixed(2)}</span>
            </div>
          </div>

          {invoice.notes && (
            <div style={{background:"#FDF3E7",border:"1px solid #C8873A",borderRadius:10,padding:"14px 18px",fontSize:13,color:"#4A5568",lineHeight:1.6}}>
              {invoice.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeeSettingsModal({ caseData, onClose, onSaved }) {
  const [form, setForm] = useState({
    fee_structure: caseData.fee_structure || "flat_fee",
    flat_fee_amount: caseData.flat_fee_amount || 0,
    hourly_rate: caseData.hourly_rate || 0,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post("billing.php?action=save_fee_settings", { case_id: caseData.id, ...form });
      if (res && res.success) onSaved(form);
      else setMsg({type:"error",text:res?.error||"Failed to save."});
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setSaving(false);
  };

  const structures = [
    {value:"flat_fee",label:"Flat Fee",desc:"Fixed amount for the case. Time is tracked but shown as no-charge."},
    {value:"hourly",label:"Hourly",desc:"Billed by time logged in 6-min (1/10 hr) increments."},
    {value:"flat_plus_hourly",label:"Flat + Hourly",desc:"Flat base fee plus additional hourly charges."},
    {value:"custom",label:"Custom",desc:"Manual line items on each invoice."},
  ];

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:16,padding:32,width:"100%",maxWidth:480,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:800}}>Fee Settings</div>
          <button onClick={onClose} style={{fontSize:22,color:"#9B9490"}}>×</button>
        </div>
        <Msg msg={msg} />
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
          {structures.map(s=>(
            <div key={s.value} onClick={()=>setForm({...form,fee_structure:s.value})}
              style={{padding:"12px 16px",borderRadius:10,border:`2px solid ${form.fee_structure===s.value?"#2D7D72":"#E2DDD5"}`,background:form.fee_structure===s.value?"#E6F4F2":"#fff",cursor:"pointer"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${form.fee_structure===s.value?"#2D7D72":"#E2DDD5"}`,background:form.fee_structure===s.value?"#2D7D72":"#fff",flexShrink:0}} />
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#1B2B4B"}}>{s.label}</div>
                  <div style={{fontSize:11,color:"#9B9490",marginTop:1}}>{s.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {(form.fee_structure==="flat_fee"||form.fee_structure==="flat_plus_hourly") && (
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Flat Fee Amount ($)</div>
            <input type="number" min="0" step="0.01" value={form.flat_fee_amount} onChange={e=>setForm({...form,flat_fee_amount:e.target.value})}
              style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
          </div>
        )}
        {(form.fee_structure==="hourly"||form.fee_structure==="flat_plus_hourly") && (
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Default Hourly Rate ($/hr)</div>
            <input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={e=>setForm({...form,hourly_rate:e.target.value})}
              style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
          </div>
        )}
        {form.fee_structure==="flat_fee" && (
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:600,color:"#9B9490",marginBottom:5}}>Reference Hourly Rate ($/hr)</div>
            <input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={e=>setForm({...form,hourly_rate:e.target.value})}
              style={{width:"100%",border:"1px solid #E2DDD5",borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none"}} />
            <div style={{fontSize:11,color:"#9B9490",marginTop:5}}>Used to estimate the value of time logged on this case. Does not change what the client is billed.</div>
          </div>
        )}
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={save} disabled={saving} style={{flex:1,padding:"10px 0",background:"#2D7D72",color:"#fff",borderRadius:9,fontSize:13,fontWeight:700}}>{saving?"Saving…":"Save Settings"}</button>
          <button onClick={onClose} style={{padding:"10px 18px",border:"1px solid #E2DDD5",borderRadius:9,fontSize:13,color:"#4A5568"}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── CALENDAR ─────────────────────────────────────────────────────────────────
const EVENT_TYPES = [
  { value: "Session", color: "#2D7D72", bg: "#E6F4F2" },
  { value: "Court Date", color: "#C0392B", bg: "#FDEEEC" },
  { value: "Deadline", color: "#C8873A", bg: "#FDF3E7" },
  { value: "Client Meeting", color: "#1B2B4B", bg: "#EEF0F8" },
  { value: "Phone Call", color: "#6B4FA0", bg: "#F3EEFF" },
  { value: "Personal Reminder", color: "#2E7D32", bg: "#EAF4EA" },
  { value: "Follow-up", color: "#0277BD", bg: "#E3F2FD" },
  { value: "Other", color: "#666", bg: "#F0F0F0" },
];

const getEventStyle = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[EVENT_TYPES.length - 1];

// Lets mediation sessions (a separate table from calendar_events) show up
// on the calendar grid alongside real calendar events, read-only — editing
// one routes back to the session edit modal, not EventModal.
const sessionToCalEvent = (s) => ({
  id: s.id,
  event_date: s.session_date,
  event_time: s.session_time,
  title: s.topic || "Session",
  event_type: "Session",
  notes: s.notes,
  _source: "session",
  _session: s,
});

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.slice(0, 5).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

function CalendarView({ events, month, onMonthChange, onDayClick, onEventClick, view, onViewChange }) {
  const [year, mon] = month.split("-").map(Number);
  const firstDay = new Date(year, mon - 1, 1).getDay();
  const daysInMonth = new Date(year, mon, 0).getDate();
  const today = new Date().toISOString().split("T")[0];

  const prevMonth = () => {
    const d = new Date(year, mon - 2, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const nextMonth = () => {
    const d = new Date(year, mon, 1);
    onMonthChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const monthName = new Date(year, mon - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });

  const getEventsForDate = (dateStr) => events.filter(e => e.event_date === dateStr);

  // Week view helpers
  const getWeekDates = () => {
    const today = new Date();
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toISOString().split("T")[0];
    });
  };

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const weekDates = view === "week" ? getWeekDates() : [];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={prevMonth} style={{ width: 32, height: 32, border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800, minWidth: 200, textAlign: "center" }}>{monthName}</div>
          <button onClick={nextMonth} style={{ width: 32, height: 32, border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["month", "week"].map(v => (
            <button key={v} onClick={() => onViewChange(v)} style={{ padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: view === v ? "2px solid #2D7D72" : "2px solid #E2DDD5", background: view === v ? "#E6F4F2" : "#fff", color: view === v ? "#2D7D72" : "#9B9490" }}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {view === "month" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 1 }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
              <div key={d} style={{ padding: "8px 0", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.05em" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, background: "#E2DDD5", borderRadius: 12, overflow: "hidden" }}>
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} style={{ background: "#F7F5F0", minHeight: 100 }} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(mon).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dayEvents = getEventsForDate(dateStr);
              const isToday = dateStr === today;
              return (
                <div key={day} onClick={() => onDayClick(dateStr)} style={{ background: "#fff", minHeight: 100, padding: "8px 6px", cursor: "pointer", transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F7F5F0"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: isToday ? "#2D7D72" : "transparent", color: isToday ? "#fff" : "#1B2B4B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: isToday ? 800 : 400, marginBottom: 4 }}>{day}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {dayEvents.slice(0, 3).map(ev => {
                      const style = getEventStyle(ev.event_type);
                      return (
                        <div key={ev.id} onClick={e => { e.stopPropagation(); onEventClick(ev); }}
                          style={{ background: style.bg, color: style.color, borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 600, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", cursor: "pointer" }}>
                          {ev.event_time && <span>{formatTime(ev.event_time)} </span>}{ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <div style={{ fontSize: 10, color: "#9B9490", fontWeight: 600 }}>+{dayEvents.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, background: "#E2DDD5", borderRadius: 12, overflow: "hidden" }}>
            {weekDates.map((dateStr, i) => {
              const dayEvents = getEventsForDate(dateStr);
              const isToday = dateStr === today;
              const d = new Date(dateStr + "T00:00:00");
              return (
                <div key={dateStr} style={{ background: isToday ? "#F0F8F7" : "#fff", minHeight: 200, padding: "10px 8px", cursor: "pointer" }} onClick={() => onDayClick(dateStr)}>
                  <div style={{ textAlign: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase" }}>{weekDays[i]}</div>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: isToday ? "#2D7D72" : "transparent", color: isToday ? "#fff" : "#1B2B4B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, margin: "4px auto 0" }}>{d.getDate()}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {dayEvents.map(ev => {
                      const style = getEventStyle(ev.event_type);
                      return (
                        <div key={ev.id} onClick={e => { e.stopPropagation(); onEventClick(ev); }}
                          style={{ background: style.bg, color: style.color, borderRadius: 6, padding: "6px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer", borderLeft: `3px solid ${style.color}` }}>
                          {ev.event_time && <div style={{ fontSize: 10, opacity: 0.8 }}>{formatTime(ev.event_time)}</div>}
                          {ev.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
        {EVENT_TYPES.map(t => (
          <div key={t.value} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: t.color }} />
            <span style={{ color: "#9B9490" }}>{t.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventModal({ event, selectedDate, caseId, cases, user, onClose, onSaved }) {
  const isNew = !event;
  const [form, setForm] = useState({
    title: event?.title || "",
    event_type: event?.event_type || "Session",
    event_date: event?.event_date || selectedDate || "",
    event_time: event?.event_time || "",
    end_time: event?.end_time || "",
    notes: event?.notes || "",
    case_id: event?.case_id || caseId || "",
  });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { setMsg({ type: "error", text: "Title is required." }); return; }
    if (!form.event_date) { setMsg({ type: "error", text: "Date is required." }); return; }
    setSaving(true);
    try {
      const payload = { ...form, case_id: form.case_id || null, created_by: user.id, created_by_name: user.name };
      let res;
      if (isNew) res = await api.post("calendar.php", { action: "create", ...payload });
      else res = await api.post("calendar.php", { action: "update", id: event.id, ...payload });
      if (res && res.success) onSaved();
      else setMsg({ type: "error", text: res?.error || "Failed to save." });
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`calendar.php?id=${event.id}`);
    onSaved();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: "100%", maxWidth: 500, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>{isNew ? "New Event" : "Edit Event"}</div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Title" value={form.title} onChange={v => setForm({...form, title: v})} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Event Type</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {EVENT_TYPES.map(t => (
                <button key={t.value} onClick={() => setForm({...form, event_type: t.value})}
                  style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: `2px solid ${t.color}`, background: form.event_type === t.value ? t.color : "#fff", color: form.event_type === t.value ? "#fff" : t.color }}>
                  {t.value}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Input label="Date" value={form.event_date} onChange={v => setForm({...form, event_date: v})} type="date" />
            <Input label="Start Time" value={form.event_time} onChange={v => setForm({...form, event_time: v})} type="time" />
            <Input label="End Time" value={form.end_time} onChange={v => setForm({...form, end_time: v})} type="time" />
          </div>
          {cases && cases.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Link to Case (optional)</div>
              <select value={form.case_id} onChange={e => setForm({...form, case_id: e.target.value})}
                style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}>
                <option value="">No case</option>
                {cases.map(c => <option key={c.id} value={c.id}>{c.case_number} — {c.client1_name} & {c.client2_name}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Notes</div>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes…" style={{ width: "100%", minHeight: 70, border: "1px solid #E2DDD5", borderRadius: 8, padding: 10, fontSize: 13, resize: "vertical", outline: "none", fontFamily: "inherit" }} />
          </div>
        </div>
        <Msg msg={msg} />
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button onClick={save} disabled={saving} style={{ flex: 1, padding: "10px 0", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>{saving ? "Saving…" : isNew ? "Add Event" : "Save Changes"}</button>
          {!isNew && <button onClick={del} style={{ padding: "10px 18px", border: "1px solid #C0392B", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#C0392B" }}>Delete</button>}
          <button onClick={onClose} style={{ padding: "10px 18px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── SCHEDULING LINK MODAL ──────────────────────────────────────────────────
function SchedulingLinkModal({ caseData, onClose }) {
  const API = API_BASE + "/scheduling.php";
  const authHeaders = { "X-Auth-Token": getToken() };
  const [sessionTypes, setSessionTypes] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionTypeId, setSessionTypeId] = useState("");
  const [clientName, setClientName] = useState(caseData.client1_name || "");
  const [clientEmail, setClientEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [newLink, setNewLink] = useState(null);
  const [msg, setMsg] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [types, inv] = await Promise.all([
      fetch(API + "?action=get_session_types", { headers: authHeaders }).then(r => r.json()).catch(() => []),
      fetch(API + "?action=get_invites&case_id=" + caseData.id, { headers: authHeaders }).then(r => r.json()).catch(() => []),
    ]);
    setSessionTypes(Array.isArray(types) ? types : []);
    setInvites(Array.isArray(inv) ? inv : []);
    if (!sessionTypeId && Array.isArray(types) && types.length > 0) setSessionTypeId(String(types[0].id));
    setLoading(false);
  }

  async function generateLink() {
    if (!sessionTypeId) { setMsg({ type: "error", text: "Pick a session type." }); return; }
    setGenerating(true);
    setMsg(null);
    const res = await fetch(API + "?action=create_invite", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ case_id: caseData.id, session_type_id: sessionTypeId, client_name: clientName, client_email: clientEmail }),
    }).then(r => r.json()).catch(() => null);
    if (res?.success) {
      setNewLink(res.url);
      loadAll();
    } else {
      setMsg({ type: "error", text: res?.error || "Could not create the link." });
    }
    setGenerating(false);
  }

  async function revokeInvite(id) {
    if (!window.confirm("Revoke this link? It will stop working if the client hasn't used it yet.")) return;
    await fetch(API + "?action=revoke_invite", { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders }, body: JSON.stringify({ id }) });
    loadAll();
  }

  function copyLink(url, id) {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const inviteUrl = (token) => `https://ourmediationcase.com/?booking=1&invite=${token}`;
  const statusColor = { pending: { bg: "#FDF3E7", text: "#C8873A" }, used: { bg: "#EAF4EA", text: "#2E7D32" }, cancelled: { bg: "#F0F0F0", text: "#9B9490" } };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800 }}>Scheduling Link</div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>Generate a link for this client to pick a time for a specific kind of meeting — it skips straight to your open times for that session type.</div>

        {loading ? <div style={{ textAlign: "center", padding: 20, color: "#9B9490" }}>Loading…</div> : (
          <>
            {sessionTypes.length === 0 ? (
              <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>No session types are set up yet — add one under Scheduling → Session Types first.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>What do they need to schedule?</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {sessionTypes.map(t => (
                      <button key={t.id} onClick={() => setSessionTypeId(String(t.id))} style={{
                        padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                        border: sessionTypeId === String(t.id) ? "2px solid #2D7D72" : "2px solid #E2DDD5",
                        background: sessionTypeId === String(t.id) ? "#E6F4F2" : "#fff",
                        color: sessionTypeId === String(t.id) ? "#2D7D72" : "#9B9490",
                      }}>{t.name} · {t.duration_minutes}m</button>
                    ))}
                  </div>
                </div>
                <Input label="Client Name" value={clientName} onChange={setClientName} />
                <Input label="Client Email (optional, pre-fills their form)" value={clientEmail} onChange={setClientEmail} type="email" />
              </div>
            )}

            <Msg msg={msg} />
            {newLink && (
              <div style={{ background: "#EAF4EA", border: "1px solid #A8D5A2", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#2E7D32", marginBottom: 6 }}>✓ Link created — copy and send it to the client</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ flex: 1, fontSize: 12, color: "#4A5568", wordBreak: "break-all" }}>{newLink}</div>
                  <button onClick={() => copyLink(newLink, "new")} style={{ padding: "6px 12px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{copiedId === "new" ? "Copied!" : "Copy"}</button>
                </div>
              </div>
            )}

            {sessionTypes.length > 0 && (
              <button onClick={generateLink} disabled={generating} style={{ width: "100%", padding: "10px 0", background: "#1B2B4B", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: generating ? 0.6 : 1, marginBottom: 20 }}>
                {generating ? "Generating…" : "🔗 Generate Link"}
              </button>
            )}

            {invites.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Links sent for this case</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {invites.map(inv => (
                    <div key={inv.id} style={{ border: "1px solid #E2DDD5", borderRadius: 9, padding: "10px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: inv.status === "pending" ? 6 : 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B" }}>{inv.session_type_name}{inv.client_name ? ` · ${inv.client_name}` : ""}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, background: statusColor[inv.status]?.bg, color: statusColor[inv.status]?.text, padding: "2px 8px", borderRadius: 20, textTransform: "capitalize" }}>{inv.status}</span>
                      </div>
                      {inv.status === "pending" && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => copyLink(inviteUrl(inv.token), inv.id)} style={{ padding: "4px 10px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>{copiedId === inv.id ? "Copied!" : "Copy Link"}</button>
                          <button onClick={() => revokeInvite(inv.id)} style={{ padding: "4px 10px", border: "1px solid #C0392B", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#C0392B" }}>Revoke</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── SIGNING MODAL ────────────────────────────────────────────────────────────
function SigningModal({ doc, user, onClose, onSigned }) {
  const [sigType, setSigType] = useState("draw");
  const [typedSig, setTypedSig] = useState("");
  const [drawing, setDrawing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const lastPos = useRef(null);

  const hasSigned = doc.signatures?.some(s => s.signer_id === user.id);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setDrawing(true);
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1B2B4B";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => setDrawing(false);

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const isCanvasEmpty = () => {
    if (!canvasRef.current) return true;
    const ctx = canvasRef.current.getContext("2d");
    const data = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    return !data.some(v => v !== 0);
  };

  const submitSignature = async () => {
    let sigData = "";
    if (sigType === "draw") {
      if (isCanvasEmpty()) { setMsg({ type: "error", text: "Please draw your signature." }); return; }
      sigData = canvasRef.current.toDataURL("image/png");
    } else {
      if (!typedSig.trim()) { setMsg({ type: "error", text: "Please type your name." }); return; }
      sigData = typedSig.trim();
    }
    setSaving(true);
    try {
      const res = await api.post("signatures.php", {
        action: "sign", document_id: doc.id, case_id: doc.case_id,
        signer_id: user.id, signer_name: user.name,
        signer_role: user.role === "client" ? (user.case_role || "Client 1") : user.role,
        signature_type: sigType, signature_data: sigData,
      });
      if (res && res.success) {
        if (res.fully_executed) setMsg({ type: "success", text: "Signed! This document is now fully executed." });
        else setMsg({ type: "success", text: "Signed successfully!" });
        setTimeout(() => onSigned(), 1500);
      } else setMsg({ type: "error", text: res?.error || "Failed to save signature." });
    } catch {
      setMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: "100%", maxWidth: 560, boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>Sign Document</div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>{doc.title} · {doc.document_type}</div>

        {hasSigned ? (
          <div style={{ padding: "16px 20px", background: "#EAF4EA", borderRadius: 10, color: "#2E7D32", fontSize: 14, fontWeight: 600, textAlign: "center" }}>✅ You have already signed this document.</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {["draw","type"].map(t => (
                <button key={t} onClick={() => setSigType(t)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 700, border: sigType === t ? "2px solid #2D7D72" : "2px solid #E2DDD5", background: sigType === t ? "#E6F4F2" : "#fff", color: sigType === t ? "#2D7D72" : "#9B9490" }}>
                  {t === "draw" ? "✏️ Draw Signature" : "⌨️ Type Signature"}
                </button>
              ))}
            </div>

            {sigType === "draw" && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 8 }}>Draw your signature in the box below:</div>
                <canvas ref={canvasRef} width={496} height={140}
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                  style={{ border: "2px solid #E2DDD5", borderRadius: 10, cursor: "crosshair", display: "block", touchAction: "none", background: "#FAFAF8", width: "100%" }} />
                <button onClick={clearCanvas} style={{ marginTop: 8, fontSize: 12, color: "#9B9490", fontWeight: 600 }}>Clear</button>
              </div>
            )}

            {sigType === "type" && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 8 }}>Type your full legal name:</div>
                <input value={typedSig} onChange={e => setTypedSig(e.target.value)} placeholder="Your full name" style={{ width: "100%", border: "2px solid #E2DDD5", borderRadius: 10, padding: "12px 16px", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
                {typedSig && <div style={{ marginTop: 12, padding: "16px 20px", background: "#FAFAF8", borderRadius: 10, border: "1px solid #E2DDD5", fontFamily: "cursive", fontSize: 28, color: "#1B2B4B" }}>{typedSig}</div>}
              </div>
            )}

            <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 16, lineHeight: 1.6, background: "#F7F5F0", padding: "10px 14px", borderRadius: 8 }}>
              By signing you agree that this constitutes your legal signature and you have read and understood this document. Your signature will be recorded with your name, date, and IP address.
            </div>

            <Msg msg={msg} />

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={submitSignature} disabled={saving} style={{ flex: 1, padding: "11px 0", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Sign Document"}
              </button>
              <button onClick={onClose} style={{ padding: "11px 20px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─── MOU BUILDER ──────────────────────────────────────────────────────────────
function MOUBuilder({ caseData, user, onClose }) {
  const [activeTab, setActiveTab] = useState("parties");
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    api.get(`mou.php?action=get&case_id=${caseData.id}`).then(res => {
      if (res && res.data) setData(res.data);
    });
  }, [caseData.id]);

  const mouSet = (section, key, val) => setData(prev => ({ ...prev, [section]: { ...(prev[section]||{}), [key]: val } }));
  const mouGet = (section, key, def="") => (data[section]||{})[key] ?? def;
  const mouSetArr = (section, arr) => setData(prev => ({ ...prev, [section]: arr }));
  const mouGetArr = (section) => Array.isArray(data[section]) ? data[section] : [];

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post("mou.php?action=save", { case_id: caseData.id, data });
      if (res && res.success) setSaveMsg({ type: "success", text: "Saved!" });
      else setSaveMsg({ type: "error", text: res?.error || "Failed to save." });
    } catch {
      setSaveMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 3000);
  };

  const exportMOU = async (format) => {
    await save();
    window.open(`${API_BASE}/mou_export.php?case_id=${caseData.id}&format=${format}&auth_token=${getToken()}`, "_blank");
  };

  // Illinois child support calculation (ICCSS)
  const calcIL_CS = () => {
    const p1Net = parseFloat(mouGet("child_support","p1_net_monthly")||0);
    const p2Net = parseFloat(mouGet("child_support","p2_net_monthly")||0);
    const combined = p1Net + p2Net;
    if (combined <= 0) return null;
    const numChildren = parseInt(mouGet("child_support","num_children")||1);
    // Illinois basic obligation table (simplified % of combined net)
    const pcts = [0.20, 0.28, 0.32, 0.35, 0.37, 0.39];
    const pct = pcts[Math.min(numChildren-1, pcts.length-1)];
    const basicObligation = combined * pct;
    const p1Share = p1Net / combined;
    const p2Share = p2Net / combined;
    const overnights = parseInt(mouGet("child_support","p1_overnights")||0);
    // Shared parenting adjustment if >= 146 overnights
    let p1Owes = basicObligation * p2Share;
    let p2Owes = basicObligation * p1Share;
    if (overnights >= 146) {
      const adjFactor = 1.5 * (overnights / 365);
      p1Owes = (basicObligation * p2Share) - (basicObligation * p1Share * (overnights/365) * 1.5);
      p2Owes = 0; // simplified
    }
    return { combined, basicObligation, p1Share, p2Share, p1Owes: Math.max(0, p1Owes), p2Owes: Math.max(0, p2Owes), pct };
  };

  // Illinois spousal maintenance guideline
  const calcIL_Maintenance = () => {
    const p1Gross = parseFloat(mouGet("maintenance","p1_gross_annual")||0);
    const p2Gross = parseFloat(mouGet("maintenance","p2_gross_annual")||0);
    const combined = p1Gross + p2Gross;
    if (combined <= 0 || combined > 500000) return null;
    // (33.3% of payor's net) - (25% of recipient's net)
    const payorNet = parseFloat(mouGet("maintenance","payor_net_annual")||0) || p1Gross * 0.75;
    const recipNet = parseFloat(mouGet("maintenance","recipient_net_annual")||0) || p2Gross * 0.75;
    const guidelineAmount = (payorNet * 0.333) - (recipNet * 0.25);
    // Cap: recipient can't earn more than 40% of combined
    const cap = combined * 0.40 - recipNet;
    const finalAmount = Math.max(0, Math.min(guidelineAmount, cap));
    // Duration: years of marriage × 0.2–1.0 depending on length
    const yearsMarried = parseFloat(mouGet("maintenance","years_married")||0);
    const durationPcts = [[5,0.20],[6,0.24],[7,0.28],[8,0.32],[9,0.36],[10,0.40],[11,0.44],[12,0.48],[13,0.52],[14,0.56],[15,0.60],[16,0.64],[17,0.68],[18,0.72],[19,0.76],[20,0.80]];
    let durationPct = 0;
    for (const [yr, p] of durationPcts) { if (yearsMarried >= yr) durationPct = p; }
    if (yearsMarried >= 20) durationPct = null; // indefinite
    const duration = durationPct !== null ? (yearsMarried * durationPct).toFixed(1) : "Indefinite";
    return { guidelineAmount: finalAmount/12, annualAmount: finalAmount, duration };
  };

  const inputS = { border: "1px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", width: "100%", background: "#fff" };
  const labelS = { fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 };
  const secHead = (title) => (
    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 800, color: "#1B2B4B", marginBottom: 6, paddingBottom: 8, borderBottom: "2px solid #1B2B4B" }}>{title}</div>
  );
  const field = (label, section, key, type="text", placeholder="") => (
    <div>
      <div style={labelS}>{label}</div>
      <input type={type} value={mouGet(section,key)} onChange={e => mouSet(section,key,e.target.value)} placeholder={placeholder} style={inputS} />
    </div>
  );
  const fieldArea = (label, section, key, placeholder="") => (
    <div>
      <div style={labelS}>{label}</div>
      <textarea value={mouGet(section,key)} onChange={e => mouSet(section,key,e.target.value)} placeholder={placeholder} rows={3}
        style={{ ...inputS, resize: "vertical", fontFamily: "inherit" }} />
    </div>
  );
  const dollarField = (label, section, key, placeholder="0.00") => (
    <div>
      <div style={labelS}>{label}</div>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#9B9490" }}>$</span>
        <input type="number" min="0" step="0.01" value={mouGet(section,key)} onChange={e => mouSet(section,key,e.target.value)} placeholder={placeholder}
          style={{ ...inputS, paddingLeft: 24 }} />
      </div>
    </div>
  );

  const TABS = [
    { id: "parties", label: "👥 Parties & Income" },
    { id: "real_estate", label: "🏠 Real Estate" },
    { id: "assets", label: "💰 Assets" },
    { id: "debts", label: "💳 Debts" },
    { id: "child_support", label: "👶 Child Support" },
    { id: "maintenance", label: "💑 Spousal Maintenance" },
    { id: "taxes", label: "📋 Tax Decisions" },
    { id: "education", label: "🎓 College & Education" },
    { id: "business", label: "🏢 Business Interests" },
    { id: "other", label: "📝 Other Terms" },
  ];

  const renderTab = () => {
    switch(activeTab) {

      case "parties": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {secHead("Parties & Income Information")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Petitioner */}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#1B2B4B", marginBottom: 16 }}>Petitioner — {caseData.client1_name}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {field("Employer", "parties", "p1_employer")}
                {dollarField("Gross Monthly Income", "parties", "p1_gross_monthly")}
                {dollarField("Net Monthly Income", "parties", "p1_net_monthly", "Auto-calculated or enter manually")}
                <div>
                  <div style={labelS}>Calculate Net from Gross?</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["Yes","No"].map(v => (
                      <button key={v} onClick={() => mouSet("parties","p1_calc_net", v)}
                        style={{ flex:1, padding:"7px 0", borderRadius:8, fontSize:12, fontWeight:700, border: mouGet("parties","p1_calc_net")=== v ? "2px solid #2D7D72":"1px solid #E2DDD5", background: mouGet("parties","p1_calc_net")===v?"#2D7D72":"#fff", color: mouGet("parties","p1_calc_net")===v?"#fff":"#4A5568"}}>{v}</button>
                    ))}
                  </div>
                </div>
                {mouGet("parties","p1_calc_net")==="Yes" && (<>
                  {dollarField("Federal Income Tax (monthly)", "parties", "p1_federal_tax")}
                  {dollarField("State Income Tax (monthly)", "parties", "p1_state_tax")}
                  {dollarField("FICA/Social Security (monthly)", "parties", "p1_fica")}
                  {dollarField("Medicare (monthly)", "parties", "p1_medicare")}
                  {dollarField("Other Deductions (monthly)", "parties", "p1_other_deductions")}
                  <div style={{ background: "#E6F4F2", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>
                    Calculated Net: ${(
                      parseFloat(mouGet("parties","p1_gross_monthly")||0) -
                      parseFloat(mouGet("parties","p1_federal_tax")||0) -
                      parseFloat(mouGet("parties","p1_state_tax")||0) -
                      parseFloat(mouGet("parties","p1_fica")||0) -
                      parseFloat(mouGet("parties","p1_medicare")||0) -
                      parseFloat(mouGet("parties","p1_other_deductions")||0)
                    ).toFixed(2)}/mo
                  </div>
                </>)}
                {field("Other Income Sources", "parties", "p1_other_income", "text", "e.g. rental income, investments")}
              </div>
            </div>
            {/* Respondent */}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#1B2B4B", marginBottom: 16 }}>Respondent — {caseData.client2_name}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {field("Employer", "parties", "p2_employer")}
                {dollarField("Gross Monthly Income", "parties", "p2_gross_monthly")}
                {dollarField("Net Monthly Income", "parties", "p2_net_monthly")}
                <div>
                  <div style={labelS}>Calculate Net from Gross?</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["Yes","No"].map(v => (
                      <button key={v} onClick={() => mouSet("parties","p2_calc_net", v)}
                        style={{ flex:1, padding:"7px 0", borderRadius:8, fontSize:12, fontWeight:700, border: mouGet("parties","p2_calc_net")=== v ? "2px solid #2D7D72":"1px solid #E2DDD5", background: mouGet("parties","p2_calc_net")===v?"#2D7D72":"#fff", color: mouGet("parties","p2_calc_net")===v?"#fff":"#4A5568"}}>{v}</button>
                    ))}
                  </div>
                </div>
                {mouGet("parties","p2_calc_net")==="Yes" && (<>
                  {dollarField("Federal Income Tax (monthly)", "parties", "p2_federal_tax")}
                  {dollarField("State Income Tax (monthly)", "parties", "p2_state_tax")}
                  {dollarField("FICA/Social Security (monthly)", "parties", "p2_fica")}
                  {dollarField("Medicare (monthly)", "parties", "p2_medicare")}
                  {dollarField("Other Deductions (monthly)", "parties", "p2_other_deductions")}
                  <div style={{ background: "#E6F4F2", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>
                    Calculated Net: ${(
                      parseFloat(mouGet("parties","p2_gross_monthly")||0) -
                      parseFloat(mouGet("parties","p2_federal_tax")||0) -
                      parseFloat(mouGet("parties","p2_state_tax")||0) -
                      parseFloat(mouGet("parties","p2_fica")||0) -
                      parseFloat(mouGet("parties","p2_medicare")||0) -
                      parseFloat(mouGet("parties","p2_other_deductions")||0)
                    ).toFixed(2)}/mo
                  </div>
                </>)}
                {field("Other Income Sources", "parties", "p2_other_income", "text", "e.g. rental income, investments")}
              </div>
            </div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {field("Date of Marriage", "parties", "date_of_marriage", "date")}
            {field("Date of Separation", "parties", "date_of_separation", "date")}
            {field("Years Married", "parties", "years_married", "number", "e.g. 8.5")}
          </div>
        </div>
      );

      case "real_estate": {
        const props = mouGetArr("real_estate");
        const addProp = () => mouSetArr("real_estate", [...props, { id: Date.now(), address:"", fmv:"", mortgage:"", equity:"", disposition:"sell", buyout_party:"", buyout_amount:"", notes:"" }]);
        const updProp = (i, k, v) => { const a=[...props]; a[i]={...a[i],[k]:v}; mouSetArr("real_estate",a); };
        const remProp = (i) => mouSetArr("real_estate", props.filter((_,j)=>j!==i));
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {secHead("Real Estate")}
            {props.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No properties added yet.</div>}
            {props.map((p,i) => {
              const equity = (parseFloat(p.fmv)||0) - (parseFloat(p.mortgage)||0);
              return (
                <div key={p.id||i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Property {i+1}</div>
                    <button onClick={() => remProp(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div><div style={labelS}>Address</div><input value={p.address} onChange={e=>updProp(i,"address",e.target.value)} style={inputS} placeholder="123 Main St, Chicago IL" /></div>
                    <div><div style={labelS}>Fair Market Value</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={p.fmv} onChange={e=>updProp(i,"fmv",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                    <div><div style={labelS}>Mortgage Balance</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={p.mortgage} onChange={e=>updProp(i,"mortgage",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                  </div>
                  <div style={{ background: "#E6F4F2", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, color: "#2D7D72", marginBottom: 12 }}>
                    Estimated Equity: ${equity.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={labelS}>Disposition</div>
                      <select value={p.disposition} onChange={e=>updProp(i,"disposition",e.target.value)} style={inputS}>
                        <option value="sell">Sell & Split Proceeds</option>
                        <option value="buyout">One Party Buys Out Other</option>
                        <option value="retain_p1">Petitioner Retains</option>
                        <option value="retain_p2">Respondent Retains</option>
                        <option value="deferred">Deferred Sale</option>
                      </select>
                    </div>
                    {p.disposition === "buyout" && <>
                      <div><div style={labelS}>Buying Party</div><select value={p.buyout_party} onChange={e=>updProp(i,"buyout_party",e.target.value)} style={inputS}><option value="">Select…</option><option value="p1">Petitioner</option><option value="p2">Respondent</option></select></div>
                      <div><div style={labelS}>Buyout Amount</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={p.buyout_amount} onChange={e=>updProp(i,"buyout_amount",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                    </>}
                    {p.disposition === "sell" && <>
                      <div><div style={labelS}>P1 Split %</div><input type="number" min="0" max="100" value={p.p1_split||50} onChange={e=>updProp(i,"p1_split",e.target.value)} style={inputS} /></div>
                      <div><div style={labelS}>P2 Split %</div><input type="number" min="0" max="100" value={p.p2_split||50} onChange={e=>updProp(i,"p2_split",e.target.value)} style={inputS} /></div>
                    </>}
                  </div>
                  <div><div style={labelS}>Notes / Additional Terms</div><textarea value={p.notes} onChange={e=>updProp(i,"notes",e.target.value)} rows={2} style={{...inputS,resize:"vertical",fontFamily:"inherit"}} placeholder="e.g. Sale to be completed within 90 days…" /></div>
                </div>
              );
            })}
            <button onClick={addProp} style={{ padding: "11px 0", border: "2px dashed #2D7D72", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent" }}>+ Add Property</button>
          </div>
        );
      }

      case "assets": {
        const ASSET_CATS = ["Bank Account","Retirement/401k","Pension","IRA","Brokerage/Investment","Vehicle","Personal Property","Life Insurance (Cash Value)","Other Asset"];
        const assets = mouGetArr("assets");
        const addAsset = () => mouSetArr("assets", [...assets, { id: Date.now(), category:"Bank Account", description:"", owner:"joint", value:"", p1_pct:50, p2_pct:50, p1_amount:"", p2_amount:"", notes:"" }]);
        const updAsset = (i,k,v) => { const a=[...assets]; a[i]={...a[i],[k]:v}; mouSetArr("assets",a); };
        const remAsset = (i) => mouSetArr("assets", assets.filter((_,j)=>j!==i));
        const p1Total = assets.reduce((s,a) => s + (parseFloat(a.p1_amount)||0), 0);
        const p2Total = assets.reduce((s,a) => s + (parseFloat(a.p2_amount)||0), 0);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {secHead("Assets")}
            {assets.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "16px 20px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#2D7D72" }}>Petitioner Total: ${p1Total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1B2B4B" }}>Respondent Total: ${p2Total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              </div>
            )}
            {assets.map((a,i) => (
              <div key={a.id||i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <select value={a.category} onChange={e=>updAsset(i,"category",e.target.value)} style={{ ...inputS, width: "auto", fontSize: 13, fontWeight: 700 }}>
                    {ASSET_CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <button onClick={() => remAsset(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 10 }}>
                  <div><div style={labelS}>Description / Account</div><input value={a.description} onChange={e=>updAsset(i,"description",e.target.value)} style={inputS} placeholder="e.g. Chase checking #1234" /></div>
                  <div><div style={labelS}>Current Value</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={a.value} onChange={e=>updAsset(i,"value",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                  <div><div style={labelS}>Owner</div><select value={a.owner} onChange={e=>updAsset(i,"owner",e.target.value)} style={inputS}><option value="joint">Joint</option><option value="p1">Petitioner</option><option value="p2">Respondent</option></select></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
                  <div><div style={labelS}>Petitioner Gets %</div><input type="number" min="0" max="100" value={a.p1_pct} onChange={e=>updAsset(i,"p1_pct",e.target.value)} style={inputS} /></div>
                  <div><div style={labelS}>Petitioner Gets $</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={a.p1_amount||((parseFloat(a.value)||0)*(parseFloat(a.p1_pct)||0)/100).toFixed(2)} onChange={e=>updAsset(i,"p1_amount",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                  <div><div style={labelS}>Respondent Gets %</div><input type="number" min="0" max="100" value={a.p2_pct} onChange={e=>updAsset(i,"p2_pct",e.target.value)} style={inputS} /></div>
                  <div><div style={labelS}>Respondent Gets $</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={a.p2_amount||((parseFloat(a.value)||0)*(parseFloat(a.p2_pct)||0)/100).toFixed(2)} onChange={e=>updAsset(i,"p2_amount",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                </div>
                {a.notes !== undefined && <div style={{marginTop:10}}><div style={labelS}>Notes</div><input value={a.notes} onChange={e=>updAsset(i,"notes",e.target.value)} style={inputS} placeholder="Additional terms…" /></div>}
              </div>
            ))}
            <button onClick={addAsset} style={{ padding: "11px 0", border: "2px dashed #2D7D72", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent" }}>+ Add Asset</button>
          </div>
        );
      }

      case "debts": {
        const DEBT_CATS = ["Credit Card","Auto Loan","Student Loan","Personal Loan","Medical Debt","Tax Liability","Other Debt"];
        const debts = mouGetArr("debts");
        const addDebt = () => mouSetArr("debts", [...debts, { id: Date.now(), category:"Credit Card", description:"", balance:"", responsible:"joint", notes:"" }]);
        const updDebt = (i,k,v) => { const a=[...debts]; a[i]={...a[i],[k]:v}; mouSetArr("debts",a); };
        const remDebt = (i) => mouSetArr("debts", debts.filter((_,j)=>j!==i));
        const p1Debts = debts.filter(d=>d.responsible==="p1").reduce((s,d)=>s+(parseFloat(d.balance)||0),0);
        const p2Debts = debts.filter(d=>d.responsible==="p2").reduce((s,d)=>s+(parseFloat(d.balance)||0),0);
        const jointDebts = debts.filter(d=>d.responsible==="joint").reduce((s,d)=>s+(parseFloat(d.balance)||0),0);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {secHead("Debts & Liabilities")}
            {debts.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "16px 20px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>Petitioner: ${p1Debts.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B" }}>Respondent: ${p2Debts.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#C8873A" }}>Joint: ${jointDebts.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              </div>
            )}
            {debts.map((d,i) => (
              <div key={d.id||i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <select value={d.category} onChange={e=>updDebt(i,"category",e.target.value)} style={{ ...inputS, width: "auto", fontWeight: 700 }}>
                    {DEBT_CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <button onClick={() => remDebt(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
                  <div><div style={labelS}>Description / Account</div><input value={d.description} onChange={e=>updDebt(i,"description",e.target.value)} style={inputS} placeholder="e.g. Chase Visa ending 4567" /></div>
                  <div><div style={labelS}>Balance</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={d.balance} onChange={e=>updDebt(i,"balance",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                  <div><div style={labelS}>Responsible Party</div><select value={d.responsible} onChange={e=>updDebt(i,"responsible",e.target.value)} style={inputS}><option value="p1">Petitioner</option><option value="p2">Respondent</option><option value="joint">Split Jointly</option></select></div>
                </div>
                <div style={{marginTop:10}}><div style={labelS}>Notes</div><input value={d.notes} onChange={e=>updDebt(i,"notes",e.target.value)} style={inputS} placeholder="Additional terms…" /></div>
              </div>
            ))}
            <button onClick={addDebt} style={{ padding: "11px 0", border: "2px dashed #C8873A", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#C8873A", background: "transparent" }}>+ Add Debt</button>
          </div>
        );
      }

      case "child_support": {
        const cs = calcIL_CS();
        const override = mouGet("child_support","override") === "yes";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {secHead("Child Support")}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              {field("Number of Children", "child_support", "num_children", "number", "e.g. 2")}
              {field("Petitioner Overnights/Year", "child_support", "p1_overnights", "number", "e.g. 182")}
              {field("Respondent Overnights/Year", "child_support", "p2_overnights", "number")}
              {dollarField("Petitioner Net Monthly Income", "child_support", "p1_net_monthly")}
              {dollarField("Respondent Net Monthly Income", "child_support", "p2_net_monthly")}
              {dollarField("Child Care Expenses (monthly)", "child_support", "childcare_monthly")}
              {dollarField("Health Insurance Premium (monthly)", "child_support", "health_ins_monthly")}
            </div>
            {cs && (
              <div style={{ background: "#E6F4F2", border: "2px solid #2D7D72", borderRadius: 12, padding: "20px 22px" }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#2D7D72", marginBottom: 12 }}>Illinois ICCSS Guideline Calculation</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 13 }}>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Combined Net Income</div><div style={{ fontWeight: 700 }}>${cs.combined.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}/mo</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Basic Obligation ({(cs.pct*100).toFixed(0)}%)</div><div style={{ fontWeight: 700 }}>${cs.basicObligation.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}/mo</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Petitioner's Share</div><div style={{ fontWeight: 700 }}>{(cs.p1Share*100).toFixed(1)}%</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Respondent's Share</div><div style={{ fontWeight: 700 }}>{(cs.p2Share*100).toFixed(1)}%</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Petitioner Pays</div><div style={{ fontSize: 18, fontWeight: 800, color: "#1B2B4B" }}>${cs.p1Owes.toFixed(2)}/mo</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Respondent Pays</div><div style={{ fontSize: 18, fontWeight: 800, color: "#1B2B4B" }}>${cs.p2Owes.toFixed(2)}/mo</div></div>
                </div>
              </div>
            )}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Agreed Amount (override guideline?)</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {["yes","no"].map(v => (
                  <button key={v} onClick={() => mouSet("child_support","override",v)}
                    style={{ flex:1, padding:"9px 0", borderRadius:8, fontSize:13, fontWeight:700, border: mouGet("child_support","override")===v?"2px solid #2D7D72":"1px solid #E2DDD5", background: mouGet("child_support","override")===v?"#2D7D72":"#fff", color: mouGet("child_support","override")===v?"#fff":"#4A5568"}}>
                    {v==="yes" ? "Yes — Enter Different Amount" : "No — Use Guideline"}
                  </button>
                ))}
              </div>
              {override && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {dollarField("Agreed Monthly Amount", "child_support", "agreed_amount")}
                  <div>
                    <div style={labelS}>Paying Party</div>
                    <select value={mouGet("child_support","paying_party")} onChange={e=>mouSet("child_support","paying_party",e.target.value)} style={inputS}>
                      <option value="">Select…</option>
                      <option value="p1">Petitioner</option>
                      <option value="p2">Respondent</option>
                    </select>
                  </div>
                </div>
              )}
              {fieldArea("Payment Terms & Additional Notes", "child_support", "notes", "e.g. Payment due on the 1st of each month via direct deposit…")}
            </div>
          </div>
        );
      }

      case "maintenance": {
        const m = calcIL_Maintenance();
        const override = mouGet("maintenance","override") === "yes";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {secHead("Spousal Maintenance")}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              {dollarField("Petitioner Gross Annual Income", "maintenance", "p1_gross_annual")}
              {dollarField("Petitioner Net Annual Income", "maintenance", "payor_net_annual")}
              {dollarField("Respondent Gross Annual Income", "maintenance", "p2_gross_annual")}
              {dollarField("Respondent Net Annual Income", "maintenance", "recipient_net_annual")}
              {field("Years Married", "maintenance", "years_married", "number", "e.g. 8.5")}
              <div>
                <div style={labelS}>Maintenance Payor</div>
                <select value={mouGet("maintenance","payor")} onChange={e=>mouSet("maintenance","payor",e.target.value)} style={inputS}>
                  <option value="">Select…</option>
                  <option value="p1">Petitioner</option>
                  <option value="p2">Respondent</option>
                </select>
              </div>
            </div>
            {m && (
              <div style={{ background: "#EEF0F8", border: "2px solid #1B2B4B", borderRadius: 12, padding: "20px 22px" }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#1B2B4B", marginBottom: 12 }}>Illinois Maintenance Guideline</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 13 }}>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Monthly Guideline Amount</div><div style={{ fontSize: 18, fontWeight: 800, color: "#1B2B4B" }}>${m.guidelineAmount.toFixed(2)}/mo</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Annual Amount</div><div style={{ fontWeight: 700 }}>${m.annualAmount.toFixed(2)}/yr</div></div>
                  <div><div style={{ color: "#9B9490", fontSize: 11, fontWeight: 600 }}>Guideline Duration</div><div style={{ fontWeight: 700 }}>{m.duration} {m.duration !== "Indefinite" ? "years" : ""}</div></div>
                </div>
              </div>
            )}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Agreed Terms</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {["yes","no"].map(v => (
                  <button key={v} onClick={() => mouSet("maintenance","override",v)}
                    style={{ flex:1, padding:"9px 0", borderRadius:8, fontSize:13, fontWeight:700, border: mouGet("maintenance","override")===v?"2px solid #1B2B4B":"1px solid #E2DDD5", background: mouGet("maintenance","override")===v?"#1B2B4B":"#fff", color: mouGet("maintenance","override")===v?"#fff":"#4A5568"}}>
                    {v==="yes" ? "Override Guideline" : "Use Guideline"}
                  </button>
                ))}
              </div>
              {override && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 12 }}>
                  {dollarField("Agreed Monthly Amount", "maintenance", "agreed_monthly")}
                  {field("Duration", "maintenance", "agreed_duration", "text", "e.g. 5 years or Indefinite")}
                  {field("Termination Conditions", "maintenance", "termination", "text", "e.g. remarriage, death, cohabitation")}
                </div>
              )}
              <div>
                <div style={labelS}>Tax Treatment</div>
                <select value={mouGet("maintenance","tax_treatment")} onChange={e=>mouSet("maintenance","tax_treatment",e.target.value)} style={{ ...inputS, marginBottom: 12 }}>
                  <option value="">Select…</option>
                  <option value="non_taxable">Non-deductible / Non-taxable (post-TCJA default)</option>
                  <option value="taxable">Deductible by payor / Taxable to recipient</option>
                </select>
              </div>
              {fieldArea("Additional Notes", "maintenance", "notes", "Payment terms, modification conditions, etc.")}
            </div>
          </div>
        );
      }

      case "taxes": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {secHead("Tax Decisions")}
          <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={labelS}>Filing Status — Year of Separation</div>
              <select value={mouGet("taxes","filing_status_sep")} onChange={e=>mouSet("taxes","filing_status_sep",e.target.value)} style={inputS}>
                <option value="">Select…</option>
                <option>Married Filing Jointly</option>
                <option>Married Filing Separately</option>
                <option>Head of Household (if applicable)</option>
              </select>
            </div>
            <div>
              <div style={labelS}>Filing Status — Going Forward</div>
              <select value={mouGet("taxes","filing_status_future")} onChange={e=>mouSet("taxes","filing_status_future",e.target.value)} style={inputS}>
                <option value="">Select…</option>
                <option>Single</option>
                <option>Head of Household</option>
              </select>
            </div>
            <div>
              <div style={labelS}>Tax Refund/Liability — Year of Separation</div>
              <select value={mouGet("taxes","refund_split")} onChange={e=>mouSet("taxes","refund_split",e.target.value)} style={inputS}>
                <option value="">Select…</option>
                <option value="50_50">Split 50/50</option>
                <option value="p1">Petitioner Receives/Pays</option>
                <option value="p2">Respondent Receives/Pays</option>
                <option value="pro_rata">Pro-rata based on income</option>
              </select>
            </div>
            <div>
              <div style={labelS}>Who Claims Children as Dependents</div>
              <select value={mouGet("taxes","dependents")} onChange={e=>mouSet("taxes","dependents",e.target.value)} style={inputS}>
                <option value="">Select…</option>
                <option value="p1_all">Petitioner — All Children</option>
                <option value="p2_all">Respondent — All Children</option>
                <option value="alternate">Alternate Years</option>
                <option value="split">Split (specify below)</option>
                <option value="majority">Parent with Majority Parenting Time</option>
              </select>
            </div>
          </div>
          {mouGet("taxes","dependents")==="alternate" && (
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div><div style={labelS}>Odd Years — Claims Children</div><select value={mouGet("taxes","dep_odd")} onChange={e=>mouSet("taxes","dep_odd",e.target.value)} style={inputS}><option value="p1">Petitioner</option><option value="p2">Respondent</option></select></div>
              <div><div style={labelS}>Even Years — Claims Children</div><select value={mouGet("taxes","dep_even")} onChange={e=>mouSet("taxes","dep_even",e.target.value)} style={inputS}><option value="p1">Petitioner</option><option value="p2">Respondent</option></select></div>
            </div>
          )}
          <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
            {fieldArea("Prior Year Returns — Any Issues?", "taxes", "prior_returns", "e.g. Joint returns to be filed for 2024, each party responsible for their share of any liability…")}
            {fieldArea("Additional Tax Terms", "taxes", "other_tax_terms", "Any other tax-related agreements…")}
          </div>
        </div>
      );

      case "education": {
        const kids = mouGetArr("education_kids");
        const addKid = () => mouSetArr("education_kids", [...kids, { id: Date.now(), name:"", school:"", p1_pct:50, p2_pct:50, cap:"", notes:"" }]);
        const updKid = (i,k,v) => { const a=[...kids]; a[i]={...a[i],[k]:v}; mouSetArr("education_kids",a); };
        const remKid = (i) => mouSetArr("education_kids", kids.filter((_,j)=>j!==i));
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {secHead("College & Education Expenses")}
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px" }}>
              <div style={labelS}>Do the parties agree to contribute to college/education expenses?</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {["yes","no","tbd"].map(v => (
                  <button key={v} onClick={() => mouSet("education","agree",v)}
                    style={{ flex:1, padding:"9px 0", borderRadius:8, fontSize:13, fontWeight:700, border: mouGet("education","agree")===v?"2px solid #2D7D72":"1px solid #E2DDD5", background: mouGet("education","agree")===v?"#2D7D72":"#fff", color: mouGet("education","agree")===v?"#fff":"#4A5568"}}>
                    {v==="tbd"?"To Be Determined":v.charAt(0).toUpperCase()+v.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {mouGet("education","agree")==="yes" && (<>
              {kids.map((k,i) => (
                <div key={k.id||i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Child {i+1}</div>
                    <button onClick={() => remKid(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
                    <div><div style={labelS}>Child's Name</div><input value={k.name} onChange={e=>updKid(i,"name",e.target.value)} style={inputS} /></div>
                    <div><div style={labelS}>Petitioner %</div><input type="number" value={k.p1_pct} onChange={e=>updKid(i,"p1_pct",e.target.value)} style={inputS} /></div>
                    <div><div style={labelS}>Respondent %</div><input type="number" value={k.p2_pct} onChange={e=>updKid(i,"p2_pct",e.target.value)} style={inputS} /></div>
                    <div><div style={labelS}>Annual Cap</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={k.cap} onChange={e=>updKid(i,"cap",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                  </div>
                  <div style={{marginTop:10}}><div style={labelS}>Notes</div><input value={k.notes} onChange={e=>updKid(i,"notes",e.target.value)} style={inputS} placeholder="e.g. Limited to in-state public university costs…" /></div>
                </div>
              ))}
              <button onClick={addKid} style={{ padding: "11px 0", border: "2px dashed #2D7D72", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#2D7D72", background: "transparent" }}>+ Add Child</button>
              {fieldArea("Additional Education Terms", "education", "notes", "e.g. Contribution begins at age 18, FAFSA to be filed jointly…")}
            </>)}
          </div>
        );
      }

      case "business": {
        const buses = mouGetArr("businesses");
        const addBus = () => mouSetArr("businesses", [...buses, { id: Date.now(), name:"", type:"", owner:"joint", valuation:"", method:"", disposition:"", p1_pct:50, p2_pct:50, notes:"" }]);
        const updBus = (i,k,v) => { const a=[...buses]; a[i]={...a[i],[k]:v}; mouSetArr("businesses",a); };
        const remBus = (i) => mouSetArr("businesses", buses.filter((_,j)=>j!==i));
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {secHead("Business Interests")}
            {buses.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No business interests added.</div>}
            {buses.map((b,i) => (
              <div key={b.id||i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Business {i+1}</div>
                  <button onClick={() => remBus(i)} style={{ fontSize: 12, color: "#C0392B", fontWeight: 700 }}>✕ Remove</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div><div style={labelS}>Business Name</div><input value={b.name} onChange={e=>updBus(i,"name",e.target.value)} style={inputS} /></div>
                  <div><div style={labelS}>Type</div><select value={b.type} onChange={e=>updBus(i,"type",e.target.value)} style={inputS}><option value="">Select…</option><option>LLC</option><option>S-Corp</option><option>C-Corp</option><option>Sole Proprietorship</option><option>Partnership</option></select></div>
                  <div><div style={labelS}>Owner</div><select value={b.owner} onChange={e=>updBus(i,"owner",e.target.value)} style={inputS}><option value="p1">Petitioner</option><option value="p2">Respondent</option><option value="joint">Joint</option></select></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div><div style={labelS}>Agreed Value</div><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#9B9490"}}>$</span><input type="number" value={b.valuation} onChange={e=>updBus(i,"valuation",e.target.value)} style={{...inputS,paddingLeft:24}} /></div></div>
                  <div><div style={labelS}>Valuation Method</div><select value={b.method} onChange={e=>updBus(i,"method",e.target.value)} style={inputS}><option value="">Select…</option><option>Agreed Value</option><option>Professional Appraisal</option><option>Book Value</option><option>Goodwill/Income Approach</option></select></div>
                  <div><div style={labelS}>Disposition</div><select value={b.disposition} onChange={e=>updBus(i,"disposition",e.target.value)} style={inputS}><option value="">Select…</option><option value="p1_retains">Petitioner Retains</option><option value="p2_retains">Respondent Retains</option><option value="buyout">Buyout</option><option value="sell">Sell</option><option value="co_own">Continue Co-Ownership</option></select></div>
                </div>
                <div style={{marginTop:10}}><div style={labelS}>Notes</div><textarea value={b.notes} onChange={e=>updBus(i,"notes",e.target.value)} rows={2} style={{...inputS,resize:"vertical",fontFamily:"inherit"}} placeholder="Additional terms, non-compete, transition arrangements…" /></div>
              </div>
            ))}
            <button onClick={addBus} style={{ padding: "11px 0", border: "2px dashed #1B2B4B", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#1B2B4B", background: "transparent" }}>+ Add Business</button>
          </div>
        );
      }

      case "other": return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {secHead("Other Terms & Agreements")}
          <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
            {fieldArea("Personal Property Division", "other", "personal_property", "e.g. household furnishings, jewelry, artwork to be divided as agreed by the parties…")}
            {fieldArea("Life Insurance Obligations", "other", "life_insurance", "e.g. Each party shall maintain $500,000 life insurance naming children as beneficiaries…")}
            {fieldArea("Health Insurance", "other", "health_insurance", "e.g. Petitioner to maintain health insurance for the children through employer…")}
            {fieldArea("Retirement Accounts — QDRO", "other", "qdro", "e.g. A QDRO shall be prepared to divide the Petitioner's 401k as follows…")}
            {fieldArea("Attorney Fees", "other", "attorney_fees", "e.g. Each party responsible for their own attorney fees…")}
            {fieldArea("Effective Date & Conditions", "other", "effective_date", "e.g. This agreement is effective upon execution by both parties and approval by the court…")}
            {fieldArea("Additional Terms", "other", "additional_terms")}
          </div>
        </div>
      );

      default: return <div style={{ color: "#9B9490" }}>Select a section.</div>;
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#F7F5F0", zIndex: 2000, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ background: "#1B2B4B", color: "#fff", padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 800 }}>MOU Builder</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{caseData.client1_name} & {caseData.client2_name} · {caseData.case_number}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.type==="success"?"#A8D5A2":"#E57373", fontWeight: 600 }}>{saveMsg.text}</span>}
          <button onClick={save} disabled={saving} style={{ padding: "7px 16px", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 7, fontSize: 12, color: "#fff", fontWeight: 600 }}>{saving?"Saving…":"💾 Save"}</button>
          <button onClick={() => exportMOU("pdf")} style={{ padding: "7px 16px", background: "#C8873A", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>🖨 Export PDF</button>
          <button onClick={() => exportMOU("docx")} style={{ padding: "7px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>📄 Export Word</button>
          <button onClick={onClose} style={{ fontSize: 20, color: "rgba(255,255,255,0.5)", marginLeft: 8 }}>×</button>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Sidebar */}
        <div style={{ width: 220, background: "#fff", borderRight: "1px solid #E2DDD5", overflowY: "auto", flexShrink: 0 }}>
          {TABS.map(t => (
            <div key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ padding: "13px 16px", cursor: "pointer", borderLeft: `3px solid ${activeTab===t.id?"#1B2B4B":"transparent"}`, background: activeTab===t.id?"#F0EDE8":"transparent", borderBottom: "1px solid #F0EDE8", fontSize: 13, fontWeight: activeTab===t.id?700:400, color: activeTab===t.id?"#1B2B4B":"#4A5568" }}>
              {t.label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: 32 }}>
          <div style={{ maxWidth: 860 }}>
            {renderTab()}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── MEDIATOR / ADMIN APP ──────────────────────────────────────────────────────
function MediatorApp({ user, onLogout }) {
  const isAdmin = user.role === "admin";

  const [tab, setTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseTab, setCaseTab] = useState("overview");
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [cases, setCases] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [comms, setComms] = useState([]);
  const [selectedGlobalCommIds, setSelectedGlobalCommIds] = useState([]);
  const [selectedCaseCommIds, setSelectedCaseCommIds] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [intakeTemplates, setIntakeTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templateMsg, setTemplateMsg] = useState(null);
  const [draggedFieldIdx, setDraggedFieldIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewSection, setPreviewSection] = useState("personal");
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [parentingPlans, setParentingPlans] = useState([]);
  const [staff, setStaff] = useState([]);
  const [invites, setInvites] = useState([]);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [caseTasks, setCaseTasks] = useState([]);
  const [caseSignatureDocs, setCaseSignatureDocs] = useState([]);
  const [showNewSignatureDoc, setShowNewSignatureDoc] = useState(false);
  const [showPPBuilder, setShowPPBuilder] = useState(false);
  const [showMOUBuilder, setShowMOUBuilder] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsFilter, setLeadsFilter] = useState("all");
  const [editingLead, setEditingLead] = useState(null);
  const [leadNotes, setLeadNotes] = useState("");
  const [mouBuilderCase, setMOUBuilderCase] = useState(null);
  const [repoDocs, setRepoDocs] = useState([]);
  const [repoCleanupMsg, setRepoCleanupMsg] = useState(null);
  const [cleaningRepo, setCleaningRepo] = useState(false);
  const [replacingDocId, setReplacingDocId] = useState(null);
  const [uploadingReplacementId, setUploadingReplacementId] = useState(null);
  const replaceFileInputRef = useRef(null);
  const [caseRepoDocs, setCaseRepoDocs] = useState([]);
  const [repoFilter, setRepoFilter] = useState("");
  const [repoSearch, setRepoSearch] = useState("");
  const [showRepoUpload, setShowRepoUpload] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningDoc, setAssigningDoc] = useState(null);
  const [editingRepoDoc, setEditingRepoDoc] = useState(null);
  const [repoFolders, setRepoFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParent, setNewFolderParent] = useState(null); // parent folder id or null for root
  const [movingDoc, setMovingDoc] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [repoDocOrder, setRepoDocOrder] = useState(() => { try { return JSON.parse(localStorage.getItem("repoDocOrder") || "[]"); } catch { return []; } });
  const dragRepoId = useRef(null);
  const [dragOverRepoId, setDragOverRepoId] = useState(null);

  const saveRepoOrder = (order) => { setRepoDocOrder(order); localStorage.setItem("repoDocOrder", JSON.stringify(order)); };
  const onRepoDocDragStart = (e, id) => { dragRepoId.current = id; e.dataTransfer.effectAllowed = "move"; };
  const onRepoDocDragOver = (e, id) => { e.preventDefault(); setDragOverRepoId(id); };
  const onRepoDocDrop = (e, targetId) => {
    e.preventDefault();
    setDragOverRepoId(null);
    const fromId = dragRepoId.current;
    if (!fromId || fromId === targetId) return;
    setRepoDocs(prev => {
      const ids = prev.map(d => d.id);
      const fromIdx = ids.indexOf(fromId);
      const toIdx = ids.indexOf(targetId);
      const reordered = [...prev];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, moved);
      saveRepoOrder(reordered.map(d => d.id));
      return reordered;
    });
  };
  const onRepoDocDragEnd = () => { dragRepoId.current = null; setDragOverRepoId(null); };

  const loadRepoFolders = async () => {
    const res = await api.get("doc_repository.php?action=list_folders").catch(() => []);
    setRepoFolders(Array.isArray(res) ? res : []);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    const res = await api.post("doc_repository.php?action=create_folder", { name, parent_id: newFolderParent }).catch(() => null);
    if (res?.success) {
      setRepoFolders(prev => [...prev, res.folder]);
      if (newFolderParent) setExpandedFolders(prev => ({ ...prev, [newFolderParent]: true }));
      setNewFolderName(""); setShowNewFolder(false); setNewFolderParent(null);
    }
  };

  const deleteFolder = async (id) => {
    // Get all descendant folder ids
    const getAllDescendants = (fid) => {
      const children = repoFolders.filter(f => f.parent_id === fid).map(f => f.id);
      return children.reduce((acc, cid) => [...acc, cid, ...getAllDescendants(cid)], []);
    };
    const toDelete = [id, ...getAllDescendants(id)];
    const count = repoDocs.filter(d => toDelete.includes(d.folder_id)).length;
    const msg = count > 0
      ? `Delete this folder and ${count} subfolder(s)? ${count} document(s) will move to All Documents.`
      : "Delete this folder and all subfolders?";
    if (!window.confirm(msg)) return;
    await api.post("doc_repository.php?action=delete_folder", { id });
    setRepoFolders(prev => prev.filter(f => !toDelete.includes(f.id)));
    setRepoDocs(prev => prev.map(d => toDelete.includes(d.folder_id) ? { ...d, folder_id: null } : d));
    if (toDelete.includes(activeFolder)) setActiveFolder(null);
  };

  const moveDocToFolder = async (docId, folderId) => {
    setRepoDocs(prev => prev.map(d => d.id === docId ? {...d, folder_id: folderId} : d));
    setMovingDoc(null);
    await api.post("doc_repository.php?action=move_doc", { id: docId, folder_id: folderId });
  };

  const getFolderDocCount = (fid) => {
    const getAllDescendants = (id) => {
      const children = repoFolders.filter(f => f.parent_id === id).map(f => f.id);
      return children.reduce((acc, cid) => [...acc, cid, ...getAllDescendants(cid)], []);
    };
    const ids = [fid, ...getAllDescendants(fid)];
    return repoDocs.filter(d => ids.includes(d.folder_id)).length;
  };

  const renderFolderTree = (parentId, depth) => {
    const children = repoFolders.filter(f => f.parent_id === (parentId || null));
    if (children.length === 0) return null;
    return children.map(f => {
      const isActive = activeFolder === f.id;
      const isExpanded = expandedFolders[f.id];
      const hasChildren = repoFolders.some(c => c.parent_id === f.id);
      const count = getFolderDocCount(f.id);
      return (
        <div key={f.id}>
          <div style={{ display: "flex", alignItems: "center", paddingLeft: depth * 16, gap: 4, marginBottom: 2 }}>
            {hasChildren
              ? <button onClick={e => { e.stopPropagation(); setExpandedFolders(prev => ({...prev, [f.id]: !isExpanded})); }}
                  style={{ fontSize: 10, color: "#9B9490", width: 16, flexShrink: 0 }}>{isExpanded ? "▼" : "▶"}</button>
              : <span style={{ width: 16, flexShrink: 0 }} />}
            <div onClick={() => setActiveFolder(f.id)} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: isActive ? "#E6F4F2" : "transparent", color: isActive ? "#2D7D72" : "#1B2B4B", fontSize: 13, fontWeight: isActive ? 700 : 400, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>📁 {f.name} <span style={{ fontSize: 11, color: "#9B9490" }}>({count})</span></span>
              <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                <button title="Add subfolder" onClick={() => { setNewFolderParent(f.id); setShowNewFolder(true); setExpandedFolders(prev => ({...prev, [f.id]: true})); }}
                  style={{ fontSize: 12, color: "#2D7D72", fontWeight: 700, padding: "1px 5px" }}>+</button>
                <button title="Delete folder" onClick={() => deleteFolder(f.id)}
                  style={{ fontSize: 13, color: "#C0392B", fontWeight: 700, padding: "1px 5px" }}>×</button>
              </div>
            </div>
          </div>
          {isExpanded && hasChildren && renderFolderTree(f.id, depth + 1)}
        </div>
      );
    });
  };

  const [showPPEditor, setShowPPEditor] = useState(false);
  const [showEmailTemplateEditor, setShowEmailTemplateEditor] = useState(false);
  const [ppBuilderCase, setPPBuilderCase] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [caseCalendarEvents, setCaseCalendarEvents] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; });
  const [calendarView, setCalendarView] = useState("month");
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showSchedulingLink, setShowSchedulingLink] = useState(false);
  const [sigDocMsg, setSigDocMsg] = useState(null);
  const [newSigDoc, setNewSigDoc] = useState({ title: "", document_type: "Retainer / Engagement Agreement", require_client1: true, require_client2: true, require_mediator: false });
  const [signingDoc, setSigningDoc] = useState(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newTask, setNewTask] = useState({ title: "", notes: "", due_date: "", priority: "Medium", assigned_to: "both" });
  const [taskMsg, setTaskMsg] = useState(null);
  const [docUploadMsg, setDocUploadMsg] = useState(null);
  const [docVisibleToClient, setDocVisibleToClient] = useState(true);
  const [loading, setLoading] = useState(true);

  const [showNewCase, setShowNewCase] = useState(false);
  const [newCaseMsg, setNewCaseMsg] = useState(null);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [bulkSelectedCases, setBulkSelectedCases] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [caseAgreements, setCaseAgreements] = useState([]);
  const [showAgreementEditor, setShowAgreementEditor] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [agreementMsg, setAgreementMsg] = useState(null);
  const [previewAgreement, setPreviewAgreement] = useState(null);
  const [agreementHeader, setAgreementHeader] = useState({ logo_url: "", practice_name: "Lakeshore Mediation", tagline: "", address: "", phone: "", email: "", website: "" });
  const [showHeaderSettings, setShowHeaderSettings] = useState(false);
  const [headerMsg, setHeaderMsg] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [timeEntries, setTimeEntries] = useState([]);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [showTimeEntry, setShowTimeEntry] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [showGenerateInvoice, setShowGenerateInvoice] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(null);
  const [showFeeSettings, setShowFeeSettings] = useState(false);
  const [billingTab, setBillingTab] = useState("time");
  const [showNewComm, setShowNewComm] = useState(false);
  const [commMsg, setCommMsg] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showClientPreview, setShowClientPreview] = useState(false);
  const [clientPreviewTab, setClientPreviewTab] = useState("home");
  const [showNewStaff, setShowNewStaff] = useState(false);
  const [showStaffInvite, setShowStaffInvite] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const SERVICE_TYPES = ["Divorce Mediation with Children", "Divorce Mediation without Children", "Discernment Mediation", "Parenting Planning", "Inclusive Mediation", "Divorce Coaching", "Divorce Financial Planning"];
  const [newCase, setNewCase] = useState({ case_number: "", client1_name: "", client2_name: "", mediator: "", stage: "Initial Assessment", status: "Active", children: 0, assigned_mediator_id: "", service_type: "" });
  const [newSession, setNewSession] = useState({ session_date: "", session_time: "", duration: 60, type: "In-Person", topic: "", status: "Upcoming", notes: "" });
  const [sessionError, setSessionError] = useState("");
  const [editingSession, setEditingSession] = useState(null);
  const [editSessionError, setEditSessionError] = useState("");
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  const [newInvoice, setNewInvoice] = useState({ invoice_number: "", invoice_date: "", amount: "", description: "", status: "Outstanding" });
  const [newInvoiceMsg, setNewInvoiceMsg] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [newComm, setNewComm] = useState({ comm_date: "", from_name: "", to_name: "", type: "Email", subject: "", summary: "" });
  const [newInvite, setNewInvite] = useState({ email: "", case_role: "Client 1" });
  const [inviteMsg, setInviteMsg] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [contacts, setContacts] = useState({});
  const [contactsSaved, setContactsSaved] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);

  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "mediator" });
  const [staffInvite, setStaffInvite] = useState({ name: "", email: "", role: "mediator" });
  const [staffInvites, setStaffInvites] = useState([]);
  const [staffMsg, setStaffMsg] = useState(null);

  const [profileName, setProfileName] = useState(user?.name || "");
  const [profileEmail, setProfileEmail] = useState(user?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileMsg, setProfileMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [avatarMsg, setAvatarMsg] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar ? `https://ourmediationcase.com${user.avatar}` : null);
  const [notifSettings, setNotifSettings] = useState({ new_session: 1, session_reminder: 1, new_task: 1, task_complete: 1, signature_request: 1, document_signed: 1, new_document: 1, invite_sent: 1 });
  const [notifMsg, setNotifMsg] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const caseEndpoint = isAdmin ? `cases.php?role=admin` : `cases.php?role=mediator&user_id=${user.id}`;
      const safeGet = (ep) => api.get(ep).catch(() => []);
      const [c, s, inv, cm, inf, pp, st] = await Promise.all([
        safeGet(caseEndpoint), safeGet("sessions.php"), safeGet("billing.php?action=get_all_invoices"),
        safeGet("communications.php"), safeGet("intake.php"), safeGet("parenting.php"),
        safeGet("staff.php"),
      ]);
      setCases(Array.isArray(c) ? c : []);
      setSessions(Array.isArray(s) ? s : []);
      setInvoices(Array.isArray(inv) ? inv : []);
      setComms(Array.isArray(cm) ? cm : []);
      setIntakeForms(Array.isArray(inf) ? inf : []);
      // Load intake templates
      const tmplRes = await api.get("intake_forms.php?action=list").catch(() => []);
      setIntakeTemplates(Array.isArray(tmplRes) ? tmplRes : []);
      setParentingPlans(Array.isArray(pp) ? pp : []);
      setStaff(Array.isArray(st) ? st : []);
      // Load agreements
      const agRes = await api.get("agreements.php?action=list").catch(() => []);
      setAgreements(Array.isArray(agRes) ? agRes : []);
      // Load agreement header settings
      const hRes = await api.get("agreements.php?action=get_header").catch(() => null);
      if (hRes && hRes.id) setAgreementHeader(hRes);
      // Load staff invites
      const invRes = await api.get("staff.php?action=get_invites").catch(() => []);
      setStaffInvites(Array.isArray(invRes) ? invRes : []);
      // Load notification settings
      const ns = await api.get(`notifications.php?user_id=${user.id}`).catch(() => null);
      if (ns) setNotifSettings(ns);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [isAdmin, user.id]);

  const loadCalendar = useCallback(async (month) => {
    try {
      const events = await api.get(`calendar.php?user_id=${user.id}&month=${month}`);
      setCalendarEvents(Array.isArray(events) ? events : []);
    } catch (e) { console.error(e); }
  }, [user.id]);

  const loadCaseExtras = useCallback(async (caseId) => {
    try {
      const safeGet = (ep) => api.get(ep).catch(() => []);
      const [inv, docs, tasks, sigdocs, cevents, ctcts, caseAgs, issues, checkins, clientNotes] = await Promise.all([
        safeGet(`invite.php?case_id=${caseId}`),
        safeGet(`documents.php?case_id=${caseId}`),
        safeGet(`tasks.php?case_id=${caseId}&role=mediator`),
        safeGet(`signatures.php?case_id=${caseId}`),
        safeGet(`calendar.php?case_id=${caseId}&month=${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,"0")}`),
        api.get(`case_contacts.php?action=get&case_id=${caseId}`).catch(() => ({})),
        safeGet(`agreements.php?action=get_case_agreements&case_id=${caseId}`),
        safeGet(`checkins.php?action=get_issues&case_id=${caseId}`),
        safeGet(`checkins.php?action=get_checkins&case_id=${caseId}`),
        safeGet(`checkins.php?action=get_client_notes&case_id=${caseId}`),
      ]);
      setInvites(Array.isArray(inv) ? inv : []);
      setCaseDocuments(Array.isArray(docs) ? docs : []);
      setCaseTasks(Array.isArray(tasks) ? tasks : []);
      setCaseSignatureDocs(Array.isArray(sigdocs) ? sigdocs : []);
      setCaseCalendarEvents(Array.isArray(cevents) ? cevents : []);
      setContacts(ctcts && typeof ctcts === 'object' ? ctcts : {});
      setCaseAgreements(Array.isArray(caseAgs) ? caseAgs : []);
      setCaseIssues(Array.isArray(issues) ? issues : []);
      setCaseCheckins(Array.isArray(checkins) ? checkins : []);
      setCaseClientNotes(Array.isArray(clientNotes) ? clientNotes : []);
    } catch (e) { console.error(e); }
  }, []);

  const loadRepoDocs = async () => {
    const res = await api.get("doc_repository.php?action=list");
    const savedOrder = JSON.parse(localStorage.getItem("repoDocOrder") || "[]");
    const docs = Array.isArray(res) ? res : [];
    if (savedOrder.length > 0) {
      const orderMap = Object.fromEntries(savedOrder.map((id, i) => [id, i]));
      docs.sort((a, b) => (orderMap[a.id] ?? 9999) - (orderMap[b.id] ?? 9999));
    }
    setRepoDocs(docs);
  };

  const triggerReplaceFile = (docId) => { setReplacingDocId(docId); replaceFileInputRef.current?.click(); };

  const handleReplaceFileChange = async (e) => {
    const file = e.target.files?.[0];
    const docId = replacingDocId;
    e.target.value = "";
    setReplacingDocId(null);
    if (!file || !docId) return;
    setUploadingReplacementId(docId);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("id", docId);
    const res = await fetch(`${API_BASE}/doc_repository.php?action=replace_file`, { method: "POST", body: fd, headers: { "X-Auth-Token": getToken() } }).then(r => r.json()).catch(() => null);
    if (res?.success) {
      setRepoDocs(prev => prev.map(d => d.id === docId ? { ...d, ...res.doc, folder_id: d.folder_id } : d));
      setRepoCleanupMsg({ type: "success", text: "File replaced." });
    } else {
      setRepoCleanupMsg({ type: "error", text: res?.error || "Could not replace the file." });
    }
    setUploadingReplacementId(null);
    setTimeout(() => setRepoCleanupMsg(null), 5000);
  };

  const loadCaseRepoDocs = async (caseId) => {
    const res = await api.get(`doc_repository.php?action=get_case_docs&case_id=${caseId}`);
    setCaseRepoDocs(Array.isArray(res) ? res : []);
  };

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadCalendar(calendarMonth); }, [loadCalendar, calendarMonth]);
  useEffect(() => { loadRepoDocs(); loadRepoFolders(); }, []);
  useEffect(() => { if (selectedCase) { loadCaseExtras(selectedCase); loadCaseRepoDocs(selectedCase); loadBilling(selectedCase); } }, [selectedCase, loadCaseExtras]);

  const activeCase = selectedCase ? cases.find(c => c.id === parseInt(selectedCase)) : null;
  const caseSessions = activeCase ? sessions.filter(s => parseInt(s.case_id) === activeCase.id) : [];
  const caseInvoices = activeCase ? invoices.filter(i => parseInt(i.case_id) === activeCase.id) : [];
  const caseComms = activeCase ? comms.filter(c => parseInt(c.case_id) === activeCase.id) : [];
  const caseIntake = activeCase ? intakeForms.filter(f => parseInt(f.case_id) === activeCase.id) : [];
  const totalOutstanding = invoices.filter(i => i.status === "Outstanding").reduce((a, b) => a + parseFloat(b.amount || 0), 0);
  const upcomingSessions = sessions.filter(s => s.status === "Upcoming");

  const staffOptions = [{ value: "", label: "Unassigned" }, ...staff.map(s => ({ value: s.id, label: s.name }))];

  const createCase = async () => {
    try {
      const assignedId = newCase.assigned_mediator_id || (isAdmin ? null : user.id);
      const res = await api.post("cases.php", { ...newCase, opened: new Date().toISOString().split("T")[0], assigned_mediator_id: assignedId || user.id });
      if (!res || res.success !== true) { setNewCaseMsg({ type: "error", text: res?.error || "Failed to create case. Please try again." }); return; }
      // Auto-assign matching intake template
      if (res.id && newCase.service_type) {
        const matchTemplate = intakeTemplates.find(t => t.service_type === newCase.service_type || t.title === newCase.service_type);
        if (matchTemplate) {
          await api.post("intake_forms.php", { action: "assign_to_case", case_id: res.id, template_id: matchTemplate.id });
        }
      }
      setShowNewCase(false);
      setNewCaseMsg(null);
      setNewCase({ case_number: "", client1_name: "", client2_name: "", mediator: "", stage: "Initial Assessment", status: "Active", children: 0, assigned_mediator_id: "", service_type: "" });
      loadData();
    } catch {
      setNewCaseMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const createSession = async () => {
    setSessionError("");
    const res = await api.post("sessions.php", { ...newSession, case_id: activeCase.id });
    if (res.success) {
      setShowNewSession(false);
      setNewSession({ session_date: "", session_time: "", duration: 60, type: "In-Person", topic: "", status: "Upcoming", notes: "" });
      loadData();
      loadCaseExtras(activeCase.id);
    } else {
      setSessionError(res.error || "Failed to save session.");
    }
  };

  const updateSession = async () => {
    setEditSessionError("");
    try {
      const res = await fetch(`${API_BASE}/sessions.php?id=${editingSession.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Auth-Token": getToken() },
        body: JSON.stringify({ ...editingSession, send_notification: sendCancelEmail })
      }).then(r => r.json());
      if (res && res.success) { setEditingSession(null); loadData(); if (activeCase) loadCaseExtras(activeCase.id); }
      else setEditSessionError(res?.error || "Failed to update session.");
    } catch {
      setEditSessionError("Could not reach the server. Please try again.");
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      const res = await api.delete(`sessions.php?id=${id}`);
      // sessions is the shared, firm-wide list both the case view, the
      // dashboard, and the calendar (merged in via sessionToCalEvent) filter
      // from — refreshing it here makes the deleted session disappear
      // everywhere at once.
      if (res && res.success) { setEditingSession(null); loadData(); }
      else alert(res?.error || "Failed to delete session.");
    } catch {
      alert("Could not reach the server. Please try again.");
    }
  };
  const createInvoice = async () => {
    setNewInvoiceMsg(null);
    const action = editingInvoiceId ? "update_invoice" : "create_invoice";
    const payload = editingInvoiceId ? { ...newInvoice, id: editingInvoiceId } : { ...newInvoice, case_id: activeCase.id };
    let res;
    try {
      res = await api.post(`billing.php?action=${action}`, payload);
    } catch (e) {
      setNewInvoiceMsg({ type: "error", text: "Network error: " + e.message });
      return;
    }
    if (res && res.success) {
      setShowNewInvoice(false);
      setEditingInvoiceId(null);
      setNewInvoice({ invoice_number: "", invoice_date: "", amount: "", description: "", status: "Outstanding" });
      loadBilling(activeCase.id);
      loadData();
    } else {
      setNewInvoiceMsg({ type: "error", text: res?.error || "Failed to save invoice." });
    }
  };

  const openEditInvoice = (inv) => {
    setEditingInvoiceId(inv.id);
    setNewInvoice({
      invoice_number: inv.invoice_number || "",
      invoice_date: inv.invoice_date || "",
      amount: inv.amount || "",
      description: inv.description || "",
      status: inv.status === "Paid" ? "Paid" : "Outstanding",
    });
    setNewInvoiceMsg(null);
    setShowNewInvoice(true);
  };
  const createComm = async () => {
    if (!newComm.subject.trim()) { setCommMsg({ type: "error", text: "Subject is required." }); return; }
    if (!newComm.comm_date) { setCommMsg({ type: "error", text: "Date is required." }); return; }
    try {
      const res = await api.post("communications.php", { ...newComm, case_id: activeCase.id });
      if (!res || res.success !== true) { setCommMsg({ type: "error", text: res?.error || "Failed to save. Please try again." }); return; }
      setShowNewComm(false);
      setCommMsg(null);
      setNewComm({ comm_date: "", from_name: "", to_name: "", type: "Email", subject: "", summary: "" });
      loadData();
    } catch {
      setCommMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };
  const saveNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await api.post("sessions.php", { case_id: activeCase.id, session_date: new Date().toISOString().split("T")[0], session_time: "", duration: "", type: "Note", topic: "Session Note", status: "Completed", notes: newNote });
      if (res.success) { setNewNote(""); loadData(); }
      else { alert("Note failed to save: " + (res.error || "Unknown error")); }
    } catch(e) { alert("Note save error: " + e.message); }
  };
  const [sendingPaymentLink, setSendingPaymentLink] = useState(null);

  const sendPaymentLink = async (inv) => {
    setSendingPaymentLink(inv.id);
    try {
      // Get client email for this case
      const clients = await api.get(`invite.php?case_id=${inv.case_id}`);
      const client = Array.isArray(clients) ? clients.find(c => c.status === 'Accepted') : null;
      const rawRes = await fetch(`${API_BASE}/stripe_payment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Auth-Token": JSON.parse(localStorage.getItem("crm_user"))?.token || "" },
        body: JSON.stringify({
          action: "create_payment_link",
          invoice_id: inv.id,
          amount: inv.amount,
          description: inv.description || "Mediation Services",
          invoice_number: inv.invoice_number,
          client_email: client?.email || "",
          client_name: client ? `${activeCase?.client1_name || "Client"}` : "",
          case_id: inv.case_id,
        })
      });
      const text = await rawRes.text();
      let res;
      try { res = JSON.parse(text); } catch { alert("Server error: " + text.substring(0, 300)); setSendingPaymentLink(null); return; }
      if (res.success) {
        alert(`Payment link created${client?.email ? " and sent to client" : ""}!\n\n${res.payment_link}`);
        loadBilling(activeCase.id);
      } else {
        alert("Error: " + (res.error || "Failed to create payment link"));
      }
    } catch(e) {
      alert("Error creating payment link");
    }
    setSendingPaymentLink(null);
  };

  const markInvoicePaid = async (inv) => {
    await api.post(`billing.php?action=update_invoice_status`, { id: inv.id, status: "Paid" });
    loadBilling(inv.case_id || activeCase?.id);
    loadData();
  };

  const [caseCardInfo, setCaseCardInfo] = useState(null);
  const [caseIssues, setCaseIssues] = useState([]);
  const [caseCheckins, setCaseCheckins] = useState([]);
  const [caseClientNotes, setCaseClientNotes] = useState([]);
  const [editingIssue, setEditingIssue] = useState(null);
  const [showNewIssue, setShowNewIssue] = useState(false);
  const loadBilling = async (caseId) => {
    if (!caseId) return;
    const id = parseInt(caseId);
    if (!id) return;
    try {
      const [entries, invs, cardInfo] = await Promise.all([
        api.get(`billing.php?action=get_entries&case_id=${id}`),
        api.get(`billing.php?action=get_invoices&case_id=${id}`),
        api.post("stripe.php", { action: "get_card", case_id: id }).catch(() => null),
      ]);
      setTimeEntries(Array.isArray(entries) ? entries : []);
      setBillingInvoices(Array.isArray(invs) ? invs : []);
      if (cardInfo) setCaseCardInfo(cardInfo);
    } catch(e) { console.error("loadBilling error:", e); }
  };

  const deleteCase = async (id) => {
    if (window.confirm("Delete this case and all associated records?")) {
      await api.delete(`cases.php?id=${id}`); setSelectedCase(null); loadData();
    }
  };

  const isWellnessOn = (caseItem) => caseItem.wellness_enabled === null || caseItem.wellness_enabled === undefined || parseInt(caseItem.wellness_enabled) === 1;

  const toggleWellness = async (caseItem) => {
    const enabled = isWellnessOn(caseItem) ? 0 : 1;
    setCases(prev => prev.map(c => c.id === caseItem.id ? { ...c, wellness_enabled: enabled } : c));
    await api.post("cases.php?action=toggle_wellness", { id: caseItem.id, enabled });
  };

  const bulkDeleteCases = async () => {
    if (bulkSelectedCases.length === 0) return;
    if (!window.confirm(`Delete ${bulkSelectedCases.length} case${bulkSelectedCases.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    await Promise.all(bulkSelectedCases.map(id => api.delete(`cases.php?id=${id}`)));
    setBulkSelectedCases([]);
    setBulkSelectMode(false);
    loadData();
  };

  const sendInvite = async () => {
    setInviteMsg(null);
    try {
      const res = await api.post("invite.php", { action: "send_invite", case_id: activeCase.id, email: newInvite.email, case_role: newInvite.case_role, mediator_name: user.name, case_name: `${activeCase.client1_name} & ${activeCase.client2_name}` });
      if (res && res.success) { setInviteMsg({ type: "success", text: `Invite sent to ${newInvite.email}` }); setNewInvite({ email: "", case_role: "Client 1" }); loadCaseExtras(activeCase.id); }
      else setInviteMsg({ type: "error", text: res?.error || "Failed to send invite." });
    } catch {
      setInviteMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const deleteComm = async (id) => {
    if (!window.confirm("Delete this communication log entry? This cannot be undone.")) return;
    await api.delete(`communications.php?id=${id}`);
    setComms(prev => prev.filter(c => c.id !== id));
  };

  const bulkDeleteComms = async (ids, clearSelection) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} communication log ${ids.length === 1 ? "entry" : "entries"}? This cannot be undone.`)) return;
    await api.post("communications.php", { action: "bulk_delete", ids });
    setComms(prev => prev.filter(c => !ids.includes(c.id)));
    clearSelection();
  };

  const resendInvite = async (inv) => {
    try {
      const res = await api.post("invite.php", { action: "resend_invite", token: inv.token, email: inv.email, mediator_name: user.name });
      if (res && res.success) setInviteMsg({ type: "success", text: `Reminder sent to ${inv.email}` });
      else setInviteMsg({ type: "error", text: res?.error || "Failed to resend invite." });
    } catch {
      setInviteMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const uploadDocument = async (file, visibleToClient = 1) => {
    if (!file || !activeCase) return;
    const formData = new FormData();
    formData.append('document', file);
    formData.append('case_id', activeCase.id);
    formData.append('uploaded_by', user.id);
    formData.append('uploader_name', user.name);
    formData.append('uploader_role', user.role);
    formData.append('visible_to_client', visibleToClient);
    try {
      const res = await fetch(`${API_BASE}/documents.php`, { method: 'POST', body: formData, headers: { 'X-Auth-Token': getToken() } });
      const result = await res.json();
      if (result.success) { setDocUploadMsg({ type: 'success', text: `${file.name} uploaded!` }); loadCaseExtras(activeCase.id); }
      else setDocUploadMsg({ type: 'error', text: result.error });
    } catch { setDocUploadMsg({ type: 'error', text: 'Upload failed. Please try again.' }); }
    setTimeout(() => setDocUploadMsg(null), 5000);
  };

  const deleteDocument = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await api.post("documents.php?action=delete", { id });
      if (res.success) loadCaseExtras(activeCase.id);
      else setDocUploadMsg({ type: 'error', text: res?.error || 'Failed to delete document.' });
    } catch {
      setDocUploadMsg({ type: 'error', text: 'Failed to delete document. Please try again.' });
    }
  };

  const createTask = async () => {
    if (!newTask.title.trim()) { setTaskMsg({ type: "error", text: "Title is required." }); return; }
    try {
      const res = await api.post("tasks.php", { action: "create", ...newTask, case_id: activeCase.id, created_by: user.id, created_by_name: user.name });
      if (res && res.success) { setShowNewTask(false); setNewTask({ title: "", notes: "", due_date: "", priority: "Medium", assigned_to: "both" }); setTaskMsg({ type: "success", text: "Task created!" }); loadCaseExtras(activeCase.id); }
      else setTaskMsg({ type: "error", text: res?.error || "Failed to create task." });
    } catch {
      setTaskMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setTaskMsg(null), 4000);
  };

  const updateTask = async () => {
    try {
      const res = await api.post("tasks.php", { action: "update", task_id: editingTask.id, title: editingTask.title, notes: editingTask.notes, due_date: editingTask.due_date, priority: editingTask.priority, assigned_to: editingTask.assigned_to });
      if (res && res.success) { setEditingTask(null); loadCaseExtras(activeCase.id); }
      else setTaskMsg({ type: "error", text: res?.error || "Failed to update task." });
    } catch {
      setTaskMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  };

  const completeTask = async (taskId) => {
    await api.post("tasks.php", { action: "mediator_complete", task_id: taskId, completed_by: user.name });
    loadCaseExtras(activeCase.id);
  };

  const uncompleteTask = async (taskId) => {
    await api.post("tasks.php", { action: "uncomplete", task_id: taskId });
    loadCaseExtras(activeCase.id);
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    await api.post("tasks.php", { action: "delete", task_id: taskId });
    loadCaseExtras(activeCase.id);
  };

  const clearTaskNotifications = async (caseId) => {
    await api.post("tasks.php", { action: "clear_notify", case_id: caseId });
    loadCaseExtras(caseId);
  };

  const uploadTaskFile = async (file, taskId) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("task_file", file);
    formData.append("task_id", taskId);
    formData.append("case_id", activeCase.id);
    formData.append("uploaded_by", user.id);
    formData.append("uploader_name", user.name);
    try {
      const res = await fetch(`${API_BASE}/tasks.php`, { method: "POST", body: formData, headers: { "X-Auth-Token": getToken() } });
      const result = await res.json();
      if (result.success) loadCaseExtras(activeCase.id);
      else setTaskMsg({ type: "error", text: result.error });
    } catch { setTaskMsg({ type: "error", text: "File upload failed." }); }
  };

  const uploadSignatureDoc = async (file) => {
    if (!file || !activeCase) return;
    if (file.type !== "application/pdf") { setSigDocMsg({ type: "error", text: "Only PDF files are accepted." }); return; }
    const formData = new FormData();
    formData.append("document", file);
    formData.append("case_id", activeCase.id);
    formData.append("title", newSigDoc.title || file.name);
    formData.append("document_type", newSigDoc.document_type);
    formData.append("require_client1", newSigDoc.require_client1 ? 1 : 0);
    formData.append("require_client2", newSigDoc.require_client2 ? 1 : 0);
    formData.append("require_mediator", newSigDoc.require_mediator ? 1 : 0);
    formData.append("created_by", user.id);
    formData.append("created_by_name", user.name);
    try {
      const res = await fetch(`${API_BASE}/signatures.php`, { method: "POST", body: formData, headers: { "X-Auth-Token": getToken() } });
      const result = await res.json();
      if (result.success) {
        setSigDocMsg({ type: "success", text: "Document uploaded and ready for signing!" });
        setShowNewSignatureDoc(false);
        setNewSigDoc({ title: "", document_type: "Retainer / Engagement Agreement", require_client1: true, require_client2: true, require_mediator: false });
        loadCaseExtras(activeCase.id);
      } else setSigDocMsg({ type: "error", text: result.error });
    } catch { setSigDocMsg({ type: "error", text: "Upload failed. Please try again." }); }
    setTimeout(() => setSigDocMsg(null), 5000);
  };

  const markExecuted = async (docId) => {
    await api.post("signatures.php", { action: "mark_executed", document_id: docId });
    loadCaseExtras(activeCase.id);
  };

  const reopenSigDoc = async (docId) => {
    await api.post("signatures.php", { action: "reopen", document_id: docId });
    loadCaseExtras(activeCase.id);
  };

  const deleteSignature = async (sigId, docId) => {
    if (!window.confirm("Remove this signature? The document will revert to Pending.")) return;
    await api.post("signatures.php", { action: "delete_signature", signature_id: sigId, document_id: docId });
    loadCaseExtras(activeCase.id);
  };

  const deleteSignatureDoc = async (id) => {
    if (!window.confirm("Delete this document and all its signatures?")) return;
    await api.delete(`signatures.php?id=${id}`);
    loadCaseExtras(activeCase.id);
  };

  const createStaff = async () => {
    try {
      const res = await api.post("staff.php", { action: "create_staff", ...newStaff, created_by: user.id });
      if (res && res.success) { setStaffMsg({ type: "success", text: "Staff account created!" }); setShowNewStaff(false); setNewStaff({ name: "", email: "", password: "", role: "mediator" }); loadData(); }
      else setStaffMsg({ type: "error", text: res?.error || "Failed to create staff account." });
    } catch {
      setStaffMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setStaffMsg(null), 4000);
  };

  const sendStaffInvite = async () => {
    try {
      const res = await api.post("staff.php", { action: "send_staff_invite", ...staffInvite, created_by: user.id });
      if (res && res.success) { setStaffMsg({ type: "success", text: `Invite sent to ${staffInvite.email}` }); setShowStaffInvite(false); setStaffInvite({ name: "", email: "", role: "mediator" });
        const invRes = await api.get("staff.php?action=get_invites"); setStaffInvites(Array.isArray(invRes) ? invRes : []);
      }
      else setStaffMsg({ type: "error", text: res?.error || "Failed to send invite." });
    } catch {
      setStaffMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setStaffMsg(null), 4000);
  };

  const updateStaff = async () => {
    try {
      const res = await api.post("staff.php", { action: "update_staff", ...editingStaff });
      if (res && res.success) { setStaffMsg({ type: "success", text: "Staff updated!" }); setEditingStaff(null); loadData(); }
      else setStaffMsg({ type: "error", text: res?.error || "Failed to update." });
    } catch {
      setStaffMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setStaffMsg(null), 4000);
  };

  const deleteStaff = async (id) => {
    if (window.confirm("Remove this staff member? Their cases will be unassigned.")) {
      await api.post("staff.php", { action: "delete_staff", id });
      loadData();
    }
  };

  const resetStaffPassword = async (id, pwd) => {
    await api.post("staff.php", { action: "reset_password", id, password: pwd });
    setStaffMsg({ type: "success", text: "Password reset!" });
    setTimeout(() => setStaffMsg(null), 4000);
  };

  const saveNotifSettings = async () => {
    try {
      const res = await api.post("notifications.php", { ...notifSettings, user_id: user.id });
      if (res && res.success) setNotifMsg({ type: "success", text: "Notification preferences saved!" });
      else setNotifMsg({ type: "error", text: res?.error || "Failed to save." });
    } catch {
      setNotifMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setNotifMsg(null), 4000);
  };

  const updateProfile = async () => {
    try {
      const res = await api.post("profile.php", { action: "update_profile", user_id: user.id, name: profileName, email: profileEmail });
      if (res && res.success) { const updated = { ...user, name: profileName, email: profileEmail }; localStorage.setItem("crm_user", JSON.stringify(updated)); setProfileMsg({ type: "success", text: "Profile updated!" }); }
      else setProfileMsg({ type: "error", text: res?.error || "Failed to update." });
    } catch {
      setProfileMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setProfileMsg(null), 4000);
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) { setPasswordMsg({ type: "error", text: "Passwords don't match." }); return; }
    if (newPassword.length < 6) { setPasswordMsg({ type: "error", text: "Minimum 6 characters." }); return; }
    try {
      const res = await api.post("profile.php", { action: "change_password", user_id: user.id, current_password: currentPassword, new_password: newPassword });
      if (res && res.success) { setPasswordMsg({ type: "success", text: "Password changed!" }); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); }
      else setPasswordMsg({ type: "error", text: res?.error || "Failed to change password." });
    } catch {
      setPasswordMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
    setTimeout(() => setPasswordMsg(null), 4000);
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("avatar", file); formData.append("user_id", user.id);
    try {
      const res = await fetch(`${API_BASE}/upload_avatar.php`, { method: "POST", body: formData, headers: { "X-Auth-Token": getToken() } });
      const data = await res.json();
      if (data.success) { const url = `https://ourmediationcase.com${data.avatar}?t=${Date.now()}`; setAvatarPreview(url); const updated = { ...user, avatar: data.avatar }; localStorage.setItem("crm_user", JSON.stringify(updated)); setAvatarMsg({ type: "success", text: "Photo updated!" }); }
      else setAvatarMsg({ type: "error", text: data.error });
    } catch { setAvatarMsg({ type: "error", text: "Upload failed." }); }
    setTimeout(() => setAvatarMsg(null), 4000);
  };

  const goToTab = (id) => { setTab(id); setSelectedCase(null); setSelectedPlan(null); };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: "⊞" },
    { id: "cases", label: "Cases", icon: "⚖" },
    { id: "scheduling", label: "Scheduling", icon: "🗓" },
    { id: "calendar", label: "Calendar", icon: "📅" },
    { id: "documents", label: "Documents", icon: "📄" },
    ...(isAdmin ? [{ id: "billing", label: "Billing", icon: "💳" }] : []),
    { id: "communications", label: "Comms", icon: "✉" },
    { id: "intake", label: "Intake Forms", icon: "📋" },
    { id: "agreements", label: "Agreements", icon: "✍" },
    { id: "parenting", label: "Parenting Plans", icon: "👨‍👩‍👧" },
    { id: "leads", label: "Leads", icon: "🎯" },
    { id: "mou", label: "MOU", icon: "📋" },
    { id: "repository", label: "Doc Repository", icon: "🗂" },
    ...(isAdmin ? [{ id: "staff", label: "Staff", icon: "👥" }] : []),
    ...(isAdmin ? [{ id: "auditlog", label: "Audit Log", icon: "🔒" }] : []),
    { id: "profile", label: "Profile", icon: "⚙" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "'DM Sans', sans-serif", background: "#F7F5F0", color: "#1B2B4B", overflow: "hidden", flexDirection: "column" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-thumb { background: #E2DDD5; border-radius: 3px; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }` + MOBILE_CSS}</style>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* Sidebar */}
      {sidebarOpen && (
      <div className="r-sidebar" style={{ width: 220, background: "#1B2B4B", display: "flex", flexDirection: "column", flexShrink: 0, overflowY: "auto" }}>
        <div style={{ padding: "28px 20px 16px", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#fff", fontWeight: 800 }}>Our<span style={{ color: "#C8873A" }}>Mediation</span>Case</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 3, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {isAdmin ? "Admin" : "Mediator"} Portal
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} title="Collapse sidebar" style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", padding: 4, flexShrink: 0 }}>✕</button>
        </div>
        <nav style={{ flex: 1, padding: "8px 12px" }}>
          {navItems.map(n => (
            <button key={n.id} onClick={() => goToTab(n.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 8, marginBottom: 2, background: tab === n.id && !selectedCase ? "rgba(255,255,255,0.12)" : "transparent", color: tab === n.id && !selectedCase ? "#fff" : "rgba(255,255,255,0.45)", fontSize: 13, fontWeight: 600, textAlign: "left" }}>
              <span style={{ fontSize: 15 }}>{n.icon}</span>{n.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
              {user.avatar ? <img src={`https://ourmediationcase.com${user.avatar}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#2D7D72", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff" }}>{user.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}</div>}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{user.name}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", textTransform: "capitalize" }}>{user.role}</div>
            </div>
          </div>
          <button onClick={onLogout} style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontWeight: 600 }}>Sign out</button>
        </div>
      </div>
      )}
      {!sidebarOpen && (
        <button onClick={() => setSidebarOpen(true)} title="Open sidebar" className="r-sidebar"
          style={{ width: 28, background: "#1B2B4B", color: "rgba(255,255,255,0.45)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 28, fontSize: 14 }}>☰</button>
      )}

      {/* Main */}
      <div className="r-main" style={{ flex: 1, overflow: "auto" }}>
        {loading ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9B9490" }}>Loading…</div> : (
          <>
            {/* ── CASE DETAIL ── */}
            {selectedCase && activeCase && (
              <div style={{ padding: "32px 36px" }}>
                <button onClick={() => setSelectedCase(null)} style={{ fontSize: 13, color: "#2D7D72", fontWeight: 600, marginBottom: 20 }}>← Back to Cases</button>
                <div className="r-case-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800 }}>{activeCase.client1_name} & {activeCase.client2_name}</h1>
                      <Badge label={activeCase.status} color={statusColor(activeCase.status)} />
                    </div>
                    <div style={{ fontSize: 13, color: "#9B9490" }}>
                      {activeCase.case_number} · {activeCase.mediator_name || activeCase.mediator} · Opened {activeCase.opened}
                    </div>
                  </div>
                  <div className="r-case-actions" style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setShowClientPreview(true)} style={{ padding: "8px 16px", border: "1px solid #9B9490", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#9B9490" }}>👁 Client View</button>
                    <button onClick={() => setShowInvite(true)} style={{ padding: "8px 16px", border: "1px solid #2D7D72", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#2D7D72" }}>✉ Invite Client</button>
                    <button onClick={() => setShowNewSession(true)} style={{ padding: "8px 16px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>+ Session</button>
                    <button onClick={() => toggleWellness(activeCase)} title="Show or hide the Wellness check-in feature for this client"
                      style={{ padding: "8px 16px", border: `1px solid ${isWellnessOn(activeCase) ? "#2D7D72" : "#E2DDD5"}`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: isWellnessOn(activeCase) ? "#2D7D72" : "#9B9490", background: isWellnessOn(activeCase) ? "#E6F4F2" : "#fff" }}>
                      💚 Wellness: {isWellnessOn(activeCase) ? "On" : "Off"}
                    </button>
                    {isAdmin && <button onClick={() => deleteCase(activeCase.id)} style={{ padding: "8px 16px", border: "1px solid #C0392B", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#C0392B" }}>Delete</button>}
                  </div>
                </div>

                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "14px 20px", marginBottom: 22, display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#4A5568" }}>{activeCase.stage}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#2D7D72" }}>{activeCase.progress}%</span>
                    </div>
                    <ProgressBar value={parseInt(activeCase.progress || 0)} />
                  </div>
                  <div className="r-case-stats" style={{ display: "flex", gap: 24, flexShrink: 0 }}>
                    <div style={{ textAlign: "center" }}><div style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>{activeCase.children}</div><div style={{ fontSize: 11, color: "#9B9490" }}>Children</div></div>
                    <div style={{ textAlign: "center" }}><div style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>{caseSessions.length}</div><div style={{ fontSize: 11, color: "#9B9490" }}>Sessions</div></div>
                    <div style={{ textAlign: "center" }}><div style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>{caseDocuments.length}</div><div style={{ fontSize: 11, color: "#9B9490" }}>Documents</div></div>
                    <div style={{ textAlign: "center" }}><div style={{ fontSize: 17, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: parseFloat(activeCase.outstanding) > 0 ? "#C8873A" : "#1B2B4B" }}>${parseFloat(activeCase.outstanding || 0).toLocaleString()}</div><div style={{ fontSize: 11, color: "#9B9490" }}>Outstanding</div></div>
                  </div>
                </div>

                <div className="r-case-tabs" style={{ display: "flex", gap: 2, borderBottom: "1px solid #E2DDD5", marginBottom: 20, overflowX: "auto" }}>
                  {["overview", "tasks", "signatures", "calendar", "parenting", "documents", "billing", "comms", "intake", "access", "agreements", "emails", "issues", ...(isWellnessOn(activeCase) ? ["wellness"] : [])].map(t => (
                    <button key={t} onClick={() => setCaseTab(t)} style={{ padding: "8px 16px", fontSize: 13, fontWeight: 600, textTransform: "capitalize", borderBottom: caseTab === t ? "2px solid #2D7D72" : "2px solid transparent", color: caseTab === t ? "#2D7D72" : "#9B9490" }}>{t}</button>
                  ))}
                </div>

                {caseTab === "overview" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {/* Card on File */}
                  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Payment on File</div>
                    {caseCardInfo?.has_card ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 40, height: 28, background: "#1B2B4B", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontSize: 10, color: "#fff", fontWeight: 700 }}>{caseCardInfo.card_brand?.toUpperCase()?.slice(0,2) || "CC"}</span>
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700 }}>•••• •••• •••• {caseCardInfo.card_last4}</div>
                          <div style={{ fontSize: 11, color: "#2D7D72", fontWeight: 700 }}>✓ Card on file</div>
                        </div>
                        <button onClick={async () => {
                          if (!window.confirm("Remove card on file?")) return;
                          await api.post("stripe.php", { action: "remove_card", case_id: activeCase.id });
                          setCaseCardInfo(null);
                        }} style={{ marginLeft: "auto", padding: "5px 12px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 11, color: "#C0392B", fontWeight: 600 }}>Remove</button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "#9B9490" }}>No card on file — client must complete payment step in intake.</div>
                    )}
                  </div>
                  {/* Contact Info */}
                  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em" }}>Contact Information</div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {contactsSaved && <span style={{ fontSize: 11, color: "#2D7D72", fontWeight: 700 }}>✓ Saved</span>}
                        <button onClick={async () => {
                          await api.post("case_contacts.php?action=save", { case_id: activeCase.id, ...contacts });
                          setContactsSaved(true);
                          setTimeout(() => setContactsSaved(false), 3000);
                        }} style={{ padding: "6px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save</button>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                      {[["p1", activeCase.client1_name], ["p2", activeCase.client2_name]].map(([p, name]) => {
                        const c = (k) => contacts[`${p}_${k}`] || "";
                        const sc = (k, v) => setContacts(prev => ({ ...prev, [`${p}_${k}`]: v }));
                        const iS = { border: "1px solid #E2DDD5", borderRadius: 7, padding: "7px 10px", fontSize: 13, outline: "none", width: "100%", background: "#fff" };
                        const lS = { fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 3 };
                        return (
                          <div key={p}>
                            <div style={{ fontWeight: 800, fontSize: 14, color: "#1B2B4B", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #F0EDE8" }}>{name}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {/* Primary phone */}
                              <div>
                                <div style={lS}>Primary Phone</div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <input value={c("phone")} onChange={e => sc("phone", e.target.value)} placeholder="(312) 555-0000" style={{ ...iS, flex: 1 }} />
                                  <select value={c("phone_type")} onChange={e => sc("phone_type", e.target.value)} style={{ ...iS, width: 100 }}>
                                    <option value="">Type</option>
                                    <option value="Mobile">Mobile</option>
                                    <option value="Home">Home</option>
                                    <option value="Work">Work</option>
                                  </select>
                                </div>
                                {c("phone_type") === "Mobile" && (
                                  <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, fontSize: 12, color: "#4A5568" }}>
                                    <input type="checkbox" checked={!!contacts[`${p}_phone_ok_text`]} onChange={e => sc("phone_ok_text", e.target.checked ? 1 : 0)} />
                                    OK to text
                                  </label>
                                )}
                              </div>
                              {/* Secondary phone */}
                              <div>
                                <div style={lS}>Secondary Phone</div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <input value={c("phone2")} onChange={e => sc("phone2", e.target.value)} placeholder="Optional" style={{ ...iS, flex: 1 }} />
                                  <select value={c("phone2_type")} onChange={e => sc("phone2_type", e.target.value)} style={{ ...iS, width: 100 }}>
                                    <option value="">Type</option>
                                    <option value="Mobile">Mobile</option>
                                    <option value="Home">Home</option>
                                    <option value="Work">Work</option>
                                  </select>
                                </div>
                                {c("phone2_type") === "Mobile" && (
                                  <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, fontSize: 12, color: "#4A5568" }}>
                                    <input type="checkbox" checked={!!contacts[`${p}_phone2_ok_text`]} onChange={e => sc("phone2_ok_text", e.target.checked ? 1 : 0)} />
                                    OK to text
                                  </label>
                                )}
                              </div>
                              {/* Emails */}
                              <div>
                                <div style={lS}>Primary Email</div>
                                <input value={c("email")} onChange={e => sc("email", e.target.value)} placeholder="email@example.com" style={iS} />
                              </div>
                              <div>
                                <div style={lS}>Secondary Email</div>
                                <input value={c("email2")} onChange={e => sc("email2", e.target.value)} placeholder="Optional" style={iS} />
                              </div>
                              {/* Address */}
                              <div>
                                <div style={lS}>Address</div>
                                <input value={c("address")} onChange={e => sc("address", e.target.value)} placeholder="Street address" style={{ ...iS, marginBottom: 6 }} />
                                <input value={c("address2")} onChange={e => sc("address2", e.target.value)} placeholder="Apt, unit, etc." style={{ ...iS, marginBottom: 6 }} />
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 6 }}>
                                  <input value={c("city")} onChange={e => sc("city", e.target.value)} placeholder="City" style={iS} />
                                  <input value={c("state")} onChange={e => sc("state", e.target.value)} placeholder="State" style={iS} />
                                  <input value={c("zip")} onChange={e => sc("zip", e.target.value)} placeholder="ZIP" style={iS} />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                    <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em" }}>Session Notes</div>
                        {caseSessions.filter(s => s.type === 'Note').length > 0 && (
                          <button onClick={() => setNotesExpanded(!notesExpanded)} style={{ fontSize: 12, color: "#2D7D72", fontWeight: 600 }}>
                            {notesExpanded ? "Show less ▲" : `Show all (${caseSessions.filter(s => s.type === 'Note').length}) ▼`}
                          </button>
                        )}
                      </div>
                      <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a session note…" style={{ width: "100%", minHeight: 90, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} />
                      <button onClick={saveNote} style={{ marginTop: 8, padding: "7px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save Note</button>
                      {caseSessions.filter(s => s.type === 'Note').sort((a, b) => parseInt(b.id) - parseInt(a.id)).slice(0, notesExpanded ? undefined : 1).map(s => (<div key={s.id} style={{ borderTop: "1px solid #E2DDD5", paddingTop: 12, marginTop: 12 }}><div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>{s.session_date}</div><div style={{ fontSize: 13, color: "#4A5568", lineHeight: 1.5 }}>{s.notes}</div></div>))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Upcoming Sessions</div>
                        {caseSessions.filter(s => s.status === "Upcoming").length === 0 ? <div style={{ fontSize: 13, color: "#9B9490" }}>No upcoming sessions.</div> : caseSessions.filter(s => s.status === "Upcoming").map(s => (
                          <div key={s.id} style={{ padding: "10px 0", borderBottom: "1px solid #E2DDD5", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{s.topic}</div>
                              <div style={{ fontSize: 11, color: "#9B9490" }}>{s.session_date} · {formatTime(s.session_time)} · {s.type}</div>
                              {s.notes && <div style={{ fontSize: 11, color: "#2D7D72", marginTop: 2 }}>📎 Has notes</div>}
                            </div>
                            <div style={{ display: "flex", gap: 10, flexShrink: 0, marginLeft: 8 }}>
                              <button onClick={() => setEditingSession({ ...s, duration: parseInt(s.duration) || 60 })} style={{ fontSize: 11, color: "#2D7D72", fontWeight: 700, whiteSpace: "nowrap" }}>Edit</button>
                              <button onClick={() => deleteSession(s.id)} style={{ fontSize: 11, color: "#C0392B", fontWeight: 700, whiteSpace: "nowrap" }}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Client Access</div>
                        {invites.length === 0 ? <div style={{ fontSize: 13, color: "#9B9490" }}>No clients invited. <button onClick={() => setShowInvite(true)} style={{ color: "#2D7D72", fontWeight: 700, fontSize: 13 }}>Invite now →</button></div> : invites.map(inv => (<div key={inv.id} style={{ padding: "8px 0", borderBottom: "1px solid #E2DDD5", display: "flex", justifyContent: "space-between" }}><div style={{ fontSize: 13 }}>{inv.email} <span style={{ color: "#9B9490", fontSize: 11 }}>({inv.case_role})</span></div><Badge label={inv.status} color={inv.status === "Accepted" ? { bg: "#EAF4EA", text: "#2E7D32" } : { bg: "#FDF3E7", text: "#C8873A" }} /></div>))}
                      </div>
                    </div>
                  </div>
                  </div>
                )}

                {caseTab === "tasks" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Client Tasks</div>
                        <div style={{ fontSize: 13, color: "#9B9490" }}>
                          {caseTasks.filter(t => t.status === "Pending").length} pending · {caseTasks.filter(t => t.status === "Complete").length} complete
                          {caseTasks.filter(t => t.notify_mediator == 1).length > 0 && (
                            <span style={{ marginLeft: 8, background: "#C0392B", color: "#fff", borderRadius: 99, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                              {caseTasks.filter(t => t.notify_mediator == 1).length} new completion{caseTasks.filter(t => t.notify_mediator == 1).length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {caseTasks.filter(t => t.notify_mediator == 1).length > 0 && (
                          <button onClick={() => clearTaskNotifications(activeCase.id)} style={{ padding: "8px 16px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Clear Notifications</button>
                        )}
                        <button onClick={() => setShowNewTask(true)} style={{ padding: "8px 18px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>+ New Task</button>
                      </div>
                    </div>
                    <Msg msg={taskMsg} />
                    {caseTasks.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No tasks yet. Add one to get started.</div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {caseTasks.map(task => {
                        const isComplete = task.status === "Complete";
                        const priorityColor = task.priority === "High" ? { bg: "#FDEEEC", text: "#C0392B" } : task.priority === "Medium" ? { bg: "#FDF3E7", text: "#C8873A" } : { bg: "#EAF4EA", text: "#2E7D32" };
                        const isEditing = editingTask?.id === task.id;
                        const isNew = task.notify_mediator == 1;
                        return (
                          <div key={task.id} style={{ background: "#fff", border: `1px solid ${isNew ? "#C0392B" : isComplete ? "#E2DDD5" : "#E2DDD5"}`, borderLeft: `4px solid ${isNew ? "#C0392B" : isComplete ? "#2E7D32" : task.priority === "High" ? "#C0392B" : task.priority === "Medium" ? "#C8873A" : "#2D7D72"}`, borderRadius: 10, padding: "18px 20px", opacity: isComplete ? 0.8 : 1 }}>
                            {isEditing ? (
                              <div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                                  <Input label="Title" value={editingTask.title} onChange={v => setEditingTask({...editingTask, title: v})} full />
                                  <Input label="Due Date" value={editingTask.due_date || ""} onChange={v => setEditingTask({...editingTask, due_date: v})} type="date" />
                                  <Select label="Priority" value={editingTask.priority} onChange={v => setEditingTask({...editingTask, priority: v})} options={["High","Medium","Low"]} />
                                  <Select label="Assigned To" value={editingTask.assigned_to} onChange={v => setEditingTask({...editingTask, assigned_to: v})} options={[{value:"both",label:"Both Clients"},{value:"Client 1",label:"Client 1"},{value:"Client 2",label:"Client 2"}]} />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Notes</div>
                                  <textarea value={editingTask.notes || ""} onChange={e => setEditingTask({...editingTask, notes: e.target.value})} style={{ width: "100%", minHeight: 70, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} />
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button onClick={updateTask} style={{ padding: "7px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save</button>
                                  <button onClick={() => setEditingTask(null)} style={{ padding: "7px 16px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: task.notes ? 10 : 0 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                                      <div style={{ fontSize: 15, fontWeight: 700, textDecoration: isComplete ? "line-through" : "none", color: isComplete ? "#9B9490" : "#1B2B4B" }}>{task.title}</div>
                                      {isNew && <span style={{ background: "#C0392B", color: "#fff", borderRadius: 99, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>NEW ✓</span>}
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                                      <Badge label={task.priority} color={priorityColor} />
                                      <Badge label={task.assigned_to === "both" ? "Both Clients" : task.assigned_to} color={{ bg: "#EEF0F8", text: "#1B2B4B" }} />
                                      {task.due_date && <span style={{ fontSize: 11, color: new Date(task.due_date) < new Date() && !isComplete ? "#C0392B" : "#9B9490", fontWeight: 600 }}>📅 Due {task.due_date}</span>}
                                      {isComplete && <span style={{ fontSize: 11, color: "#2E7D32", fontWeight: 600 }}>✓ Completed by {task.completed_by}</span>}
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 12 }}>
                                    {!isComplete && <button onClick={() => completeTask(task.id)} style={{ padding: "6px 14px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Mark Complete</button>}
                                    {isComplete && <button onClick={() => uncompleteTask(task.id)} style={{ padding: "6px 14px", border: "1px solid #C8873A", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#C8873A" }}>Unmark</button>}
                                    <button onClick={() => setEditingTask({...task})} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                                    <button onClick={() => deleteTask(task.id)} style={{ padding: "6px 14px", border: "1px solid #C0392B", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                                  </div>
                                </div>
                                {task.notes && <div style={{ fontSize: 13, color: "#4A5568", background: "#F7F5F0", borderRadius: 7, padding: "10px 14px", marginBottom: 10, lineHeight: 1.6 }}>{task.notes}</div>}
                                {task.files && task.files.length > 0 && (
                                  <div style={{ marginTop: 10 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 6 }}>ATTACHED FILES</div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                      {task.files.map(f => (
                                        <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "#F7F5F0", borderRadius: 7, padding: "8px 12px" }}>
                                          <span>📎</span>
                                          <a href={"https://ourmediationcase.com" + f.file_path} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#2D7D72", fontWeight: 600, textDecoration: "none", flex: 1 }}>{f.file_name}</a>
                                          <button onClick={() => api.delete(`tasks.php?file_id=${f.id}`).then(() => loadCaseExtras(activeCase.id))} style={{ fontSize: 11, color: "#C0392B", fontWeight: 600 }}>Remove</button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div style={{ marginTop: 10 }}>
                                  <label style={{ fontSize: 12, color: "#2D7D72", fontWeight: 600, cursor: "pointer" }}>
                                    📎 Attach file
                                    <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) uploadTaskFile(e.target.files[0], task.id); e.target.value=""; }} />
                                  </label>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {caseTab === "signatures" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Signature Documents</div>
                        <div style={{ fontSize: 13, color: "#9B9490" }}>
                          {caseSignatureDocs.filter(d => d.status === "Executed").length} executed · {caseSignatureDocs.filter(d => d.status !== "Executed").length} pending
                        </div>
                      </div>
                      <button onClick={() => setShowNewSignatureDoc(true)} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ Upload Document</button>
                    </div>
                    <Msg msg={sigDocMsg} />
                    {caseSignatureDocs.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No signature documents yet.</div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {caseSignatureDocs.map(doc => {
                        const sigs = doc.signatures || [];
                        const isExecuted = doc.status === "Executed";
                        const signers = [];
                        if (doc.require_client1) signers.push("Client 1");
                        if (doc.require_client2) signers.push("Client 2");
                        if (doc.require_mediator) signers.push("Mediator");
                        return (
                          <div key={doc.id} style={{ background: "#fff", border: `1px solid ${isExecuted ? "#A8D5A2" : "#E2DDD5"}`, borderLeft: `4px solid ${isExecuted ? "#2E7D32" : "#C8873A"}`, borderRadius: 12, padding: "20px 24px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                                  <span style={{ fontSize: 20 }}>📝</span>
                                  <div style={{ fontWeight: 700, fontSize: 15 }}>{doc.title}</div>
                                  <Badge label={isExecuted ? "✓ Executed" : doc.status === "Partially Signed" ? "Partially Signed" : "Pending"} color={isExecuted ? { bg: "#EAF4EA", text: "#2E7D32" } : doc.status === "Partially Signed" ? { bg: "#FDF3E7", text: "#C8873A" } : { bg: "#F0F0F0", text: "#555" }} />
                                </div>
                                <div style={{ fontSize: 12, color: "#9B9490" }}>{doc.document_type} · Uploaded {doc.created_at?.split(" ")[0]} by {doc.created_by_name}</div>
                              </div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <a href={"https://ourmediationcase.com" + doc.file_path} target="_blank" rel="noreferrer" style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>View PDF</a>
                                {!isExecuted && <button onClick={() => setSigningDoc(doc)} style={{ padding: "6px 14px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Sign</button>}
                                {!isExecuted && sigs.length > 0 && <button onClick={() => markExecuted(doc.id)} style={{ padding: "6px 14px", background: "#1B2B4B", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Mark Executed</button>}
                                {isExecuted && <button onClick={() => reopenSigDoc(doc.id)} style={{ padding: "6px 14px", border: "1px solid #C8873A", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C8873A" }}>Reopen</button>}
                                <button onClick={() => deleteSignatureDoc(doc.id)} style={{ padding: "6px 14px", border: "1px solid #C0392B", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                              </div>
                            </div>
                            <div style={{ background: "#F7F5F0", borderRadius: 10, padding: "14px 18px" }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Signature Status</div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {signers.map(role => {
                                  const sig = sigs.find(s => s.signer_role === role || (role === "Mediator" && (s.signer_role === "mediator" || s.signer_role === "admin")));
                                  return (
                                    <div key={role} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#fff", borderRadius: 8, border: "1px solid #E2DDD5" }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ fontSize: 18 }}>{sig ? "✅" : "⏳"}</span>
                                        <div>
                                          <div style={{ fontSize: 13, fontWeight: 600 }}>{role}</div>
                                          {sig && <div style={{ fontSize: 11, color: "#9B9490" }}>Signed by {sig.signer_name} · {sig.signed_at?.split(" ")[0]}</div>}
                                          {!sig && <div style={{ fontSize: 11, color: "#9B9490" }}>Awaiting signature</div>}
                                        </div>
                                      </div>
                                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        {sig && sig.signature_type === "draw" && (
                                          <img src={sig.signature_data} alt="signature" style={{ height: 36, maxWidth: 120, border: "1px solid #E2DDD5", borderRadius: 4, background: "#fff" }} />
                                        )}
                                        {sig && sig.signature_type === "type" && (
                                          <span style={{ fontFamily: "cursive", fontSize: 18, color: "#1B2B4B" }}>{sig.signature_data}</span>
                                        )}
                                        {sig && <button onClick={() => deleteSignature(sig.id, doc.id)} style={{ fontSize: 11, color: "#C0392B", fontWeight: 600 }}>Remove</button>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {caseTab === "calendar" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>Case Calendar</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => setShowSchedulingLink(true)} style={{ padding: "8px 16px", border: "1px solid #1B2B4B", color: "#1B2B4B", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>🔗 Scheduling Link</button>
                        <button onClick={() => setShowNewSession(true)} style={{ padding: "8px 16px", border: "1px solid #2D7D72", color: "#2D7D72", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>+ Session</button>
                        <button onClick={() => { setEditingEvent(null); setSelectedCalendarDate(null); setShowEventModal(true); }} style={{ padding: "8px 16px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>+ Event</button>
                      </div>
                    </div>
                    <CalendarView
                      events={[...caseCalendarEvents, ...caseSessions.map(sessionToCalEvent)]}
                      month={calendarMonth}
                      onMonthChange={async (m) => {
                        setCalendarMonth(m);
                        const evs = await api.get(`calendar.php?case_id=${activeCase.id}&month=${m}`);
                        setCaseCalendarEvents(Array.isArray(evs) ? evs : []);
                      }}
                      onDayClick={d => { setSelectedCalendarDate(d); setEditingEvent(null); setShowEventModal(true); }}
                      onEventClick={ev => { if (ev._source === "session") setEditingSession(ev._session); else { setEditingEvent(ev); setShowEventModal(true); } }}
                      view={calendarView}
                      onViewChange={setCalendarView}
                    />
                    {showEventModal && (
                      <EventModal
                        event={editingEvent}
                        selectedDate={selectedCalendarDate}
                        caseId={activeCase.id}
                        cases={null}
                        user={user}
                        onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
                        onSaved={() => { setShowEventModal(false); setEditingEvent(null); loadCaseExtras(activeCase.id); loadCalendar(calendarMonth); }}
                      />
                    )}
                    {showSchedulingLink && (
                      <SchedulingLinkModal caseData={activeCase} onClose={() => setShowSchedulingLink(false)} />
                    )}
                  </div>
                )}

                {caseTab === "parenting" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>Parenting Plan</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => setShowPPEditor(true)} style={{ padding: "7px 14px", border: "1px solid #2D7D72", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#2D7D72" }}>⚙ Template Editor</button>
                        <button onClick={() => { setPPBuilderCase(activeCase); setShowPPBuilder(true); }} style={{ padding: "7px 14px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>📝 Open Builder</button>
                        <button onClick={() => window.open(`${API_BASE}/pp_export.php?case_id=${activeCase.id}&format=pdf&auth_token=${getToken()}`, "_blank")} style={{ padding: "7px 14px", border: "1px solid #C8873A", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#C8873A" }}>🖨 Export PDF</button>
                        <button onClick={() => window.open(`${API_BASE}/pp_export.php?case_id=${activeCase.id}&format=docx&auth_token=${getToken()}`, "_blank")} style={{ padding: "7px 14px", border: "1px solid #4A5568", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#4A5568" }}>📄 Export Word</button>
                      </div>
                    </div>
                    <div style={{ background: "#E6F4F2", border: "1px solid #2D7D72", borderRadius: 10, padding: "16px 20px", fontSize: 13, color: "#2D7D72", lineHeight: 1.6 }}>
                      Click <strong>Open Builder</strong> to fill out the parenting plan for this case. When complete, use the export buttons to generate a PDF or Word document.
                    </div>
                    {showPPEditor && <PPTemplateEditor onClose={() => setShowPPEditor(false)} />}
                    {showPPBuilder && ppBuilderCase && <PPBuilder caseData={ppBuilderCase} user={user} onClose={() => { setShowPPBuilder(false); setPPBuilderCase(null); }} />}
                  </div>
                )}

                {caseTab === "documents" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Case Documents</div>
                        <div style={{ fontSize: 13, color: "#9B9490" }}>PDF, Word, Excel, JPG, PNG · Max 10MB</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F7F5F0", border: "1px solid #E2DDD5", borderRadius: 8, padding: "7px 14px" }}>
                          <input type="checkbox" id="visibleToggle" checked={docVisibleToClient} onChange={e => setDocVisibleToClient(e.target.checked)} style={{ cursor: "pointer", width: 14, height: 14 }} />
                          <label htmlFor="visibleToggle" style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", cursor: "pointer", whiteSpace: "nowrap" }}>
                            {docVisibleToClient ? "👁 Visible to client" : "🔒 Internal only"}
                          </label>
                        </div>
                        <label style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                          + Upload Document
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) uploadDocument(e.target.files[0], docVisibleToClient ? 1 : 0); e.target.value = ""; }} />
                        </label>
                      </div>
                    </div>
                    {docUploadMsg && <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, fontSize: 13, background: docUploadMsg.type === "success" ? "#EAF4EA" : "#FDEEEC", color: docUploadMsg.type === "success" ? "#2E7D32" : "#C0392B" }}>{docUploadMsg.text}</div>}
                    {caseDocuments.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No documents uploaded yet.</div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {caseDocuments.map(d => {
                        const fileIcon = d.file_type?.includes("pdf") ? "📕" : d.file_type?.includes("word") || d.file_type?.includes("document") ? "📝" : d.file_type?.includes("sheet") || d.file_type?.includes("excel") ? "📊" : d.file_type?.includes("image") ? "🖼" : "📄";
                        const fileSizeKB = d.file_size ? (d.file_size / 1024).toFixed(0) + " KB" : "";
                        return (
                          <div key={d.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                              <span style={{ fontSize: 24 }}>{fileIcon}</span>
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{d.file_name}</div>
                                <div style={{ fontSize: 11, color: "#9B9490" }}>
                                  Uploaded by {d.uploader_name} · {d.created_at?.split(" ")[0]} {fileSizeKB && <span>· {fileSizeKB}</span>}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                              <Badge label={d.uploader_role === "client" ? "Client" : d.uploader_role === "admin" ? "Admin" : "Mediator"} color={d.uploader_role === "client" ? { bg: "#E6F4F2", text: "#2D7D72" } : { bg: "#EEF0F8", text: "#1B2B4B" }} />
                              <Badge label={d.visible_to_client == 1 ? "👁 Visible to client" : "🔒 Internal"} color={d.visible_to_client == 1 ? { bg: "#EAF4EA", text: "#2E7D32" } : { bg: "#F0F0F0", text: "#555" }} />
                              <a href={"https://ourmediationcase.com" + d.file_path} target="_blank" rel="noreferrer" style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>Download</a>
                              <button onClick={() => deleteDocument(d.id)} style={{ padding: "6px 14px", border: "1px solid #C0392B", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Repository docs assigned to this case */}
                    {caseRepoDocs.length > 0 && (
                      <div style={{ marginTop: 28 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>
                          🗂 From Document Repository ({caseRepoDocs.length})
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {caseRepoDocs.map(d => {
                            const typeColors = { "Template": { bg: "#E6F4F2", text: "#2D7D72" }, "Reference": { bg: "#EEF0F8", text: "#1B2B4B" }, "Legal Form": { bg: "#FDEEEC", text: "#C0392B" }, "Handout": { bg: "#FDF3E7", text: "#C8873A" }, "Additional Legal Forms": { bg: "#F0F0F0", text: "#6B5B95" } };
                            const tc = typeColors[d.doc_type] || { bg: "#F0F0F0", text: "#666" };
                            return (
                              <div key={d.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderLeft: "4px solid #2D7D72", borderRadius: 10, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                                  <span style={{ fontSize: 24 }}>📎</span>
                                  <div>
                                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                                      <span style={{ fontSize: 14, fontWeight: 600 }}>{d.title}</span>
                                      <span style={{ fontSize: 10, fontWeight: 700, background: tc.bg, color: tc.text, padding: "2px 7px", borderRadius: 20 }}>{d.doc_type}</span>
                                    </div>
                                    {d.description && <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 2 }}>{d.description}</div>}
                                    <div style={{ fontSize: 11, color: "#9B9490" }}>Assigned by {d.assigned_by_name} · {d.assigned_at?.split(" ")[0]}</div>
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <button onClick={async () => {
                                    const newVal = d.visible_to_client == 1 ? 0 : 1;
                                    await api.post("doc_repository.php?action=toggle_visibility", { assignment_id: d.assignment_id, case_id: activeCase.id, visible_to_client: newVal });
                                    setCaseRepoDocs(prev => prev.map(x => x.assignment_id === d.assignment_id ? {...x, visible_to_client: newVal} : x));
                                  }} style={{ padding: "6px 12px", border: `1px solid ${d.visible_to_client == 1 ? "#2E7D32" : "#E2DDD5"}`, borderRadius: 7, fontSize: 11, fontWeight: 600, color: d.visible_to_client == 1 ? "#2E7D32" : "#9B9490", background: d.visible_to_client == 1 ? "#EAF4EA" : "#fff" }}>
                                    {d.visible_to_client == 1 ? "👁 Visible to Client" : "🔒 Internal Only"}
                                  </button>
                                  {d.file_path && <a href={"https://ourmediationcase.com" + d.file_path} target="_blank" rel="noreferrer" style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>Download</a>}
                                  {d.link_url && <a href={d.link_url} target="_blank" rel="noreferrer" style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>🔗 Open Link</a>}
                                  <button onClick={async () => {
                                    if (!window.confirm("Remove this document from the case?")) return;
                                    await api.post("doc_repository.php?action=unassign", { repo_doc_id: d.id, case_id: activeCase.id });
                                    setCaseRepoDocs(prev => prev.filter(x => x.assignment_id !== d.assignment_id));
                                  }} style={{ padding: "6px 14px", border: "1px solid #C0392B", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Remove</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {caseTab === "billing" && (
                  <div>
                    {/* Stats row */}
                    <div className="r-grid4" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }}>
                      <Stat label="Total Billed" value={`$${parseFloat(activeCase.total_billed||0).toLocaleString()}`} />
                      <Stat label="Outstanding" value={`$${parseFloat(activeCase.outstanding||0).toLocaleString()}`} accent={parseFloat(activeCase.outstanding)>0?"#C8873A":undefined} />
                      <Stat label="Time Logged" value={(() => { const total = timeEntries.reduce((a,e)=>a+(parseInt(e.hours)||0)*60+(parseInt(e.minutes)||0),0); return `${Math.floor(total/60)}h ${total%60}m`; })()} />
                      <Stat label="Unbilled Entries" value={timeEntries.filter(e=>!e.invoice_id).length} accent={timeEntries.filter(e=>!e.invoice_id).length>0?"#C8873A":undefined} />
                      <Stat label="Time Value" sub="billed + unbilled" value={`$${timeEntries.reduce((a,e)=>a+parseFloat(e.amount||0),0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} />
                    </div>

                    {/* Fee structure banner */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"#EEF0F8", border:"1px solid #C5CAE9", borderRadius:10, padding:"12px 18px", marginBottom:20 }}>
                      <div style={{ display:"flex", gap:16, alignItems:"center" }}>
                        <span style={{ fontSize:12, fontWeight:700, color:"#1B2B4B" }}>Fee Structure:</span>
                        <Badge label={activeCase.fee_structure==="flat_fee"?"Flat Fee":activeCase.fee_structure==="hourly"?"Hourly":activeCase.fee_structure==="flat_plus_hourly"?"Flat + Hourly":"Custom"} color={{bg:"#1B2B4B",text:"#fff"}} />
                        {(activeCase.fee_structure==="flat_fee"||activeCase.fee_structure==="flat_plus_hourly") && activeCase.flat_fee_amount>0 && <span style={{fontSize:12,color:"#4A5568"}}>Flat: <strong>${parseFloat(activeCase.flat_fee_amount).toLocaleString()}</strong></span>}
                        {(activeCase.fee_structure==="hourly"||activeCase.fee_structure==="flat_plus_hourly") && activeCase.hourly_rate>0 && <span style={{fontSize:12,color:"#4A5568"}}>Rate: <strong>${parseFloat(activeCase.hourly_rate)}/hr</strong></span>}
                      </div>
                      <button onClick={()=>setShowFeeSettings(true)} style={{fontSize:12,color:"#2D7D72",fontWeight:700}}>⚙ Edit Fee Settings</button>
                    </div>

                    {/* Sub-tabs */}
                    <div style={{ display:"flex", gap:4, marginBottom:20, background:"#F7F5F0", borderRadius:10, padding:4 }}>
                      {[["time","⏱ Time Log"],["invoices","🧾 Invoices"]].map(([id,label])=>(
                        <button key={id} onClick={()=>setBillingTab(id)}
                          style={{flex:1,padding:"8px 0",borderRadius:8,fontSize:13,fontWeight:700,background:billingTab===id?"#fff":"transparent",color:billingTab===id?"#1B2B4B":"#9B9490",boxShadow:billingTab===id?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {/* TIME LOG TAB */}
                    {billingTab==="time" && (
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                          <div style={{fontSize:13,color:"#9B9490"}}>{timeEntries.filter(e=>!e.invoice_id).length} unbilled · {timeEntries.filter(e=>e.invoice_id).length} invoiced</div>
                          <button onClick={()=>{setEditingTimeEntry(null);setShowTimeEntry(true);}} style={{padding:"8px 18px",background:"#2D7D72",color:"#fff",borderRadius:8,fontSize:13,fontWeight:700}}>+ Log Time</button>
                        </div>
                        {timeEntries.length===0 && <div style={{fontSize:13,color:"#9B9490",fontStyle:"italic"}}>No time entries yet.</div>}
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {timeEntries.map(e=>{
                            const hrs = parseInt(e.hours) || Math.floor((parseInt(e.duration_minutes)||0)/60);
                            const mins = parseInt(e.minutes) || ((parseInt(e.duration_minutes)||0)%60);
                            const dur=hrs>0?(mins>0?`${hrs}h ${mins}m`:`${hrs}h`):(mins>0?`${mins}m`:"0m");
                            return (
                              <div key={e.id} style={{background:"#fff",border:`1px solid ${e.billable?"#E2DDD5":"#C5CAE9"}`,borderLeft:`4px solid ${e.billable?"#2D7D72":"#9B9490"}`,borderRadius:10,padding:"12px 18px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                <div style={{flex:1}}>
                                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4,flexWrap:"wrap"}}>
                                    <span style={{fontSize:11,fontWeight:700,background:e.billable?"#E6F4F2":"#F0F0F0",color:e.billable?"#2D7D72":"#555",padding:"2px 8px",borderRadius:20}}>{e.billable?"Billable":"Non-Billable"}</span>
                                    <span style={{fontSize:11,fontWeight:700,background:"#EEF0F8",color:"#1B2B4B",padding:"2px 8px",borderRadius:20}}>{e.category}</span>
                                    {e.invoice_id && <span style={{fontSize:11,fontWeight:700,background:"#EAF4EA",color:"#2E7D32",padding:"2px 8px",borderRadius:20}}>✓ Invoiced</span>}
                                  </div>
                                  <div style={{fontSize:14,fontWeight:600,color:"#1B2B4B",marginBottom:2}}>{e.description}</div>
                                  <div style={{fontSize:11,color:"#9B9490"}}>{e.entry_date} · {dur} · {e.staff_name} {e.amount>0?`· $${parseFloat(e.amount).toFixed(2)}${!e.billable?" (no charge)":""}`:"· No rate set"}</div>
                                </div>
                                {!e.invoice_id && (
                                  <div style={{display:"flex",gap:6,marginLeft:12}}>
                                    <button onClick={()=>{setEditingTimeEntry(e);setShowTimeEntry(true);}} style={{padding:"5px 12px",border:"1px solid #E2DDD5",borderRadius:7,fontSize:11,fontWeight:600,color:"#4A5568"}}>Edit</button>
                                    <button onClick={async()=>{if(!window.confirm("Delete this entry?"))return;await api.post("billing.php?action=delete_entry",{id:e.id});loadBilling(activeCase.id);}} style={{padding:"5px 12px",border:"1px solid #C0392B",borderRadius:7,fontSize:11,fontWeight:600,color:"#C0392B"}}>Delete</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {timeEntries.filter(e=>!e.invoice_id).length>0 && (
                          <button onClick={()=>setShowGenerateInvoice(true)} style={{marginTop:20,width:"100%",padding:"12px 0",background:"#1B2B4B",color:"#fff",borderRadius:10,fontSize:13,fontWeight:700}}>
                            🧾 Generate Invoice from {timeEntries.filter(e=>!e.invoice_id).length} Unbilled {timeEntries.filter(e=>!e.invoice_id).length===1?"Entry":"Entries"}
                          </button>
                        )}
                      </div>
                    )}

                    {/* INVOICES TAB */}
                    {billingTab==="invoices" && (
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                          <div style={{fontSize:13,color:"#9B9490"}}>{billingInvoices.length} invoice{billingInvoices.length!==1?"s":""}</div>
                          <button onClick={()=>{setEditingInvoiceId(null);setNewInvoice({invoice_number:"",invoice_date:"",amount:"",description:"",status:"Outstanding"});setShowNewInvoice(true);}} style={{padding:"8px 18px",background:"#2D7D72",color:"#fff",borderRadius:8,fontSize:13,fontWeight:700}}>+ Custom Invoice</button>
                        </div>
                        {billingInvoices.length===0 && <div style={{fontSize:13,color:"#9B9490",fontStyle:"italic"}}>No invoices yet. Log time and generate an invoice, or create a custom one.</div>}
                        <div style={{display:"flex",flexDirection:"column",gap:10}}>
                          {billingInvoices.map(inv=>(
                            <div key={inv.id} style={{background:"#fff",border:"1px solid #E2DDD5",borderRadius:12,padding:"16px 20px"}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                                <div>
                                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                                    <span style={{fontWeight:800,fontSize:15,color:"#1B2B4B"}}>{inv.invoice_number}</span>
                                    <Badge label={inv.status} color={inv.status==="Paid"?{bg:"#EAF4EA",text:"#2E7D32"}:inv.status==="Sent"?{bg:"#EEF0F8",text:"#1B2B4B"}:{bg:"#FDF3E7",text:"#C8873A"}} />
                                  </div>
                                  <div style={{fontSize:13,color:"#9B9490"}}>{inv.invoice_date} · {inv.fee_structure}</div>
                                </div>
                                <div style={{textAlign:"right"}}>
                                  <div style={{fontSize:20,fontWeight:800,color:"#1B2B4B"}}>${parseFloat(inv.amount||0).toLocaleString()}</div>
                                  {inv.subtotal!==inv.amount && <div style={{fontSize:11,color:"#9B9490"}}>Subtotal: ${parseFloat(inv.subtotal||0).toFixed(2)}</div>}
                                </div>
                              </div>
                              {/* Line items preview */}
                              {inv.line_items && inv.line_items.length>0 ? (
                                <div style={{background:"#F7F5F0",borderRadius:8,padding:"10px 14px",marginBottom:10}}>
                                  {inv.line_items.slice(0,3).map((li,i)=>(
                                    <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#4A5568",padding:"3px 0",borderBottom:i<Math.min(inv.line_items.length,3)-1?"1px solid #E2DDD5":"none"}}>
                                      <span style={{flex:1,marginRight:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{li.billable?"":<span style={{color:"#9B9490",marginRight:4}}>[No Charge]</span>}{li.description}</span>
                                      <span style={{fontWeight:600,color:li.billable?"#1B2B4B":"#9B9490"}}>{li.billable?`$${parseFloat(li.amount).toFixed(2)}`:"—"}</span>
                                    </div>
                                  ))}
                                  {inv.line_items.length>3 && <div style={{fontSize:11,color:"#9B9490",marginTop:4}}>+{inv.line_items.length-3} more items…</div>}
                                </div>
                              ) : inv.description && (
                                <div style={{background:"#F7F5F0",borderRadius:8,padding:"10px 14px",marginBottom:10,fontSize:12,color:"#4A5568"}}>{inv.description}</div>
                              )}
                              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                <button onClick={()=>setShowInvoiceDetail(inv)} style={{padding:"6px 14px",border:"1px solid #E2DDD5",borderRadius:7,fontSize:12,fontWeight:600,color:"#4A5568"}}>👁 View Full</button>
                                {(!inv.line_items || inv.line_items.length===0) && <button onClick={()=>openEditInvoice(inv)} style={{padding:"6px 14px",border:"1px solid #E2DDD5",borderRadius:7,fontSize:12,fontWeight:600,color:"#4A5568"}}>✏️ Edit</button>}
                                {inv.status==="Outstanding" && <button onClick={()=>{api.post("billing.php?action=update_invoice_status",{id:inv.id,status:"Sent"}).then(()=>loadBilling(activeCase.id));}} style={{padding:"6px 14px",border:"1px solid #1B2B4B",borderRadius:7,fontSize:12,fontWeight:600,color:"#1B2B4B"}}>📤 Mark Sent</button>}
                                {inv.status!=="Paid" && <button onClick={()=>sendPaymentLink(inv)} disabled={sendingPaymentLink===inv.id} style={{padding:"6px 14px",background:"#1B2B4B",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700,opacity:sendingPaymentLink===inv.id?0.6:1}}>{sendingPaymentLink===inv.id?"Sending…":"💳 Send Payment Link"}</button>}
                                {inv.stripe_payment_link && inv.status!=="Paid" && <a href={inv.stripe_payment_link} target="_blank" rel="noreferrer" style={{padding:"6px 14px",border:"1px solid #2D7D72",borderRadius:7,fontSize:12,fontWeight:600,color:"#2D7D72",textDecoration:"none"}}>🔗 View Link</a>}
                                {inv.status!=="Paid" && <button onClick={()=>{api.post("billing.php?action=update_invoice_status",{id:inv.id,status:"Paid"}).then(()=>{loadBilling(activeCase.id);loadData();});}} style={{padding:"6px 14px",background:"#2D7D72",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700}}>✓ Mark Paid</button>}
                                <button onClick={async()=>{if(!window.confirm("Delete this invoice? Time entries will be freed."))return;await api.post("billing.php?action=delete_invoice",{id:inv.id});loadBilling(activeCase.id);loadData();}} style={{padding:"6px 14px",border:"1px solid #C0392B",borderRadius:7,fontSize:12,fontWeight:600,color:"#C0392B"}}>Delete</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* TIME ENTRY MODAL */}
                    {showTimeEntry && <TimeEntryModal entry={editingTimeEntry} caseData={activeCase} user={user} staff={staff} onClose={()=>{setShowTimeEntry(false);setEditingTimeEntry(null);}} onSaved={()=>{loadBilling(activeCase.id);setShowTimeEntry(false);setEditingTimeEntry(null);}} />}

                    {/* GENERATE INVOICE MODAL */}
                    {showGenerateInvoice && <GenerateInvoiceModal caseData={activeCase} entries={timeEntries.filter(e=>!e.invoice_id)} user={user} onClose={()=>setShowGenerateInvoice(false)} onGenerated={(inv)=>{loadBilling(activeCase.id);loadData();setShowGenerateInvoice(false);setBillingTab("invoices");if(inv)setShowInvoiceDetail(inv);}} />}

                    {/* INVOICE DETAIL MODAL */}
                    {showInvoiceDetail && <InvoiceDetailModal invoice={showInvoiceDetail} caseData={activeCase} onClose={()=>setShowInvoiceDetail(null)} onResend={()=>{}} />}

                    {/* FEE SETTINGS MODAL */}
                    {showFeeSettings && <FeeSettingsModal caseData={activeCase} onClose={()=>setShowFeeSettings(false)} onSaved={(updated)=>{Object.assign(activeCase,updated);setShowFeeSettings(false);loadData();}} />}
                  </div>
                )}

                {caseTab === "comms" && (
                  <div>
                    <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                      <button onClick={() => { setNewComm({ comm_date: new Date().toISOString().slice(0, 10), from_name: "", to_name: "", type: "Email", subject: "", summary: "" }); setCommMsg(null); setShowNewComm(true); }} style={{ padding: "8px 18px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>+ Log Communication</button>
                      {selectedCaseCommIds.length > 0 && <button onClick={() => bulkDeleteComms(selectedCaseCommIds, () => setSelectedCaseCommIds([]))} style={{ padding: "8px 18px", border: "1px solid #C0392B", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#C0392B" }}>Delete Selected ({selectedCaseCommIds.length})</button>}
                    </div>
                    {caseComms.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No communications yet.</div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {caseComms.map(c => (<div key={c.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px", display: "flex", gap: 12 }}><input type="checkbox" checked={selectedCaseCommIds.includes(c.id)} onChange={() => setSelectedCaseCommIds(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])} style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} /><div style={{ flex: 1 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><div style={{ fontWeight: 700 }}>{c.subject}</div><div style={{ display: "flex", gap: 8, alignItems: "center" }}><Badge label={c.type} color={{ bg: "#E6F4F2", text: "#2D7D72" }} /><button onClick={() => deleteComm(c.id)} style={{ padding: "4px 10px", border: "1px solid #FDEEEC", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#C0392B" }}>Delete</button></div></div><div style={{ fontSize: 12, color: "#9B9490", marginBottom: 6 }}>{c.from_name} → {c.to_name} · {c.comm_date}</div><div style={{ fontSize: 13, color: "#4A5568" }}>{c.summary}</div></div></div>))}
                    </div>
                  </div>
                )}

                {caseTab === "intake" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {caseIntake.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No intake forms yet.</div>}
                    {caseIntake.map(f => (<div key={f.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontWeight: 700 }}>{f.client} ({f.role})</div><div style={{ fontSize: 12, color: "#9B9490" }}>Submitted {f.submitted_date}</div></div><Badge label={f.status} color={f.status === "Complete" ? { bg: "#E6F4F2", text: "#2D7D72" } : { bg: "#FDF3E7", text: "#C8873A" }} /></div>))}
                  </div>
                )}

                {caseTab === "access" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Client Portal Access</div><div style={{ fontSize: 13, color: "#9B9490" }}>Manage who can access this case.</div></div>
                      <button onClick={() => setShowInvite(true)} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>✉ Invite Client</button>
                    </div>
                    <Msg msg={inviteMsg} />
                    {invites.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No invites sent yet.</div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {invites.map(inv => (<div key={inv.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontWeight: 700, fontSize: 14 }}>{inv.email}</div><div style={{ fontSize: 12, color: "#9B9490" }}>{inv.case_role} · Invited {inv.created_at?.split(" ")[0]}</div></div><div style={{ display: "flex", gap: 10, alignItems: "center" }}><Badge label={inv.status} color={inv.status === "Accepted" ? { bg: "#EAF4EA", text: "#2E7D32" } : { bg: "#FDF3E7", text: "#C8873A" }} />{inv.status === "Pending" && <button onClick={() => resendInvite(inv)} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Resend</button>}</div></div>))}
                    </div>
                  </div>
                )}

                {caseTab === "agreements" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Case Agreements</div><div style={{ fontSize: 13, color: "#9B9490" }}>Assign agreements clients must sign during intake.</div></div>
                    </div>
                    <Msg msg={agreementMsg} />
                    {/* Assigned agreements */}
                    {caseAgreements.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                        {caseAgreements.map(ag => {
                          return (
                            <div key={ag.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 20px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{ag.title}</div>
                                  {ag.service_type && <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 8 }}>{ag.service_type}</div>}
                                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                    <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                      <input type="checkbox" checked={!!ag.required_at_intake} onChange={async (e) => {
                                        await api.post("agreements.php", { action: "assign_to_case", case_id: activeCase.id, agreement_id: ag.id, required_at_intake: e.target.checked ? 1 : 0, include_in_welcome: ag.include_in_welcome });
                                        loadCaseExtras(activeCase.id);
                                      }} /> Required at intake
                                    </label>
                                    <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                      <input type="checkbox" checked={!!ag.include_in_welcome} onChange={async (e) => {
                                        await api.post("agreements.php", { action: "assign_to_case", case_id: activeCase.id, agreement_id: ag.id, required_at_intake: ag.required_at_intake, include_in_welcome: e.target.checked ? 1 : 0 });
                                        loadCaseExtras(activeCase.id);
                                      }} /> Include in welcome email
                                    </label>
                                  </div>
                                  {ag.signers_list?.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 4 }}>SIGNED BY</div>
                                      {ag.signers_list.map((s, i) => (
                                        <div key={i} style={{ fontSize: 12, color: "#2D7D72" }}>✓ {s.name} · {s.signature_type === 'drawn' ? 'drawn' : 'typed'} · {s.signed_at?.split(' ')[0]}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button onClick={async () => {
                                  await api.post("agreements.php", { action: "remove_from_case", case_id: activeCase.id, agreement_id: ag.id });
                                  await loadCaseExtras(activeCase.id);
                                }} style={{ padding: "5px 12px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 11, color: "#C0392B", fontWeight: 600 }}>Remove</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Add agreement from library */}
                    <div style={{ background: "#F7F5F0", borderRadius: 10, padding: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#9B9490", marginBottom: 10 }}>ADD FROM LIBRARY</div>
                      {agreements.filter(a => !caseAgreements.find(ca => ca.id === a.id)).length === 0
                        ? <div style={{ fontSize: 13, color: "#9B9490" }}>All agreements assigned. <button onClick={() => setTab("agreements")} style={{ color: "#2D7D72", fontWeight: 700, fontSize: 13 }}>Manage library →</button></div>
                        : agreements.filter(a => !caseAgreements.find(ca => ca.id === a.id)).map(a => (
                          <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #E2DDD5" }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</div>
                              {a.service_type && <div style={{ fontSize: 11, color: "#9B9490" }}>{a.service_type}</div>}
                            </div>
                            <button onClick={async () => {
                              const res = await api.post("agreements.php", { action: "assign_to_case", case_id: activeCase.id, agreement_id: a.id, required_at_intake: 1, include_in_welcome: 1 });
                              if (res.success) {
                                await loadCaseExtras(activeCase.id);
                                setAgreementMsg({ type: "success", text: `${a.title} added.` });
                                setTimeout(() => setAgreementMsg(null), 3000);
                              }
                            }} style={{ padding: "6px 14px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Add</button>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {caseTab === "emails" && (
                  <ClientOnboardingPanel
                    activeCase={activeCase}
                    invites={invites}
                    caseRepoDocs={caseRepoDocs}
                    onOpenInvite={() => setShowInvite(true)}
                    onResendInvite={resendInvite}
                  />
                )}

                {caseTab === "issues" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Negotiation Issues</div><div style={{ fontSize: 13, color: "#9B9490" }}>Track progress on each issue being negotiated.</div></div>
                      <button onClick={() => { setEditingIssue(null); setShowNewIssue(true); }} style={{ padding: "8px 18px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>+ Add Issue</button>
                    </div>

                    {/* Issue status summary */}
                    {caseIssues.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
                        {[["Unresolved","#C0392B","#FDEEEC"],["In Progress","#C8873A","#FDF3E7"],["Tentatively Agreed","#2D7D72","#E6F4F2"],["Final","#1B2B4B","#EEF0F8"]].map(([status, color, bg]) => (
                          <div key={status} style={{ background: bg, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color }}>{caseIssues.filter(i => i.status === status).length}</div>
                            <div style={{ fontSize: 11, color, fontWeight: 600 }}>{status}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {caseIssues.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No issues added yet.</div>}

                    {/* Issues grouped by category */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {caseIssues.map(issue => {
                        const statusColors = { "Unresolved": { bg: "#FDEEEC", text: "#C0392B" }, "In Progress": { bg: "#FDF3E7", text: "#C8873A" }, "Tentatively Agreed": { bg: "#E6F4F2", text: "#2D7D72" }, "Final": { bg: "#EEF0F8", text: "#1B2B4B" } };
                        const sc = statusColors[issue.status] || statusColors["Unresolved"];
                        return editingIssue?.id === issue.id ? (
                          <div key={issue.id} style={{ background: "#fff", border: "1px solid #2D7D72", borderRadius: 10, padding: 16 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 200px", gap: 10, marginBottom: 10 }}>
                              <Input label="Issue" value={editingIssue.title} onChange={v => setEditingIssue(e => ({ ...e, title: v }))} />
                              <Select label="Category" value={editingIssue.category} onChange={v => setEditingIssue(e => ({ ...e, category: v }))} options={["Property","Finances","Parenting","Support","Debt","Other"]} />
                              <Select label="Status" value={editingIssue.status} onChange={v => setEditingIssue(e => ({ ...e, status: v }))} options={["Unresolved","In Progress","Tentatively Agreed","Final"]} />
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 4 }}>Notes</div>
                              <textarea value={editingIssue.notes || ""} onChange={e => setEditingIssue(i => ({ ...i, notes: e.target.value }))} style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, resize: "vertical", minHeight: 60, boxSizing: "border-box" }} />
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={async () => {
                                await api.post("checkins.php", { action: "save_issue", ...editingIssue });
                                setEditingIssue(null);
                                loadCaseExtras(activeCase.id);
                              }} style={{ padding: "6px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save</button>
                              <button onClick={() => setEditingIssue(null)} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div key={issue.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>{issue.title}</div>
                                {issue.category && <span style={{ fontSize: 11, color: "#9B9490", background: "#F7F5F0", padding: "2px 8px", borderRadius: 10 }}>{issue.category}</span>}
                                <span style={{ fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.text, padding: "2px 10px", borderRadius: 20 }}>{issue.status}</span>
                              </div>
                              {issue.notes && <div style={{ fontSize: 12, color: "#4A5568", lineHeight: 1.5 }}>{issue.notes}</div>}
                            </div>
                            <div style={{ display: "flex", gap: 6, marginLeft: 12 }}>
                              <button onClick={() => { setShowNewIssue(false); setEditingIssue({ ...issue }); }} style={{ padding: "5px 12px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                              <button onClick={async () => { await api.post("checkins.php", { action: "delete_issue", id: issue.id }); loadCaseExtras(activeCase.id); }} style={{ padding: "5px 12px", border: "1px solid #FDEEEC", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#C0392B" }}>✕</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* New issue form */}
                    {showNewIssue && (
                      <div style={{ background: "#fff", border: "1px solid #2D7D72", borderRadius: 10, padding: 16, marginTop: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>New Issue</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 200px", gap: 10, marginBottom: 10 }}>
                          <Input label="Issue Title" value={editingIssue?.title || ""} onChange={v => setEditingIssue(e => ({ ...(e||{}), title: v, case_id: activeCase.id }))} />
                          <Select label="Category" value={editingIssue?.category || "General"} onChange={v => setEditingIssue(e => ({ ...(e||{}), category: v }))} options={["Property","Finances","Parenting","Support","Debt","Other","General"]} />
                          <Select label="Status" value={editingIssue?.status || "Unresolved"} onChange={v => setEditingIssue(e => ({ ...(e||{}), status: v }))} options={["Unresolved","In Progress","Tentatively Agreed","Final"]} />
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={async () => {
                            if (!editingIssue?.title) return;
                            await api.post("checkins.php", { action: "save_issue", case_id: activeCase.id, ...editingIssue });
                            setShowNewIssue(false); setEditingIssue(null);
                            loadCaseExtras(activeCase.id);
                          }} style={{ padding: "6px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Add Issue</button>
                          <button onClick={() => { setShowNewIssue(false); setEditingIssue(null); }} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Client Notes */}
                    {caseClientNotes.length > 0 && (
                      <div style={{ marginTop: 28 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Client Notes</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {caseClientNotes.map(note => (
                            <div key={note.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 18px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>{note.user_name}</span>
                                <span style={{ fontSize: 11, color: "#9B9490" }}>{new Date(note.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                              </div>
                              <div style={{ fontSize: 13, color: "#2D3748", lineHeight: 1.6 }}>{note.note}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {caseTab === "wellness" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Wellness Tracker</div>
                      <div style={{ fontSize: 13, color: "#9B9490" }}>Daily check-ins from both parties. Flagged entries are highlighted.</div>
                    </div>

                    {caseCheckins.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No check-ins yet. Clients complete these from their portal.</div>}

                    {/* Group by date */}
                    {(() => {
                      const byDate = {};
                      caseCheckins.forEach(c => { if (!byDate[c.checkin_date]) byDate[c.checkin_date] = []; byDate[c.checkin_date].push(c); });
                      const ScoreBar = ({ label, value, max = 10, color = "#2D7D72" }) => value ? (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#4A5568", marginBottom: 3 }}>
                            <span>{label}</span><span style={{ fontWeight: 700 }}>{value}/{max}</span>
                          </div>
                          <div style={{ height: 6, background: "#E2DDD5", borderRadius: 3 }}>
                            <div style={{ height: 6, borderRadius: 3, background: color, width: `${(value/max)*100}%` }} />
                          </div>
                        </div>
                      ) : null;

                      return Object.entries(byDate).map(([date, checkins]) => (
                        <div key={date} style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                            {new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: `repeat(${checkins.length}, 1fr)`, gap: 12 }}>
                            {checkins.map(c => (
                              <div key={c.id} style={{ background: c.flag_for_mediator ? "#FDF3E7" : "#fff", border: `1px solid ${c.flag_for_mediator ? "#C8873A" : "#E2DDD5"}`, borderRadius: 10, padding: "14px 16px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                  <span style={{ fontWeight: 700, fontSize: 13 }}>{c.user_name}</span>
                                  {c.flag_for_mediator ? <span style={{ fontSize: 11, color: "#C8873A", fontWeight: 700 }}>⚑ Flagged</span> : null}
                                </div>
                                <ScoreBar label="Overall Mood" value={c.overall_mood} color="#2D7D72" />
                                <ScoreBar label="Stress Level" value={c.stress_level} color={c.stress_level >= 7 ? "#C0392B" : "#C8873A"} />
                                <ScoreBar label="Co-parenting" value={c.coparenting_score} color="#1B2B4B" />
                                <ScoreBar label="Readiness" value={c.readiness_score} color="#2D7D72" />
                                <ScoreBar label="Sleep Quality" value={c.sleep_quality} max={5} color="#9B9490" />
                                {c.notes && <div style={{ fontSize: 12, color: "#4A5568", marginTop: 8, fontStyle: "italic", lineHeight: 1.5 }}>"{c.notes}"</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}

              </div>
            )}

            {!selectedCase && tab === "dashboard" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ marginBottom: 28 }}>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, marginBottom: 4 }}>Good morning, {user.name?.split(" ")[0]}</h1>
                  <div style={{ color: "#9B9490", fontSize: 14 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
                </div>
                <div className="r-grid4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
                  <Stat label="Active Cases" value={cases.filter(c => c.status === "Active").length} />
                  <Stat label="Outstanding" value={`$${totalOutstanding.toLocaleString()}`} accent="#C8873A" />
                  <Stat label="Upcoming Sessions" value={upcomingSessions.length} />
                  <Stat label={isAdmin ? "Staff Members" : "Resolved Cases"} value={isAdmin ? staff.length : cases.filter(c => c.status === "Resolved").length} accent={isAdmin ? "#2D7D72" : "#2E7D32"} />
                </div>
                <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>Upcoming Sessions</div>
                    {upcomingSessions.length === 0 ? <div style={{ fontSize: 13, color: "#9B9490" }}>No upcoming sessions.</div> : upcomingSessions.slice(0, 5).map(s => { const c = cases.find(c => c.id === parseInt(s.case_id)); return (<div key={s.id} style={{ padding: "11px 0", borderBottom: "1px solid #E2DDD5", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}><div><div style={{ fontSize: 13, fontWeight: 600 }}>{c?.client1_name} & {c?.client2_name}</div><div style={{ fontSize: 11, color: "#9B9490" }}>{s.session_date} · {formatTime(s.session_time)} · {s.topic}</div></div><button onClick={() => deleteSession(s.id)} style={{ fontSize: 11, color: "#C0392B", fontWeight: 700, whiteSpace: "nowrap", marginLeft: 8 }}>Delete</button></div>); })}
                  </div>
                  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 22 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>Case Progress</div>
                    {cases.length === 0 ? <div style={{ fontSize: 13, color: "#9B9490" }}>No cases yet. <button onClick={() => { setTab("cases"); setShowNewCase(true); }} style={{ color: "#2D7D72", fontWeight: 700, fontSize: 13 }}>Create your first →</button></div> : cases.map(c => (<div key={c.id} style={{ marginBottom: 16, cursor: "pointer" }} onClick={() => { setSelectedCase(c.id); setCaseTab("overview"); }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{c.client1_name} & {c.client2_name}</div><span style={{ fontSize: 12, fontWeight: 700, color: "#2D7D72" }}>{c.progress}%</span></div><ProgressBar value={parseInt(c.progress || 0)} /></div>))}
                  </div>
                </div>
              </div>
            )}

            {/* ── CASES ── */}
            {!selectedCase && tab === "cases" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div className="r-case-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12 }}>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800 }}>
                    {isAdmin ? "All Cases" : "My Cases"}
                  </h1>
                  <div className="r-case-actions" style={{ display: "flex", gap: 8 }}>
                    {isAdmin && bulkSelectMode ? (
                      <>
                        <button onClick={bulkDeleteCases} disabled={bulkSelectedCases.length === 0}
                          style={{ padding: "9px 16px", background: bulkSelectedCases.length > 0 ? "#C0392B" : "#ccc", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>
                          🗑 Delete ({bulkSelectedCases.length})
                        </button>
                        <button onClick={() => { setBulkSelectMode(false); setBulkSelectedCases([]); }}
                          style={{ padding: "9px 16px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        {isAdmin && <button onClick={() => setBulkSelectMode(true)} style={{ padding: "9px 16px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Select</button>}
                        <button onClick={() => setShowNewCase(true)} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ New Case</button>
                      </>
                    )}
                  </div>
                </div>
                {cases.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No cases yet.</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {cases.map(c => (
                    <div key={c.id} style={{ background: "#fff", border: `1px solid ${bulkSelectMode && bulkSelectedCases.includes(c.id) ? "#C0392B" : "#E2DDD5"}`, borderRadius: 12, padding: "20px 24px", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)"} onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => {
                        if (bulkSelectMode) {
                          setBulkSelectedCases(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]);
                        } else {
                          setSelectedCase(c.id); setCaseTab("overview");
                        }
                      }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                          {bulkSelectMode && (
                            <input type="checkbox" checked={bulkSelectedCases.includes(c.id)} onChange={() => {}} style={{ width: 16, height: 16, flexShrink: 0 }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                              <div style={{ fontWeight: 800, fontSize: 16, fontFamily: "'Playfair Display', serif" }}>{c.client1_name} & {c.client2_name}</div>
                              <Badge label={c.status} color={statusColor(c.status)} />
                              {isAdmin && c.mediator_name && <span style={{ fontSize: 11, color: "#9B9490", background: "#F7F5F0", padding: "2px 8px", borderRadius: 10 }}>👤 {c.mediator_name}</span>}
                            </div>
                            <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 10 }}>{c.case_number} · Opened {c.opened}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}><div style={{ flex: 1, maxWidth: 220 }}><ProgressBar value={parseInt(c.progress || 0)} /></div><span style={{ fontSize: 11, color: "#2D7D72", fontWeight: 700 }}>{c.stage}</span></div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          <div style={{ textAlign: "right" }}><div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: parseFloat(c.outstanding) > 0 ? "#C8873A" : "#1B2B4B" }}>${parseFloat(c.outstanding || 0).toLocaleString()}</div><div style={{ fontSize: 11, color: "#9B9490" }}>Outstanding</div></div>
                          {isAdmin && !bulkSelectMode && <button onClick={e => { e.stopPropagation(); deleteCase(c.id); }} style={{ padding: "7px 14px", border: "1px solid #C0392B", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#C0392B", background: "#fff", flexShrink: 0 }}>Delete</button>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SESSIONS ── */}
            {!selectedCase && tab === "calendar" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div className="r-case-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12 }}>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800 }}>Calendar</h1>
                  <div className="r-case-actions">
                    <button onClick={() => { setEditingEvent(null); setSelectedCalendarDate(null); setShowEventModal(true); }} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ New Event</button>
                  </div>
                </div>
                <div className="r-cal-wrap"><CalendarView
                  events={[...calendarEvents, ...sessions.filter(s => (s.session_date||"").slice(0,7) === calendarMonth).map(sessionToCalEvent)]}
                  month={calendarMonth}
                  onMonthChange={m => setCalendarMonth(m)}
                  onDayClick={d => { setSelectedCalendarDate(d); setEditingEvent(null); setShowEventModal(true); }}
                  onEventClick={ev => { if (ev._source === "session") setEditingSession(ev._session); else { setEditingEvent(ev); setShowEventModal(true); } }}
                  view={calendarView}
                  onViewChange={setCalendarView}
                /></div>
                {showEventModal && (
                  <EventModal
                    event={editingEvent}
                    selectedDate={selectedCalendarDate}
                    caseId={null}
                    cases={cases}
                    user={user}
                    onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
                    onSaved={() => { setShowEventModal(false); setEditingEvent(null); loadCalendar(calendarMonth); }}
                  />
                )}
              </div>
            )}

            {/* ── DOCUMENTS ── */}
            {!selectedCase && tab === "documents" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>All Documents</h1>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 24 }}>All documents across your cases. Open a case to upload new documents.</div>
                {(() => {
                  const allDocs = cases.flatMap(c => {
                    return [];
                  });
                  return (
                    <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 24, fontSize: 13, color: "#9B9490", textAlign: "center" }}>
                      Open any case and click the <strong>Documents</strong> tab to upload and manage files for that case.
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── BILLING (admin only) ── */}
            {!selectedCase && tab === "billing" && isAdmin && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 24 }}>Billing</h1>
                <div className="r-grid4" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                  <Stat label="Total Revenue" value={`$${invoices.filter(i => i.status === "Paid").reduce((a, b) => a + parseFloat(b.amount || 0), 0).toLocaleString()}`} />
                  <Stat label="Outstanding" value={`$${totalOutstanding.toLocaleString()}`} accent="#C8873A" />
                  <Stat label="Invoices" value={invoices.length} sub={`${invoices.filter(i => i.status === "Paid").length} paid`} />
                </div>
                {invoices.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No invoices yet.</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {invoices.map(inv => { const c = cases.find(c => c.id === parseInt(inv.case_id)); return (<div key={inv.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontWeight: 700 }}>{inv.invoice_number} · ${parseFloat(inv.amount || 0).toLocaleString()}</div><div style={{ fontSize: 12, color: "#9B9490" }}>{c?.client1_name} & {c?.client2_name} · {inv.description} · {inv.invoice_date}</div></div><div style={{ display: "flex", gap: 10, alignItems: "center" }}><Badge label={inv.status} color={inv.status === "Paid" ? { bg: "#EAF4EA", text: "#2E7D32" } : { bg: "#FDF3E7", text: "#C8873A" }} />{inv.status === "Outstanding" && <button onClick={() => markInvoicePaid(inv)} style={{ padding: "6px 14px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Mark Paid</button>}</div></div>); })}
                </div>
              </div>
            )}

            {/* ── COMMUNICATIONS ── */}
            {!selectedCase && tab === "communications" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Communications</h1>
                  <div style={{ display: "flex", gap: 10 }}>
                    {selectedGlobalCommIds.length > 0 && <button onClick={() => bulkDeleteComms(selectedGlobalCommIds, () => setSelectedGlobalCommIds([]))} style={{ padding: "9px 18px", border: "1px solid #C0392B", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#C0392B" }}>Delete Selected ({selectedGlobalCommIds.length})</button>}
                    <button onClick={() => setShowEmailTemplateEditor(true)} style={{ padding: "9px 18px", border: "1px solid #2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>⚙ Email Templates</button>
                  </div>
                </div>
                {showEmailTemplateEditor && <EmailTemplateEditor onClose={() => setShowEmailTemplateEditor(false)} />}
                {comms.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No communications logged yet.</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {comms.map(c => { const cas = cases.find(ca => ca.id === parseInt(c.case_id)); if (!cas) return null; return (<div key={c.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "16px 22px", display: "flex", gap: 12 }}><input type="checkbox" checked={selectedGlobalCommIds.includes(c.id)} onChange={() => setSelectedGlobalCommIds(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])} style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} /><div style={{ flex: 1 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><div style={{ fontWeight: 700 }}>{c.subject}</div><div style={{ display: "flex", gap: 8, alignItems: "center" }}><Badge label={c.type} color={{ bg: "#E6F4F2", text: "#2D7D72" }} /><button onClick={() => deleteComm(c.id)} style={{ padding: "4px 10px", border: "1px solid #FDEEEC", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#C0392B" }}>Delete</button></div></div><div style={{ fontSize: 12, color: "#9B9490", marginBottom: 6 }}>{cas?.client1_name} & {cas?.client2_name} · {c.from_name} → {c.to_name} · {c.comm_date}</div><div style={{ fontSize: 13, color: "#4A5568" }}>{c.summary}</div></div></div>); })}
                </div>
              </div>
            )}

            {/* ── INTAKE FORMS ── */}
            {!selectedCase && tab === "intake" && !showTemplateEditor && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Intake Forms</h1>
                    <div style={{ fontSize: 14, color: "#9B9490", marginTop: 4 }}>Build and manage intake templates for each service type.</div>
                  </div>
                  <button onClick={() => {
                    setEditingTemplate({ title: "", service_type: "", description: "", fields: [] });
                    setShowTemplateEditor(true);
                  }} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ New Template</button>
                </div>
                <Msg msg={templateMsg} />
                {intakeTemplates.length === 0 && (
                  <div style={{ fontSize: 13, color: "#9B9490", textAlign: "center", padding: "40px 0" }}>
                    No templates yet. Visit <code>https://ourmediationcase.com/api/seed_intake.php</code> to load the default template, then refresh.
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {intakeTemplates.map(t => (
                    <div key={t.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 24px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{t.title}</div>
                          {t.service_type && <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 4 }}>{t.service_type}</div>}
                          {t.description && <div style={{ fontSize: 12, color: "#4A5568" }}>{t.description}</div>}
                          <div style={{ fontSize: 11, color: "#9B9490", marginTop: 6 }}>Updated {new Date(t.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={async () => {
                            const full = await api.get(`intake_forms.php?action=get&id=${t.id}`);
                            setEditingTemplate(full);
                            setShowTemplateEditor(true);
                          }} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                          <button onClick={async () => {
                            const res = await api.post("intake_forms.php", { action: "duplicate_template", id: t.id });
                            if (res.success) {
                              const full = await api.get(`intake_forms.php?action=get&id=${res.id}`);
                              setEditingTemplate(full);
                              setShowTemplateEditor(true);
                              const r = await api.get("intake_forms.php?action=list"); setIntakeTemplates(Array.isArray(r) ? r : []);
                            }
                          }} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Duplicate</button>
                          <button onClick={async () => {
                            if (!window.confirm("Delete this template?")) return;
                            await api.post("intake_forms.php", { action: "delete_template", id: t.id });
                            const r = await api.get("intake_forms.php?action=list"); setIntakeTemplates(Array.isArray(r) ? r : []);
                          }} style={{ padding: "6px 14px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── INTAKE TEMPLATE EDITOR ── */}
            {!selectedCase && tab === "intake" && showTemplateEditor && editingTemplate && (() => {
              const fields = editingTemplate.fields || [];
              const personalFields = fields.filter(f => f.section === "personal");
              const sharedFields = fields.filter(f => f.section === "shared");

              const FIELD_TYPES = [
                { value: "text", label: "Text" },
                { value: "textarea", label: "Text Area" },
                { value: "date", label: "Date" },
                { value: "phone", label: "Phone" },
                { value: "email", label: "Email" },
                { value: "number", label: "Number" },
                { value: "radio", label: "Yes/No or Options" },
                { value: "select", label: "Dropdown" },
                { value: "checkbox", label: "Checkbox" },
                { value: "children", label: "Children List" },
              ];

              const addField = (section) => {
                const newField = { id: `f${Date.now()}`, section, type: "text", label: "New Field", required: false, placeholder: "" };
                setEditingTemplate(t => ({ ...t, fields: [...(t.fields || []), newField] }));
              };

              const updateField = (id, updates) => {
                setEditingTemplate(t => ({ ...t, fields: t.fields.map(f => f.id === id ? { ...f, ...updates } : f) }));
              };

              const removeField = (id) => {
                setEditingTemplate(t => ({ ...t, fields: t.fields.filter(f => f.id !== id) }));
              };

              const moveField = (fromIdx, toIdx, allFields) => {
                const newFields = [...allFields];
                const [moved] = newFields.splice(fromIdx, 1);
                newFields.splice(toIdx, 0, moved);
                // Rebuild full fields array preserving other section
                const otherFields = (editingTemplate.fields || []).filter(f => f.section !== moved.section);
                setEditingTemplate(t => ({ ...t, fields: [...otherFields, ...newFields] }));
              };

              const renderFieldEditor = (field, idx, sectionFields) => (
                <div key={field.id}
                  style={{ background: dragOverIdx === idx ? "#E6F4F2" : "#F7F5F0", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 16px", marginBottom: 8 }}
                  onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                  onDrop={() => {
                    if (draggedFieldIdx !== null && draggedFieldIdx !== idx) moveField(draggedFieldIdx, idx, sectionFields);
                    setDraggedFieldIdx(null); setDragOverIdx(null);
                  }}
                  onDragEnd={() => { setDraggedFieldIdx(null); setDragOverIdx(null); }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div
                      draggable
                      onDragStart={() => setDraggedFieldIdx(idx)}
                      style={{ color: "#C8C8C8", fontSize: 18, cursor: "grab", paddingTop: 2, flexShrink: 0, userSelect: "none" }}>⠿</div>
                    <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 140px 80px", gap: 8 }}>
                      <input value={field.label} onChange={e => updateField(field.id, { label: e.target.value })}
                        style={{ padding: "7px 10px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 13, fontWeight: 600 }} placeholder="Field label" />
                      <select value={field.type} onChange={e => updateField(field.id, { type: e.target.value })}
                        style={{ padding: "7px 10px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 12 }}>
                        {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                      </select>
                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                        <input type="checkbox" checked={!!field.required} onChange={e => updateField(field.id, { required: e.target.checked })} />
                        Required
                      </label>
                    </div>
                    <button onClick={() => removeField(field.id)} style={{ color: "#C0392B", background: "none", border: "none", cursor: "pointer", fontSize: 16, paddingTop: 2 }}>✕</button>
                  </div>
                  {(field.type === "radio" || field.type === "select") && (
                    <div style={{ marginTop: 8, marginLeft: 28 }}>
                      <div style={{ fontSize: 11, color: "#9B9490", marginBottom: 4 }}>Options (comma separated)</div>
                      <input value={(field.options || []).join(", ")} onChange={e => updateField(field.id, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                        style={{ width: "100%", padding: "6px 10px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 12, boxSizing: "border-box" }} placeholder="e.g. Yes, No, Not Sure" />
                    </div>
                  )}
                  {/* Show If */}
                  <div style={{ marginTop: 6, marginLeft: 28, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, color: "#9B9490", whiteSpace: "nowrap" }}>Show only if</span>
                    <select value={field.showIf?.[0] || ""} onChange={e => {
                      const trigger = e.target.value;
                      updateField(field.id, { showIf: trigger ? [trigger, field.showIf?.[1] || "Yes"] : null });
                    }} style={{ padding: "4px 8px", border: "1px solid #E2DDD5", borderRadius: 5, fontSize: 11, maxWidth: 140 }}>
                      <option value="">— always show —</option>
                      {fields.filter(f2 => f2.id !== field.id && f2.section === field.section && (f2.type === "radio" || f2.type === "select")).map(f2 => (
                        <option key={f2.id} value={f2.id}>{f2.label}</option>
                      ))}
                    </select>
                    {field.showIf?.[0] && (
                      <>
                        <span style={{ fontSize: 11, color: "#9B9490" }}>is</span>
                        <select value={field.showIf?.[1] || ""} onChange={e => updateField(field.id, { showIf: [field.showIf[0], e.target.value] })}
                          style={{ padding: "4px 8px", border: "1px solid #E2DDD5", borderRadius: 5, fontSize: 11, maxWidth: 100 }}>
                          {(fields.find(f2 => f2.id === field.showIf[0])?.options || ["Yes", "No"]).map((o, i) => (
                            <option key={i} value={o}>{o}</option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                  {field.type !== "children" && (
                    <div style={{ marginTop: 6, marginLeft: 28 }}>
                      <input value={field.placeholder || ""} onChange={e => updateField(field.id, { placeholder: e.target.value })}
                        style={{ width: "100%", padding: "5px 10px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 11, color: "#9B9490", boxSizing: "border-box" }} placeholder="Placeholder text (optional)" />
                    </div>
                  )}
                </div>
              );

              return (
                <div className="r-page" style={{ padding: "32px 36px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <button onClick={() => { setShowTemplateEditor(false); setEditingTemplate(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9B9490" }}>←</button>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800 }}>{editingTemplate.id ? "Edit Template" : "New Template"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setPreviewAnswers({}); setPreviewSection("personal"); setShowTemplatePreview(true); }} style={{ padding: "9px 18px", border: "1px solid #2D7D72", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#2D7D72" }}>👁 Preview</button>
                      <button onClick={() => { setShowTemplateEditor(false); setEditingTemplate(null); }} style={{ padding: "9px 18px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                      <button onClick={async () => {
                        if (!editingTemplate.title) { setTemplateMsg({ type: "error", text: "Title is required." }); return; }
                        const res = await api.post("intake_forms.php", { action: "save_template", ...editingTemplate });
                        if (res.success) {
                          setShowTemplateEditor(false); setEditingTemplate(null);
                          const r = await api.get("intake_forms.php?action=list"); setIntakeTemplates(Array.isArray(r) ? r : []);
                          setTemplateMsg({ type: "success", text: "Template saved." });
                          setTimeout(() => setTemplateMsg(null), 3000);
                        } else setTemplateMsg({ type: "error", text: res.error || "Failed to save." });
                      }} style={{ padding: "9px 24px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Template</button>
                    </div>
                  </div>
                  <Msg msg={templateMsg} />

                  {/* Template meta */}
                  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 20, marginBottom: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <Input label="Template Name" value={editingTemplate.title} onChange={v => setEditingTemplate(t => ({ ...t, title: v }))} />
                    <Select label="Service Type" value={editingTemplate.service_type || ""} onChange={v => setEditingTemplate(t => ({ ...t, service_type: v }))} options={["", ...SERVICE_TYPES]} />
                    <div style={{ gridColumn: "1/-1" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Description (optional)</div>
                      <input value={editingTemplate.description || ""} onChange={e => setEditingTemplate(t => ({ ...t, description: e.target.value }))}
                        style={{ width: "100%", padding: "9px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} placeholder="Brief description of this intake form" />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                    {/* Personal Section */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>Personal Section</div>
                          <div style={{ fontSize: 11, color: "#9B9490" }}>Each client fills out separately</div>
                        </div>
                        <button onClick={() => addField("personal")} style={{ padding: "5px 12px", background: "#1B2B4B", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>+ Field</button>
                      </div>
                      {personalFields.length === 0 && <div style={{ fontSize: 12, color: "#9B9490", padding: "16px 0" }}>No fields yet. Click + Field to add one.</div>}
                      {personalFields.map((f, i) => renderFieldEditor(f, i, personalFields))}
                    </div>

                    {/* Shared Section */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>Shared Section</div>
                          <div style={{ fontSize: 11, color: "#9B9490" }}>First client fills out, second confirms</div>
                        </div>
                        <button onClick={() => addField("shared")} style={{ padding: "5px 12px", background: "#2D7D72", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>+ Field</button>
                      </div>
                      {sharedFields.length === 0 && <div style={{ fontSize: 12, color: "#9B9490", padding: "16px 0" }}>No fields yet. Click + Field to add one.</div>}
                      {sharedFields.map((f, i) => renderFieldEditor(f, i, sharedFields))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* INTAKE TEMPLATE PREVIEW MODAL */}
            {showTemplatePreview && editingTemplate && (() => {
              const allSteps = ["personal", "shared", "agreements", "payment"];
              const stepLabels = { personal: "Personal Info", shared: "Shared Info", agreements: "Agreements", payment: "Payment" };
              const fields = (editingTemplate.fields || []).filter(f => f.section === previewSection);

              const isVisible = (field) => {
                if (!field.showIf || !field.showIf[0]) return true;
                return previewAnswers[field.showIf[0]] === field.showIf[1];
              };

              const renderPreviewField = (field) => {
                if (!isVisible(field)) return null;
                const labelStyle = { fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6, display: "block" };
                const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, boxSizing: "border-box" };
                return (
                  <div key={field.id} style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>{field.label}{field.required && <span style={{ color: "#C0392B", marginLeft: 3 }}>*</span>}</label>
                    {field.type === "textarea" && <textarea placeholder={field.placeholder || "Client enters text here..."} style={{ ...inputStyle, minHeight: 80, resize: "none" }} />}
                    {field.type === "select" && (
                      <select style={inputStyle} onChange={e => setPreviewAnswers(a => ({ ...a, [field.id]: e.target.value }))}>
                        <option value="">{field.placeholder || "Select an option..."}</option>
                        {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
                      </select>
                    )}
                    {field.type === "radio" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {(field.options || ["Yes", "No"]).map((o, i) => (
                          <label key={i} onClick={() => setPreviewAnswers(a => ({ ...a, [field.id]: o }))}
                            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "8px 12px", border: `1px solid ${previewAnswers[field.id] === o ? "#2D7D72" : "#E2DDD5"}`, borderRadius: 7, cursor: "pointer", background: previewAnswers[field.id] === o ? "#E6F4F2" : "#fff" }}>
                            <input type="radio" readOnly checked={previewAnswers[field.id] === o} onChange={() => {}} /> {o}
                          </label>
                        ))}
                      </div>
                    )}
                    {field.type === "checkbox" && (
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" onChange={e => setPreviewAnswers(a => ({ ...a, [field.id]: e.target.checked ? "Yes" : "No" }))} /> {field.placeholder || field.label}
                      </label>
                    )}
                    {field.type === "children" && (
                      <div style={{ background: "#F7F5F0", borderRadius: 8, padding: 16, fontSize: 13, color: "#9B9490" }}>
                        Child entry form — clients add each child's name, date of birth, and current residence
                      </div>
                    )}
                    {!["textarea","select","radio","checkbox","children"].includes(field.type) && (
                      <input type={field.type === "phone" ? "tel" : field.type === "email" ? "email" : field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
                        placeholder={field.placeholder || "Client enters " + field.label.toLowerCase() + "..."}
                        style={inputStyle} />
                    )}
                  </div>
                );
              };

              return (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                  <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 700, maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {/* Header */}
                    <div style={{ padding: "16px 24px", borderBottom: "1px solid #E2DDD5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Client Preview — Full Intake Flow</div>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 800 }}>{editingTemplate.title}</div>
                      </div>
                      <button onClick={() => { setShowTemplatePreview(false); setPreviewAnswers({}); }} style={{ fontSize: 20, background: "none", border: "none", cursor: "pointer", color: "#9B9490" }}>✕</button>
                    </div>

                    {/* Step tabs */}
                    <div style={{ display: "flex", borderBottom: "1px solid #E2DDD5", overflowX: "auto" }}>
                      {allSteps.map((s, i) => (
                        <button key={s} onClick={() => { setPreviewSection(s); setPreviewAnswers({}); }}
                          style={{ padding: "10px 18px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", background: "none", border: "none", borderBottom: previewSection === s ? "2px solid #2D7D72" : "2px solid transparent", color: previewSection === s ? "#2D7D72" : "#9B9490", cursor: "pointer" }}>
                          {i + 1}. {stepLabels[s]}
                        </button>
                      ))}
                    </div>

                    {/* Step content */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px", background: "#F7F5F0" }}>
                      <div style={{ background: "#fff", borderRadius: 12, padding: "24px 28px" }}>

                        {/* Personal / Shared form fields */}
                        {(previewSection === "personal" || previewSection === "shared") && (
                          <>
                            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
                              {previewSection === "personal" ? "Personal Information" : "Case Information"}
                            </div>
                            <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 20 }}>
                              {previewSection === "personal" ? "Please complete your personal information below." : "First client fills this out — second client reviews and confirms."}
                            </div>
                            {fields.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No fields in this section.</div>}
                            {fields.map(f => renderPreviewField(f))}
                            {fields.length > 0 && (
                              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                                <div style={{ padding: "10px 28px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, opacity: 0.6 }}>Continue →</div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Agreements preview */}
                        {previewSection === "agreements" && (
                          <>
                            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Agreements</div>
                            <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 20 }}>Each assigned agreement will be shown one at a time. The client reads and signs before proceeding.</div>
                            {caseAgreements.length === 0 && agreements.length === 0 ? (
                              <div style={{ fontSize: 13, color: "#9B9490", background: "#F7F5F0", borderRadius: 8, padding: 16 }}>No agreements assigned to this case yet. Assign agreements from the case's Agreements tab.</div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {(caseAgreements.length > 0 ? caseAgreements : agreements.slice(0, 2)).map((ag, i) => (
                                  <div key={ag.id} style={{ border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div>
                                      <div style={{ fontWeight: 700, fontSize: 14 }}>{ag.title}</div>
                                      <div style={{ fontSize: 11, color: "#9B9490", marginTop: 2 }}>Client reads full text and signs (typed or drawn)</div>
                                    </div>
                                    <div style={{ fontSize: 11, color: "#C8873A", fontWeight: 700, background: "#FDF3E7", padding: "3px 10px", borderRadius: 20 }}>Requires signature</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ marginTop: 20, borderTop: "1px solid #E2DDD5", paddingTop: 16 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 8 }}>Signature area (per agreement)</div>
                              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                                <div style={{ padding: "5px 14px", background: "#1B2B4B", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>✍ Type</div>
                                <div style={{ padding: "5px 14px", border: "1px solid #E2DDD5", borderRadius: 6, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>✏ Draw</div>
                              </div>
                              <div style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "14px 16px", color: "#9B9490", fontSize: 14, fontFamily: "'Playfair Display', serif", background: "#F7F5F0" }}>Client types full legal name here…</div>
                              <div style={{ fontSize: 11, color: "#9B9490", marginTop: 8, lineHeight: 1.6 }}>By signing, the client acknowledges they have read and agree to the terms. This electronic signature is legally binding.</div>
                            </div>
                          </>
                        )}

                        {/* Payment preview */}
                        {previewSection === "payment" && (
                          <>
                            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Payment Authorization</div>
                            <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 20 }}>A payment method on file is required. Client selects their preferred option.</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                              {["💳 Credit or debit card", "🏦 Bank account (ACH)", "💵 Cash", "📝 Check"].map((opt, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", border: `1px solid ${i === 0 ? "#2D7D72" : "#E2DDD5"}`, borderRadius: 8, background: i === 0 ? "#E6F4F2" : "#fff", fontSize: 13, fontWeight: 600 }}>
                                  <input type="radio" readOnly checked={i === 0} onChange={() => {}} /> {opt}
                                  {i === 0 && <span style={{ fontSize: 11, color: "#9B9490", fontWeight: 400, marginLeft: 4 }}>2.9% processing fee applies</span>}
                                </div>
                              ))}
                            </div>
                            <div style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "14px 16px", background: "#F7F5F0", fontSize: 13, color: "#9B9490", marginBottom: 14 }}>
                              Card number field (Stripe secure element)
                            </div>
                            <div style={{ background: "#F7F5F0", borderRadius: 8, padding: "14px 16px", fontSize: 12, color: "#4A5568", lineHeight: 1.7, marginBottom: 14 }}>
                              By providing your payment information, you authorize Lakeshore Mediation to charge this payment method for mediation services as outlined in your fee agreement. Lakeshore Mediation may utilize your payment method on file for any balances, including late cancellation and no-show fees, without additional authorization as outlined in the agreement. In cases of no-show or late cancellation fees, you agree not to dispute charges as services not provided.
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                              <div style={{ padding: "10px 28px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, opacity: 0.6 }}>Save Card & Continue →</div>
                            </div>
                          </>
                        )}

                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── PARENTING PLANS ── */}
            {!selectedCase && tab === "parenting" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Parenting Plans</h1>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setShowPPEditor(true)} style={{ padding: "9px 18px", border: "1px solid #2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>⚙ Template Editor</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                  {cases.filter(c => c.status === "Active").map(c => (
                    <div key={c.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: "22px 24px", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                      onClick={() => { setPPBuilderCase(c); setShowPPBuilder(true); }}>
                      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 800, marginBottom: 6 }}>{c.client1_name} & {c.client2_name}</div>
                      <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 16 }}>{c.case_number}</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={e => { e.stopPropagation(); setPPBuilderCase(c); setShowPPBuilder(true); }} style={{ flex: 1, padding: "8px 0", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>📝 Open Builder</button>
                        <button onClick={e => { e.stopPropagation(); window.open(`${API_BASE}/pp_export.php?case_id=${c.id}&format=pdf&auth_token=${getToken()}`, "_blank"); }} style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>🖨 PDF</button>
                        <button onClick={e => { e.stopPropagation(); window.open(`${API_BASE}/pp_export.php?case_id=${c.id}&format=docx&auth_token=${getToken()}`, "_blank"); }} style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>📄 Word</button>
                      </div>
                    </div>
                  ))}
                  {cases.filter(c => c.status === "Active").length === 0 && (
                    <div style={{ fontSize: 13, color: "#9B9490" }}>No active cases. Parenting plans are available for active cases.</div>
                  )}
                </div>
                {showPPEditor && <PPTemplateEditor onClose={() => setShowPPEditor(false)} />}
                {showPPBuilder && ppBuilderCase && <PPBuilder caseData={ppBuilderCase} user={user} onClose={() => { setShowPPBuilder(false); setPPBuilderCase(null); }} />}
              </div>
            )}

            {/* ── AGREEMENTS LIBRARY ── */}
            {!selectedCase && tab === "agreements" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Agreements Library</h1>
                    <div style={{ color: "#9B9490", fontSize: 14 }}>Create and manage agreement templates for your services.</div>
                  </div>
                  <button onClick={() => { setShowAgreementEditor(true); setEditingAgreement({ title: "", body: "", service_type: "" }); }} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ New Agreement</button>
                  <button onClick={() => setShowHeaderSettings(true)} style={{ padding: "9px 16px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>⚙ Header Settings</button>
                </div>
                <Msg msg={agreementMsg} />
                {agreements.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No agreements yet. Create your first one above.</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {agreements.map(ag => (
                    <div key={ag.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 24px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>{ag.title}</div>
                          {ag.service_type && <div style={{ fontSize: 12, color: "#9B9490", marginTop: 4 }}>{ag.service_type}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 8, marginLeft: 16 }}>
                          <button onClick={() => setPreviewAgreement(ag)} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Preview</button>
                          <button onClick={() => { setEditingAgreement(ag); setShowAgreementEditor(true); }} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                          <button onClick={async () => { if (!window.confirm("Delete this agreement?")) return; await api.post("agreements.php", { action: "delete_agreement", id: ag.id }); const r = await api.get("agreements.php?action=list"); setAgreements(Array.isArray(r) ? r : []); }} style={{ padding: "6px 14px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CLIENT PREVIEW SLIDE-OVER */}
            {showClientPreview && activeCase && (
              <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex" }}>
                {/* Backdrop */}
                <div style={{ flex: 1, background: "rgba(0,0,0,0.4)" }} onClick={() => setShowClientPreview(false)} />
                {/* Panel */}
                <div style={{ width: "min(520px, 95vw)", background: "#F7F5F0", display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", boxShadow: "-8px 0 40px rgba(0,0,0,0.2)" }}>
                  {/* Header */}
                  <div style={{ background: "#1B2B4B", padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                    <div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 800, color: "#fff" }}>Our<span style={{ color: "#5BC8F5" }}>Mediation</span>Case</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>Client Portal Preview</div>
                    </div>
                    <button onClick={() => setShowClientPreview(false)} style={{ color: "rgba(255,255,255,0.6)", fontSize: 20, background: "none", border: "none", cursor: "pointer" }}>✕</button>
                  </div>

                  {/* Tab bar */}
                  <div style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid #E2DDD5", background: "#fff", flexShrink: 0 }}>
                    {["home", "tasks", "sessions", ...(isWellnessOn(activeCase) ? ["wellness"] : []), "notes", "documents"].map(t => (
                      <button key={t} onClick={() => setClientPreviewTab(t)} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap", background: "none", border: "none", borderBottom: clientPreviewTab === t ? "2px solid #2D7D72" : "2px solid transparent", color: clientPreviewTab === t ? "#2D7D72" : "#9B9490", cursor: "pointer" }}>{t}</button>
                    ))}
                  </div>

                  {/* Tab content */}
                  {(() => {
                    const previewTab = clientPreviewTab;
                    const cardStyle = { background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "18px 20px", marginBottom: 12 };
                    const labelStyle = { fontSize: 11, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 };
                    return (
                      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

                          {previewTab === "home" && (
                            <div>
                              <div style={cardStyle}>
                                <div style={labelStyle}>My Case</div>
                                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{activeCase.client1_name} & {activeCase.client2_name}</div>
                                <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 12 }}>{activeCase.case_number} · {activeCase.stage}</div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                  {[["Stage", activeCase.stage], ["Status", activeCase.status], ["Children", activeCase.children], ["Mediator", activeCase.mediator]].map(([l, v]) => (
                                    <div key={l} style={{ background: "#F7F5F0", borderRadius: 8, padding: "10px 12px" }}>
                                      <div style={{ fontSize: 10, color: "#9B9490", fontWeight: 700, textTransform: "uppercase" }}>{l}</div>
                                      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{v || "—"}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {caseIssues.length > 0 && (
                                <div style={cardStyle}>
                                  <div style={labelStyle}>Negotiation Progress</div>
                                  {caseIssues.map(issue => {
                                    const colors = { "Unresolved": "#C0392B", "In Progress": "#C8873A", "Tentatively Agreed": "#2D7D72", "Final": "#1B2B4B" };
                                    return (
                                      <div key={issue.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #F7F5F0" }}>
                                        <div style={{ fontSize: 13 }}>{issue.title}</div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: colors[issue.status] || "#9B9490" }}>{issue.status}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {previewTab === "tasks" && (
                            <div style={cardStyle}>
                              <div style={labelStyle}>Action Items</div>
                              {caseTasks.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No tasks assigned yet.</div>}
                              {caseTasks.map(t => (
                                <div key={t.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid #F7F5F0" }}>
                                  <div style={{ width: 16, height: 16, borderRadius: 4, border: "2px solid #E2DDD5", marginTop: 2, flexShrink: 0, background: t.status === "Complete" ? "#2D7D72" : "#fff" }} />
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, textDecoration: t.status === "Complete" ? "line-through" : "none", color: t.status === "Complete" ? "#9B9490" : "#1B2B4B" }}>{t.title}</div>
                                    {t.due_date && <div style={{ fontSize: 11, color: "#9B9490", marginTop: 2 }}>Due {t.due_date}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {previewTab === "sessions" && (
                            <div style={cardStyle}>
                              <div style={labelStyle}>Sessions</div>
                              {caseSessions.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No sessions scheduled yet.</div>}
                              {caseSessions.slice(0, 5).map(s => (
                                <div key={s.id} style={{ padding: "10px 0", borderBottom: "1px solid #F7F5F0" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.type} Session</div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: s.status === "Upcoming" ? "#2D7D72" : "#9B9490" }}>{s.status}</div>
                                  </div>
                                  <div style={{ fontSize: 12, color: "#9B9490", marginTop: 2 }}>{s.session_date}{s.session_time ? " at " + s.session_time : ""}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {previewTab === "wellness" && isWellnessOn(activeCase) && (
                            <div>
                              <div style={cardStyle}>
                                <div style={labelStyle}>Today's Check-in</div>
                                <div style={{ fontSize: 13, color: "#4A5568", marginBottom: 16 }}>How are you doing today?</div>
                                {[["Overall Mood", 10], ["Stress Level", 10], ["Co-parenting", 10], ["Readiness", 10], ["Sleep Quality", 5]].map(([label, max]) => (
                                  <div key={label} style={{ marginBottom: 14 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                                      <span style={{ fontWeight: 600 }}>{label}</span><span style={{ color: "#9B9490" }}>1–{max}</span>
                                    </div>
                                    <div style={{ height: 6, background: "#E2DDD5", borderRadius: 3 }}>
                                      <div style={{ height: 6, borderRadius: 3, background: "#2D7D72", width: "50%" }} />
                                    </div>
                                  </div>
                                ))}
                                <div style={{ marginTop: 16, padding: "10px 0", borderTop: "1px solid #E2DDD5" }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Notes for mediator (optional)</div>
                                  <div style={{ background: "#F7F5F0", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#9B9490" }}>Client types here…</div>
                                </div>
                              </div>
                              {caseCheckins.length > 0 && (
                                <div style={cardStyle}>
                                  <div style={labelStyle}>Recent Check-ins</div>
                                  {caseCheckins.slice(0, 3).map(c => (
                                    <div key={c.id} style={{ padding: "8px 0", borderBottom: "1px solid #F7F5F0", fontSize: 12 }}>
                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ fontWeight: 600 }}>{c.checkin_date}</span>
                                        <span style={{ color: "#2D7D72" }}>Mood: {c.overall_mood}/10</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {previewTab === "notes" && (
                            <div style={cardStyle}>
                              <div style={labelStyle}>My Notes</div>
                              <div style={{ background: "#F7F5F0", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#9B9490", marginBottom: 12 }}>Write a note between sessions…</div>
                              {caseClientNotes.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No notes yet.</div>}
                              {caseClientNotes.map(note => (
                                <div key={note.id} style={{ padding: "10px 0", borderBottom: "1px solid #F7F5F0" }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9B9490", marginBottom: 4 }}>{note.user_name} · {note.created_at?.split(" ")[0]}</div>
                                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>{note.note}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {previewTab === "documents" && (
                            <div style={cardStyle}>
                              <div style={labelStyle}>Documents</div>
                              {caseDocuments.length === 0 && <div style={{ fontSize: 13, color: "#9B9490" }}>No documents shared yet.</div>}
                              {caseDocuments.map(d => (
                                <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F7F5F0" }}>
                                  <div style={{ fontSize: 20 }}>📄</div>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{d.file_name}</div>
                                    <div style={{ fontSize: 11, color: "#9B9490" }}>{d.uploaded_at?.split(" ")[0]}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                        </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* AGREEMENT HEADER SETTINGS MODAL */}
            {showHeaderSettings && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 620, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>Agreement Header</div>
                    <button onClick={() => setShowHeaderSettings(false)} style={{ fontSize: 20, background: "none", border: "none", cursor: "pointer", color: "#9B9490" }}>✕</button>
                  </div>
                  <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>This header appears at the top of every agreement clients see.</div>
                  <div style={{ background: "#F7F5F0", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
                    <AgreementHeader header={agreementHeader} />
                    {!agreementHeader.practice_name && !agreementHeader.logo_url && <div style={{ fontSize: 12, color: "#9B9490", textAlign: "center" }}>Fill in the fields below to see a preview</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Logo URL</div>
                      <input value={agreementHeader.logo_url || ""} onChange={e => setAgreementHeader(h => ({ ...h, logo_url: e.target.value }))}
                        placeholder="https://yourdomain.com/logo.png"
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
                      <div style={{ fontSize: 11, color: "#9B9490", marginTop: 4 }}>Upload your logo to your server and paste the URL here.</div>
                    </div>
                    <Input label="Practice Name" value={agreementHeader.practice_name || ""} onChange={v => setAgreementHeader(h => ({ ...h, practice_name: v }))} />
                    <Input label="Tagline (optional)" value={agreementHeader.tagline || ""} onChange={v => setAgreementHeader(h => ({ ...h, tagline: v }))} />
                    <Input label="Address" value={agreementHeader.address || ""} onChange={v => setAgreementHeader(h => ({ ...h, address: v }))} />
                    <Input label="Phone" value={agreementHeader.phone || ""} onChange={v => setAgreementHeader(h => ({ ...h, phone: v }))} />
                    <Input label="Email" value={agreementHeader.email || ""} onChange={v => setAgreementHeader(h => ({ ...h, email: v }))} />
                    <Input label="Website" value={agreementHeader.website || ""} onChange={v => setAgreementHeader(h => ({ ...h, website: v }))} />
                  </div>
                  <Msg msg={headerMsg} />
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
                    <button onClick={() => setShowHeaderSettings(false)} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                    <button onClick={async () => {
                      const res = await api.post("agreements.php", { action: "save_header", ...agreementHeader });
                      if (res.success) { setHeaderMsg({ type: "success", text: "Header saved." }); setTimeout(() => { setHeaderMsg(null); setShowHeaderSettings(false); }, 1500); }
                      else setHeaderMsg({ type: "error", text: "Failed to save." });
                    }} style={{ padding: "9px 24px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Header</button>
                  </div>
                </div>
              </div>
            )}

            {/* AGREEMENT EDITOR MODAL */}
            {/* AGREEMENT PREVIEW MODAL */}
            {previewAgreement && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {/* Header */}
                  <div style={{ padding: "20px 28px", borderBottom: "1px solid #E2DDD5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Client Preview</div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800 }}>{previewAgreement.title}</div>
                    </div>
                    <button onClick={() => setPreviewAgreement(null)} style={{ fontSize: 20, background: "none", border: "none", cursor: "pointer", color: "#9B9490" }}>✕</button>
                  </div>
                  {/* Agreement body — scrollable */}
                  <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px", background: "#F7F5F0" }}>
                    <div style={{ background: "#fff", borderRadius: 10, padding: "28px 32px", fontSize: 13, lineHeight: 1.9, color: "#2D3748", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                      <AgreementHeader header={agreementHeader} />
                      <div dangerouslySetInnerHTML={{ __html: previewAgreement.body }} />
                    </div>
                  </div>
                  {/* Signature area preview */}
                  <div style={{ padding: "20px 28px", borderTop: "1px solid #E2DDD5", background: "#fff" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", marginBottom: 10 }}>SIGNATURE PREVIEW</div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <div style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: "#1B2B4B", color: "#fff" }}>✍ Type</div>
                      <div style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600, border: "1px solid #E2DDD5", color: "#4A5568" }}>✏ Draw</div>
                    </div>
                    <div style={{ border: "1px solid #E2DDD5", borderRadius: 8, padding: "14px 16px", color: "#9B9490", fontSize: 14, fontFamily: "'Playfair Display', serif", background: "#F7F5F0" }}>
                      Client types their full legal name here…
                    </div>
                    <div style={{ fontSize: 11, color: "#9B9490", marginTop: 10, lineHeight: 1.6 }}>
                      By signing above, I acknowledge that I have read, understood, and agree to the terms of this {previewAgreement.title}. This electronic signature is legally binding.
                    </div>
                    <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                      <div style={{ padding: "10px 28px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, opacity: 0.7 }}>Sign & Continue →</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AGREEMENT EDITOR MODAL */}
            {showAgreementEditor && editingAgreement && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 820, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>{editingAgreement.id ? "Edit Agreement" : "New Agreement"}</div>
                    <button onClick={() => setShowAgreementEditor(false)} style={{ fontSize: 20, background: "none", border: "none", cursor: "pointer", color: "#9B9490" }}>✕</button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Input label="Agreement Title" value={editingAgreement.title} onChange={v => setEditingAgreement(a => ({ ...a, title: v }))} />
                    <Input label="Service Type (optional, e.g. Mediation, Divorce Coaching)" value={editingAgreement.service_type || ""} onChange={v => setEditingAgreement(a => ({ ...a, service_type: v }))} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#4A5568", marginBottom: 6 }}>Agreement Text</div>
                      <RichTextArea value={editingAgreement.body || ""} onChange={v => setEditingAgreement(a => ({ ...a, body: v }))} placeholder="Enter the full agreement text here..." />
                    </div>
                    <Msg msg={agreementMsg} />
                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                      <button onClick={() => setShowAgreementEditor(false)} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                      <button onClick={async () => {
                        if (!editingAgreement.title || !editingAgreement.body) { setAgreementMsg({ type: "error", text: "Title and body are required." }); return; }
                        const res = await api.post("agreements.php", { action: "save_agreement", ...editingAgreement });
                        if (res.success) {
                          setShowAgreementEditor(false); setEditingAgreement(null);
                          const r = await api.get("agreements.php?action=list"); setAgreements(Array.isArray(r) ? r : []);
                          setAgreementMsg({ type: "success", text: "Agreement saved." });
                          setTimeout(() => setAgreementMsg(null), 3000);
                        } else setAgreementMsg({ type: "error", text: res.error || "Failed to save." });
                      }} style={{ padding: "9px 24px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Agreement</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── LEADS ── */}
            {!selectedCase && tab === "leads" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Leads</h1>
                    <div style={{ fontSize: 13, color: "#9B9490", marginTop: 4 }}>Inquiries from your website contact form.</div>
                  </div>
                  <button onClick={async () => {
                    const res = await api.get(`leads.php?action=list&status=${leadsFilter}`);
                    setLeads(Array.isArray(res) ? res : []);
                  }} style={{ padding: "9px 18px", border: "1px solid #E2DDD5", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#4A5568" }}>↻ Refresh</button>
                </div>

                {/* Load leads on mount */}
                {leads.length === 0 && (() => {
                  api.get(`leads.php?action=list&status=all`).then(res => setLeads(Array.isArray(res) ? res : []));
                  return null;
                })()}

                {/* Status filter */}
                <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
                  {[["all","All"],["new","🆕 New"],["contacted","📞 Contacted"],["converted","✅ Converted"],["lost","❌ Lost"]].map(([v,l]) => (
                    <button key={v} onClick={async () => {
                      setLeadsFilter(v);
                      const res = await api.get(`leads.php?action=list&status=${v}`);
                      setLeads(Array.isArray(res) ? res : []);
                    }} style={{ padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                      border: leadsFilter === v ? "2px solid #1B2B4B" : "2px solid #E2DDD5",
                      background: leadsFilter === v ? "#1B2B4B" : "#fff",
                      color: leadsFilter === v ? "#fff" : "#9B9490" }}>{l}</button>
                  ))}
                  <div style={{ marginLeft: "auto", fontSize: 13, color: "#9B9490", alignSelf: "center" }}>
                    {leads.filter(l => l.status === "new").length} new · {leads.length} total
                  </div>
                </div>

                {/* Leads list */}
                {leads.length === 0 && <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No leads yet. Once someone fills out your website contact form, they'll appear here.</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {leads.map(lead => {
                    const statusColors = { new: { bg: "#FDF3E7", text: "#C8873A", label: "New" }, contacted: { bg: "#EEF0F8", text: "#1B2B4B", label: "Contacted" }, converted: { bg: "#E6F4F2", text: "#2D7D72", label: "Converted" }, lost: { bg: "#FDEEEC", text: "#C0392B", label: "Lost" } };
                    const sc = statusColors[lead.status] || statusColors.new;
                    const isEditing = editingLead?.id === lead.id;
                    return (
                      <div key={lead.id} style={{ background: "#fff", border: lead.status === "new" ? "2px solid #C8873A" : "1px solid #E2DDD5", borderRadius: 14, padding: "20px 24px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 800 }}>{lead.name}</div>
                              <span style={{ fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.text, padding: "2px 10px", borderRadius: 20 }}>{sc.label}</span>
                              {lead.status === "new" && <span style={{ fontSize: 11, color: "#C8873A", fontWeight: 700 }}>● New</span>}
                            </div>
                            <div style={{ fontSize: 13, color: "#4A5568", display: "flex", gap: 16, flexWrap: "wrap" }}>
                              {lead.email && <span>✉ {lead.email}</span>}
                              {lead.phone && <span>📞 {lead.phone}</span>}
                              {lead.preferred_contact && <span>Prefers: {lead.preferred_contact}</span>}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: "#9B9490", textAlign: "right" }}>
                            {new Date(lead.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                          </div>
                        </div>

                        {lead.services && <div style={{ fontSize: 13, marginBottom: 8 }}><span style={{ fontWeight: 600, color: "#1B2B4B" }}>Services: </span>{lead.services}</div>}
                        {lead.situation && <div style={{ fontSize: 13, color: "#4A5568", lineHeight: 1.6, marginBottom: 12, background: "#F7F5F0", borderRadius: 8, padding: "10px 14px" }}>{lead.situation}</div>}
                        {lead.notes && !isEditing && <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 10, fontStyle: "italic" }}>Notes: {lead.notes}</div>}

                        {/* Edit notes */}
                        {isEditing && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 4 }}>Notes</div>
                            <textarea value={leadNotes} onChange={e => setLeadNotes(e.target.value)} rows={3} placeholder="Add notes about this lead…"
                              style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit" }} />
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          {/* Status buttons */}
                          {[["new","New"],["contacted","Contacted"],["lost","Lost"]].filter(([v]) => v !== lead.status).map(([v, l]) => (
                            <button key={v} onClick={async () => {
                              await api.post("leads.php?action=update", { id: lead.id, status: v, notes: lead.notes || "" });
                              setLeads(prev => prev.map(ld => ld.id === lead.id ? {...ld, status: v} : ld));
                            }} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>→ {l}</button>
                          ))}

                          {isEditing ? (
                            <>
                              <button onClick={async () => {
                                await api.post("leads.php?action=update", { id: lead.id, status: lead.status, notes: leadNotes });
                                setLeads(prev => prev.map(ld => ld.id === lead.id ? {...ld, notes: leadNotes} : ld));
                                setEditingLead(null);
                              }} style={{ padding: "6px 14px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 11, fontWeight: 700 }}>Save Notes</button>
                              <button onClick={() => setEditingLead(null)} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, color: "#9B9490" }}>Cancel</button>
                            </>
                          ) : (
                            <button onClick={() => { setEditingLead(lead); setLeadNotes(lead.notes || ""); }}
                              style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>✏ Notes</button>
                          )}

                          {lead.status !== "converted" && (
                            <button onClick={async () => {
                              if (!window.confirm(`Convert ${lead.name} to a new case?`)) return;
                              const res = await api.post("leads.php?action=convert", { lead_id: lead.id });
                              if (res.success) {
                                setLeads(prev => prev.map(ld => ld.id === lead.id ? {...ld, status: "converted", converted_case_id: res.case_id} : ld));
                                alert(`Case created! Case ID: ${res.case_id}`);
                              }
                            }} style={{ padding: "6px 14px", background: "#1B2B4B", color: "#fff", borderRadius: 7, fontSize: 11, fontWeight: 700 }}>⚖ Convert to Case</button>
                          )}
                          {lead.converted_case_id && (
                            <span style={{ fontSize: 11, color: "#2D7D72", fontWeight: 600 }}>✓ Case #{lead.converted_case_id}</span>
                          )}
                          <button onClick={async () => {
                            if (!window.confirm("Delete this lead?")) return;
                            await api.post("leads.php?action=delete", { id: lead.id });
                            setLeads(prev => prev.filter(ld => ld.id !== lead.id));
                          }} style={{ padding: "6px 14px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#C0392B", marginLeft: "auto" }}>🗑 Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── SCHEDULING ── */}
            {!selectedCase && tab === "scheduling" && (
              <SchedulingTab />
            )}

            {/* ── MOU ── */}
            {!selectedCase && tab === "mou" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Memorandum of Understanding</h1>
                    <div style={{ fontSize: 13, color: "#9B9490", marginTop: 4 }}>Build financial agreements for each case.</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                  {cases.filter(c => c.status === "Active").map(c => (
                    <div key={c.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: "22px 24px", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                      onClick={() => { setMOUBuilderCase(c); setShowMOUBuilder(true); }}>
                      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 800, marginBottom: 6 }}>{c.client1_name} & {c.client2_name}</div>
                      <div style={{ fontSize: 12, color: "#9B9490", marginBottom: 16 }}>{c.case_number}</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={e => { e.stopPropagation(); setMOUBuilderCase(c); setShowMOUBuilder(true); }} style={{ flex: 1, padding: "8px 0", background: "#1B2B4B", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>📋 Open MOU Builder</button>
                        <button onClick={e => { e.stopPropagation(); window.open(`${API_BASE}/mou_export.php?case_id=${c.id}&format=pdf&auth_token=${getToken()}`, "_blank"); }} style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>🖨 PDF</button>
                        <button onClick={e => { e.stopPropagation(); window.open(`${API_BASE}/mou_export.php?case_id=${c.id}&format=docx&auth_token=${getToken()}`, "_blank"); }} style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>📄 Word</button>
                      </div>
                    </div>
                  ))}
                  {cases.filter(c => c.status === "Active").length === 0 && (
                    <div style={{ fontSize: 13, color: "#9B9490" }}>No active cases.</div>
                  )}
                </div>
                {showMOUBuilder && mouBuilderCase && <MOUBuilder caseData={mouBuilderCase} user={user} onClose={() => { setShowMOUBuilder(false); setMOUBuilderCase(null); }} />}
              </div>
            )}

            {!selectedCase && tab === "repository" && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Document Repository</h1>
                    <div style={{ fontSize: 13, color: "#9B9490", marginTop: 4 }}>Upload and manage reusable documents. Assign them to cases anytime.</div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {repoDocs.some(d => d.file_exists === false) && (
                      <button onClick={async () => {
                        const missingCount = repoDocs.filter(d => d.file_exists === false).length;
                        if (!window.confirm(`Remove ${missingCount} document${missingCount === 1 ? "" : "s"} whose files are missing from the server? This only deletes the repository entries — there's nothing to recover, the underlying files are already gone.`)) return;
                        setCleaningRepo(true);
                        const res = await api.post("doc_repository.php?action=delete_missing", {}).catch(() => null);
                        if (res?.success) {
                          setRepoDocs(prev => prev.filter(d => d.file_exists !== false));
                          setRepoCleanupMsg({ type: "success", text: `Removed ${res.deleted} broken entr${res.deleted === 1 ? "y" : "ies"}.` });
                        } else {
                          setRepoCleanupMsg({ type: "error", text: "Could not clean up missing files." });
                        }
                        setCleaningRepo(false);
                        setTimeout(() => setRepoCleanupMsg(null), 5000);
                      }} disabled={cleaningRepo} style={{ padding: "10px 20px", border: "2px solid #C0392B", color: "#C0392B", borderRadius: 9, fontSize: 13, fontWeight: 700, background: "#fff", opacity: cleaningRepo ? 0.6 : 1 }}>
                        {cleaningRepo ? "Cleaning up…" : "⚠ Clean Up Missing Files"}
                      </button>
                    )}
                    <button onClick={() => setShowNewFolder(true)} style={{ padding: "10px 20px", border: "2px solid #1B2B4B", color: "#1B2B4B", borderRadius: 9, fontSize: 13, fontWeight: 700, background: "#fff" }}>📁 New Folder</button>
                    <button onClick={() => setShowRepoUpload(true)} style={{ padding: "10px 20px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ Upload Document</button>
                  </div>
                </div>
                <Msg msg={repoCleanupMsg} />
                <input type="file" ref={replaceFileInputRef} onChange={handleReplaceFileChange} style={{ display: "none" }} />

                {/* New folder modal */}
                {showNewFolder && (
                  <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 24px", marginBottom: 20, display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 20 }}>📁</span>
                    {newFolderParent && <span style={{ fontSize: 12, color: "#9B9490", whiteSpace: "nowrap" }}>in {repoFolders.find(f => f.id === newFolderParent)?.name}</span>}
                    <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); setNewFolderParent(null); }}}
                      placeholder={newFolderParent ? "Subfolder name…" : "Folder name…"} autoFocus
                      style={{ flex: 1, border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }} />
                    <button onClick={createFolder} style={{ padding: "8px 16px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Create</button>
                    <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); setNewFolderParent(null); }} style={{ padding: "8px 12px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, color: "#9B9490" }}>Cancel</button>
                  </div>
                )}

                {/* Move to folder modal */}
                {movingDoc && (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ background: "#fff", borderRadius: 14, padding: "28px 32px", width: 380 }}>
                      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Move to Folder</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                        <button onClick={() => moveDocToFolder(movingDoc.id, null)}
                          style={{ padding: "10px 14px", borderRadius: 8, textAlign: "left", fontSize: 13, fontWeight: movingDoc.folder_id === null ? 700 : 400, border: movingDoc.folder_id === null ? "2px solid #2D7D72" : "1px solid #E2DDD5", background: movingDoc.folder_id === null ? "#E6F4F2" : "#fff" }}>
                          📂 All Documents (no folder)
                        </button>
                        {(() => {
                          const renderMoveTree = (parentId, depth) => repoFolders.filter(f => f.parent_id === (parentId||null)).map(f => (
                            <div key={f.id}>
                              <button onClick={() => moveDocToFolder(movingDoc.id, f.id)}
                                style={{ width: "100%", padding: "8px 14px", paddingLeft: 14 + depth*16, borderRadius: 8, textAlign: "left", fontSize: 13, fontWeight: movingDoc.folder_id === f.id ? 700 : 400, border: movingDoc.folder_id === f.id ? "2px solid #2D7D72" : "1px solid #E2DDD5", background: movingDoc.folder_id === f.id ? "#E6F4F2" : "#fff", marginBottom: 4 }}>
                                {"  ".repeat(depth)}📁 {f.name}
                              </button>
                              {renderMoveTree(f.id, depth+1)}
                            </div>
                          ));
                          return renderMoveTree(null, 0);
                        })()}
                      </div>
                      <button onClick={() => setMovingDoc(null)} style={{ width: "100%", padding: "9px 0", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, color: "#9B9490" }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Folder sidebar + content */}
                <div style={{ display: "flex", gap: 20 }}>
                  {/* Folder tree */}
                  {repoFolders.length > 0 && (
                    <div style={{ width: 220, flexShrink: 0, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "12px 8px" }}>
                      <div onClick={() => setActiveFolder(null)}
                        style={{ padding: "8px 10px", borderRadius: 8, cursor: "pointer", marginBottom: 4, fontWeight: activeFolder === null ? 700 : 400, background: activeFolder === null ? "#E6F4F2" : "transparent", color: activeFolder === null ? "#2D7D72" : "#1B2B4B", fontSize: 13 }}>
                        📂 All Documents <span style={{ fontSize: 11, color: "#9B9490" }}>({repoDocs.length})</span>
                      </div>
                      {renderFolderTree(null, 0)}
                    </div>
                  )}

                  <div style={{ flex: 1 }}>
                    {/* Filters */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
                      <input value={repoSearch} onChange={e => setRepoSearch(e.target.value)} placeholder="Search documents…"
                        style={{ flex: 1, minWidth: 200, border: "1px solid #E2DDD5", borderRadius: 8, padding: "8px 14px", fontSize: 13, outline: "none" }} />
                      {["", ...DOC_TYPES].map(t => (
                        <button key={t} onClick={() => setRepoFilter(t)}
                          style={{ padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: repoFilter === t ? "2px solid #2D7D72" : "2px solid #E2DDD5", background: repoFilter === t ? "#E6F4F2" : "#fff", color: repoFilter === t ? "#2D7D72" : "#9B9490" }}>
                          {t || "All"}
                        </button>
                      ))}
                    </div>

                {/* Doc grid */}
                {(() => {
                  const getFolderAndDescendants = (fid) => {
                    const children = repoFolders.filter(f => f.parent_id === fid).map(f => f.id);
                    return [fid, ...children.flatMap(cid => getFolderAndDescendants(cid))];
                  };
                  const activeFolderIds = activeFolder ? getFolderAndDescendants(activeFolder) : null;
                  const filtered = repoDocs.filter(d =>
                    (!repoFilter || d.doc_type === repoFilter) &&
                    (!repoSearch || d.title.toLowerCase().includes(repoSearch.toLowerCase())) &&
                    (activeFolderIds === null ? true : activeFolderIds.includes(d.folder_id))
                  );
                  const typeColors = { "Template": { bg: "#E6F4F2", text: "#2D7D72" }, "Reference": { bg: "#EEF0F8", text: "#1B2B4B" }, "Legal Form": { bg: "#FDEEEC", text: "#C0392B" }, "Handout": { bg: "#FDF3E7", text: "#C8873A" }, "Additional Legal Forms": { bg: "#F0F0F0", text: "#6B5B95" } };
                  if (filtered.length === 0) return <div style={{ fontSize: 13, color: "#9B9490", fontStyle: "italic" }}>No documents found. Upload one to get started.</div>;
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                      {filtered.map(doc => {
                        const tc = typeColors[doc.doc_type] || { bg: "#F0F0F0", text: "#666" };
                        const ext = doc.file_name?.split('.').pop().toUpperCase() || '';
                        const isDragOver = dragOverRepoId === doc.id;
                        const metaParts = [doc.file_name || (doc.link_url ? "🔗 Link" : null), doc.uploaded_by_name].filter(Boolean);
                        return (
                          <div key={doc.id}
                            draggable
                            onDragStart={e => onRepoDocDragStart(e, doc.id)}
                            onDragOver={e => onRepoDocDragOver(e, doc.id)}
                            onDrop={e => onRepoDocDrop(e, doc.id)}
                            onDragEnd={onRepoDocDragEnd}
                            style={{ background: "#fff", border: `2px solid ${isDragOver ? "#2D7D72" : "#E2DDD5"}`, borderRadius: 14, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 10, cursor: "grab", transform: isDragOver ? "scale(1.01)" : "scale(1)", transition: "border-color 0.15s, transform 0.15s", boxShadow: isDragOver ? "0 4px 20px rgba(45,125,114,0.15)" : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                  <span style={{ fontSize: 16, color: "#C8C4BE", cursor: "grab" }} title="Drag to reorder">⠿</span>
                                  <span style={{ fontSize: 20 }}>{ext === 'PDF' ? '📄' : ext === 'DOCX' || ext === 'DOC' ? '📝' : doc.file_name ? '📎' : '🔗'}</span>
                                  <span style={{ fontSize: 10, fontWeight: 700, background: tc.bg, color: tc.text, padding: "2px 8px", borderRadius: 20 }}>{doc.doc_type}</span>
                                  {doc.file_exists === false && <span style={{ fontSize: 10, fontWeight: 700, background: "#FDEEEC", color: "#C0392B", padding: "2px 8px", borderRadius: 20 }}>⚠ Missing</span>}
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "#1B2B4B", marginBottom: 4 }}>{doc.title}</div>
                                {doc.description && <div style={{ fontSize: 12, color: "#9B9490", lineHeight: 1.5 }}>{doc.description}</div>}
                                {metaParts.length > 0 && <div style={{ fontSize: 11, color: "#9B9490", marginTop: 6 }}>{metaParts.join(" · ")}</div>}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {doc.file_path && (doc.file_exists === false ? (
                                <>
                                  <span title="The file is missing from the server" style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#C8C4BE" }}>👁 View</span>
                                  <button onClick={() => triggerReplaceFile(doc.id)} disabled={uploadingReplacementId === doc.id}
                                    style={{ padding: "6px 12px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 11, fontWeight: 700, opacity: uploadingReplacementId === doc.id ? 0.6 : 1 }}>
                                    {uploadingReplacementId === doc.id ? "Uploading…" : "🔄 Replace File"}
                                  </button>
                                </>
                              ) : (
                                <a href={`https://ourmediationcase.com${doc.file_path}`} target="_blank" rel="noreferrer"
                                  style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>👁 View</a>
                              ))}
                              {doc.link_url && (
                                <a href={doc.link_url} target="_blank" rel="noreferrer"
                                  style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568", textDecoration: "none" }}>🔗 Open Link</a>
                              )}
                              <button onClick={() => { setAssigningDoc(doc); setShowAssignModal(true); }}
                                style={{ padding: "6px 12px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 11, fontWeight: 700 }}>📋 Assign to Case</button>
                              <button onClick={() => setEditingRepoDoc(doc)}
                                style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>✏️ Edit</button>
                              {repoFolders.length > 0 && <button onClick={() => setMovingDoc(doc)}
                                style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>📁 Move</button>}
                              <button onClick={async () => { if (!window.confirm("Delete this document from the repository?")) return; await api.post("doc_repository.php?action=delete", { id: doc.id }); setRepoDocs(prev => prev.filter(d => d.id !== doc.id)); }}
                                style={{ padding: "6px 12px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#C0392B" }}>🗑 Delete</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                  </div>
                </div>

                {/* Upload Modal */}
                {showRepoUpload && (
                  <RepoUploadModal user={user} onClose={() => setShowRepoUpload(false)}
                    folders={repoFolders}
                    initialFolder={activeFolder}
                    onSaved={doc => {
                      setRepoDocs(prev => [doc, ...prev]);
                      setShowRepoUpload(false);
                    }} />
                )}

                {/* Edit Modal */}
                {editingRepoDoc && (
                  <RepoEditModal doc={editingRepoDoc} onClose={() => setEditingRepoDoc(null)}
                    onSaved={updated => { setRepoDocs(prev => prev.map(d => d.id === updated.id ? updated : d)); setEditingRepoDoc(null); }} />
                )}

                {/* Assign Modal */}
                {showAssignModal && assigningDoc && (
                  <RepoAssignModal doc={assigningDoc} cases={cases} user={user}
                    onClose={() => { setShowAssignModal(false); setAssigningDoc(null); }}
                    onAssigned={() => { setShowAssignModal(false); setAssigningDoc(null); }} />
                )}
              </div>
            )}

            {!selectedCase && tab === "staff" && isAdmin && (
              <div className="r-page" style={{ padding: "32px 36px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Staff Management</h1>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setShowStaffInvite(true)} style={{ padding: "9px 18px", border: "1px solid #2D7D72", borderRadius: 9, fontSize: 13, fontWeight: 700, color: "#2D7D72" }}>✉ Send Invite</button>
                    <button onClick={() => setShowNewStaff(true)} style={{ padding: "9px 18px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>+ Create Account</button>
                  </div>
                </div>

                <Msg msg={staffMsg} />

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
                  <Stat label="Total Staff" value={staff.length} />
                  <Stat label="Mediators" value={staff.filter(s => s.role === "mediator").length} accent="#2D7D72" />
                  <Stat label="Admins" value={staff.filter(s => s.role === "admin").length} accent="#1B2B4B" />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {staff.map(s => (
                    <div key={s.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: "20px 24px" }}>
                      {editingStaff?.id === s.id ? (
                        <div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                            <Input label="Name" value={editingStaff.name} onChange={v => setEditingStaff({ ...editingStaff, name: v })} />
                            <Input label="Email" value={editingStaff.email} onChange={v => setEditingStaff({ ...editingStaff, email: v })} type="email" />
                            <Select label="Role" value={editingStaff.role} onChange={v => setEditingStaff({ ...editingStaff, role: v })} options={["mediator", "admin"]} />
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={updateStaff} style={{ padding: "7px 16px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Save</button>
                            <button onClick={() => setEditingStaff(null)} style={{ padding: "7px 16px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 44, height: 44, borderRadius: "50%", background: s.role === "admin" ? "#1B2B4B" : "#2D7D72", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#fff" }}>
                              {s.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name} {s.id === user.id && <span style={{ fontSize: 11, color: "#9B9490" }}>(you)</span>}</div>
                              <div style={{ fontSize: 12, color: "#9B9490" }}>{s.email}</div>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <Badge label={s.role} color={s.role === "admin" ? { bg: "#EEF0F8", text: "#1B2B4B" } : { bg: "#E6F4F2", text: "#2D7D72" }} />
                            {s.id !== user.id && (
                              <>
                                <button onClick={() => setEditingStaff(s)} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                                <button onClick={() => { const pwd = window.prompt("Enter new password for " + s.name + ":"); if (pwd && pwd.length >= 6) resetStaffPassword(s.id, pwd); else if (pwd) alert("Password must be at least 6 characters."); }} style={{ padding: "6px 14px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Reset PW</button>
                                <button onClick={() => deleteStaff(s.id)} style={{ padding: "6px 14px", border: "1px solid #C0392B", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#C0392B" }}>Remove</button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pending Invites */}
                {staffInvites.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#9B9490", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Sent Invites</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {staffInvites.map((inv, i) => {
                        const isPending = inv.status === "Pending";
                        const isExpired = inv.status === "Expired" || (isPending && new Date(inv.expires_at) < new Date());
                        const isAccepted = inv.status === "Accepted";
                        const statusColor = isAccepted ? { bg: "#E6F4F2", text: "#2D7D72" } : isExpired ? { bg: "#F0F0F0", text: "#9B9490" } : { bg: "#FDF3E7", text: "#C8873A" };
                        const statusLabel = isAccepted ? "✓ Signed Up" : isExpired ? "Expired" : "Pending";
                        return (
                          <div key={i} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.name || <span style={{ color: "#9B9490", fontStyle: "italic" }}>No name</span>}</div>
                              <div style={{ fontSize: 12, color: "#9B9490", marginTop: 2 }}>{inv.email} · {inv.role} · Sent {new Date(inv.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                              {!isAccepted && !isExpired && inv.expires_at && (
                                <div style={{ fontSize: 11, color: "#C8873A", marginTop: 2 }}>Expires {new Date(inv.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, background: statusColor.bg, color: statusColor.text, padding: "3px 10px", borderRadius: 20 }}>{statusLabel}</span>
                              {!isAccepted && (
                                <button onClick={async () => {
                                  if (!window.confirm(`Resend invite to ${inv.email}?`)) return;
                                  const res = await api.post("staff.php", { action: "send_staff_invite", name: inv.name, email: inv.email, role: inv.role, created_by: user.id });
                                  if (res.success) { setStaffMsg({ type: "success", text: `Invite resent to ${inv.email}` }); const r = await api.get("staff.php?action=get_invites"); setStaffInvites(Array.isArray(r) ? r : []); }
                                }} style={{ padding: "5px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#4A5568" }}>Resend</button>
                              )}
                              <button onClick={async () => {
                                if (!window.confirm(`Delete invite for ${inv.email}? This will cancel the invite link.`)) return;
                                const res = await api.post("staff.php", { action: "delete_invite", id: inv.id });
                                if (res.success) { const r = await api.get("staff.php?action=get_invites"); setStaffInvites(Array.isArray(r) ? r : []); }
                              }} style={{ padding: "5px 12px", border: "1px solid #FDEEEC", borderRadius: 7, fontSize: 11, fontWeight: 600, color: "#C0392B" }}>Delete</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── AUDIT LOG ── */}
            {!selectedCase && tab === "auditlog" && isAdmin && (
              <AuditLogTab />
            )}

            {/* ── PROFILE ── */}
            {!selectedCase && tab === "profile" && (
              <div className="r-page" style={{ padding: "32px 36px", maxWidth: 640 }}>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Profile & Settings</h1>
                <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 32 }}>Manage your account details and password.</div>
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28, marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      {avatarPreview ? <img src={avatarPreview} alt="" style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid #E2DDD5" }} /> : <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#2D7D72", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, color: "#fff" }}>{profileName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}</div>}
                      <label style={{ position: "absolute", bottom: 0, right: 0, width: 24, height: 24, background: "#1B2B4B", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12 }}>📷<input type="file" accept="image/*" style={{ display: "none" }} onChange={e => uploadAvatar(e.target.files[0])} /></label>
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800 }}>{user.name}</div>
                      <div style={{ fontSize: 13, color: "#9B9490" }}>{user.role} · {user.email}</div>
                      {avatarMsg && <div style={{ marginTop: 4, fontSize: 12, color: avatarMsg.type === "success" ? "#2E7D32" : "#C0392B", fontWeight: 600 }}>{avatarMsg.text}</div>}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Update Profile</div>
                  <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                    <Input label="Full Name" value={profileName} onChange={setProfileName} />
                    <Input label="Email Address" value={profileEmail} onChange={setProfileEmail} type="email" />
                  </div>
                  <Msg msg={profileMsg} />
                  <button onClick={updateProfile} style={{ padding: "9px 22px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Profile</button>
                </div>
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28, marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Change Password</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
                    <Input label="Current Password" value={currentPassword} onChange={setCurrentPassword} type="password" />
                    <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <Input label="New Password" value={newPassword} onChange={setNewPassword} type="password" />
                      <Input label="Confirm New Password" value={confirmPassword} onChange={setConfirmPassword} type="password" />
                    </div>
                  </div>
                  <Msg msg={passwordMsg} />
                  <button onClick={changePassword} style={{ padding: "9px 22px", background: "#1B2B4B", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Change Password</button>
                </div>
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28, marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Email Notifications</div>
                  <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 18 }}>Choose which emails you receive. Changes apply immediately.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {[
                      { key: "new_session", label: "New session scheduled", desc: "When a session is added to a case" },
                      { key: "session_reminder", label: "Session reminder", desc: "Day before a scheduled session" },
                      { key: "new_task", label: "New task assigned", desc: "When a task is created" },
                      { key: "task_complete", label: "Task marked complete", desc: "When a client completes a task" },
                      { key: "signature_request", label: "Signature request", desc: "When a document is sent for signing" },
                      { key: "document_signed", label: "Document signed", desc: "When someone signs a document" },
                      { key: "new_document", label: "New document uploaded", desc: "When a file is added to a case" },
                      { key: "invite_sent", label: "Client invite sent", desc: "Confirmation when you send an invite" },
                    ].map(({ key, label, desc }, i) => (
                      <label key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: i < 7 ? "1px solid #F0EDE8" : "none", cursor: "pointer" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1B2B4B" }}>{label}</div>
                          <div style={{ fontSize: 11, color: "#9B9490", marginTop: 2 }}>{desc}</div>
                        </div>
                        <div onClick={() => setNotifSettings({ ...notifSettings, [key]: notifSettings[key] ? 0 : 1 })}
                          style={{ width: 44, height: 24, borderRadius: 99, background: notifSettings[key] ? "#2D7D72" : "#E2DDD5", position: "relative", transition: "background 0.2s", flexShrink: 0, cursor: "pointer" }}>
                          <div style={{ position: "absolute", top: 3, left: notifSettings[key] ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                        </div>
                      </label>
                    ))}
                  </div>
                  <Msg msg={notifMsg} />
                  <button onClick={saveNotifSettings} style={{ marginTop: 18, padding: "9px 22px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Preferences</button>
                </div>
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#C0392B", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, paddingBottom: 8, borderBottom: "1px solid #E2DDD5" }}>Account</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div><div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Sign out</div><div style={{ fontSize: 12, color: "#9B9490" }}>You'll need to sign back in.</div></div>
                    <button onClick={onLogout} style={{ padding: "9px 22px", border: "1px solid #C0392B", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#C0392B" }}>Sign Out</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODALS ── */}
      {showNewCase && (
        <Modal title="New Case" onClose={() => { setShowNewCase(false); setNewCaseMsg(null); }}>
          <Msg msg={newCaseMsg} />
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="Case Number" value={newCase.case_number} onChange={v => setNewCase({ ...newCase, case_number: v })} placeholder="e.g. DM-2026-001" />
            <Input label="Mediator Name" value={newCase.mediator} onChange={v => setNewCase({ ...newCase, mediator: v })} />
            <Input label="Client 1 Full Name" value={newCase.client1_name} onChange={v => setNewCase({ ...newCase, client1_name: v })} />
            <Input label="Client 2 Full Name" value={newCase.client2_name} onChange={v => setNewCase({ ...newCase, client2_name: v })} />
            <div style={{ gridColumn: "1/-1" }}>
              <Select label="Service Type" value={newCase.service_type} onChange={v => setNewCase({ ...newCase, service_type: v })} options={["", ...SERVICE_TYPES]} />
              {newCase.service_type && intakeTemplates.find(t => t.service_type === newCase.service_type || t.title === newCase.service_type) && (
                <div style={{ fontSize: 11, color: "#2D7D72", marginTop: 4 }}>✓ Intake template will be auto-assigned</div>
              )}
              {newCase.service_type && !intakeTemplates.find(t => t.service_type === newCase.service_type || t.title === newCase.service_type) && (
                <div style={{ fontSize: 11, color: "#C8873A", marginTop: 4 }}>No matching intake template — you can assign one manually later</div>
              )}
            </div>
            <Select label="Status" value={newCase.status} onChange={v => setNewCase({ ...newCase, status: v })} options={["Active", "On Hold", "Resolved"]} />
            <Select label="Stage" value={newCase.stage} onChange={v => setNewCase({ ...newCase, stage: v })} options={["Initial Assessment", "Parenting Plan", "Asset Division", "Agreement Drafting", "Agreement Signed"]} />
            <Input label="Number of Children" value={newCase.children} onChange={v => setNewCase({ ...newCase, children: v })} type="number" />
            {isAdmin && (
              <Select label="Assign to Mediator" value={newCase.assigned_mediator_id} onChange={v => setNewCase({ ...newCase, assigned_mediator_id: v })} options={staffOptions} />
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createCase} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Create Case</button>
            <button onClick={() => { setShowNewCase(false); setNewCaseMsg(null); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showNewSession && (
        <Modal title="New Session" onClose={() => setShowNewSession(false)}>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Input label="Topic" value={newSession.topic} onChange={v => setNewSession({ ...newSession, topic: v })} full />
            <Input label="Date" value={newSession.session_date} onChange={v => setNewSession({ ...newSession, session_date: v })} type="date" />
            <Input label="Time (HH:MM)" value={newSession.session_time} onChange={v => setNewSession({ ...newSession, session_time: v })} type="time" />
            <Input label="Duration (minutes)" value={newSession.duration} onChange={v => setNewSession({ ...newSession, duration: parseInt(v) || 60 })} type="number" placeholder="60" />
            <Select label="Type" value={newSession.type} onChange={v => setNewSession({ ...newSession, type: v })} options={["In-Person", "Virtual", "Phone"]} />
            <Select label="Status" value={newSession.status} onChange={v => setNewSession({ ...newSession, status: v })} options={["Upcoming", "Completed", "Cancelled"]} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Notes / Zoom Link</div>
            <textarea value={newSession.notes} onChange={e => setNewSession({ ...newSession, notes: e.target.value })} placeholder="Add Zoom link or any notes for clients…" style={{ width: "100%", minHeight: 80, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} />
          </div>
          {sessionError && <div style={{ marginBottom: 12, padding: "10px 14px", background: "#FDEEEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{sessionError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createSession} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save Session</button>
            <button onClick={() => { setShowNewSession(false); setSessionError(""); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {editingSession && (
        <Modal title="Edit Session" onClose={() => { setEditingSession(null); setEditSessionError(""); }}>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Input label="Topic" value={editingSession.topic} onChange={v => setEditingSession({ ...editingSession, topic: v })} full />
            <Input label="Date" value={editingSession.session_date} onChange={v => setEditingSession({ ...editingSession, session_date: v })} type="date" />
            <Input label="Time (HH:MM)" value={editingSession.session_time} onChange={v => setEditingSession({ ...editingSession, session_time: v })} type="time" />
            <Input label="Duration (minutes)" value={editingSession.duration} onChange={v => setEditingSession({ ...editingSession, duration: parseInt(v) || 60 })} type="number" placeholder="60" />
            <Select label="Type" value={editingSession.type} onChange={v => setEditingSession({ ...editingSession, type: v })} options={["In-Person", "Virtual", "Phone"]} />
            <Select label="Status" value={editingSession.status} onChange={v => setEditingSession({ ...editingSession, status: v })} options={["Upcoming", "Completed", "Cancelled", "No-Show"]} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Notes / Zoom Link</div>
            <textarea value={editingSession.notes || ""} onChange={e => setEditingSession({ ...editingSession, notes: e.target.value })} placeholder="Add Zoom link or any notes for clients…" style={{ width: "100%", minHeight: 80, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} />
          </div>

          {editingSession.status === "Cancelled" ? (
            <div style={{ marginBottom: 16, padding: "14px 16px", background: "#FDEEEC", borderRadius: 8, border: "1px solid #F5C6C6" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#C0392B", marginBottom: 8 }}>⚠️ This session will be marked as cancelled.</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#4A5568", cursor: "pointer" }}>
                <input type="checkbox" checked={sendCancelEmail} onChange={e => setSendCancelEmail(e.target.checked)} />
                Send cancellation email to client
              </label>
            </div>
          ) : (
            <div style={{ marginBottom: 16, padding: "10px 14px", background: "#FDF3E7", borderRadius: 8, fontSize: 12, color: "#C8873A" }}>
              ⚠️ Saving will email clients with the updated session details.
            </div>
          )}

          {editSessionError && <div style={{ marginBottom: 12, padding: "10px 14px", background: "#FDEEEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{editSessionError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={updateSession} style={{ padding: "9px 20px", background: editingSession.status === "Cancelled" ? "#C0392B" : "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>
              {editingSession.status === "Cancelled" ? "Cancel Session" : "Save & Notify Client"}
            </button>
            <button onClick={() => { setEditingSession(null); setEditSessionError(""); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Close</button>
            <button onClick={() => deleteSession(editingSession.id)} style={{ padding: "9px 20px", border: "1px solid #C0392B", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#C0392B" }}>Delete</button>
          </div>
        </Modal>
      )}

      {showNewInvoice && (
        <Modal title={editingInvoiceId ? "Edit Invoice" : "New Invoice"} onClose={() => { setShowNewInvoice(false); setNewInvoiceMsg(null); setEditingInvoiceId(null); }}>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Input label="Invoice Number" value={newInvoice.invoice_number} onChange={v => setNewInvoice({ ...newInvoice, invoice_number: v })} placeholder="e.g. INV-001" />
            <Input label="Date" value={newInvoice.invoice_date} onChange={v => setNewInvoice({ ...newInvoice, invoice_date: v })} type="date" />
            <Input label="Amount ($)" value={newInvoice.amount} onChange={v => setNewInvoice({ ...newInvoice, amount: v })} type="number" />
            <Select label="Status" value={newInvoice.status} onChange={v => setNewInvoice({ ...newInvoice, status: v })} options={["Outstanding", "Paid"]} />
            <Input label="Description" value={newInvoice.description} onChange={v => setNewInvoice({ ...newInvoice, description: v })} full />
          </div>
          <Msg msg={newInvoiceMsg} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createInvoice} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>{editingInvoiceId ? "Save Changes" : "Save Invoice"}</button>
            <button onClick={() => { setShowNewInvoice(false); setNewInvoiceMsg(null); setEditingInvoiceId(null); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showNewComm && (
        <Modal title="Log Communication" onClose={() => { setShowNewComm(false); setCommMsg(null); }}>
          <Msg msg={commMsg} />
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Input label="Subject" value={newComm.subject} onChange={v => setNewComm({ ...newComm, subject: v })} full />
            <Input label="Date" value={newComm.comm_date} onChange={v => setNewComm({ ...newComm, comm_date: v })} type="date" />
            <Input label="From" value={newComm.from_name} onChange={v => setNewComm({ ...newComm, from_name: v })} />
            <Input label="To" value={newComm.to_name} onChange={v => setNewComm({ ...newComm, to_name: v })} />
            <Select label="Type" value={newComm.type} onChange={v => setNewComm({ ...newComm, type: v })} options={["Email", "Phone", "In Person", "Text"]} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Summary</div>
            <textarea value={newComm.summary} onChange={e => setNewComm({ ...newComm, summary: e.target.value })} style={{ width: "100%", minHeight: 80, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createComm} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save</button>
            <button onClick={() => { setShowNewComm(false); setCommMsg(null); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showNewSignatureDoc && (
        <Modal title="Upload Signature Document" onClose={() => { setShowNewSignatureDoc(false); setSigDocMsg(null); }} wide>
          <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>Upload a PDF document that requires signatures. Clients will be able to sign directly in their portal.</div>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="Document Title" value={newSigDoc.title} onChange={v => setNewSigDoc({...newSigDoc, title: v})} full />
            <Select label="Document Type" value={newSigDoc.document_type} onChange={v => setNewSigDoc({...newSigDoc, document_type: v})} options={["Retainer / Engagement Agreement","Mediation Agreement","Parenting Plan","Settlement Agreement","Custom Document"]} />
          </div>
          <div style={{ background: "#F7F5F0", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#4A5568", marginBottom: 12 }}>Who needs to sign?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[{key:"require_client1",label:"Client 1"},{key:"require_client2",label:"Client 2"},{key:"require_mediator",label:"Mediator / Me"}].map(({key,label}) => (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13 }}>
                  <input type="checkbox" checked={newSigDoc[key]} onChange={e => setNewSigDoc({...newSigDoc, [key]: e.target.checked})} style={{ width: 16, height: 16 }} />
                  <span style={{ fontWeight: 600 }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <Msg msg={sigDocMsg} />
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 8 }}>Select PDF File (max 20MB)</div>
            <label style={{ display: "inline-block", padding: "10px 22px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              📎 Choose PDF &amp; Upload
              <input type="file" accept="application/pdf" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) uploadSignatureDoc(e.target.files[0]); }} />
            </label>
          </div>
        </Modal>
      )}

      {signingDoc && (
        <SigningModal doc={signingDoc} user={user} onClose={() => setSigningDoc(null)} onSigned={() => { setSigningDoc(null); loadCaseExtras(activeCase.id); }} />
      )}

      {showNewTask && (
        <Modal title="New Task" onClose={() => { setShowNewTask(false); setTaskMsg(null); }}>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <Input label="Task Title" value={newTask.title} onChange={v => setNewTask({...newTask, title: v})} full />
            <Input label="Due Date" value={newTask.due_date} onChange={v => setNewTask({...newTask, due_date: v})} type="date" />
            <Select label="Priority" value={newTask.priority} onChange={v => setNewTask({...newTask, priority: v})} options={["High","Medium","Low"]} />
            <Select label="Assign To" value={newTask.assigned_to} onChange={v => setNewTask({...newTask, assigned_to: v})} options={[{value:"both",label:"Both Clients"},{value:"Client 1",label:"Client 1"},{value:"Client 2",label:"Client 2"}]} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Notes / Description</div>
            <textarea value={newTask.notes} onChange={e => setNewTask({...newTask, notes: e.target.value})} placeholder="Describe what the client needs to do…" style={{ width: "100%", minHeight: 90, border: "1px solid #E2DDD5", borderRadius: 7, padding: 10, fontSize: 13, resize: "vertical", outline: "none" }} />
          </div>
          <Msg msg={taskMsg} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createTask} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Create Task</button>
            <button onClick={() => { setShowNewTask(false); setTaskMsg(null); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showInvite && (
        <Modal title="Invite Client to Portal" onClose={() => { setShowInvite(false); setInviteMsg(null); }}>
          <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>The client will receive an email with a registration link valid for 7 days.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
            <Input label="Client Email Address" value={newInvite.email} onChange={v => setNewInvite({ ...newInvite, email: v })} type="email" />
            <Select label="Client Role" value={newInvite.case_role} onChange={v => setNewInvite({ ...newInvite, case_role: v })} options={["Client 1", "Client 2"]} />
          </div>
          <Msg msg={inviteMsg} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={sendInvite} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Send Invite</button>
            <button onClick={() => { setShowInvite(false); setInviteMsg(null); }} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showNewStaff && (
        <Modal title="Create Staff Account" onClose={() => setShowNewStaff(false)}>
          <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>Create an account directly and share the credentials with your staff member.</div>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="Full Name" value={newStaff.name} onChange={v => setNewStaff({ ...newStaff, name: v })} />
            <Input label="Email Address" value={newStaff.email} onChange={v => setNewStaff({ ...newStaff, email: v })} type="email" />
            <Input label="Temporary Password" value={newStaff.password} onChange={v => setNewStaff({ ...newStaff, password: v })} type="password" />
            <Select label="Role" value={newStaff.role} onChange={v => setNewStaff({ ...newStaff, role: v })} options={["mediator", "admin"]} />
          </div>
          <Msg msg={staffMsg} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createStaff} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Create Account</button>
            <button onClick={() => setShowNewStaff(false)} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {showStaffInvite && (
        <Modal title="Invite Staff Member" onClose={() => setShowStaffInvite(false)}>
          <div style={{ fontSize: 13, color: "#9B9490", marginBottom: 20 }}>Send an invite link so your staff member can create their own account and password.</div>
          <div className="r-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="Name (optional)" value={staffInvite.name} onChange={v => setStaffInvite({ ...staffInvite, name: v })} />
            <Input label="Email Address" value={staffInvite.email} onChange={v => setStaffInvite({ ...staffInvite, email: v })} type="email" />
            <Select label="Role" value={staffInvite.role} onChange={v => setStaffInvite({ ...staffInvite, role: v })} options={["mediator", "admin"]} />
          </div>
          <Msg msg={staffMsg} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={sendStaffInvite} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Send Invite</button>
            <button onClick={() => setShowStaffInvite(false)} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
          </div>
        </Modal>
      )}
      </div>{/* end inner flex */}
      {/* ── BOTTOM NAV (mobile) ── */}
      <nav className="r-bottom-nav">
        {navItems.map(n => (
          <button key={n.id} onClick={() => goToTab(n.id)} className={tab === n.id && !selectedCase ? "active" : ""}>
            <span className="r-nav-icon">{n.icon}</span>
            {n.label}
          </button>
        ))}
        <button onClick={onLogout}>
          <span className="r-nav-icon">🚪</span>
          Out
        </button>
      </nav>
    </div>
  );
}

// ─── SCHEDULING TAB ───────────────────────────────────────────────────────────
function dstr(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function addDaysStr(dateStr, n) { const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() + n); return dstr(d); }
function sundayOf(dateStr) { const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() - d.getDay()); return dstr(d); }

// value: array of allowed session_type ids, or null/empty = all types allowed.
function TypeChipSelector({ value, sessionTypes, onChange }) {
  const isAll = !value || value.length === 0;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: 26 }}>
      <button type="button" onClick={() => onChange(null)} style={{
        padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
        background: isAll ? "#1B2B4B" : "#f3f4f6", color: isAll ? "#fff" : "#6b7280",
      }}>All types</button>
      {sessionTypes.map(t => {
        const active = isAll || (value || []).includes(t.id);
        return (
          <button key={t.id} type="button" onClick={() => {
            if (isAll) { onChange([t.id]); return; }
            const set = new Set(value);
            if (set.has(t.id)) set.delete(t.id); else set.add(t.id);
            onChange(set.size === 0 ? null : Array.from(set));
          }} style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: active ? "#E6F4F2" : "#f3f4f6", color: active ? "#2D7D72" : "#9ca3af",
          }}>{t.name}</button>
        );
      })}
    </div>
  );
}

function SchedulingTab() {
  const API = API_BASE + "/scheduling.php";
  const GCAL_API = API_BASE + "/google_calendar.php";
  const authHeaders = { "X-Auth-Token": getToken() };
  const [subTab, setSubTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState({});
  const [blocks, setBlocks] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [template, setTemplate] = useState([]);
  const [weekStart, setWeekStart] = useState(sundayOf(dstr(new Date())));
  const [weekData, setWeekData] = useState({ week_start: null, status: "draft", blocks: [] });
  const [weekLoading, setWeekLoading] = useState(true);
  const [weekMsg, setWeekMsg] = useState(null);
  const [savingWeek, setSavingWeek] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [newBlock, setNewBlock] = useState({ block_date: "", is_full_day: true, start_time: "", end_time: "", reason: "" });
  const [gcalStatus, setGcalStatus] = useState({ connected: false, email: null });
  const [gcalMsg, setGcalMsg] = useState(null);
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [linksMsg, setLinksMsg] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm, setLinkForm] = useState({ label: "", session_type_id: "", source_label: "", is_active: 1 });
  const [calendarLink, setCalendarLink] = useState(null);

  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const bookingLink = "https://ourmediationcase.com/?booking=1";

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadWeek(weekStart); }, [weekStart]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gcalResult = urlParams.get("google_calendar");
    if (gcalResult) {
      setGcalMsg(gcalResult === "connected" ? { type: "success", text: "Google Calendar connected!" } : { type: "error", text: "Could not connect Google Calendar. Please try again." });
      window.history.replaceState({}, "", window.location.pathname);
    }
    loadGcalStatus();
  }, []);

  async function loadGcalStatus() {
    const r = await fetch(GCAL_API + "?action=status", { headers: authHeaders }).then(x => x.json()).catch(() => null);
    if (r) setGcalStatus(r);
  }

  async function disconnectGoogleCalendar() {
    if (!window.confirm("Disconnect Google Calendar? Confirmed bookings will stop syncing and existing calendar events will no longer block the site.")) return;
    await fetch(GCAL_API + "?action=disconnect", { method: "POST", headers: authHeaders });
    setGcalMsg({ type: "success", text: "Disconnected." });
    setTimeout(() => setGcalMsg(null), 4000);
    loadGcalStatus();
  }

  async function loadAll() {
    setLoading(true);
    const [bRes, sRes, typesRes, templateRes] = await Promise.all([
      fetch(API + "?action=get_bookings&status=" + statusFilter, { headers: authHeaders }).then(r => r.json()).catch(() => []),
      fetch(API + "?action=get_settings", { headers: authHeaders }).then(r => r.json()).catch(() => ({})),
      fetch(API + "?action=get_session_types", { headers: authHeaders }).then(r => r.json()).catch(() => []),
      fetch(API + "?action=get_template", { headers: authHeaders }).then(r => r.json()).catch(() => []),
    ]);
    setBookings(Array.isArray(bRes) ? bRes : []);
    if (sRes.settings) setSettings(sRes.settings);
    if (sRes.blocks) setBlocks(sRes.blocks);
    setSessionTypes(Array.isArray(typesRes) ? typesRes : []);
    setTemplate(Array.isArray(templateRes) ? templateRes : []);
    setLoading(false);
  }

  async function loadWeek(ws) {
    setWeekLoading(true);
    const r = await fetch(API + "?action=get_week&week_start=" + ws, { headers: authHeaders }).then(x => x.json()).catch(() => null);
    if (r?.week_start) setWeekData(r);
    setWeekLoading(false);
  }

  async function loadBookings() {
    const bRes = await fetch(API + "?action=get_bookings&status=" + statusFilter, { headers: authHeaders }).then(r => r.json()).catch(() => []);
    setBookings(Array.isArray(bRes) ? bRes : []);
  }

  useEffect(() => { loadBookings(); }, [statusFilter]);

  async function updateBooking(id, status, notes) {
    await fetch(API + "?action=update_booking", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ id, status, notes })
    });
    loadBookings();
  }

  async function deleteBooking(id) {
    if (!window.confirm("Delete this booking?")) return;
    await fetch(API + "?action=delete_booking", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ id })
    });
    loadBookings();
  }

  async function saveSettings() {
    const r = await fetch(API + "?action=save_settings", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(settings)
    }).then(x => x.json()).catch(() => ({ error: "Network error" }));
    setMsg(r.success ? { type: "success", text: "Settings saved!" } : { type: "error", text: r.error });
    setTimeout(() => setMsg(null), 3000);
  }

  async function saveSessionTypes() {
    const r = await fetch(API + "?action=save_session_types", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ types: sessionTypes })
    }).then(x => x.json()).catch(() => ({ error: "Network error" }));
    setMsg(r.success ? { type: "success", text: "Session types saved!" } : { type: "error", text: r.error });
    setTimeout(() => setMsg(null), 3000);
    loadAll();
  }

  async function saveTemplate() {
    const r = await fetch(API + "?action=save_template", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ blocks: template })
    }).then(x => x.json()).catch(() => ({ error: "Network error" }));
    setMsg(r.success ? { type: "success", text: "Recurring pattern saved! It will pre-fill weeks you haven't opened yet." } : { type: "error", text: r.error });
    setTimeout(() => setMsg(null), 4000);
  }

  async function saveWeek() {
    setSavingWeek(true);
    const r = await fetch(API + "?action=save_week", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ week_start: weekData.week_start, blocks: weekData.blocks })
    }).then(x => x.json()).catch(() => ({ error: "Network error" }));
    setWeekMsg(r.success ? { type: "success", text: "Saved — publish this week to make it bookable." } : { type: "error", text: r.error });
    setTimeout(() => setWeekMsg(null), 4000);
    setSavingWeek(false);
    loadWeek(weekStart);
  }

  async function publishWeek() {
    setSavingWeek(true);
    await fetch(API + "?action=publish_week", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ week_start: weekData.week_start })
    });
    setSavingWeek(false);
    loadWeek(weekStart);
  }

  async function unpublishWeek() {
    setSavingWeek(true);
    await fetch(API + "?action=unpublish_week", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ week_start: weekData.week_start })
    });
    setSavingWeek(false);
    loadWeek(weekStart);
  }

  async function addBlock() {
    if (!newBlock.block_date) return;
    await fetch(API + "?action=add_block", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(newBlock)
    });
    setNewBlock({ block_date: "", is_full_day: true, start_time: "", end_time: "", reason: "" });
    loadAll();
  }

  async function deleteBlock(id) {
    await fetch(API + "?action=delete_block", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ id })
    });
    loadAll();
  }

  async function loadLinks() {
    setLinksLoading(true);
    const r = await fetch(API + "?action=get_links", { headers: authHeaders }).then(x => x.json()).catch(() => []);
    setLinks(Array.isArray(r) ? r : []);
    setLinksLoading(false);
  }
  useEffect(() => { if (subTab === "links") loadLinks(); }, [subTab]);

  function startNewLink() {
    setEditingLink({ isNew: true });
    setLinkForm({ label: "", session_type_id: sessionTypes[0]?.id || "", source_label: "", is_active: 1 });
    setLinksMsg(null);
  }
  function startEditLink(l) {
    setEditingLink(l);
    setLinkForm({ label: l.label, session_type_id: l.session_type_id, source_label: l.source_label, is_active: l.is_active });
    setLinksMsg(null);
  }
  async function saveLink() {
    if (!linkForm.label.trim()) { setLinksMsg({ type: "error", text: "A name is required." }); return; }
    try {
      const payload = { ...linkForm, id: editingLink?.isNew ? undefined : editingLink?.id };
      const res = await fetch(API + "?action=save_link", {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(payload)
      }).then(x => x.json()).catch(() => null);
      if (!res || res.success !== true) { setLinksMsg({ type: "error", text: res?.error || "Failed to save link." }); return; }
      setEditingLink(null);
      loadLinks();
    } catch {
      setLinksMsg({ type: "error", text: "Could not reach the server. Please try again." });
    }
  }
  async function deleteLink(id) {
    if (!window.confirm("Delete this booking link? The public URL will stop working.")) return;
    await fetch(API + "?action=delete_link", {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ id })
    });
    loadLinks();
  }

  function fmtDate(d) { return d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : ""; }
  function fmtShortDate(d) { return d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : ""; }
  function fmtTime(t) {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 || 12;
    return `${hr}:${String(m).padStart(2,"0")} ${ampm}`;
  }

  const statusColor = { pending: "#f59e0b", confirmed: "#2D7D72", cancelled: "#9ca3af", completed: "#1B2B4B" };

  const tabs = [
    { id: "bookings", label: "Bookings" },
    { id: "week", label: "Weekly Schedule" },
    { id: "template", label: "Recurring Pattern" },
    { id: "types", label: "Session Types" },
    { id: "blocks", label: "Blocked Times" },
    { id: "links", label: "Booking Links" },
    { id: "settings", label: "Settings" },
  ];

  return (
    <div className="r-page" style={{ padding: "32px 36px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800 }}>Scheduling</h1>
          <div style={{ fontSize: 13, color: "#9B9490", marginTop: 4 }}>Manage client self-booking and appointments.</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ fontSize: 12, color: "#6b7280", background: "#f3f4f6", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px" }}>
            🔗 <strong>Booking link:</strong>{" "}
            <a href={bookingLink} target="_blank" rel="noreferrer" style={{ color: "#2D7D72", fontWeight: 600 }}>{bookingLink}</a>
          </div>
          <button onClick={() => navigator.clipboard.writeText(bookingLink)} style={{ padding: "8px 14px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>Copy Link</button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, background: "#f3f4f6", borderRadius: 10, padding: 4, width: "fit-content" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ padding: "7px 18px", borderRadius: 7, fontSize: 13, fontWeight: 600, background: subTab === t.id ? "#fff" : "transparent", color: subTab === t.id ? "#1B2B4B" : "#6b7280", boxShadow: subTab === t.id ? "0 1px 4px rgba(0,0,0,.08)" : "none" }}>{t.label}</button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 60, color: "#9B9490" }}>Loading…</div> : <>

        {/* ── BOOKINGS ── */}
        {subTab === "bookings" && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {["pending","confirmed","completed","cancelled","all"].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: statusFilter === s ? "#1B2B4B" : "#f3f4f6", color: statusFilter === s ? "#fff" : "#6b7280", textTransform: "capitalize" }}>{s}</button>
              ))}
            </div>
            {bookings.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, color: "#9B9490", background: "#fff", borderRadius: 12, border: "1px solid #E2DDD5" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🗓</div>
                <div style={{ fontWeight: 600 }}>No {statusFilter === "all" ? "" : statusFilter} bookings</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Share your booking link with clients to get started.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {bookings.map(b => (
                  <div key={b.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 12, padding: 20, display: "flex", gap: 20, alignItems: "flex-start" }}>
                    <div style={{ width: 4, borderRadius: 4, alignSelf: "stretch", background: statusColor[b.status] || "#e2e8f0", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{b.client_name}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{b.client_email}{b.client_phone ? ` · ${b.client_phone}` : ""}</div>
                        <span style={{ fontSize: 11, fontWeight: 700, background: statusColor[b.status] + "22", color: statusColor[b.status], padding: "2px 8px", borderRadius: 20, textTransform: "capitalize" }}>{b.status}</span>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13 }}>
                        <strong>{fmtDate(b.booking_date)}</strong> · {fmtTime(b.start_time)} – {fmtTime(b.end_time)} · {b.session_type}{b.duration_minutes ? ` (${b.duration_minutes}m)` : ""}
                      </div>
                      {b.notes && <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>📝 {b.notes}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      {b.status === "pending" && (
                        <button onClick={() => updateBooking(b.id, "confirmed", b.notes)} style={{ padding: "6px 12px", background: "#2D7D72", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>✓ Confirm</button>
                      )}
                      {b.status === "confirmed" && (
                        <button onClick={() => updateBooking(b.id, "completed", b.notes)} style={{ padding: "6px 12px", background: "#1B2B4B", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>✓ Done</button>
                      )}
                      {b.status !== "cancelled" && b.status !== "completed" && (
                        <button onClick={() => updateBooking(b.id, "cancelled", b.notes)} style={{ padding: "6px 12px", background: "#fee2e2", color: "#dc2626", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Cancel</button>
                      )}
                      <button onClick={() => deleteBooking(b.id)} style={{ padding: "6px 10px", background: "#f3f4f6", color: "#9ca3af", borderRadius: 7, fontSize: 12 }}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── SETTINGS ── */}
        {subTab === "settings" && (
          <div style={{ maxWidth: 520, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { key: "business_name", label: "Practice / Business Name" },
                { key: "booking_message", label: "Intro Message on Booking Page" },
                { key: "confirmation_email", label: "Notification Email (receives new booking alerts)", type: "email" },
                { key: "buffer_minutes", label: "Buffer Between Sessions (minutes)" },
                { key: "advance_days", label: "How Far Ahead Clients Can Book (days)" },
                { key: "min_notice_hours", label: "Minimum Notice Required (hours)" },
              ].map(f => (
                <div key={f.key} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B" }}>{f.label}</label>
                  <input type={f.type || "text"} value={settings[f.key] || ""} onChange={e => setSettings({ ...settings, [f.key]: e.target.value })}
                    style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "9px 12px", fontSize: 13 }} />
                </div>
              ))}
            </div>
            {msg && <div style={{ marginTop: 14, padding: "10px 14px", background: msg.type === "success" ? "#dcfce7" : "#fee2e2", color: msg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13 }}>{msg.text}</div>}
            <button onClick={saveSettings} style={{ marginTop: 20, padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Save Settings</button>
          </div>
        )}

        {/* ── GOOGLE CALENDAR ── */}
        {subTab === "settings" && (
          <div style={{ maxWidth: 520, background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 28, marginTop: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: "#1B2B4B" }}>Google Calendar</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>Confirmed bookings are pushed to this calendar, and any event already on it blocks that time from being booked on the site.</div>
            {gcalMsg && <div style={{ marginBottom: 14, padding: "10px 14px", background: gcalMsg.type === "success" ? "#dcfce7" : "#fee2e2", color: gcalMsg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13 }}>{gcalMsg.text}</div>}
            {gcalStatus.connected ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F7F5F0", borderRadius: 10, padding: "12px 16px" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B" }}>🟢 Connected</div>
                  {gcalStatus.email && <div style={{ fontSize: 12, color: "#9B9490", marginTop: 2 }}>{gcalStatus.email}</div>}
                </div>
                <button onClick={disconnectGoogleCalendar} style={{ padding: "8px 16px", border: "2px solid #C0392B", color: "#C0392B", borderRadius: 8, fontSize: 12, fontWeight: 700, background: "#fff" }}>Disconnect</button>
              </div>
            ) : (
              <a href={GCAL_API + "?action=connect&auth_token=" + encodeURIComponent(getToken())}
                style={{ display: "inline-block", padding: "10px 20px", background: "#1B2B4B", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                Connect Google Calendar
              </a>
            )}
          </div>
        )}

        {/* ── WEEKLY SCHEDULE ── */}
        {subTab === "week" && (
          <div style={{ maxWidth: 680 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setWeekStart(addDaysStr(weekStart, -7))} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 13, fontWeight: 700, color: "#4A5568" }}>← Prev</button>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1B2B4B" }}>{fmtShortDate(weekStart)} – {fmtShortDate(addDaysStr(weekStart, 6))}</div>
                <button onClick={() => setWeekStart(addDaysStr(weekStart, 7))} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 13, fontWeight: 700, color: "#4A5568" }}>Next →</button>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: weekData.status === "published" ? "#dcfce7" : "#FDF3E7", color: weekData.status === "published" ? "#166534" : "#C8873A" }}>
                {weekData.status === "published" ? "🟢 Published — bookable now" : "🟡 Draft — not visible to clients"}
              </span>
            </div>

            {weekLoading ? <div style={{ textAlign: "center", padding: 40, color: "#9B9490" }}>Loading…</div> : (
              <>
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>Pre-filled from your recurring pattern the first time you open a week. Add as many time blocks per day as you need, then publish to make this week bookable.</div>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIdx => {
                    const date = addDaysStr(weekData.week_start || weekStart, dayIdx);
                    const dayBlocks = weekData.blocks.map((b, idx) => ({ ...b, _idx: idx })).filter(b => b.block_date === date);
                    return (
                      <div key={date} style={{ padding: "14px 0", borderBottom: dayIdx < 6 ? "1px solid #f3f4f6" : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1B2B4B" }}>{fmtShortDate(date)}</span>
                          <button onClick={() => setWeekData({ ...weekData, blocks: [...weekData.blocks, { block_date: date, start_time: "09:00", end_time: "17:00" }] })}
                            style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72" }}>+ Add Time Block</button>
                        </div>
                        {dayBlocks.length === 0 ? <div style={{ fontSize: 12, color: "#9ca3af" }}>No open times</div> : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {dayBlocks.map(b => (
                              <div key={b._idx} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <input type="time" value={b.start_time} onChange={e => setWeekData({ ...weekData, blocks: weekData.blocks.map((t, i) => i === b._idx ? { ...t, start_time: e.target.value } : t) })}
                                    style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                                  <span style={{ color: "#9ca3af" }}>to</span>
                                  <input type="time" value={b.end_time} onChange={e => setWeekData({ ...weekData, blocks: weekData.blocks.map((t, i) => i === b._idx ? { ...t, end_time: e.target.value } : t) })}
                                    style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                                  <button onClick={() => setWeekData({ ...weekData, blocks: weekData.blocks.filter((_, i) => i !== b._idx) })} style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Remove</button>
                                </div>
                                <TypeChipSelector value={b.session_type_ids} sessionTypes={sessionTypes}
                                  onChange={ids => setWeekData({ ...weekData, blocks: weekData.blocks.map((t, i) => i === b._idx ? { ...t, session_type_ids: ids } : t) })} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {weekMsg && <div style={{ padding: "10px 14px", background: weekMsg.type === "success" ? "#dcfce7" : "#fee2e2", color: weekMsg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{weekMsg.text}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={saveWeek} disabled={savingWeek} style={{ padding: "10px 24px", background: "#1B2B4B", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: savingWeek ? 0.6 : 1 }}>{savingWeek ? "Saving…" : "Save Changes"}</button>
                  {weekData.status === "published" ? (
                    <button onClick={unpublishWeek} disabled={savingWeek} style={{ padding: "10px 24px", border: "2px solid #C0392B", color: "#C0392B", borderRadius: 9, fontSize: 13, fontWeight: 700, background: "#fff", opacity: savingWeek ? 0.6 : 1 }}>Unpublish</button>
                  ) : (
                    <button onClick={publishWeek} disabled={savingWeek} style={{ padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: savingWeek ? 0.6 : 1 }}>Publish Week</button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── RECURRING PATTERN ── */}
        {subTab === "template" && (
          <div style={{ maxWidth: 560 }}>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>Your typical weekly pattern — used to pre-fill a week the first time you open it under Weekly Schedule. Editing this won't change weeks you've already set up. Add multiple blocks per day (e.g. a morning and an afternoon block).</div>
              {days.map((dayName, dayIdx) => {
                const dayBlocks = template.map((b, idx) => ({ ...b, _idx: idx })).filter(b => Number(b.day_of_week) === dayIdx);
                return (
                  <div key={dayIdx} style={{ padding: "14px 0", borderBottom: dayIdx < 6 ? "1px solid #f3f4f6" : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "#1B2B4B" }}>{dayName}</span>
                      <button onClick={() => setTemplate([...template, { day_of_week: dayIdx, start_time: "09:00", end_time: "17:00", is_active: 1 }])}
                        style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72" }}>+ Add Block</button>
                    </div>
                    {dayBlocks.length === 0 ? <div style={{ fontSize: 12, color: "#9ca3af" }}>Unavailable</div> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {dayBlocks.map(b => (
                          <div key={b._idx} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <input type="checkbox" checked={!!b.is_active} onChange={e => setTemplate(template.map((t, i) => i === b._idx ? { ...t, is_active: e.target.checked ? 1 : 0 } : t))} style={{ width: 16, height: 16, cursor: "pointer" }} />
                              <input type="time" value={b.start_time} onChange={e => setTemplate(template.map((t, i) => i === b._idx ? { ...t, start_time: e.target.value } : t))}
                                style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                              <span style={{ color: "#9ca3af" }}>to</span>
                              <input type="time" value={b.end_time} onChange={e => setTemplate(template.map((t, i) => i === b._idx ? { ...t, end_time: e.target.value } : t))}
                                style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                              <button onClick={() => setTemplate(template.filter((_, i) => i !== b._idx))} style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Remove</button>
                            </div>
                            <TypeChipSelector value={b.session_type_ids} sessionTypes={sessionTypes}
                              onChange={ids => setTemplate(template.map((t, i) => i === b._idx ? { ...t, session_type_ids: ids } : t))} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {msg && <div style={{ padding: "10px 14px", background: msg.type === "success" ? "#dcfce7" : "#fee2e2", color: msg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{msg.text}</div>}
            <button onClick={saveTemplate} style={{ padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Save Recurring Pattern</button>
          </div>
        )}

        {/* ── SESSION TYPES ── */}
        {subTab === "types" && (
          <div style={{ maxWidth: 560 }}>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>Clients pick one of these when booking — its duration determines which open times they can choose from.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {sessionTypes.map((t, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <input type="checkbox" checked={!!t.is_active} onChange={e => setSessionTypes(sessionTypes.map((x, xi) => xi === i ? { ...x, is_active: e.target.checked ? 1 : 0 } : x))} style={{ width: 16, height: 16, cursor: "pointer" }} title="Active" />
                    <input type="text" value={t.name} onChange={e => setSessionTypes(sessionTypes.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} placeholder="e.g. Consult"
                      style={{ flex: 1, border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "7px 10px", fontSize: 13 }} />
                    <input type="number" min="1" value={t.duration_minutes} onChange={e => setSessionTypes(sessionTypes.map((x, xi) => xi === i ? { ...x, duration_minutes: e.target.value } : x))}
                      style={{ width: 90, border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "7px 10px", fontSize: 13 }} />
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>min</span>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280", cursor: "pointer" }}>
                      <input type="checkbox" checked={!!t.creates_lead} onChange={e => setSessionTypes(sessionTypes.map((x, xi) => xi === i ? { ...x, creates_lead: e.target.checked ? 1 : 0 } : x))} style={{ width: 16, height: 16, cursor: "pointer" }} />
                      Creates a lead when booked
                    </label>
                    <button onClick={() => setSessionTypes(sessionTypes.filter((_, xi) => xi !== i))} style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Remove</button>
                  </div>
                ))}
              </div>
              <button onClick={() => setSessionTypes([...sessionTypes, { name: "", duration_minutes: 60, is_active: 1, creates_lead: 0 }])} style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: "#2D7D72" }}>+ Add Session Type</button>
            </div>
            {msg && <div style={{ padding: "10px 14px", background: msg.type === "success" ? "#dcfce7" : "#fee2e2", color: msg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{msg.text}</div>}
            <button onClick={saveSessionTypes} style={{ padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Save Session Types</button>
          </div>
        )}

        {/* ── BLOCKED TIMES ── */}
        {subTab === "blocks" && (
          <div style={{ maxWidth: 600 }}>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#1B2B4B" }}>Block a Date or Time</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>Date *</label>
                  <input type="date" value={newBlock.block_date} onChange={e => setNewBlock({ ...newBlock, block_date: e.target.value })} style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" }} />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                  <input type="checkbox" id="fullDayChk" checked={newBlock.is_full_day} onChange={e => setNewBlock({ ...newBlock, is_full_day: e.target.checked })} style={{ width: 16, height: 16 }} />
                  <label htmlFor="fullDayChk" style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Full day off</label>
                </div>
                {!newBlock.is_full_day && <>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>From</label>
                    <input type="time" value={newBlock.start_time} onChange={e => setNewBlock({ ...newBlock, start_time: e.target.value })} style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>To</label>
                    <input type="time" value={newBlock.end_time} onChange={e => setNewBlock({ ...newBlock, end_time: e.target.value })} style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" }} />
                  </div>
                </>}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>Reason (optional)</label>
                  <input type="text" value={newBlock.reason} onChange={e => setNewBlock({ ...newBlock, reason: e.target.value })} placeholder="e.g. Holiday, Vacation…" style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" }} />
                </div>
              </div>
              <button onClick={addBlock} style={{ padding: "9px 20px", background: "#1B2B4B", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Add Block</button>
            </div>
            {blocks.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9B9490", background: "#fff", borderRadius: 12, border: "1px solid #E2DDD5" }}>No blocked times. Add dates above to prevent bookings.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {blocks.map(b => (
                  <div key={b.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{fmtDate(b.block_date)}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{b.is_full_day ? "Full day" : `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`}{b.reason ? ` · ${b.reason}` : ""}</div>
                    </div>
                    <button onClick={() => deleteBlock(b.id)} style={{ padding: "5px 10px", background: "#fee2e2", color: "#dc2626", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── BOOKING LINKS ── */}
        {subTab === "links" && (
          <div style={{ maxWidth: 680 }}>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
              Named, reusable public links — e.g. one embedded on lakeshoremediation.com, locked to a single session type, with its own calendar completely separate from the main booking link above. Bookings made through a link are tagged with its source so you know where they came from.
            </div>
            {editingLink && (
              <div style={{ background: "#fff", border: "2px solid #2D7D72", borderRadius: 14, padding: 24, marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#1B2B4B" }}>{editingLink.isNew ? "New Booking Link" : "Edit Booking Link"}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>Name *</label>
                    <input type="text" value={linkForm.label} onChange={e => setLinkForm({ ...linkForm, label: e.target.value })} placeholder="e.g. Lakeshore Website — Free Consult" style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>Session Type *</label>
                    <select value={linkForm.session_type_id} onChange={e => setLinkForm({ ...linkForm, session_type_id: e.target.value })} style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%", background: "#fff" }}>
                      {sessionTypes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.duration_minutes}m)</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", display: "block", marginBottom: 4 }}>Source Label</label>
                    <input type="text" value={linkForm.source_label} onChange={e => setLinkForm({ ...linkForm, source_label: e.target.value })} placeholder="Defaults to the name above" style={{ border: "1.5px solid #E2DDD5", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%" }} />
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>Shown as the Source on leads created through this link.</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 20 }}>
                    <input type="checkbox" checked={!!linkForm.is_active} onChange={e => setLinkForm({ ...linkForm, is_active: e.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Active</span>
                  </div>
                </div>
                {linksMsg && <div style={{ padding: "10px 14px", background: linksMsg.type === "success" ? "#dcfce7" : "#fee2e2", color: linksMsg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{linksMsg.text}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveLink} style={{ padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>Save</button>
                  <button onClick={() => setEditingLink(null)} style={{ padding: "9px 20px", border: "1px solid #E2DDD5", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#4A5568" }}>Cancel</button>
                </div>
              </div>
            )}
            {!editingLink && <button onClick={startNewLink} style={{ marginBottom: 16, padding: "9px 20px", background: "#2D7D72", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700 }}>+ New Booking Link</button>}

            {linksLoading ? <div style={{ textAlign: "center", padding: 40, color: "#9B9490" }}>Loading…</div> : links.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9B9490", background: "#fff", borderRadius: 12, border: "1px solid #E2DDD5" }}>No booking links yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {links.map(l => {
                  const url = `https://ourmediationcase.com/?booking=1&link=${l.slug}`;
                  return (
                    <div key={l.id} style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 10, padding: "14px 18px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{l.label}{!l.is_active && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#9ca3af" }}>INACTIVE</span>}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{l.session_type_name} ({l.duration_minutes}m) · Source: {l.source_label}</div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button onClick={() => setCalendarLink(l)} style={{ padding: "6px 12px", background: "#1B2B4B", color: "#fff", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Manage Calendar</button>
                          <button onClick={() => startEditLink(l)} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#4A5568" }}>Edit</button>
                          <button onClick={() => deleteLink(l.id)} style={{ padding: "6px 12px", background: "#fee2e2", color: "#dc2626", borderRadius: 7, fontSize: 12, fontWeight: 700 }}>Delete</button>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f3f4f6", border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 12px" }}>
                        <span style={{ fontSize: 12, color: "#4A5568", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{url}</span>
                        <button onClick={() => navigator.clipboard.writeText(url)} style={{ padding: "4px 10px", background: "#2D7D72", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>Copy</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </>}

      {calendarLink && <LinkCalendarModal link={calendarLink} sessionTypes={sessionTypes} onClose={() => setCalendarLink(null)} />}
    </div>
  );
}

// ─── LINK CALENDAR MODAL ────────────────────────────────────────────────────
// A trimmed-down, self-contained version of the Weekly Schedule + Recurring
// Pattern editors above, scoped to one booking link's own independent
// calendar (link_id on the backend) instead of the main public booking page.
function LinkCalendarModal({ link, sessionTypes, onClose }) {
  const API = API_BASE + "/scheduling.php";
  const authHeaders = { "X-Auth-Token": getToken() };
  const [tab, setTab] = useState("week");
  const [template, setTemplate] = useState([]);
  const [weekStart, setWeekStart] = useState(sundayOf(dstr(new Date())));
  const [weekData, setWeekData] = useState({ week_start: null, status: "draft", blocks: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  function fmtShortDate(d) { return d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : ""; }

  async function loadTemplate() {
    const r = await fetch(`${API}?action=get_template&link_id=${link.id}`, { headers: authHeaders }).then(x => x.json()).catch(() => []);
    setTemplate(Array.isArray(r) ? r : []);
  }
  async function loadWeek(ws) {
    setLoading(true);
    const r = await fetch(`${API}?action=get_week&week_start=${ws}&link_id=${link.id}`, { headers: authHeaders }).then(x => x.json()).catch(() => null);
    if (r?.week_start) setWeekData(r);
    setLoading(false);
  }
  useEffect(() => { loadTemplate(); loadWeek(weekStart); document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  useEffect(() => { loadWeek(weekStart); }, [weekStart]);

  async function saveTemplate() {
    const r = await fetch(`${API}?action=save_template`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ link_id: link.id, blocks: template })
    }).then(x => x.json()).catch(() => ({ error: "Network error" }));
    setMsg(r.success ? { type: "success", text: "Recurring pattern saved!" } : { type: "error", text: r.error });
    setTimeout(() => setMsg(null), 4000);
  }
  async function saveWeek() {
    setSaving(true);
    const r = await fetch(`${API}?action=save_week`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ link_id: link.id, week_start: weekData.week_start, blocks: weekData.blocks })
    }).then(x => x.json()).catch(() => ({ error: "Network error" }));
    setMsg(r.success ? { type: "success", text: "Saved — publish this week to make it bookable." } : { type: "error", text: r.error });
    setTimeout(() => setMsg(null), 4000);
    setSaving(false);
    loadWeek(weekStart);
  }
  async function publishWeek() {
    setSaving(true);
    await fetch(`${API}?action=publish_week`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ link_id: link.id, week_start: weekData.week_start })
    });
    setSaving(false);
    loadWeek(weekStart);
  }
  async function unpublishWeek() {
    setSaving(true);
    await fetch(`${API}?action=unpublish_week`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({ link_id: link.id, week_start: weekData.week_start })
    });
    setSaving(false);
    loadWeek(weekStart);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#F7F5F0", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 800, color: "#1B2B4B" }}>{link.label}</div>
            <div style={{ fontSize: 12, color: "#9B9490", marginTop: 3 }}>{link.session_type_name} ({link.duration_minutes}m) — this link's own independent calendar</div>
          </div>
          <button onClick={onClose} style={{ fontSize: 22, color: "#9B9490" }}>×</button>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "#fff", borderRadius: 10, padding: 4, width: "fit-content" }}>
          {[{ id: "week", label: "Weekly Schedule" }, { id: "template", label: "Recurring Pattern" }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "7px 18px", borderRadius: 7, fontSize: 13, fontWeight: 600, background: tab === t.id ? "#F7F5F0" : "transparent", color: tab === t.id ? "#1B2B4B" : "#6b7280" }}>{t.label}</button>
          ))}
        </div>

        {tab === "week" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setWeekStart(addDaysStr(weekStart, -7))} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 13, fontWeight: 700, color: "#4A5568" }}>← Prev</button>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1B2B4B" }}>{fmtShortDate(weekStart)} – {fmtShortDate(addDaysStr(weekStart, 6))}</div>
                <button onClick={() => setWeekStart(addDaysStr(weekStart, 7))} style={{ padding: "6px 12px", border: "1px solid #E2DDD5", borderRadius: 7, fontSize: 13, fontWeight: 700, color: "#4A5568" }}>Next →</button>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: weekData.status === "published" ? "#dcfce7" : "#FDF3E7", color: weekData.status === "published" ? "#166534" : "#C8873A" }}>
                {weekData.status === "published" ? "🟢 Published — bookable now" : "🟡 Draft — not visible yet"}
              </span>
            </div>
            {loading ? <div style={{ textAlign: "center", padding: 40, color: "#9B9490" }}>Loading…</div> : (
              <>
                <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 16 }}>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayIdx => {
                    const date = addDaysStr(weekData.week_start || weekStart, dayIdx);
                    const dayBlocks = weekData.blocks.map((b, idx) => ({ ...b, _idx: idx })).filter(b => b.block_date === date);
                    return (
                      <div key={date} style={{ padding: "14px 0", borderBottom: dayIdx < 6 ? "1px solid #f3f4f6" : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1B2B4B" }}>{fmtShortDate(date)}</span>
                          <button onClick={() => setWeekData({ ...weekData, blocks: [...weekData.blocks, { block_date: date, start_time: "09:00", end_time: "17:00" }] })}
                            style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72" }}>+ Add Time Block</button>
                        </div>
                        {dayBlocks.length === 0 ? <div style={{ fontSize: 12, color: "#9ca3af" }}>No open times</div> : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {dayBlocks.map(b => (
                              <div key={b._idx} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <input type="time" value={b.start_time} onChange={e => setWeekData({ ...weekData, blocks: weekData.blocks.map((t, i) => i === b._idx ? { ...t, start_time: e.target.value } : t) })}
                                  style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                                <span style={{ color: "#9ca3af" }}>to</span>
                                <input type="time" value={b.end_time} onChange={e => setWeekData({ ...weekData, blocks: weekData.blocks.map((t, i) => i === b._idx ? { ...t, end_time: e.target.value } : t) })}
                                  style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                                <button onClick={() => setWeekData({ ...weekData, blocks: weekData.blocks.filter((_, i) => i !== b._idx) })} style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Remove</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {msg && <div style={{ padding: "10px 14px", background: msg.type === "success" ? "#dcfce7" : "#fee2e2", color: msg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{msg.text}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={saveWeek} disabled={saving} style={{ padding: "10px 24px", background: "#1B2B4B", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save Changes"}</button>
                  {weekData.status === "published" ? (
                    <button onClick={unpublishWeek} disabled={saving} style={{ padding: "10px 24px", border: "2px solid #C0392B", color: "#C0392B", borderRadius: 9, fontSize: 13, fontWeight: 700, background: "#fff", opacity: saving ? 0.6 : 1 }}>Unpublish</button>
                  ) : (
                    <button onClick={publishWeek} disabled={saving} style={{ padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1 }}>Publish Week</button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "template" && (
          <div>
            <div style={{ background: "#fff", border: "1px solid #E2DDD5", borderRadius: 14, padding: 24, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>This link's typical weekly pattern — used to pre-fill a week the first time you open it above. Editing this won't change weeks you've already set up.</div>
              {days.map((dayName, dayIdx) => {
                const dayBlocks = template.map((b, idx) => ({ ...b, _idx: idx })).filter(b => Number(b.day_of_week) === dayIdx);
                return (
                  <div key={dayIdx} style={{ padding: "14px 0", borderBottom: dayIdx < 6 ? "1px solid #f3f4f6" : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "#1B2B4B" }}>{dayName}</span>
                      <button onClick={() => setTemplate([...template, { day_of_week: dayIdx, start_time: "09:00", end_time: "17:00", is_active: 1 }])}
                        style={{ fontSize: 11, fontWeight: 700, color: "#2D7D72" }}>+ Add Block</button>
                    </div>
                    {dayBlocks.length === 0 ? <div style={{ fontSize: 12, color: "#9ca3af" }}>Unavailable</div> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {dayBlocks.map(b => (
                          <div key={b._idx} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <input type="checkbox" checked={!!b.is_active} onChange={e => setTemplate(template.map((t, i) => i === b._idx ? { ...t, is_active: e.target.checked ? 1 : 0 } : t))} style={{ width: 16, height: 16, cursor: "pointer" }} />
                            <input type="time" value={b.start_time} onChange={e => setTemplate(template.map((t, i) => i === b._idx ? { ...t, start_time: e.target.value } : t))}
                              style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                            <span style={{ color: "#9ca3af" }}>to</span>
                            <input type="time" value={b.end_time} onChange={e => setTemplate(template.map((t, i) => i === b._idx ? { ...t, end_time: e.target.value } : t))}
                              style={{ border: "1.5px solid #E2DDD5", borderRadius: 7, padding: "5px 8px", fontSize: 13 }} />
                            <button onClick={() => setTemplate(template.filter((_, i) => i !== b._idx))} style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Remove</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {msg && <div style={{ padding: "10px 14px", background: msg.type === "success" ? "#dcfce7" : "#fee2e2", color: msg.type === "success" ? "#166534" : "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{msg.text}</div>}
            <button onClick={saveTemplate} style={{ padding: "10px 24px", background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 13, fontWeight: 700 }}>Save Recurring Pattern</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── RESET PASSWORD PAGE ───────────────────────────────────────────────────────
function ResetPasswordPage({ token }) {
  const [status, setStatus] = useState("loading");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/forgot_password.php?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(res => { if (res.valid) { setStatus("form"); } else { setError(res.error || "This reset link is invalid or has expired."); setStatus("error"); } })
      .catch(() => { setError("Could not connect to server."); setStatus("error"); });
  }, []);

  const handleReset = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setError(""); setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/forgot_password.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reset", token, password })
      }).then(r => r.json());
      if (res.success) { setStatus("done"); setTimeout(() => { window.location.href = "https://ourmediationcase.com/"; }, 2000); }
      else { setError(res.error || "Reset failed."); }
    } catch { setError("Could not connect to server."); }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1B2B4B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 16 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }`}</style>
      <div style={{ background: "#fff", borderRadius: 16, padding: "44px 40px", width: "100%", maxWidth: 420, boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, color: "#1B2B4B" }}>Our<span style={{ color: "#2D7D72" }}>Mediation</span>Case</div>
          <div style={{ fontSize: 13, color: "#9B9490", marginTop: 6 }}>Set a new password</div>
        </div>
        {status === "loading" && <div style={{ textAlign: "center", color: "#9B9490" }}>Validating reset link…</div>}
        {status === "error" && <div style={{ padding: "14px 18px", background: "#FDEEEC", borderRadius: 10, color: "#C0392B", fontSize: 13, textAlign: "center" }}>{error}<br/><br/><a href="https://ourmediationcase.com/" style={{ color: "#2D7D72", fontWeight: 600 }}>Back to Login</a></div>}
        {status === "done" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Password updated!</div>
            <div style={{ fontSize: 13, color: "#9B9490" }}>Redirecting you to login…</div>
          </div>
        )}
        {status === "form" && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
              <Input label="New Password" value={password} onChange={setPassword} type="password" />
              <Input label="Confirm New Password" value={confirm} onChange={setConfirm} type="password" />
            </div>
            {error && <div style={{ marginBottom: 14, padding: "10px 14px", background: "#FDEEEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{error}</div>}
            <button onClick={handleReset} disabled={submitting} style={{ width: "100%", padding: 12, background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Updating…" : "Set New Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PUBLIC BOOKING PAGE ────────────────────────────────────────────────────
function PublicBookingPage() {
  const API = API_BASE + "/scheduling.php";
  const [inviteToken] = useState(() => new URLSearchParams(window.location.search).get("invite"));
  const [linkSlug] = useState(() => new URLSearchParams(window.location.search).get("link"));
  const [inviteError, setInviteError] = useState("");
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState("");
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [info, setInfo] = useState({ business_name: "", booking_message: "", slots: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (inviteToken) {
      fetch(API + "?action=get_invite&token=" + encodeURIComponent(inviteToken))
        .then(r => r.json())
        .then(res => {
          if (!res.valid) { setInviteError(res.error || "This link is no longer valid."); setTypesLoading(false); return; }
          setForm(f => ({ ...f, client_name: res.client_name || "", client_email: res.client_email || "" }));
          setTypesLoading(false);
          chooseType({ id: res.session_type_id, name: res.session_type_name, duration_minutes: res.duration_minutes });
        })
        .catch(() => { setInviteError("Could not load this link. Please try again later."); setTypesLoading(false); });
      return;
    }
    if (linkSlug) {
      fetch(API + "?action=get_link&slug=" + encodeURIComponent(linkSlug))
        .then(r => r.json())
        .then(res => {
          if (!res.valid) { setInviteError(res.error || "This link is not available."); setTypesLoading(false); return; }
          setTypesLoading(false);
          chooseType({ id: res.session_type_id, name: res.session_type_name, duration_minutes: res.duration_minutes });
        })
        .catch(() => { setInviteError("Could not load this link. Please try again later."); setTypesLoading(false); });
      return;
    }
    fetch(API + "?action=get_session_types")
      .then(r => r.json())
      .then(res => { setTypes(Array.isArray(res) ? res : []); setTypesLoading(false); })
      .catch(() => { setTypesError("Could not load session types. Please try again later."); setTypesLoading(false); });
  }, []);

  function chooseType(type) {
    setSelectedType(type);
    setSelectedDate(null);
    setSelectedSlot(null);
    setLoading(true);
    setLoadError("");
    const linkParam = linkSlug ? "&link=" + encodeURIComponent(linkSlug) : "";
    fetch(API + "?action=get_availability&session_type_id=" + type.id + linkParam)
      .then(r => r.json())
      .then(res => { setInfo(res); setLoading(false); })
      .catch(() => { setLoadError("Could not load availability. Please try again later."); setLoading(false); });
  }

  const slotsByDate = {};
  for (const s of info.slots || []) {
    (slotsByDate[s.date] = slotsByDate[s.date] || []).push(s);
  }
  const dates = Object.keys(slotsByDate).sort();

  function fmtDateLabel(d) {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  }
  function fmtTimeLabel(t) {
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 || 12;
    return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
  }

  async function submitBooking() {
    if (!form.client_name.trim() || !form.client_email.trim()) {
      setSubmitError("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(API + "?action=create_booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, session_type_id: selectedType.id, booking_date: selectedSlot.date, start_time: selectedSlot.start_time, invite_token: inviteToken || undefined, link: linkSlug || undefined }),
      }).then(r => r.json());
      if (res.success) {
        setDone(true);
      } else {
        setSubmitError(res.error || "Could not complete booking. Please try another time.");
      }
    } catch {
      setSubmitError("Could not connect to server.");
    }
    setSubmitting(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: "#F7F5F1", fontFamily: "'DM Sans', sans-serif", padding: "40px 16px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } button { cursor: pointer; border: none; background: none; font-family: inherit; } input, select, textarea { font-family: inherit; }`}</style>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, color: "#1B2B4B" }}>Our<span style={{ color: "#2D7D72" }}>Mediation</span>Case</div>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: "36px 32px", boxShadow: "0 24px 80px rgba(0,0,0,0.08)" }}>
          {typesLoading ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9B9490" }}>Loading…</div>
          ) : inviteError ? (
            <div style={{ padding: "14px 18px", background: "#FDEEEC", borderRadius: 10, color: "#C0392B", fontSize: 13, textAlign: "center" }}>{inviteError}</div>
          ) : typesError ? (
            <div style={{ padding: "14px 18px", background: "#FDEEEC", borderRadius: 10, color: "#C0392B", fontSize: 13, textAlign: "center" }}>{typesError}</div>
          ) : done ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 44, marginBottom: 16 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Request sent!</div>
              <div style={{ fontSize: 13, color: "#9B9490", lineHeight: 1.6 }}>
                We've received your request for {selectedType.name} on {fmtDateLabel(selectedSlot.date)} at {fmtTimeLabel(selectedSlot.start_time)}.<br/>
                You'll receive a confirmation once it's reviewed.
              </div>
            </div>
          ) : !selectedType ? (
            <>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: "#1B2B4B", marginBottom: 16 }}>Schedule a Session</h1>
              {types.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9B9490" }}>No session types are currently available. Please check back soon.</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", marginBottom: 10 }}>What kind of session?</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {types.map(t => (
                      <button key={t.id} onClick={() => chooseType(t)} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 18px", borderRadius: 10, border: "1.5px solid #E2DDD5", background: "#fff", textAlign: "left",
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#1B2B4B" }}>{t.name}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#2D7D72", background: "#E6F4F2", padding: "4px 10px", borderRadius: 20 }}>{t.duration_minutes} min</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: "#1B2B4B", marginBottom: 6 }}>
                {info.business_name || "Schedule a Session"}
              </h1>
              {info.booking_message && (
                <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 16 }}>{info.booking_message}</div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F7F5F0", borderRadius: 10, padding: "10px 14px", marginBottom: 24 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1B2B4B" }}>{selectedType.name} · {selectedType.duration_minutes} min</span>
                {!inviteToken && !linkSlug && <button onClick={() => { setSelectedType(null); setSelectedSlot(null); setSelectedDate(null); }} style={{ fontSize: 12, color: "#2D7D72", fontWeight: 700 }}>Change</button>}
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: 60, color: "#9B9490" }}>Loading available times…</div>
              ) : loadError ? (
                <div style={{ padding: "14px 18px", background: "#FDEEEC", borderRadius: 10, color: "#C0392B", fontSize: 13, textAlign: "center" }}>{loadError}</div>
              ) : dates.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9B9490" }}>No times are currently available for this session type. Please check back soon.</div>
              ) : !selectedSlot ? (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", marginBottom: 10 }}>1. Pick a date</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
                    {dates.map(d => (
                      <button key={d} onClick={() => setSelectedDate(d)} style={{
                        padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                        background: selectedDate === d ? "#1B2B4B" : "#f3f4f6", color: selectedDate === d ? "#fff" : "#4A5568",
                      }}>{fmtDateLabel(d)}</button>
                    ))}
                  </div>

                  {selectedDate && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1B2B4B", marginBottom: 10 }}>2. Pick a time</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {slotsByDate[selectedDate].map(s => (
                          <button key={s.start_time} onClick={() => setSelectedSlot(s)} style={{
                            padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, background: "#EEF0F8", color: "#1B2B4B",
                          }}>{fmtTimeLabel(s.start_time)}</button>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <button onClick={() => setSelectedSlot(null)} style={{ fontSize: 12, color: "#2D7D72", fontWeight: 700, marginBottom: 16 }}>← Choose a different time</button>
                  <div style={{ background: "#EEF0F8", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#1B2B4B", fontWeight: 600, marginBottom: 20 }}>
                    {fmtDateLabel(selectedSlot.date)} at {fmtTimeLabel(selectedSlot.start_time)}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
                    <Input label="Full Name" value={form.client_name} onChange={v => setForm({ ...form, client_name: v })} />
                    <Input label="Email" type="email" value={form.client_email} onChange={v => setForm({ ...form, client_email: v })} />
                    <Input label="Phone" value={form.client_phone} onChange={v => setForm({ ...form, client_phone: v })} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#9B9490", marginBottom: 5 }}>Notes (optional)</div>
                      <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                        style={{ width: "100%", border: "1px solid #E2DDD5", borderRadius: 7, padding: "8px 12px", fontSize: 13, outline: "none", resize: "vertical" }} />
                    </div>
                  </div>

                  {submitError && <div style={{ marginBottom: 14, padding: "10px 14px", background: "#FDEEEC", borderRadius: 8, fontSize: 13, color: "#C0392B" }}>{submitError}</div>}
                  <button onClick={submitBooking} disabled={submitting} style={{ width: "100%", padding: 12, background: "#2D7D72", color: "#fff", borderRadius: 9, fontSize: 14, fontWeight: 700, opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? "Requesting…" : "Request This Time"}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ROOT APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crm_user")); } catch { return null; }
  });
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("booking")) { setSessionChecked(true); return; }
    const onStaffPath = window.location.pathname.includes("staff-register");
    const hasRegisterToken = urlParams.get("staff-token") || urlParams.get("reset-token") || (onStaffPath ? urlParams.get("token") : urlParams.get("token"));
    if (hasRegisterToken) { setSessionChecked(true); return; }
    const checkSession = async () => {
      const stored = localStorage.getItem("crm_user");
      if (!stored) { setSessionChecked(true); return; }
      try {
        const res = await api.get("auth.php?action=validate");
        if (res && (res.valid === false || res.error)) {
          localStorage.removeItem("crm_user");
          setUser(null);
        }
      } catch(e) {}
      setSessionChecked(true);
    };
    checkSession();
    const interval = setInterval(checkSession, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const logout = async () => {
    try { await api.post("auth.php", { action: "logout" }); } catch(e) {}
    localStorage.removeItem("crm_user");
    setUser(null);
  };

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("booking")) return <PublicBookingPage />;

  const isStaffRegisterPath = window.location.pathname.includes("staff-register");
  const rawToken = urlParams.get("token");
  const staffRegisterToken = urlParams.get("staff-token") || (isStaffRegisterPath ? rawToken : null);
  const registerToken = !isStaffRegisterPath ? rawToken : null;
  const resetToken = urlParams.get("reset-token");

  if (!sessionChecked) return (
    <div style={{ minHeight:"100vh", background:"#1B2B4B", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:"#fff", fontSize:14, opacity:0.5 }}>Loading…</div>
    </div>
  );

  if (staffRegisterToken && !user) return <StaffRegisterPage token={staffRegisterToken} onSuccess={() => { window.location.href = "https://ourmediationcase.com/"; }} />;
  if (registerToken && !user) return <RegisterPage token={registerToken} onSuccess={() => { window.location.href = "https://ourmediationcase.com/"; }} />;
  if (resetToken && !user) return <ResetPasswordPage token={resetToken} />;
  if (!user) return <LoginScreen onLogin={u => setUser(u)} />;
  if (user.role === "client") return <ClientPortal user={user} onLogout={logout} />;

  return <MediatorApp user={user} onLogout={logout} />;
}
